import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ClinicianFollowupDialog from './ClinicianFollowupDialog';
import AddBaselineGoalDialog from './AddBaselineGoalDialog';
import ExerciseTrendDialog from './exerciseTrendDialog';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Grid, Typography, Card, CardHeader, Divider, List, ListItem, ListItemText, Box, TextField, Button, Chip, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip, Tabs, Tab, makeStyles, IconButton, MenuItem, CircularProgress, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Select, Avatar } from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import Drawer from '@material-ui/core/Drawer';
import SaveIcon from '@material-ui/icons/Save';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import BarChartIcon from '@material-ui/icons/BarChart';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import FormatListBulletedIcon from '@material-ui/icons/FormatListBulleted';
import FormatListNumberedIcon from '@material-ui/icons/FormatListNumbered';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import ExerciseLibrary from '../exerciseLibraryFolder/exerciseLibrary';
import AdviceLibrary, { getAdviceIcon } from '../exerciseLibraryFolder/adviceLibrary';
import ExerciseEditDialog from '../exerciseLibraryFolder/ExerciseEditDialog';
import ExerciseImage, { resolveExerciseImageUrl } from '../exerciseLibraryFolder/ExerciseImage';
import {
  formatAddedExerciseProgramJournalLine,
  formatReplacedExerciseProgramJournalLine,
  buildExerciseEditParams,
  clearLatestExerciseProgramUpdate,
  formatExercisePrescriptionSummary as formatSharedExercisePrescriptionSummary,
  formatExerciseScheduleLabel,
  normalizeEditedExerciseParams,
  normalizeScheduleType,
  persistExerciseProgramChanges,
  readLatestExerciseProgramUpdate,
  summarizeExerciseProgramChanges,
} from './exerciseProgramEditingUtils';
import { apiBaseUrl } from '../../../utilities/configureApiFetch';

const JOURNEY_CONTEXT_CACHE_TTL_MS = 5 * 60 * 1000;
const INITIAL_VISIBLE_JOURNAL_SESSION_COUNT = 10;
const INITIAL_JOURNAL_NOTE_COUNT = 5;
const journeyContextCache = new Map();
const JOURNEY_CONTEXT_RESTORE_ENABLED = false;
const journeyPhaseExerciseCache = new Map();
const journeyPatientNotesInFlight = new Map();

const useStyles = makeStyles(() => ({
  root: {
    borderRadius: 16,
    boxShadow: "0 6px 32px rgba(0,45,85,0.18)",
    background: "transparent",
    marginBottom: 0,
    padding: 0,
    marginTop: 0,
    position: 'relative',
  },
  actions: {
    justifyContent: 'flex-end'
  },
  tabRoot: {
      color: "#FFFFFF",
      backgroundColor: '#00e676'
    },
  sectionHeaderRow: {
    height: 'clamp(42px, calc(24px + 1.45vw), 56px)',
  },
  sectionHeaderCell: {
    fontWeight: 'bold',
    color: '#002D55',
    borderBottom: '2px solid #FF7F27',
    verticalAlign: 'middle',
    background: '#f7faff',
    height: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    minHeight: 'clamp(42px, calc(24px + 1.45vw), 56px)',
    paddingTop: 'clamp(8px, calc(1px + 0.55vw), 12px) !important',
    paddingBottom: 'clamp(8px, calc(1px + 0.55vw), 12px) !important',
    paddingLeft: 'clamp(10px, calc(2px + 0.625vw), 16px) !important',
    paddingRight: 'clamp(10px, calc(2px + 0.625vw), 16px) !important',
    boxSizing: 'border-box',
  },
  sectionHeaderTitle: {
    fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem) !important',
    fontWeight: 700,
    lineHeight: 1.2,
    color: '#002D55',
    paddingLeft: 5,
  },
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 6px) clamp(8px, 0.85vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '1px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(3px, 0.32vw, 5px) clamp(7px, 0.85vw, 15px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(70px, 5.2vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '1px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  journeyAdviceDialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
  },
  journeyAdviceDialogTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  journeyAdviceDialogTitleText: {
    flex: 1,
    color: '#002D55',
    fontWeight: 600,
    fontSize: 'clamp(0.95rem, calc(4px + 0.72vw), 1.15rem)',
  },
  journeyAdviceDialogCloseButton: {
    color: '#64748B',
    padding: 6,
  },
  journeyAdviceDialogContent: {
    padding: '24px !important',
    overflow: 'auto',
    backgroundColor: '#e6eaf0',
  },
  journeyAdviceDialogSectionLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  journeyAdviceDialogSectionPanel: {
    backgroundColor: '#f7faff',
    borderRadius: 18,
    padding: 16,
    marginBottom: 24,
    border: '1px solid #dce8f5',
  },
  journeyAdviceDialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'flex-end',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
  },
  journeyAdviceDialogConfirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.45vw, 6px) clamp(10px, 1.1vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 'clamp(84px, 8vw, 100px)',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  journeyAdviceDialogCancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: 'clamp(4px, 0.45vw, 6px) clamp(10px, 1.1vw, 16px)',
    fontSize: 'clamp(0.69rem, 0.75vw, 0.875rem)',
    textTransform: 'none',
    minWidth: 'clamp(84px, 8vw, 100px)',
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(245, 245, 245, 0.62)',
    zIndex: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  journeyKpiDialogPaper: {
    borderRadius: 18,
    overflow: 'hidden',
    backgroundColor: '#f7faff',
    border: '1px solid rgba(0,45,85,0.08)',
    boxShadow: '0 18px 44px rgba(0,45,85,0.16)',
  },
  journeyKpiDialogTitle: {
    padding: '18px 24px 10px 24px',
    backgroundColor: '#f7faff',
  },
  journeyKpiDialogContent: {
    padding: '16px 24px 20px 24px',
    backgroundColor: '#f7faff',
  },
  journeyKpiDialogGroup: {
    borderRadius: 16,
    border: '1px solid rgba(0,45,85,0.08)',
    backgroundColor: '#ffffff',
    boxShadow: '0 6px 18px rgba(0,45,85,0.06)',
    overflow: 'hidden',
  },
  journeyKpiDialogGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '14px 16px',
    backgroundColor: '#f7faff',
    borderBottom: '1px solid rgba(0,45,85,0.08)',
  },
  journeyKpiDialogRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    padding: '12px 16px',
    borderBottom: '1px solid rgba(207,216,220,0.72)',
  },
  journeyKpiDialogDraftRow: {
    background: '#fff7ee',
    borderBottom: '1px solid #ffd7a8',
    boxShadow: 'inset 4px 0 0 #fb741a',
  },
  journeyKpiDialogRowLast: {
    borderBottom: 'none',
  },
  journeyKpiDialogToggleButton: {
    minWidth: 42,
    width: 42,
    height: 42,
    borderRadius: 999,
    border: '1px solid rgba(0,45,85,0.12)',
    backgroundColor: '#002D55',
    color: '#fff',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  journeyKpiDialogToggleButtonPaused: {
    backgroundColor: '#FF7F27',
    '&:hover': {
      backgroundColor: '#002D55',
    },
  },
  journeyKpiDialogAddButton: {
    fontSize: 'clamp(0.6rem, 0.72vw, 0.75rem)',
    textTransform: 'none',
    borderColor: '#fb741a',
    color: '#fb741a',
    borderRadius: 8,
    minWidth: 0,
    height: 24,
    lineHeight: 1.4,
    fontWeight: 700,
  },
  journeyKpiDialogDraftChip: {
    backgroundColor: '#fff3e0',
    color: '#8a5b00',
    fontWeight: 700,
  },
  journeyKpiDialogDraftToggleButton: {
    backgroundColor: '#fff3e0',
    color: '#fb741a',
    borderColor: '#ffd7a8',
  },
  journeyKpiDialogInlineField: {
    background: '#fff',
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#fb741a',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fb741a',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fb741a',
      },
    },
  },
  journeyProgramModeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'clamp(4px, 0.39vw, 6px)',
  },
  journeyProgramTabsWrapper: {
    minHeight: 'auto',
    marginLeft: 'clamp(2px, 0.2vw, 4px)',
    '& .MuiTabs-scroller': {
      overflow: 'visible !important',
    },
  },
  journeyProgramTabsFlexContainer: {
    gap: 'clamp(6px, 0.52vw, 8px)',
  },
  journeyProgramTab: {
    textTransform: 'none',
    borderRadius: 8,
    minHeight: 'clamp(24px, 2vw, 30px)',
    minWidth: 'auto',
    padding: 'clamp(2px, 0.2vw, 4px) clamp(8px, 0.72vw, 12px)',
    color: '#7d8b99',
    fontWeight: 700,
    fontSize: 'clamp(0.7rem, 0.75vw, 0.86rem)',
    backgroundColor: 'transparent',
    '&.MuiTab-root': {
      minHeight: 'clamp(24px, 2vw, 30px)',
    },
    '&.Mui-selected': {
      color: '#002D55',
      backgroundColor: '#ffffff',
    },
    '&:hover': {
      color: '#FF7F27',
    },
  },
  journeyProgramTabSelected: {},
  journeyProgramTabsIndicator: {
    height: 2,
    borderRadius: 999,
    backgroundColor: '#fb741a',
  },
  orangeSelect: {
    background: '#FFFFFF',
    color: '#002D55',
    fontWeight: 500,
    fontSize: 'clamp(0.69rem, 1.04vw, 0.90rem)',
    border: '1px solid #ccc',
    borderRadius: 14,
    padding: 'clamp(2.3px, 0.29vw, 3.5px) clamp(12px, 0.15vw, 18px)',
    paddingLeft: '0px !important',
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 0.2s',
    '& .MuiSelect-select': {
      padding: 'clamp(0px, 0.29vw, 0px) clamp(12px, 0.15vw, 24px) !important',
      minHeight: 'unset !important',
      lineHeight: 'normal',
      background: 'transparent',
    },
    '&:focus': {
      borderColor: '#FF7F27',
      outline: '#FF7F27 solid 1px',
    },
    '&:hover': {
      borderColor: '#FF7F27',
    },
  },
  menuItem: {
    fontSize: 'clamp(0.69rem, 0.69vw, 0.93rem) !important',
    color: '#002D55',
  },
  journeyInlineAddPlaceholder: {
    borderRadius: 14,
    padding: 'clamp(12px, 1vw, 16px)',
    background: 'rgba(255,255,255,0.72)',
    border: '1px dashed #b9cde2',
    boxShadow: '0 2px 8px rgba(0,45,85,0.04)',
    minHeight: 'clamp(72px, 7vw, 92px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
    '&:hover': {
      borderColor: '#FF7F27',
      background: '#ffffff',
      boxShadow: '0 6px 18px rgba(0,45,85,0.08)',
    },
    '&:focus': {
      outline: '2px solid rgba(255,127,39,0.35)',
      outlineOffset: 2,
      borderColor: '#FF7F27',
      background: '#ffffff',
    },
  },
  journeyInlineAddPlaceholderPlus: {
    width: 30,
    height: 30,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff3e8',
    color: '#fb741a',
    fontSize: '1.2rem',
    fontWeight: 700,
    margin: '0 auto 8px',
  },
  journeyInlineAddPlaceholderLabel: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)',
  },
  journeyInlineAddPlaceholderHint: {
    color: '#7a8ea5',
    fontSize: 'clamp(0.62rem, 0.65vw, 0.76rem)',
    marginTop: 4,
  },
}));

// Helpers for rich text journal editor
const textToHtml = (text) => {
  if (!text) return '';
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
};

const isHtmlEmpty = (html) => {
  if (!html) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
};

const decodeHtmlEntities = (value) => {
  let decoded = (value || '').toString();

  for (let iteration = 0; iteration < 5; iteration += 1) {
    const next = decoded
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
    if (next === decoded) {
      break;
    }
    decoded = next;
  }

  return decoded;
};

const htmlToText = (html) => {
  if (!html) return '';
  return decodeHtmlEntities(
    html.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '')
  );
};

const sanitizeBasicHtml = (html) => {
  if (!html) return '';
  // Preserve style attributes on hr and div (for separators and system note styling)
  return html
    .replace(/<(hr|div)\b([^>]*)>/gi, (match, tag, attrs) => {
      const styleMatch = attrs.match(/style="([^"]*)"/i);
      return styleMatch ? `<${tag} style="${styleMatch[1]}">` : `<${tag}>`;
    })
    .replace(/<(b|i|u|strong|em|h[1-3]|ul|ol|li|p)\b[^>]*>/gi, '<$1>')
    .replace(/<(?!\/?(?:b|i|u|strong|em|br|h[1-3]|ul|ol|li|p|hr|div)\b)[^>]+>/gi, '');
};

const JOURNAL_SYMPTOM_HEADING = '<p><strong>Symptom development since last consultation:</strong></p>';
const JOURNAL_ADJUSTMENTS_HEADING = '<p><strong>Treatment and adjustments made today:</strong></p>';
const JOURNAL_BASELINE_GOAL_HEADING = '<p><strong>Baseline / Goal adjustment:</strong></p>';
const JOURNAL_PLAN_HEADING = '<p><strong>Plan until next time:</strong></p>';
const JOURNAL_PATIENT_ACCEPT_HEADING = '<p><strong>Patient accept:</strong></p>';
const JOURNAL_SYMPTOM_HEADING_TEXT = 'Symptom development since last consultation:';
const JOURNAL_ADJUSTMENTS_HEADING_TEXT = 'Treatment and adjustments made today:';
const JOURNAL_PLAN_HEADING_TEXT = 'Plan until next time:';
const JOURNAL_PATIENT_ACCEPT_HEADING_TEXT = 'Patient accept:';
const JOURNAL_CURRENT_PROGRAM_HEADING_TEXT = 'Current exercise program & symptom response:';
const JOURNAL_NO_ADJUSTMENTS_TEXT = 'No adjustments.';
const JOURNAL_PATIENT_ACCEPT_DEFAULT_TEXT = 'Patient accepts the proposed plan until the next follow-up.';
const JOURNAL_SYMPTOM_GENERATED_START = '<div style="display:none">vp-symptom-generated:start</div>';
const JOURNAL_SYMPTOM_GENERATED_END = '<div style="display:none">vp-symptom-generated:end</div>';
const JOURNAL_PLAN_GENERATED_START = '<div style="display:none">vp-plan-generated:start</div>';
const JOURNAL_PLAN_GENERATED_END = '<div style="display:none">vp-plan-generated:end</div>';
const JOURNAL_SECTION_SEPARATOR_HTML = '<hr style="border:none;border-top:1px solid #cfd8dc;margin:8px 0;">';

const escapeHtml = (value) => (value || '')
  .toString()
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const escapeRegExp = (value) => (value || '').toString().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const normalizeClipboardText = (value) => (value || '')
  .toString()
  .replace(/\r/g, '')
  .replace(/vp-(?:symptom|plan)-generated:(?:start|end)/gi, '')
  .replace(/\u00a0/g, ' ')
  .replace(/[ \t]+\n/g, '\n')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

const htmlToClipboardText = (html) => {
  if (!html) return '';
  return normalizeClipboardText(
    html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<li\b[^>]*>/gi, '• ')
      .replace(/<\/(?:p|div|h[1-6]|ul|ol|li)>/gi, '\n')
      .replace(/<(?:p|div|h[1-6]|ul|ol)\b[^>]*>/gi, '')
      .replace(/<[^>]*>/g, '')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ')
  );
};

const extractJournalSectionText = (noteHtml, headingText) => {
  if (!noteHtml || !headingText) {
    return '';
  }

  const sectionPattern = new RegExp(
    `<p><strong>\\s*${escapeRegExp(headingText)}\\s*<\\/strong><\\/p>([\\s\\S]*?)(?=(<hr\\b[^>]*>|<p><strong>|$))`,
    'i'
  );
  const match = noteHtml.match(sectionPattern);
  if (!match) {
    return '';
  }

  return htmlToClipboardText(match[1] || '');
};

const DIFFICULTY_MARK_EPSILON = 0.75;

const normalizeDifficultyText = (value) => (value || '')
  .toString()
  .replace(/[_.-]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const isNearDifficultyMark = (value, target) => Number.isFinite(value) && Math.abs(value - target) <= DIFFICULTY_MARK_EPSILON;

const getDifficultyReasonKey = (difficulty) => {
  const diffRaw = normalizeDifficultyText(difficulty);
  const diffNum = Number.parseFloat(difficulty);

  if (!diffRaw && !Number.isFinite(diffNum)) {
    return null;
  }

  if (diffRaw === 'too easy' || diffRaw === 'easy' || isNearDifficultyMark(diffNum, 33.33)) {
    return 'too_easy';
  }

  if (diffRaw === 'too hard' || diffRaw === 'hard' || isNearDifficultyMark(diffNum, 66.66)) {
    return 'too_hard';
  }

  if (diffRaw.includes('lack') || diffRaw.includes('equipment') || isNearDifficultyMark(diffNum, 100)) {
    return 'no_equipment';
  }

  if (diffRaw.startsWith('good') || isNearDifficultyMark(diffNum, 0) || (Number.isFinite(diffNum) && diffNum < 33.33)) {
    return 'good';
  }

  return null;
};

const getDifficultyDisplayLabel = (difficulty) => {
  const reasonKey = getDifficultyReasonKey(difficulty);

  if (reasonKey === 'too_easy') {
    return 'Too easy.';
  }

  if (reasonKey === 'too_hard') {
    return 'Too hard.';
  }

  if (reasonKey === 'no_equipment') {
    return 'Lack of equipment.';
  }

  if (reasonKey === 'good') {
    return 'Good.';
  }

  return difficulty ?? '-';
};

const SYSTEM_GENERATED_MARKER = '*';
const SYSTEM_GENERATED_HINT = '* system generated';

/**
 * REGULATORY COMPLIANCE ANCHOR: CLASS I / NON-MDR SAFEGUARD
 *
 * This legacy mode formatter preserves historical provenance for old
 * adjustment rows containing suggest_only / pending semantics. It is a
 * read-only compatibility path and does not reactivate decision support.
 */
const getLoggedAdjustmentModeAtTime = (adjustment) => {
  const explicitMode = adjustment?.mode_at_time || adjustment?.modeAtTime || null;
  if (explicitMode) {
    return explicitMode;
  }

  if (adjustment?.status === 'pending' || adjustment?.status === 'rejected') {
    return 'suggest_only';
  }

  if (adjustment?.status === 'applied') {
    return adjustment?.resolved_by != null || adjustment?.resolvedBy != null
      ? 'suggest_only'
      : 'auto_replace';
  }

  return null;
};

const getLoggedAdjustmentModeSourceAtTime = (adjustment) => {
  return adjustment?.mode_source_at_time || adjustment?.modeSourceAtTime || null;
};

const isLoggedAdjustmentSystemGenerated = (adjustment) => {
  return adjustment?.status === 'applied' && getLoggedAdjustmentModeAtTime(adjustment) === 'auto_replace';
};

const getLoggedAdjustmentReasonText = (adjustment) => {
  const reasonKey = adjustment?.reason || null;

  if (reasonKey === 'too_painful') {
    const painValue = Number.parseFloat(adjustment?.pain_level ?? adjustment?.painLevel);
    if (Number.isFinite(painValue)) {
      const roundedPain = Math.round(painValue * 10) / 10;
      return `too painful (${roundedPain}/100)`;
    }
    return 'too painful';
  }

  if (reasonKey === 'too_hard') {
    return 'too hard';
  }

  if (reasonKey === 'too_easy') {
    return 'too easy';
  }

  if (reasonKey === 'no_equipment') {
    return 'lack of equipment';
  }

  return String(reasonKey || 'adjusted').replace(/_/g, ' ');
};

const getIssueActionLabel = (reasonKey) => {
  if (!reasonKey) {
    return 'adjusted';
  }
  return reasonKey === 'too_easy' ? 'progressed' : 'regressed';
};

const joinReasonTexts = (reasonTexts = []) => {
  const filtered = reasonTexts.filter(Boolean);
  if (filtered.length === 0) {
    return '';
  }
  if (filtered.length === 1) {
    return filtered[0];
  }
  if (filtered.length === 2) {
    return `${filtered[0]} and ${filtered[1]}`;
  }
  return `${filtered.slice(0, -1).join(', ')}, and ${filtered[filtered.length - 1]}`;
};

const getFeedbackReasonDetails = ({ pain, difficulty }) => {
  const details = [];

  if (pain != null && pain > 51) {
    details.push({ reasonKey: 'too_painful', reason: `too painful (${pain}/100)` });
  }

  const difficultyReasonKey = getDifficultyReasonKey(difficulty);
  if (difficultyReasonKey === 'too_hard') {
    details.push({ reasonKey: 'too_hard', reason: 'too hard' });
  } else if (difficultyReasonKey === 'too_easy') {
    details.push({ reasonKey: 'too_easy', reason: 'too easy' });
  } else if (difficultyReasonKey === 'no_equipment') {
    details.push({ reasonKey: 'no_equipment', reason: 'lack of equipment' });
  }

  return details;
};

const formatIssueAdjustmentLine = (issue, selectedReplacement, options = {}) => {
  const includeMarker = options.includeMarker !== false;
  const marker = includeMarker && issue.isSystemGenerated ? SYSTEM_GENERATED_MARKER : '';
  const issueName = `[${issue.name}]`;

  if (!selectedReplacement) {
    return `${issueName}: ${issue.reason}${marker}`;
  }

  return `${issueName}: ${issue.reason} (${getIssueActionLabel(issue.reasonKey || (Array.isArray(issue.reasonKeys) ? issue.reasonKeys[0] : null))}) --> [${selectedReplacement}]${marker}`;
};

const getProgramAdjustmentDisplayIdentity = (line = '') => {
  const normalizedLine = (line || '').toString().trim();
  if (!normalizedLine) {
    return null;
  }

  const standardIdentity = getProgramAdjustmentLineIdentity(normalizedLine.replace(/\*$/, ''));
  if (standardIdentity) {
    return standardIdentity;
  }

  const issueMatch = normalizedLine.match(/^\[([^\]]+)\]:.*-->\s*\[([^\]]+)\]\*?$/i);
  if (issueMatch) {
    return `replaced:${issueMatch[1].trim().toLowerCase()}`;
  }

  return normalizeProgramSummaryLineKey(normalizedLine.replace(/\*$/, ''));
};

const dedupeProgramAdjustmentDisplayLines = (lines = []) => {
  const seen = new Set();
  return (lines || []).filter(Boolean).filter((line) => {
    const identity = getProgramAdjustmentDisplayIdentity(line);
    if (!identity) {
      return true;
    }
    if (seen.has(identity)) {
      return false;
    }
    seen.add(identity);
    return true;
  });
};

const mergeAdjustmentIssues = (baseIssue, loggedIssue) => ({
  ...baseIssue,
  ...loggedIssue,
  name: baseIssue?.name || loggedIssue?.name,
  reason: baseIssue?.reason || loggedIssue?.reason,
  reasonKey: baseIssue?.reasonKey || loggedIssue?.reasonKey,
  reasonKeys: Array.isArray(baseIssue?.reasonKeys) && baseIssue.reasonKeys.length > 0
    ? baseIssue.reasonKeys
    : (Array.isArray(loggedIssue?.reasonKeys) ? loggedIssue.reasonKeys : []),
  feedbackId: baseIssue?.feedbackId || loggedIssue?.feedbackId,
  originalIdentifier: baseIssue?.originalIdentifier || loggedIssue?.originalIdentifier,
  issueKey: baseIssue?.issueKey || loggedIssue?.issueKey,
  replacement: loggedIssue?.replacement || baseIssue?.replacement,
  replacementIdentifier: loggedIssue?.replacementIdentifier || baseIssue?.replacementIdentifier,
  adjusted: Boolean(baseIssue?.adjusted || loggedIssue?.adjusted),
  pending: Boolean(baseIssue?.pending || loggedIssue?.pending),
  adjId: loggedIssue?.adjId || baseIssue?.adjId,
  modeAtTime: loggedIssue?.modeAtTime || baseIssue?.modeAtTime,
  modeSourceAtTime: loggedIssue?.modeSourceAtTime || baseIssue?.modeSourceAtTime,
  isSystemGenerated: Boolean(baseIssue?.isSystemGenerated || loggedIssue?.isSystemGenerated),
  sessionId: baseIssue?.sessionId || loggedIssue?.sessionId,
});

const PROGRAM_ADJUSTMENT_TOKEN_REGEX = /(\[[^\]]+\]|too painful \(\d+\/100\)|too painful|too hard|too easy|lack of equipment|Replaced|Updated|Removed|Added|progressed|regressed|schedule|dose type|dose|time|pause|sets|with \d+ days between sessions|-->|\*)/gi;

const getProgramAdjustmentTokenStyle = (token) => {
  const normalized = (token || '').toLowerCase();

  if (/^\[[^\]]+\]$/.test(token)) {
    return {
      color: '#002D55',
      fontWeight: 700,
    };
  }

  if (['replaced', 'updated', 'removed', 'added'].includes(normalized)) {
    return { color: '#1565c0', fontWeight: 700 };
  }

  if (['progressed', 'regressed'].includes(normalized)) {
    return { color: '#2e7d32', fontWeight: 700 };
  }

  if (normalized.startsWith('too painful') || normalized === 'too hard' || normalized === 'too easy' || normalized === 'lack of equipment') {
    return { color: '#c45100', fontWeight: 700 };
  }

  if (normalized === 'schedule' || normalized === 'dose type' || normalized === 'dose' || normalized === 'time' || normalized === 'pause' || normalized === 'sets') {
    return { color: '#6a1b9a', fontWeight: 700, fontStyle: 'italic' };
  }

  if (normalized.startsWith('with ') && normalized.includes('days between sessions')) {
    return { color: '#00838f', fontWeight: 700 };
  }

  if (token === '-->') {
    return { color: '#546e7a', fontWeight: 700 };
  }

  if (token === '*') {
    return { color: '#78909c', fontWeight: 700 };
  }

  return null;
};

const renderProgramAdjustmentText = (line, keyPrefix) => {
  const parts = String(line || '').split(PROGRAM_ADJUSTMENT_TOKEN_REGEX).filter(part => part !== '');

  return parts.map((part, index) => {
    const tokenStyle = getProgramAdjustmentTokenStyle(part);
    if (tokenStyle) {
      return (
        <span key={`${keyPrefix}-token-${index}`} style={tokenStyle}>
          {part}
        </span>
      );
    }
    return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
  });
};

const buildPlanText = (trend, issueList = [], options = {}) => {
  const adjustedIssues = issueList.filter(issue => issue.adjusted && issue.replacement);
  const pendingIssues = issueList.filter(issue => issue.pending && issue.replacement);
  const hasProgramChanges = Array.isArray(options.programLines)
    ? options.programLines.some(Boolean)
    : Boolean(options.hasProgramChanges);

  if (pendingIssues.length > 0) {
    return 'Continue the current program within tolerance and confirm the suggested exercise adjustment if symptoms remain provoked.';
  }
  if (adjustedIssues.length > 0) {
    if (adjustedIssues.length === 1) {
      return `Continue the updated program, including ${adjustedIssues[0].replacement}, and monitor symptom response until next follow-up.`;
    }
    return 'Continue the updated exercise program and monitor response to the replacements until next follow-up.';
  }
  if (hasProgramChanges) {
    return 'Continue the updated exercise program and monitor response until next follow-up.';
  }
  if (issueList.length > 0) {
    return 'Continue exercises within tolerance and review the provoking exercise response at the next follow-up.';
  }
  if (trend === 'worsened') {
    return 'Keep the program symptom-guided and reassess progression at the next follow-up.';
  }
  if (trend === 'mixed') {
    return 'Continue the current plan and monitor symptom variation until next follow-up.';
  }
  return 'Continue as planned.';
};

const buildAdjustmentsSectionHtml = (lines = []) => {
  if (!Array.isArray(lines) || lines.length === 0) {
    return `<p>${JOURNAL_NO_ADJUSTMENTS_TEXT}</p>`;
  }
  return `<p>${lines.map(line => escapeHtml(line)).join('<br>')}</p>`;
};

const normalizeAdjustmentLine = (line) => (line || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();

const removePlaceholderLine = (lines = [], placeholderText = JOURNAL_NO_ADJUSTMENTS_TEXT) => {
  const normalizedPlaceholder = normalizeAdjustmentLine(placeholderText);
  const filteredLines = (lines || []).filter(Boolean);

  if (filteredLines.length <= 1) {
    return filteredLines;
  }

  return filteredLines.filter(line => normalizeAdjustmentLine(line) !== normalizedPlaceholder);
};

const extractLeadingParagraphHtmlBlocks = (html = '') => {
  const blocks = [];
  let remainingHtml = html || '';

  while (true) {
    const match = remainingHtml.match(/^\s*(<p>[\s\S]*?<\/p>)/i);
    if (!match) {
      break;
    }
    blocks.push(match[1]);
    remainingHtml = remainingHtml.slice(match[0].length);
  }

  return blocks;
};

const isGeneratedSymptomSummaryText = (text = '') => {
  const normalizedText = normalizeAdjustmentLine(text);
  return normalizedText === 'patient has seen an improvement of symptoms and function.'
    || normalizedText === 'patient has seen a worsening of symptoms and function.'
    || normalizedText === 'patient has had a mixed change in symptoms and function.'
    || normalizedText === 'no clear change in symptoms or function since last consultation.';
};

const isGeneratedSymptomDetailText = (text = '') => {
  const normalizedText = normalizeAdjustmentLine(text);
  return normalizedText === 'no follow-up assessment data available.'
    || normalizedText.includes('→')
    || normalizedText.includes(': no change (');
};

const isGeneratedSymptomConsultationMetricText = (text = '') => {
  const normalizedText = normalizeAdjustmentLine(text);
  return /^patient has completed \d+ sessions since last consultation\.?$/i.test(normalizedText)
    || normalizedText.startsWith('average pain per exercise:');
};

const isGeneratedSymptomResponseText = (text = '') => {
  const normalizedText = normalizeAdjustmentLine(text);
  return normalizedText === 'exercise response ok.'
    || normalizedText === 'exercise response ok, except for:'
    || normalizedText === 'exercise response ok since last consultation, except for:'
    || normalizedText.startsWith('exercise response ok, except for ')
    || normalizedText.startsWith('exercise response ok since last consultation, except for ')
    || normalizedText === 'exercise response since last consultation:'
    || normalizedText === 'exercise response since last consultation: ok'
    || normalizedText === 'no exercises flagged as too hard, too easy, too painful, or lack of equipment since last consultation.'
    || normalizedText.startsWith('- ');
};

const buildGeneratedSymptomHtml = (paragraphs = []) => {
  // Do NOT wrap in hidden-div markers: the markers live inside contentEditable, causing
  // user-typed paragraphs to land between them and get stripped on the next sync.
  // stripExistingGeneratedSymptomHtml has a text-based fallback that handles marker-free content.
  return Array.isArray(paragraphs) ? paragraphs.filter(Boolean).join('') : '';
};

const stripExistingGeneratedSymptomHtml = (bodyHtml = '') => {
  if (!bodyHtml) {
    return '';
  }

  const markerPattern = /(?:<!--vp-symptom-generated:start-->|<div style="display:none">vp-symptom-generated:start<\/div>)([\s\S]*?)(?:<!--vp-symptom-generated:end-->|<div style="display:none">vp-symptom-generated:end<\/div>)/i;
  if (markerPattern.test(bodyHtml)) {
    return bodyHtml.replace(markerPattern, '').trim();
  }

  const paragraphBlocks = extractLeadingParagraphHtmlBlocks(bodyHtml);
  if (paragraphBlocks.length < 2) {
    return bodyHtml.trim();
  }

  let stripCount = 0;

  let nextParagraphIndex = 0;
  const firstParagraphText = htmlToText(paragraphBlocks[nextParagraphIndex]).trim();
  const startsWithGeneratedSummary = isGeneratedSymptomSummaryText(firstParagraphText);
  const startsWithGeneratedDetail = isGeneratedSymptomDetailText(firstParagraphText);

  if (!startsWithGeneratedSummary && !startsWithGeneratedDetail) {
    return bodyHtml.trim();
  }

  stripCount += 1;
  nextParagraphIndex += 1;

  while (paragraphBlocks[nextParagraphIndex] && !htmlToText(paragraphBlocks[nextParagraphIndex]).trim()) {
    stripCount += 1;
    nextParagraphIndex += 1;
  }

  if (startsWithGeneratedSummary
    && paragraphBlocks[nextParagraphIndex]
    && isGeneratedSymptomDetailText(htmlToText(paragraphBlocks[nextParagraphIndex]).trim())) {
    stripCount += 1;
    nextParagraphIndex += 1;

    while (paragraphBlocks[nextParagraphIndex] && !htmlToText(paragraphBlocks[nextParagraphIndex]).trim()) {
      stripCount += 1;
      nextParagraphIndex += 1;
    }
  }

  let consultationMetricCount = 0;
  while (
    paragraphBlocks[nextParagraphIndex]
    && isGeneratedSymptomConsultationMetricText(htmlToText(paragraphBlocks[nextParagraphIndex]).trim())
  ) {
    stripCount += 1;
    nextParagraphIndex += 1;
    consultationMetricCount += 1;
  }

  const responseParagraph = paragraphBlocks[nextParagraphIndex];
  let hasGeneratedResponse = false;
  if (responseParagraph && isGeneratedSymptomResponseText(htmlToText(responseParagraph).trim())) {
    stripCount += 1;
    hasGeneratedResponse = true;

    const normalizedResponseParagraph = normalizeAdjustmentLine(htmlToText(responseParagraph).trim());
    if ((normalizedResponseParagraph === 'exercise response ok, except for:'
      || normalizedResponseParagraph === 'exercise response ok since last consultation, except for:'
      || normalizedResponseParagraph === 'exercise response since last consultation:')
      && paragraphBlocks[nextParagraphIndex + 1]
      && isGeneratedSymptomResponseText(htmlToText(paragraphBlocks[nextParagraphIndex + 1]).trim())) {
      stripCount += 1;
    }
  }

  if (consultationMetricCount === 0 && !hasGeneratedResponse) {
    return bodyHtml.trim();
  }

  let remainingHtml = bodyHtml;
  for (let index = 0; index < stripCount; index += 1) {
    remainingHtml = remainingHtml.replace(/^\s*<p>[\s\S]*?<\/p>/i, '');
  }

  return remainingHtml.trim();
};

const mergeSymptomDevelopmentSection = (noteHtml, symptomHtml) => {
  if (!symptomHtml) {
    return noteHtml || '';
  }

  const sectionPattern = /<p><strong>\s*Symptom development since last consultation:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|$))/i;
  const match = (noteHtml || '').match(sectionPattern);

  if (!match) {
    if (!noteHtml || !noteHtml.trim()) {
      return `${JOURNAL_SYMPTOM_HEADING}${symptomHtml}`;
    }

    const separator = /<hr\b[^>]*>/i.test(noteHtml)
      ? ''
      : '<hr style="border:none;border-top:1px solid #cfd8dc;margin:8px 0;">';

    return `${JOURNAL_SYMPTOM_HEADING}${symptomHtml}${separator}${noteHtml}`;
  }

  const existingBody = match[1] || '';
  const manualBody = stripExistingGeneratedSymptomHtml(existingBody);
  const nextBody = `${symptomHtml}${manualBody}`;

  if (nextBody === existingBody) {
    return noteHtml || '';
  }

  return (noteHtml || '').replace(sectionPattern, `${JOURNAL_SYMPTOM_HEADING}${nextBody}`);
};

const extractAdjustmentLinesFromHtml = (html = '') => {
  return (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .split(/\n+/)
    .map(line => decodeHtmlEntities(line).trim())
    .filter(Boolean);
};

const getProgramAdjustmentLineIdentity = (line = '') => {
  const normalizedLine = (line || '').toString().trim();
  const issueReplacementMatch = normalizedLine.match(/^\[([^\]]+)\]:.*-->\s*\[([^\]]+)\]\*?$/i);
  if (issueReplacementMatch) {
    return `replaced:${issueReplacementMatch[1].trim().toLowerCase()}`;
  }

  const exerciseMatch = normalizedLine.match(/\[([^\]]+)\]/);
  if (!exerciseMatch) {
    return null;
  }

  const label = exerciseMatch[1].trim().toLowerCase();

  if (/^Updated\s+\[[^\]]+\]/i.test(normalizedLine)) {
    return `updated:${label}`;
  }
  if (/^Replaced\s+\[[^\]]+\]/i.test(normalizedLine)) {
    return `replaced:${label}`;
  }
  if (/^Removed\s+\[[^\]]+\]/i.test(normalizedLine)) {
    return `removed:${label}`;
  }
  if (/^Added\s+\[[^\]]+\]/i.test(normalizedLine)) {
    return `added:${label}`;
  }

  return null;
};

const isAdviceAdjustmentLine = (line = '') => /^(Updated|Added|Removed)\s+advice\s+\[[^\]]+\]/i.test((line || '').toString().trim());

const isGeneratedPlanText = (text = '') => {
  const normalizedText = normalizeAdjustmentLine(text);

  if (!normalizedText) {
    return false;
  }

  return normalizedText === 'continue the current program within tolerance and confirm the suggested exercise adjustment if symptoms remain provoked.'
    || normalizedText.startsWith('continue the updated program, including ')
    || normalizedText === 'continue the updated exercise program and monitor response to the replacements until next follow-up.'
    || normalizedText === 'continue the updated exercise program and monitor response until next follow-up.'
    || normalizedText === 'continue exercises within tolerance and review the provoking exercise response at the next follow-up.'
    || normalizedText === 'keep the program symptom-guided and reassess progression at the next follow-up.'
    || normalizedText === 'continue the current plan and monitor symptom variation until next follow-up.'
    || normalizedText === 'continue as planned.';
};

// Do NOT wrap in hidden-div markers: they live inside contentEditable and the browser can
// insert user-typed paragraphs between start/end markers, which get stripped on next sync.
// stripExistingGeneratedPlanHtml has a text-based fallback that handles marker-free content.
const buildGeneratedPlanHtml = (planText = '') => `<p>${escapeHtml(planText)}</p>`;

const buildStaticJournalParagraphHtml = (text = '') => `<p>${escapeHtml(text)}</p>`;

const getGeneratedPlanPrefix = (text = '') => {
  const trimmedText = String(text || '').trim();
  if (!trimmedText) {
    return '';
  }

  const patterns = [
    /^Continue the current program within tolerance and confirm the suggested exercise adjustment if symptoms remain provoked\./i,
    /^Continue the updated program, including .*? until next follow-up\./i,
    /^Continue the updated exercise program and monitor response to the replacements until next follow-up\./i,
    /^Continue the updated exercise program and monitor response until next follow-up\./i,
    /^Continue exercises within tolerance and review the provoking exercise response at the next follow-up\./i,
    /^Keep the program symptom-guided and reassess progression at the next follow-up\./i,
    /^Continue the current plan and monitor symptom variation until next follow-up\./i,
    /^Continue as planned\./i,
  ];

  for (const pattern of patterns) {
    const match = trimmedText.match(pattern);
    if (match) {
      return match[0].trim();
    }
  }

  return '';
};

const extractLeadingParagraphEditParts = (bodyHtml = '', getPrefix) => {
  const leadingParagraphPattern = /^\s*<p>([\s\S]*?)<\/p>/i;
  const leadingParagraphMatch = String(bodyHtml || '').match(leadingParagraphPattern);

  if (!leadingParagraphMatch) {
    return { matchedPrefix: false, inlineSuffix: '', manualBody: String(bodyHtml || '').trim() };
  }

  const leadingParagraphHtml = leadingParagraphMatch[0];
  const leadingParagraphText = htmlToText(leadingParagraphHtml).trim();
  const prefixText = typeof getPrefix === 'function' ? getPrefix(leadingParagraphText) : '';

  if (!prefixText) {
    return { matchedPrefix: false, inlineSuffix: '', manualBody: String(bodyHtml || '').trim() };
  }

  return {
    matchedPrefix: true,
    inlineSuffix: leadingParagraphText.slice(prefixText.length).trim(),
    manualBody: String(bodyHtml || '').replace(leadingParagraphPattern, '').trim(),
  };
};

const insertSectionBeforeTrailingSections = (noteHtml, sectionHtml, trailingPatterns = []) => {
  const currentNoteHtml = noteHtml || '';

  if (!currentNoteHtml.trim()) {
    return sectionHtml;
  }

  let insertIndex = -1;
  trailingPatterns.forEach((pattern) => {
    const match = currentNoteHtml.match(pattern);
    if (match && typeof match.index === 'number') {
      insertIndex = insertIndex === -1 ? match.index : Math.min(insertIndex, match.index);
    }
  });

  if (insertIndex === -1) {
    const suffixSeparator = /<hr\b[^>]*>\s*$/i.test(currentNoteHtml) ? '' : JOURNAL_SECTION_SEPARATOR_HTML;
    return `${currentNoteHtml}${suffixSeparator}${sectionHtml}`;
  }

  const before = currentNoteHtml.slice(0, insertIndex).trimEnd();
  const after = currentNoteHtml.slice(insertIndex).trimStart();
  const separatorBefore = before && !/<hr\b[^>]*>\s*$/i.test(before) ? JOURNAL_SECTION_SEPARATOR_HTML : '';
  const separatorAfter = after && !/^<hr\b[^>]*>/i.test(after) ? JOURNAL_SECTION_SEPARATOR_HTML : '';

  return `${before}${separatorBefore}${sectionHtml}${separatorAfter}${after}`;
};

const removeSectionWithAdjacentSeparator = (noteHtml, sectionPattern) => {
  const currentNoteHtml = noteHtml || '';
  const match = currentNoteHtml.match(sectionPattern);

  if (!match || typeof match.index !== 'number') {
    return currentNoteHtml;
  }

  const sectionStart = match.index;
  const sectionEnd = sectionStart + match[0].length;
  const before = currentNoteHtml.slice(0, sectionStart);
  const after = currentNoteHtml.slice(sectionEnd);

  const trailingSeparatorMatch = before.match(/\s*<hr\b[^>]*>\s*$/i);
  if (trailingSeparatorMatch) {
    return `${before.slice(0, before.length - trailingSeparatorMatch[0].length)}${after}`.trim();
  }

  const leadingSeparatorMatch = after.match(/^\s*<hr\b[^>]*>\s*/i);
  if (leadingSeparatorMatch) {
    return `${before}${after.slice(leadingSeparatorMatch[0].length)}`.trim();
  }

  return `${before}${after}`.trim();
};

const stripExistingGeneratedPlanHtml = (bodyHtml = '') => {
  if (!bodyHtml) {
    return { matchedPrefix: false, inlineSuffix: '', manualBody: '' };
  }

  const markerPattern = /(?:<!--vp-plan-generated:start-->|<div style="display:none">vp-plan-generated:start<\/div>)([\s\S]*?)(?:<!--vp-plan-generated:end-->|<div style="display:none">vp-plan-generated:end<\/div>)/i;
  const cleanedBodyHtml = markerPattern.test(bodyHtml)
    ? bodyHtml.replace(markerPattern, '').trim()
    : bodyHtml.trim();

  return extractLeadingParagraphEditParts(cleanedBodyHtml, getGeneratedPlanPrefix);
};

const mergePlanUntilNextTimeSection = (noteHtml, planText) => {
  if (!planText) {
    return noteHtml || '';
  }

  const sectionPattern = /<p><strong>\s*Plan until next time:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|$))/i;
  const patientAcceptPattern = /<p><strong>\s*Patient accept:\s*<\/strong><\/p>[\s\S]*?(?=(<hr\b[^>]*>|$))/i;
  const generatedPlanHtml = buildGeneratedPlanHtml(planText);
  const match = (noteHtml || '').match(sectionPattern);

  if (!match) {
    if (!noteHtml || !noteHtml.trim()) {
      return `${JOURNAL_PLAN_HEADING}${generatedPlanHtml}`;
    }

    return insertSectionBeforeTrailingSections(
      noteHtml,
      `${JOURNAL_PLAN_HEADING}${generatedPlanHtml}`,
      [patientAcceptPattern]
    );
  }

  const existingBody = match[1] || '';
  const { inlineSuffix, manualBody } = stripExistingGeneratedPlanHtml(existingBody);
  const mergedPlanText = inlineSuffix ? `${planText} ${inlineSuffix}`.trim() : planText;
  const nextBody = `${buildGeneratedPlanHtml(mergedPlanText)}${manualBody}`;
  const replacementSectionHtml = `${JOURNAL_PLAN_HEADING}${nextBody}`;

  const withoutPlanSection = removeSectionWithAdjacentSeparator(noteHtml || '', sectionPattern);
  const reorderedHtml = insertSectionBeforeTrailingSections(
    withoutPlanSection,
    replacementSectionHtml,
    [patientAcceptPattern]
  );

  if (nextBody === existingBody && reorderedHtml === (noteHtml || '')) {
    return noteHtml || '';
  }

  return reorderedHtml;
};

const canAutoSyncGeneratedPlanSection = (noteHtml = '') => {
  const currentNoteHtml = noteHtml || '';
  const sectionPattern = /<p><strong>\s*Plan until next time:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|$))/i;
  const match = currentNoteHtml.match(sectionPattern);

  if (!match) {
    return true;
  }

  const existingBody = String(match[1] || '').trim();
  if (!existingBody) {
    return true;
  }

  return stripExistingGeneratedPlanHtml(existingBody).matchedPrefix;
};

const upsertPatientAcceptSection = (noteHtml, acceptText = JOURNAL_PATIENT_ACCEPT_DEFAULT_TEXT) => {
  if (!acceptText) {
    return noteHtml || '';
  }

  const currentNoteHtml = noteHtml || '';
  const sectionPattern = /<p><strong>\s*Patient accept:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|$))/i;
  const planPattern = /(<p><strong>\s*Plan until next time:\s*<\/strong><\/p>[\s\S]*?)(?=(<hr\b[^>]*>|$))/i;
  const acceptBodyHtml = buildStaticJournalParagraphHtml(acceptText);
  const patientAcceptSectionHtml = `${JOURNAL_PATIENT_ACCEPT_HEADING}${acceptBodyHtml}`;
  const existingSectionMatch = currentNoteHtml.match(sectionPattern);
  const withoutAcceptSection = existingSectionMatch
    ? removeSectionWithAdjacentSeparator(currentNoteHtml, sectionPattern)
    : currentNoteHtml;

  if (existingSectionMatch) {
    const existingBody = (existingSectionMatch[1] || '').trim();
    const { matchedPrefix, inlineSuffix, manualBody } = extractLeadingParagraphEditParts(existingBody, (text) => {
      const trimmedText = String(text || '').trim();
      return trimmedText.toLowerCase().startsWith(String(acceptText || '').trim().toLowerCase())
        ? trimmedText.slice(0, String(acceptText || '').trim().length)
        : '';
    });

    const nextSectionHtml = matchedPrefix
      ? `${JOURNAL_PATIENT_ACCEPT_HEADING}${buildStaticJournalParagraphHtml(inlineSuffix ? `${acceptText} ${inlineSuffix}`.trim() : acceptText)}${manualBody}`
      : `${JOURNAL_PATIENT_ACCEPT_HEADING}${existingBody || acceptBodyHtml}`;

    return insertSectionBeforeTrailingSections(withoutAcceptSection, nextSectionHtml, []);
  }

  if (!currentNoteHtml.trim()) {
    return patientAcceptSectionHtml;
  }

  if (planPattern.test(currentNoteHtml)) {
    return currentNoteHtml.replace(planPattern, `$1${JOURNAL_SECTION_SEPARATOR_HTML}${patientAcceptSectionHtml}`);
  }

  return `${currentNoteHtml}${JOURNAL_SECTION_SEPARATOR_HTML}${patientAcceptSectionHtml}`;
};

const mergeTreatmentAdjustmentsSection = (noteHtml, lines = []) => {
  const normalizedIncomingLines = Array.from(new Set((lines || []).filter(Boolean).map(line => line.toString().trim())));
  const sectionPattern = /<p><strong>\s*Treatment and adjustments made today:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|$))/i;
  const match = (noteHtml || '').match(sectionPattern);

  if (!match) {
    if (normalizedIncomingLines.length === 0) {
      return noteHtml || '';
    }
    return upsertTreatmentAdjustmentsSection(noteHtml || '', normalizedIncomingLines);
  }

  const existingBody = match[1] || '';
  const existingLines = removePlaceholderLine(extractAdjustmentLinesFromHtml(existingBody));
  const incomingLineIdentities = new Set(normalizedIncomingLines.map(getProgramAdjustmentLineIdentity).filter(Boolean));
  const hasIncomingAdviceLines = normalizedIncomingLines.some(isAdviceAdjustmentLine);
  const incomingExerciseLabels = new Set(
    normalizedIncomingLines
      .filter(line => !isAdviceAdjustmentLine(line))
      .map(line => {
        const match = line.match(/\[([^\]]+)\]/);
        return match ? match[1].trim().toLowerCase() : null;
      })
      .filter(Boolean)
  );
  // When an exercise is being Replaced, all prior actions on that exercise (e.g. Updated)
  // should be evicted — not just lines with the same identity prefix.
  const incomingReplacedExercises = new Set(
    normalizedIncomingLines
      .map(line => { const m = line.match(/^Replaced\s+\[([^\]]+)\]/i); return m ? m[1].trim().toLowerCase() : null; })
      .filter(Boolean)
  );
  const filteredExistingLines = existingLines.filter(line => {
    if (hasIncomingAdviceLines && isAdviceAdjustmentLine(line)) {
      return false;
    }
    const existingExerciseMatch = line.match(/\[([^\]]+)\]/);
    if (!isAdviceAdjustmentLine(line)
      && existingExerciseMatch
      && incomingExerciseLabels.has(existingExerciseMatch[1].trim().toLowerCase())
      && /^(Added|Updated|Removed|Replaced)\s+\[/i.test(line)) {
      return false;
    }
    const identity = getProgramAdjustmentLineIdentity(line);
    if (!identity) return true;
    if (incomingLineIdentities.has(identity)) return false;
    // Remove any action on an exercise that is now superseded by a Replaced line
    if (incomingReplacedExercises.size > 0) {
      const exMatch = line.match(/\[([^\]]+)\]/);
      if (exMatch && incomingReplacedExercises.has(exMatch[1].trim().toLowerCase())) return false;
    }
    return true;
  });

  const mergedLines = [];
  const seenNormalized = new Set();

  removePlaceholderLine([...filteredExistingLines, ...normalizedIncomingLines]).forEach(line => {
    const normalized = normalizeAdjustmentLine(line);
    if (!normalized || seenNormalized.has(normalized)) {
      return;
    }
    seenNormalized.add(normalized);
    mergedLines.push(line);
  });

  if (JSON.stringify(mergedLines) === JSON.stringify(existingLines)) {
    return noteHtml || '';
  }

  const mergedBodyHtml = buildAdjustmentsSectionHtml(mergedLines);
  return (noteHtml || '').replace(sectionPattern, `${JOURNAL_ADJUSTMENTS_HEADING}${mergedBodyHtml}`);
};

const upsertTreatmentAdjustmentsSection = (noteHtml, lines = []) => {
  const bodyHtml = buildAdjustmentsSectionHtml(lines);
  const sectionPattern = /<p><strong>\s*Treatment and adjustments made today:\s*<\/strong><\/p>[\s\S]*?(?=(<hr\b[^>]*>|$))/i;
  const hasExistingSection = sectionPattern.test(noteHtml || '');

  if ((!lines || lines.length === 0) && !hasExistingSection) {
    return noteHtml || '';
  }

  if (!noteHtml || !noteHtml.trim()) {
    return `${JOURNAL_ADJUSTMENTS_HEADING}${bodyHtml}`;
  }

  if (hasExistingSection) {
    return noteHtml.replace(sectionPattern, `${JOURNAL_ADJUSTMENTS_HEADING}${bodyHtml}`);
  }

  const separator = /<hr\b[^>]*>/i.test(noteHtml)
    ? ''
    : '<hr style="border:none;border-top:1px solid #cfd8dc;margin:8px 0;">';

  return `${noteHtml}${separator}${JOURNAL_ADJUSTMENTS_HEADING}${bodyHtml}`;
};

const removeLineFromAdjustmentsSection = (noteHtml, lineText) => {
  if (!lineText || !noteHtml) return noteHtml || '';
  const sectionPattern = /<p><strong>\s*Treatment and adjustments made today:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|$))/i;
  const match = (noteHtml || '').match(sectionPattern);
  if (!match) return noteHtml;

  const normalizedTarget = normalizeProgramSummaryLineKey(lineText);
  const remaining = removePlaceholderLine(
    extractAdjustmentLinesFromHtml(match[1] || '').filter(
      l => normalizeProgramSummaryLineKey(l) !== normalizedTarget
    )
  );

  const newBody = buildAdjustmentsSectionHtml(remaining);
  return (noteHtml || '').replace(sectionPattern, `${JOURNAL_ADJUSTMENTS_HEADING}${newBody}`);
};

const buildBaselineGoalSectionHtml = (lines = []) => {
  if (!Array.isArray(lines) || lines.length === 0) {
    return '';
  }
  return `<p>${lines.map(line => escapeHtml(line)).join('<br>')}</p>`;
};

const extractBaselineGoalLinesFromHtml = (html = '') => {
  if (!html) return [];
  return html
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .split('\n')
    .map(line => line.replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").trim())
    .filter(Boolean);
};

const mergeBaselineGoalSection = (noteHtml, lines = []) => {
  const normalizedIncoming = Array.from(new Set((lines || []).filter(Boolean).map(l => l.toString().trim())));
  const sectionPattern = /<p><strong>\s*Baseline \/ Goal adjustment:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|<p><strong>|$))/i;
  const planPattern = /<p><strong>\s*Plan until next time:\s*<\/strong><\/p>[\s\S]*?(?=(<hr\b[^>]*>|$))/i;
  const patientAcceptPattern = /<p><strong>\s*Patient accept:\s*<\/strong><\/p>[\s\S]*?(?=(<hr\b[^>]*>|$))/i;
  const match = (noteHtml || '').match(sectionPattern);

  if (!match) {
    if (normalizedIncoming.length === 0) return noteHtml || '';
    const bodyHtml = buildBaselineGoalSectionHtml(normalizedIncoming);
    if (!noteHtml || !noteHtml.trim()) {
      return `${JOURNAL_BASELINE_GOAL_HEADING}${bodyHtml}`;
    }
    return insertSectionBeforeTrailingSections(
      noteHtml,
      `${JOURNAL_BASELINE_GOAL_HEADING}${bodyHtml}`,
      [planPattern, patientAcceptPattern]
    );
  }

  const existingBody = match[1] || '';
  const existingLines = extractBaselineGoalLinesFromHtml(existingBody);
  const merged = [];
  const seen = new Set();
  [...existingLines, ...normalizedIncoming].forEach(line => {
    const norm = line.replace(/\s+/g, ' ').trim().toLowerCase();
    if (!norm || seen.has(norm)) return;
    seen.add(norm);
    merged.push(line);
  });

  if (merged.length === 0) {
    // Drop the empty section entirely
    return removeSectionWithAdjacentSeparator(noteHtml || '', sectionPattern);
  }

  const newBody = buildBaselineGoalSectionHtml(merged);
  const withoutBaselineSection = removeSectionWithAdjacentSeparator(noteHtml || '', sectionPattern);
  return insertSectionBeforeTrailingSections(
    withoutBaselineSection,
    `${JOURNAL_BASELINE_GOAL_HEADING}${newBody}`,
    [planPattern, patientAcceptPattern]
  );
};

const removeLineFromBaselineGoalSection = (noteHtml, lineText) => {
  if (!lineText || !noteHtml) return noteHtml || '';
  const sectionPattern = /<p><strong>\s*Baseline \/ Goal adjustment:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|<p><strong>|$))/i;
  const match = (noteHtml || '').match(sectionPattern);
  if (!match) return noteHtml;

  const normalizedTarget = lineText.replace(/\s+/g, ' ').trim().toLowerCase();
  const remaining = extractBaselineGoalLinesFromHtml(match[1] || '').filter(
    l => l.replace(/\s+/g, ' ').trim().toLowerCase() !== normalizedTarget
  );

  if (remaining.length === 0) {
    return removeSectionWithAdjacentSeparator(noteHtml || '', sectionPattern);
  }

  return (noteHtml || '').replace(sectionPattern, `${JOURNAL_BASELINE_GOAL_HEADING}${buildBaselineGoalSectionHtml(remaining)}`);
};

// Extract the program-adjustment lines that have already been persisted in
// the currently-displayed (saved) journal note's "Treatment and adjustments
// made today:" section. Used so the annotation box below the journal text
// keeps showing what was saved even after the staged in-memory state has
// been cleared (post-save, version navigation, etc.).
const getSavedAdjustmentLinesFromNote = (noteHtml = '') => {
  if (!noteHtml) return [];
  const sectionPattern = /<p><strong>\s*Treatment and adjustments made today:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|<p><strong>|$))/i;
  const match = noteHtml.match(sectionPattern);
  if (!match) return [];
  return removePlaceholderLine(extractAdjustmentLinesFromHtml(match[1] || ''))
    .filter(line => !isGeneratedPlanText(line));
};

// Mirror of getSavedAdjustmentLinesFromNote for the "Baseline / Goal
// adjustment:" section of the saved journal note.
const getSavedBaselineGoalLinesFromNote = (noteHtml = '') => {
  if (!noteHtml) return [];
  const sectionPattern = /<p><strong>\s*Baseline \/ Goal adjustment:\s*<\/strong><\/p>([\s\S]*?)(?=(<hr\b[^>]*>|<p><strong>|$))/i;
  const match = noteHtml.match(sectionPattern);
  if (!match) return [];
  return extractBaselineGoalLinesFromHtml(match[1] || '');
};

const normalizeProgramSummaryLineKey = (line = '') => (line || '').toString().replace(/\*$/, '').replace(/\s+/g, ' ').trim().toLowerCase();

const dedupeProgramSummaryLines = (lines = []) => {
  const seen = new Set();
  return (lines || []).filter(Boolean).filter((line) => {
    const key = normalizeProgramSummaryLineKey(line);
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const dedupeProgramSummaryItems = (items = []) => {
  const seen = new Set();
  return (items || []).filter(Boolean).filter((item) => {
    const key = JSON.stringify(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const getJournalProgramSummaryPart = (summary) => {
  if (!summary) return null;
  if (summary.journalSummary) return summary.journalSummary;
  return summary.source === 'journal' ? summary : null;
};

const getExternalProgramSummaryPart = (summary) => {
  if (!summary) return null;
  if (summary.externalSummary) return summary.externalSummary;
  return summary.source && summary.source !== 'journal' ? summary : null;
};

const buildProgramSummarySignature = (summary) => JSON.stringify(summary ? {
  phaseId: summary.phaseId || null,
  source: summary.source || null,
  journalLines: summary.journalLines || [],
  replacements: summary.replacements || [],
  updated: summary.updated || [],
  removed: summary.removed || [],
  added: summary.added || [],
  reordered: summary.reordered || [],
  journalSummary: summary.journalSummary ? {
    phaseId: summary.journalSummary.phaseId || null,
    source: summary.journalSummary.source || null,
    journalLines: summary.journalSummary.journalLines || [],
    replacements: summary.journalSummary.replacements || [],
    updated: summary.journalSummary.updated || [],
    removed: summary.journalSummary.removed || [],
    added: summary.journalSummary.added || [],
    reordered: summary.journalSummary.reordered || [],
  } : null,
  externalSummary: summary.externalSummary ? {
    phaseId: summary.externalSummary.phaseId || null,
    source: summary.externalSummary.source || null,
    journalLines: summary.externalSummary.journalLines || [],
    replacements: summary.externalSummary.replacements || [],
    updated: summary.externalSummary.updated || [],
    removed: summary.externalSummary.removed || [],
    added: summary.externalSummary.added || [],
    reordered: summary.externalSummary.reordered || [],
  } : null,
} : null);

const mergeProgramChangeSummaryEntries = ({ journalSummary = null, externalSummary = null } = {}) => {
  const effectiveJournalSummary = journalSummary && journalSummary.hasChanges !== false ? journalSummary : null;
  const effectiveExternalSummary = externalSummary && externalSummary.hasChanges !== false ? externalSummary : null;

  if (!effectiveJournalSummary && !effectiveExternalSummary) {
    return null;
  }

  const mergedJournalLines = dedupeProgramSummaryLines([
    ...(effectiveExternalSummary?.journalLines || []),
    ...(effectiveJournalSummary?.journalLines || []),
  ]);

  return {
    phaseId: effectiveJournalSummary?.phaseId || effectiveExternalSummary?.phaseId || null,
    source: effectiveJournalSummary ? 'journal' : (effectiveExternalSummary?.source || 'program'),
    journalSummary: effectiveJournalSummary,
    externalSummary: effectiveExternalSummary,
    journalLines: mergedJournalLines,
    replacements: dedupeProgramSummaryItems([...(effectiveExternalSummary?.replacements || []), ...(effectiveJournalSummary?.replacements || [])]),
    updated: dedupeProgramSummaryItems([...(effectiveExternalSummary?.updated || []), ...(effectiveJournalSummary?.updated || [])]),
    removed: dedupeProgramSummaryItems([...(effectiveExternalSummary?.removed || []), ...(effectiveJournalSummary?.removed || [])]),
    added: dedupeProgramSummaryItems([...(effectiveExternalSummary?.added || []), ...(effectiveJournalSummary?.added || [])]),
    reordered: dedupeProgramSummaryItems([...(effectiveExternalSummary?.reordered || []), ...(effectiveJournalSummary?.reordered || [])]),
    hasChanges: mergedJournalLines.length > 0
      || (effectiveExternalSummary?.reordered || []).length > 0
      || (effectiveJournalSummary?.reordered || []).length > 0,
  };
};

const removeLineFromProgramSummaryPart = (summaryPart, lineText) => {
  if (!summaryPart || !lineText) {
    return summaryPart;
  }

  const normalizedTarget = normalizeProgramSummaryLineKey(lineText);
  const replacedMatch = lineText.match(/^Replaced \[([^\]]+)\] with \[([^\]]+)\]/i)
    || lineText.match(/^\[([^\]]+)\]:.*-->\s*\[([^\]]+)\]\*?$/i);
  const removedMatch = lineText.match(/^Removed \[([^\]]+)\] from the exercise program/i);
  const addedMatch = lineText.match(/^Added \[([^\]]+)\](?:\s+•.*?)?\s+to the exercise program/i);
  const updatedMatch = lineText.match(/^Updated \[([^\]]+)\]:/i);
  const normalizeLabel = (value) => (value || '').toString().trim().toLowerCase();

  const nextJournalLines = (summaryPart.journalLines || []).filter(
    (line) => normalizeProgramSummaryLineKey(line) !== normalizedTarget
  );
  let nextReplacements = summaryPart.replacements || [];
  let nextRemoved = summaryPart.removed || [];
  let nextAdded = summaryPart.added || [];
  let nextUpdated = summaryPart.updated || [];

  if (replacedMatch) {
    const fromLabel = normalizeLabel(replacedMatch[1]);
    nextReplacements = nextReplacements.filter((item) => normalizeLabel(item?.from) !== fromLabel);
  } else if (removedMatch) {
    const removedLabel = normalizeLabel(removedMatch[1]);
    nextRemoved = nextRemoved.filter((item) => normalizeLabel(item?.title) !== removedLabel);
  } else if (addedMatch) {
    const addedLabel = normalizeLabel(addedMatch[1]);
    nextAdded = nextAdded.filter((item) => normalizeLabel(item?.title || item?.identifier) !== addedLabel);
  } else if (updatedMatch) {
    const updatedLabel = normalizeLabel(updatedMatch[1]);
    nextUpdated = nextUpdated.filter((item) => normalizeLabel(item?.title) !== updatedLabel);
  }

  const hasChanges = nextJournalLines.length > 0 || (summaryPart.reordered || []).length > 0;
  if (!hasChanges) {
    return null;
  }

  return {
    ...summaryPart,
    journalLines: nextJournalLines,
    replacements: nextReplacements,
    removed: nextRemoved,
    added: nextAdded,
    updated: nextUpdated,
    hasChanges,
  };
};

// Reverse-parse a persisted baseline/goal annotation line (as written by
// buildLineText in AddBaselineGoalDialog) back into a minimal initialItem
// structure for re-opening the dialog in edit mode. Returns null when the
// line format is unrecognised.
const parsePersistedBaselineGoalLine = (lineText) => {
  const text = String(lineText || '').trim();
  // "Added function goal: <goalText> = <value>/100"
  const goalMatch = text.match(/^Added function goal:\s*(.+?)\s*=\s*(\d+)\/\d+d?$/i);
  if (goalMatch) {
    const goalPhrase = goalMatch[1].trim();
    const score = goalMatch[2];
    const id = `persisted-edit-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    return {
      id,
      mode: 'goal',
      lineText: text,
      edit: {
        kpi_type: 'function_goal',
        journalPhrase: goalPhrase,
        numerical_value: score,
      },
    };
  }
  // "Added pain baseline: <journalPhrase> = <value>/100" or /7d"
  const baselineMatch = text.match(/^Added pain baseline:\s*(.+?)\s*=\s*(\d+)\/\d+d?$/i);
  if (baselineMatch) {
    const journalPhrase = baselineMatch[1].trim();
    const score = baselineMatch[2];
    // Map journalPhrase back to kpi_subtype via a simple text scan
    const PHRASE_TO_SUBGROUP = [
      { test: /best\/lowest/i, subgroup: 'painAtBest' },
      { test: /worst\/highest/i, subgroup: 'painAtWorst' },
      { test: /as it is.*current|current/i, subgroup: 'painCurrent' },
      { test: /symptom.*frequency|frequency/i, subgroup: 'frequency' },
      { test: /when at.*rest|at rest/i, subgroup: 'painAtRest' },
      { test: /with.*activity|activity/i, subgroup: 'painWithActivity' },
      { test: /difficulty/i, subgroup: 'difficulty' },
    ];
    let subgroup = 'custom';
    for (const entry of PHRASE_TO_SUBGROUP) {
      if (entry.test.test(journalPhrase)) { subgroup = entry.subgroup; break; }
    }
    const id = `persisted-edit-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
    return {
      id,
      mode: 'baseline',
      lineText: text,
      edit: {
        kpi_type: 'pain_baseline',
        journalPhrase,
        numerical_value: score,
        kpi_subtype: subgroup,
      },
    };
  }
  return null;
};

const isPersistedFollowupToggleBaselineGoalLine = (lineText) => /^(Paused|Resumed)\s+(pain baseline|function goal):/i.test(String(lineText || '').trim());

const JOURNEY_PAIN_SUBTYPE_LABELS = {
  current: 'Pain (current)',
  best: 'Pain (at best)',
  worst: 'Pain (at worst)',
  frequency: 'Frequency',
  rest: 'Pain (at rest)',
  activity: 'Pain (with activity)',
  difficulty: 'Difficulty',
  custom: 'Custom pain baseline',
};

const JOURNEY_PAIN_SUBTYPE_ORDER = {
  current: 0,
  best: 1,
  worst: 2,
  frequency: 3,
  rest: 4,
  activity: 5,
  difficulty: 6,
  custom: 7,
};

const isJourneyIdentifierLikeText = (value) => {
  const text = String(value || '').trim();
  if (!text) return false;
  return text.includes('_') || /^[A-Z0-9]+(?:_[A-Z0-9]+)+$/i.test(text);
};

const getJourneyPainSubtypeKey = (kpiItem) => {
  const blob = [
    kpiItem && kpiItem.kpi_subtype,
    kpiItem && kpiItem.subgroup,
    kpiItem && kpiItem.question_identifier,
    kpiItem && kpiItem.identifier,
    kpiItem && kpiItem.mapped_key,
    kpiItem && kpiItem.label,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toLowerCase())
    .join(' ');

  if (!blob) return '';
  if (blob.includes('custom')) return 'custom';
  if (blob.includes('frequency')) return 'frequency';
  if (blob.includes('atbest') || /\bbest\b/.test(blob)) return 'best';
  if (blob.includes('atworst') || /\bworst\b/.test(blob)) return 'worst';
  if (blob.includes('atrest') || /\brest\b/.test(blob)) return 'rest';
  if (blob.includes('withactivity') || /\bactivity\b/.test(blob)) return 'activity';
  if (blob.includes('difficulty')) return 'difficulty';
  if (blob.includes('current')) return 'current';
  return String((kpiItem && kpiItem.kpi_subtype) || '').trim().toLowerCase();
};

const getJourneyKpiMenuLabel = (kpiItem) => {
  if (!kpiItem) return 'KPI';
  const directLabel = [
    kpiItem.label,
    kpiItem.answer_text,
    kpiItem.journalPhrase,
    kpiItem.journal_phrase,
    kpiItem.text_value,
  ]
    .map((value) => String(value || '').trim())
    .find((value) => value && !isJourneyIdentifierLikeText(value));
  if (directLabel) return directLabel;
  if (kpiItem.kpi_type === 'pain_baseline') {
    const subtype = getJourneyPainSubtypeKey(kpiItem);
    return JOURNEY_PAIN_SUBTYPE_LABELS[subtype] || 'Pain baseline';
  }
  if (kpiItem.kpi_type === 'function_goal') return 'Function goal';
  return 'KPI';
};

const getJourneyPauseLineText = (kpiItem) => {
  const label = getJourneyKpiMenuLabel(kpiItem);
  if (kpiItem && kpiItem.kpi_type === 'function_goal') return `Paused function goal: ${label}`;
  return `Paused pain baseline: ${label}`;
};

const getJourneyResumeLineText = (kpiItem) => {
  const label = getJourneyKpiMenuLabel(kpiItem);
  if (kpiItem && kpiItem.kpi_type === 'function_goal') return `Resumed function goal: ${label}`;
  return `Resumed pain baseline: ${label}`;
};

const getJourneyKpiMetaText = (kpiItem) => {
  if (!kpiItem) return '';
  const typeLabel = kpiItem.kpi_type === 'function_goal' ? 'Function goal' : 'Pain baseline';
  const numericValue = kpiItem.numerical_value ?? kpiItem.numeric_value ?? kpiItem.value;
  const hasNumericValue = numericValue !== null && numericValue !== undefined && String(numericValue).trim() !== '';
  return [typeLabel, hasNumericValue ? `Current value ${numericValue}/100` : null].filter(Boolean).join(' • ');
};

const getJourneyKpiIdentityKey = (kpiItem, modeHint = '') => {
  if (!kpiItem) return '';
  const resolvedMode = modeHint || (kpiItem.kpi_type === 'function_goal' ? 'goal' : 'baseline');
  if (resolvedMode === 'goal') {
    return `goal:${String(kpiItem.journalPhrase || kpiItem.journal_phrase || kpiItem.label || kpiItem.answer_text || '').trim().toLowerCase()}`;
  }

  const subtype = getJourneyPainSubtypeKey(kpiItem);
  if (subtype === 'custom') {
    return `baseline:${subtype}:${String(kpiItem.journalPhrase || kpiItem.journal_phrase || kpiItem.label || '').trim().toLowerCase()}`;
  }
  return `baseline:${subtype}`;
};

const JOURNEY_INLINE_BASELINE_TYPE_OPTIONS = [
  { value: 'best', label: 'best/lowest' },
  { value: 'worst', label: 'worst/highest' },
  { value: 'current', label: 'current' },
  { value: 'rest', label: 'rest' },
  { value: 'activity', label: 'activity' },
  { value: 'frequency', label: 'frequency' },
  { value: 'difficulty', label: 'difficulty' },
  { value: 'custom', label: 'custom…' },
];

const JOURNEY_INLINE_BASELINE_LABELS = {
  frequency: 'Patient reported symptom:',
  best: 'Patient reported pain when at:',
  worst: 'Patient reported pain when at:',
  current: 'Patient reported pain as it is:',
  rest: 'Patient reported pain when at:',
  activity: 'Patient reported pain with:',
  difficulty: 'Patient reported:',
  custom: 'Patient reported:',
};

const JOURNEY_INLINE_BASELINE_SUFFIX = {
  best: 'best/lowest',
  worst: 'worst/highest',
  current: 'current',
  rest: 'rest',
  activity: 'activity',
  frequency: 'frequency',
  difficulty: 'difficulty',
  custom: '',
};

const JOURNEY_INLINE_THEME_MAP = {
  best: { theme: 'painAnamnesis_painAtBest', subgroup: 'painAtBest' },
  worst: { theme: 'painAnamnesis_painAtWorst', subgroup: 'painAtWorst' },
  current: { theme: 'painAnamnesis_painCurrent', subgroup: 'painCurrent' },
  frequency: { theme: 'painAnamnesis_frequency', subgroup: 'frequency' },
  rest: { theme: 'painAnamnesis_painAtRest', subgroup: 'painAtRest' },
  activity: { theme: 'painAnamnesis_painWithActivity', subgroup: 'painWithActivity' },
  difficulty: { theme: 'painAnamnesis_difficulty', subgroup: 'difficulty' },
  custom: { theme: 'painAnamnesis_custom', subgroup: 'custom' },
};

const buildJourneyInlineEditorState = (mode) => ({
  mode,
  editingItemId: null,
  editingEditId: null,
  sourceLineText: '',
  baselineType: 'best',
  score: '',
  customMetric: '',
  goalText: '',
  error: '',
});

const buildJourneyInlineEditorStateFromStagedItem = (stagedItem) => {
  const baseState = buildJourneyInlineEditorState(stagedItem && stagedItem.mode ? stagedItem.mode : 'baseline');
  if (!stagedItem || !stagedItem.edit) return baseState;

  const edit = stagedItem.edit || {};
  const score = edit.numerical_value !== undefined && edit.numerical_value !== null
    ? String(edit.numerical_value)
    : (edit.numeric_value !== undefined && edit.numeric_value !== null ? String(edit.numeric_value) : '');

  if (stagedItem.mode === 'goal') {
    return {
      ...baseState,
      editingItemId: stagedItem.id,
      editingEditId: edit.id || null,
      sourceLineText: stagedItem.lineText || '',
      goalText: String(edit.journalPhrase || '').trim(),
      score,
    };
  }

  const subgroup = edit.kpi_subtype || edit.subgroup;
  const matched = Object.entries(JOURNEY_INLINE_THEME_MAP).find(([, value]) => value.subgroup === subgroup);
  const baselineType = matched ? matched[0] : 'best';
  let customMetric = '';
  if (baselineType === 'custom') {
    const phrase = String(edit.journalPhrase || '');
    const idx = phrase.indexOf(':');
    customMetric = idx >= 0 ? phrase.slice(idx + 1).trim() : phrase.trim();
  }

  return {
    ...baseState,
    editingItemId: stagedItem.id,
    editingEditId: edit.id || null,
    sourceLineText: stagedItem.lineText || '',
    baselineType,
    customMetric,
    score,
  };
};

const parsePersistedFollowupToggleLine = (lineText) => {
  const text = String(lineText || '').trim();
  const match = text.match(/^(Paused|Resumed)\s+(pain baseline|function goal):\s*(.+)$/i);
  if (!match) return null;

  const action = String(match[1] || '').trim().toLowerCase() === 'paused' ? 'pause' : 'resume';
  const mode = String(match[2] || '').trim().toLowerCase() === 'function goal' ? 'goal' : 'baseline';
  const label = String(match[3] || '').trim();
  if (!label) return null;

  return {
    action,
    mode,
    label,
    reversePaused: action === 'pause' ? false : true,
  };
};

const RichNoteEditor = React.memo(React.forwardRef(({ value, onChange, readOnly, label, hideToolbar }, ref) => {
  const editorRef = React.useRef(null);
  const internalHtml = React.useRef('');

  React.useEffect(() => {
    const html = sanitizeBasicHtml(textToHtml(value || ''));
    if (editorRef.current && html !== internalHtml.current) {
      editorRef.current.innerHTML = html;
      internalHtml.current = html;
    }
  }, [value]);

  const emitChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      internalHtml.current = html;
      if (onChange) onChange(html);
    }
  };

  const execCmd = (e, cmd, value) => {
    e.preventDefault();
    editorRef.current?.focus();
    document.execCommand(cmd, false, value || null);
    emitChange();
  };

  React.useImperativeHandle(ref, () => ({
    execCmd,
    setContent: (html) => {
      const safe = sanitizeBasicHtml(textToHtml(html || ''));
      if (editorRef.current) {
        editorRef.current.innerHTML = safe;
        internalHtml.current = safe;
        emitChange();
      }
    },
  }));

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    emitChange();
  };

  const toolbarButtons = (
    <>
      <IconButton size="small" onMouseDown={e => execCmd(e, 'bold')} title="Bold (Ctrl+B)" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
        <FormatBoldIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
      </IconButton>
      <IconButton size="small" onMouseDown={e => execCmd(e, 'italic')} title="Italic (Ctrl+I)" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
        <FormatItalicIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
      </IconButton>
      <IconButton size="small" onMouseDown={e => execCmd(e, 'underline')} title="Underline (Ctrl+U)" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
        <FormatUnderlinedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
      </IconButton>
      <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
      <IconButton size="small" onMouseDown={e => execCmd(e, 'insertUnorderedList')} title="Bullet List" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
        <FormatListBulletedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
      </IconButton>
      <IconButton size="small" onMouseDown={e => execCmd(e, 'insertOrderedList')} title="Numbered List" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
        <FormatListNumberedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
      </IconButton>
      <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
      <IconButton size="small" onMouseDown={e => execCmd(e, 'formatBlock', '<h1>')} title="Heading 1" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0, width: 'auto' }}>
        <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H1</span>
      </IconButton>
      <IconButton size="small" onMouseDown={e => execCmd(e, 'formatBlock', '<h2>')} title="Heading 2" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0, width: 'auto' }}>
        <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H2</span>
      </IconButton>
      <IconButton size="small" onMouseDown={e => execCmd(e, 'formatBlock', '<h3>')} title="Heading 3" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0, width: 'auto' }}>
        <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H3</span>
      </IconButton>
      <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
      <IconButton size="small" onMouseDown={e => execCmd(e, 'formatBlock', '<p>')} title="Normal text" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0, width: 'auto' }}>
        <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 400, lineHeight: 1 }}>¶</span>
      </IconButton>
    </>
  );

  return (
    <Box style={{ marginBottom: 'clamp(8px, 0.78vw, 12px)', minHeight: '6.4em' }}>
      {label && (
        <Typography variant="caption" style={{ color: '#002D55', fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)', marginBottom: 'clamp(2px, 0.2vw, 4px)', display: 'block', fontWeight: 600 }}>
          {label}
        </Typography>
      )}
      {!readOnly && !hideToolbar && (
        <Box display="flex" alignItems="center" style={{ gap: 'clamp(2px, 0.2vw, 4px)', marginBottom: 'clamp(4px, 0.32vw, 6px)', flexWrap: 'wrap' }}>
          {toolbarButtons}
        </Box>
      )}
      <div
        ref={editorRef}
        contentEditable={!readOnly}
        className="journal-rich-editor"
        onInput={emitChange}
        onPaste={handlePaste}
        suppressContentEditableWarning
        style={{
          background: readOnly ? '#f5faff' : '#fff',
          color: '#002D55',
          fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)',
          lineHeight: 1.55,
          borderRadius: 8,
          padding: 'clamp(10px, 1.04vw, 16px)',
          minHeight: '6.4em',
          textAlign: 'left',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          outline: 'none',
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontWeight: 400,
          letterSpacing: 0.1,
          cursor: readOnly ? 'default' : 'text',
          transition: 'border-color 0.2s',
        }}
        onFocus={e => { if (!readOnly) e.target.style.borderColor = '#FF7F27'; }}
        onBlur={e => { e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)'; }}
      />
    </Box>
  );
}));

const SelectedPatientJourney = ({ className, selectedPatient, diagnosis, treatmentId, assessmentDate, libraryExercises = [], exercises = [], allPhaseAdvices = [], phases = [], selectedPhaseId, setSelectedPhaseId, onSaveJourneyAdvices, fetchExercisesForPhase, ...rest }) => {
  const classes = useStyles();
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [journeyAdviceVisibility, setJourneyAdviceVisibility] = useState({});
  // Mirrors the Diagnosis assessment-tab "Add baseline / Add goal" buttons:
  // both open the same ClinicianFollowupDialog, just on different steps
  // (0 = pain/frequency baseline, 1 = function goal).
  const [followupInitialStep, setFollowupInitialStep] = useState(0);
  // Stand-alone creator dialog — actually persists a NEW pain_baseline /
  // function_goal KPI via the same `journal_versions` endpoint the Diagnosis
  // tab uses for its inline creator rows. Distinct from the follow-up dialog,
  // which only records new measurements against existing KPIs.
  const [addCreatorDialog, setAddCreatorDialog] = useState({ open: false, mode: 'baseline', sessionId: null, initialItem: null });

  // Inject custom style for author dropdown hover (light blue)
  useEffect(() => {
    const styleId = 'author-dropdown-hover-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        .author-dropdown-menuitem:hover,
        .author-dropdown-menuitem.Mui-selected,
        .author-dropdown-menuitem.Mui-focusVisible {
          background-color: #f5faff !important;
        }
        .author-dropdown-select:hover,
        .author-dropdown-select:focus,
        .author-dropdown-select.Mui-focused {
          border-radius: 14px !important;
        }
        .author-dropdown-select {
          padding-left: clamp(4px, 0.42vw, 8px) !important;
          font-size: clamp(0.625rem, 0.65vw, 0.75rem) !important;
        }
        .journal-rich-editor h1,
        .journal-rich-editor h2,
        .journal-rich-editor h3 {
          color: #002D55 !important;
          margin: 0.25em 0;
        }
        .journal-rich-editor ul,
        .journal-rich-editor ol {
          color: #002D55;
          padding-left: 1.5em;
          margin: 0.25em 0;
        }
        .journal-rich-editor p,
        .journal-rich-editor div,
        .journal-rich-editor li {
          color: #002D55;
          font-size: clamp(0.72rem, 0.78vw, 0.9rem);
          line-height: 1.55;
          font-family: inherit;
        }
        .journal-rich-editor p,
        .journal-rich-editor div {
          margin: 0 0 clamp(4px, 0.39vw, 6px) 0;
        }
        .journal-rich-editor hr {
          border: none;
          border-top: 1px solid #cfd8dc;
          margin: clamp(6px, 0.52vw, 8px) 0;
        }
        .journal-rich-editor li {
          color: #002D55;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const style = document.getElementById(styleId);
      if (style) style.remove();
    };
  }, []);
  const token = useSelector(state => state.auth.token);
  const authUser = useSelector(state => state.auth.user);
  const [completedSessionsList, setCompletedSessionsList] = useState([]);
  const [plannedSessionsPerDayOverall, setPlannedSessionsPerDayOverall] = useState(null);
  const [progress, setProgress] = useState(0);
  const [title, setTitle] = useState('');
  const [isJournalLoading, setIsJournalLoading] = useState(false);
  const [sessionFeedbackList, setSessionFeedbackList] = useState([]);
  const [exerciseTrendDialogOpen, setExerciseTrendDialogOpen] = useState(false);

  const [exerciseFeedbackList, setExerciseFeedbackList] = useState([]);

  // Responsive colSpan logic
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  // If xs/sm/md, colSpan = 12, else use original
  const responsiveColSpan = (defaultColSpan) => (isXs || isSm || isMd ? 12 : defaultColSpan);


  // Journal notes state, keyed by session ID
  // journalNotes: { [sessionId]: { note: string, noteId: int|null } }
  const [journalNotes, setJournalNotes] = useState({}); // { [sessionId]: { note, noteId } }
  const [savingNotes, setSavingNotes] = useState({}); // { [sessionId]: boolean }
  const [copiedSessions, setCopiedSessions] = useState({}); // { [sessionId]: boolean }
  const [fetchedNotes, setFetchedNotes] = useState({}); // { [sessionId]: [notes] }
  // Patient-scoped journal notes (no session yet). Loaded from
  // /treatment/patient/journal_notes/<patient_id>/.
  const [patientJournalNotes, setPatientJournalNotes] = useState([]); // [{id, note, ...}]
  const [patientNoteDraft, setPatientNoteDraft] = useState('');
  const [patientNoteEditingId, setPatientNoteEditingId] = useState(null); // null = composing new
  const [patientNoteSaving, setPatientNoteSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  // Track which note is being viewed for each session
  const [noteIndices, setNoteIndices] = useState({}); // { [sessionId]: index }
  // State for edit mode and editable value per session
  const [editingNote, setEditingNote] = useState({}); // { [sessionId]: boolean }
  const editingNoteRef = useRef({}); // stable mirror of editingNote for callbacks that must not re-create on every keystroke
  useEffect(() => { editingNoteRef.current = editingNote; }, [editingNote]);
  const [editNoteValue, setEditNoteValue] = useState({}); // { [sessionId]: string }
  const [adjustmentMode, setAdjustmentMode] = useState('auto_replace');
  const [generatedIssuesBySession, setGeneratedIssuesBySession] = useState({});
  const [suggestionDecisions, setSuggestionDecisions] = useState({});
  const [adjustmentLogs, setAdjustmentLogs] = useState([]);
  const [libraryDrawerOpen, setLibraryDrawerOpen] = useState(false);
  const [editingSuggestionContext, setEditingSuggestionContext] = useState(null);
  const [journalLibraryExercises, setJournalLibraryExercises] = useState([]);
  const [sessionExerciseDrafts, setSessionExerciseDrafts] = useState({});
  const [sessionAdviceDrafts, setSessionAdviceDrafts] = useState({});
  const [programEditorOpen, setProgramEditorOpen] = useState(false);
  const [programEditorContext, setProgramEditorContext] = useState(null);
  const [programEditorLoading, setProgramEditorLoading] = useState(false);
  const [adviceEditorOpen, setAdviceEditorOpen] = useState(false);
  const [adviceEditorContext, setAdviceEditorContext] = useState(null);
  const [adviceEditorLoading, setAdviceEditorLoading] = useState(false);
  const [adviceEditorSaving, setAdviceEditorSaving] = useState(false);
  const [adviceLibraryDrawerOpen, setAdviceLibraryDrawerOpen] = useState(false);
  const [adviceLibraryContext, setAdviceLibraryContext] = useState(null);
  const [draftAdviceDialogOpen, setDraftAdviceDialogOpen] = useState(false);
  const [draftAdviceEditContext, setDraftAdviceEditContext] = useState(null);
  const [draftEditedAdviceParams, setDraftEditedAdviceParams] = useState({ title: '', advice: '', importance: 1 });
  const [draftExerciseDialogOpen, setDraftExerciseDialogOpen] = useState(false);
  const [draftExerciseEditContext, setDraftExerciseEditContext] = useState(null);
  const [draftEditedExerciseParams, setDraftEditedExerciseParams] = useState(buildExerciseEditParams({}));
  const journeyBulkEditParamDefaults = useMemo(() => ({ sets: 3, repetitions: 10, time: 30, doseType: 'time', pause: 15, frequency: 1, frequencyType: 'daily' }), []);
  const [journeyBulkEditParam, setJourneyBulkEditParam] = useState('sets');
  const [journeyBulkEditValue, setJourneyBulkEditValue] = useState(3);
  const [programChangeSummaries, setProgramChangeSummaries] = useState({});
  const [pendingExternalProgramUpdate, setPendingExternalProgramUpdate] = useState(null);
  const [savingExternalProgramUpdate, setSavingExternalProgramUpdate] = useState(false);
  const handledProgramUpdateKeysRef = useRef(new Set());
  const journeyTreatmentContextRef = useRef(null);
  const adviceLibraryDraftCacheRef = useRef(null);
  // Drag-and-drop reorder state for the Journal program editor sidebar.
  // Holds the source list index while a card is being dragged, and the
  // current hover target for visual feedback.
  const [programEditorDragIndex, setProgramEditorDragIndex] = useState(null);
  const [programEditorDragOverIndex, setProgramEditorDragOverIndex] = useState(null);
  const [inlineProgramDragSessionId, setInlineProgramDragSessionId] = useState(null);
  const [inlineProgramDragIndex, setInlineProgramDragIndex] = useState(null);
  const [inlineProgramDragOverIndex, setInlineProgramDragOverIndex] = useState(null);
  const [inlineAdviceDragSessionId, setInlineAdviceDragSessionId] = useState(null);
  const [inlineAdviceDragIndex, setInlineAdviceDragIndex] = useState(null);
  const [inlineAdviceDragOverIndex, setInlineAdviceDragOverIndex] = useState(null);
  const [adviceEditorDragIndex, setAdviceEditorDragIndex] = useState(null);
  const [adviceEditorDragOverIndex, setAdviceEditorDragOverIndex] = useState(null);
  const [adviceEditorIndexDrafts, setAdviceEditorIndexDrafts] = useState({});
  const [expandedJourneyAdviceCards, setExpandedJourneyAdviceCards] = useState({});
  // Per-card index text-field draft so the user can type freely (incl.
  // empty/intermediate values) before committing on blur or Enter.
  const [programEditorIndexDrafts, setProgramEditorIndexDrafts] = useState({});

  // Tracks adjustment lines the user has explicitly removed via the per-line
  // remove icon for HISTORICAL (already saved) lines. Used so the line stays
  // hidden across re-derivations from the saved note text or from
  // adjustment-log issues — until the journal note is saved (and the
  // backend reflects the rollback) or editing is cancelled.
  // Shape: { [sessionId]: Set<normalizedLineKey> }
  const [removedHistoricalLines, setRemovedHistoricalLines] = useState({});

  // Staged baseline/goal additions (per session). Persisted only when the
  // associated journal note is saved. Shape:
  //   { [sessionId]: [{ id, mode: 'baseline'|'goal', edit, lineText }] }
  const [baselineGoalStaged, setBaselineGoalStaged] = useState({});
  const [journeyKpiItems, setJourneyKpiItems] = useState([]);
  const [journeyActionMenu, setJourneyActionMenu] = useState({
    open: false,
    sessionId: null,
    baselineGoalSnapshot: [],
    noteSnapshot: '',
    hadDraftNote: false,
    inlineEditor: null,
  });

  const availableLibraryExercises = useMemo(() => (
    journalLibraryExercises.length > 0
      ? journalLibraryExercises
      : (Array.isArray(libraryExercises) ? libraryExercises : [])
  ), [journalLibraryExercises, libraryExercises]);

  const sanitizeJourneyAdjustmentMode = useCallback((mode, fallback = 'auto_replace') => {
    const resolvedMode = mode || fallback;
    return resolvedMode === 'suggest_only' ? 'disabled' : resolvedMode;
  }, []);

  const resolvedJourneyTreatmentId = treatmentId || selectedPatient?.treatment_id || null;
  const journeyTreatmentContextKey = selectedPatient?.user_id && resolvedJourneyTreatmentId
    ? `${selectedPatient.user_id}::${resolvedJourneyTreatmentId}`
    : null;
  const selectedTreatmentExerciseRegion = diagnosis?.exercise_library_region
    || selectedPatient?.exercise_library_region
    || (selectedPatient?.diagnosis || []).find((item) => String(item?.treatment_id || item?.id || '') === String(resolvedJourneyTreatmentId || ''))?.exercise_library_region
    || '';

  useEffect(() => {
    if (!journeyTreatmentContextKey || journeyTreatmentContextRef.current === journeyTreatmentContextKey) {
      return;
    }

    journeyTreatmentContextRef.current = journeyTreatmentContextKey;
    handledProgramUpdateKeysRef.current = new Set();
    lastFetchRef.current = null;
    batchFetchInitiatedRef.current = null;
    fetchedNotesRef.current = new Set();
    setPendingExternalProgramUpdate(null);
    setCompletedSessionsList([]);
    setPlannedSessionsPerDayOverall(null);
    setProgress(0);
    setTitle('');
    setSessionFeedbackList([]);
    setExerciseFeedbackList([]);
    setGeneratedIssuesBySession({});
    setSuggestionDecisions({});
    setAdjustmentLogs([]);
    setJournalNotes({});
    setFetchedNotes({});
    setNoteIndices({});
    setEditingNote({});
    setEditNoteValue({});
    setSessionExerciseDrafts({});
    setSessionAdviceDrafts({});
    setProgramChangeSummaries({});
    setBaselineGoalStaged({});
    setRemovedHistoricalLines({});
    setJourneyActionMenu({
      open: false,
      sessionId: null,
      baselineGoalSnapshot: [],
      noteSnapshot: '',
      hadDraftNote: false,
      inlineEditor: null,
    });
    setJourneyKpiItems([]);
  }, [journeyTreatmentContextKey]);

  const fetchJourneyKpiItems = useCallback(async () => {
    if (!resolvedJourneyTreatmentId || !token) {
      setJourneyKpiItems([]);
      return [];
    }
    const requestContextKey = journeyTreatmentContextKey;
    try {
      const response = await fetch(`${apiBaseUrl}/treatment/treatments/${resolvedJourneyTreatmentId}/kpi_items/`, {
        headers: {
          Authorization: `Token ${token}`,
          Accept: 'application/json',
        },
      });
      if (!response.ok) return [];
      const data = await response.json();
      const items = Array.isArray(data && data.kpi_items) ? data.kpi_items : [];
      if (journeyTreatmentContextRef.current !== requestContextKey) return [];
      setJourneyKpiItems(items);
      return items;
    } catch (e) {
      return [];
    }
  }, [journeyTreatmentContextKey, resolvedJourneyTreatmentId, token]);

  useEffect(() => {
    fetchJourneyKpiItems().catch(() => {});
  }, [fetchJourneyKpiItems]);

  const cloneJourneyStagedItems = useCallback((items = []) => {
    return (items || []).map((item) => ({
      ...item,
      edit: item && item.edit ? { ...item.edit } : item.edit,
    }));
  }, []);

  const getCurrentJourneyNoteDraft = useCallback((sessionId, draftSource = null) => {
    const source = draftSource || editNoteValue;
    const hasDraft = Object.prototype.hasOwnProperty.call(source, sessionId);
    if (hasDraft) {
      return { note: source[sessionId] || '', hasDraft: true };
    }
    const sessionNotes = fetchedNotes[sessionId] || [];
    const noteIdx = noteIndices[sessionId] || 0;
    const savedNote = (journalNotes[sessionId] && journalNotes[sessionId].note !== undefined)
      ? journalNotes[sessionId].note
      : (sessionNotes[noteIdx] && sessionNotes[noteIdx].note) || '';
    return { note: savedNote || '', hasDraft: false };
  }, [editNoteValue, fetchedNotes, journalNotes, noteIndices]);

  const replaceJourneyBaselineGoalDraftNote = useCallback((sessionId, previousItems = [], nextItems = []) => {
    if (!sessionId) return;
    const previousLines = (previousItems || [])
      .filter((item) => item && !item.suppressAnnotation && item.lineText)
      .map((item) => item.lineText);
    const nextLines = (nextItems || [])
      .filter((item) => item && !item.suppressAnnotation && item.lineText)
      .map((item) => item.lineText);

    setEditNoteValue((prev) => {
      const { note: currentNote, hasDraft } = getCurrentJourneyNoteDraft(sessionId, prev);
      let nextNote = currentNote;

      previousLines.forEach((lineText) => {
        nextNote = removeLineFromBaselineGoalSection(nextNote, lineText);
      });

      if (nextLines.length > 0) {
        nextNote = mergeBaselineGoalSection(nextNote, nextLines);
      }

      if (nextNote === currentNote) {
        return prev;
      }

      if (!hasDraft && !nextNote) {
        return prev;
      }

      return { ...prev, [sessionId]: nextNote };
    });
  }, [getCurrentJourneyNoteDraft]);

  const openJourneyActionMenu = useCallback((sessionId) => {
    const { note, hasDraft } = getCurrentJourneyNoteDraft(sessionId);
    setJourneyActionMenu({
      open: true,
      sessionId,
      baselineGoalSnapshot: cloneJourneyStagedItems(baselineGoalStaged[sessionId] || []),
      noteSnapshot: note,
      hadDraftNote: hasDraft,
      inlineEditor: null,
    });
  }, [baselineGoalStaged, cloneJourneyStagedItems, getCurrentJourneyNoteDraft]);

  const openJourneyActionMenuForEdit = useCallback((sessionId, stagedItem) => {
    if (!sessionId || !stagedItem) return;
    const { note, hasDraft } = getCurrentJourneyNoteDraft(sessionId);
    setJourneyActionMenu({
      open: true,
      sessionId,
      baselineGoalSnapshot: cloneJourneyStagedItems(baselineGoalStaged[sessionId] || []),
      noteSnapshot: note,
      hadDraftNote: hasDraft,
      inlineEditor: buildJourneyInlineEditorStateFromStagedItem(stagedItem),
    });
  }, [baselineGoalStaged, cloneJourneyStagedItems, getCurrentJourneyNoteDraft]);

  const closeJourneyActionMenu = useCallback(() => {
    setJourneyActionMenu({
      open: false,
      sessionId: null,
      baselineGoalSnapshot: [],
      noteSnapshot: '',
      hadDraftNote: false,
      inlineEditor: null,
    });
  }, []);

  const handleCancelJourneyActionMenu = useCallback(() => {
    const sessionId = journeyActionMenu.sessionId;
    if (sessionId) {
      const snapshotItems = cloneJourneyStagedItems(journeyActionMenu.baselineGoalSnapshot || []);
      setBaselineGoalStaged((prev) => {
        const out = { ...prev };
        if (snapshotItems.length === 0) delete out[sessionId];
        else out[sessionId] = snapshotItems;
        return out;
      });
      setEditNoteValue((prev) => {
        if (journeyActionMenu.hadDraftNote) {
          return { ...prev, [sessionId]: journeyActionMenu.noteSnapshot || '' };
        }
        if (!Object.prototype.hasOwnProperty.call(prev, sessionId)) return prev;
        const out = { ...prev };
        delete out[sessionId];
        return out;
      });
    }
    closeJourneyActionMenu();
  }, [cloneJourneyStagedItems, closeJourneyActionMenu, journeyActionMenu]);

  const handleConfirmJourneyActionMenu = useCallback(() => {
    closeJourneyActionMenu();
  }, [closeJourneyActionMenu]);

  const openJourneyInlineEditor = useCallback((mode, stagedItem = null) => {
    setJourneyActionMenu((prev) => ({
      ...prev,
      inlineEditor: stagedItem
        ? buildJourneyInlineEditorStateFromStagedItem(stagedItem)
        : (prev.inlineEditor && prev.inlineEditor.mode === mode && !prev.inlineEditor.editingItemId
          ? prev.inlineEditor
          : buildJourneyInlineEditorState(mode)),
    }));
  }, []);

  const closeJourneyInlineEditor = useCallback(() => {
    setJourneyActionMenu((prev) => ({ ...prev, inlineEditor: null }));
  }, []);

  const updateJourneyInlineEditor = useCallback((patch) => {
    setJourneyActionMenu((prev) => {
      if (!prev.inlineEditor) return prev;
      return {
        ...prev,
        inlineEditor: {
          ...prev.inlineEditor,
          ...patch,
          error: patch && Object.prototype.hasOwnProperty.call(patch, 'error')
            ? patch.error
            : '',
        },
      };
    });
  }, []);

  const getJourneyEffectivePausedState = useCallback((sessionId, kpiItem) => {
    if (!kpiItem || !kpiItem.id) return false;
    const serverPaused = Boolean(kpiItem.paused_from_followup);
    const stagedItems = baselineGoalStaged[sessionId] || [];
    const stagedToggle = stagedItems.find((item) => item && (item.action === 'pause' || item.action === 'resume') && String(item.edit && item.edit.id) === String(kpiItem.id));
    if (!stagedToggle) return serverPaused;
    return stagedToggle.action === 'pause';
  }, [baselineGoalStaged]);

  const buildJourneyDraftKpiItem = useCallback((stagedItem) => {
    if (!stagedItem || stagedItem.action === 'pause' || stagedItem.action === 'resume') return null;
    const edit = stagedItem.edit || {};
    return {
      ...edit,
      id: `draft-${stagedItem.id}`,
      stagedItemId: stagedItem.id,
      kpi_type: stagedItem.mode === 'goal' ? 'function_goal' : 'pain_baseline',
      label: edit.journalPhrase || edit.label || '',
      journalPhrase: edit.journalPhrase || '',
      numerical_value: edit.numerical_value,
      numeric_value: edit.numeric_value !== undefined ? edit.numeric_value : edit.numerical_value,
      paused_from_followup: false,
      is_draft_added: true,
      replaceLineText: stagedItem.replaceLineText || '',
    };
  }, []);

  const getJourneyKpiItemsByMode = useCallback((sessionId, mode) => {
    const draftStageItems = (baselineGoalStaged[sessionId] || [])
      .filter((item) => item && item.action === 'add' && item.mode === mode);
    const overriddenPersistedKeys = new Set(
      draftStageItems
        .filter((item) => item && item.replaceLineText)
        .map((item) => getJourneyKpiIdentityKey(item.edit || {}, item.mode))
        .filter(Boolean)
    );
    const persistedItems = (journeyKpiItems || [])
      .filter((item) => item && !item.is_deleted)
      .filter((item) => mode === 'baseline' ? item.kpi_type === 'pain_baseline' : item.kpi_type === 'function_goal')
      .filter((item) => !overriddenPersistedKeys.has(getJourneyKpiIdentityKey(item, mode)))
      .map((item) => ({ ...item, is_draft_added: false }));
    const draftItems = draftStageItems
      .map(buildJourneyDraftKpiItem)
      .filter(Boolean);

    return [...persistedItems, ...draftItems].sort((left, right) => {
        if (mode === 'baseline') {
          const leftSubtype = getJourneyPainSubtypeKey(left);
          const rightSubtype = getJourneyPainSubtypeKey(right);
          const leftOrder = Object.prototype.hasOwnProperty.call(JOURNEY_PAIN_SUBTYPE_ORDER, leftSubtype)
            ? JOURNEY_PAIN_SUBTYPE_ORDER[leftSubtype]
            : 99;
          const rightOrder = Object.prototype.hasOwnProperty.call(JOURNEY_PAIN_SUBTYPE_ORDER, rightSubtype)
            ? JOURNEY_PAIN_SUBTYPE_ORDER[rightSubtype]
            : 99;
          if (leftOrder !== rightOrder) return leftOrder - rightOrder;
        }
        return getJourneyKpiMenuLabel(left).localeCompare(getJourneyKpiMenuLabel(right));
      });
  }, [baselineGoalStaged, buildJourneyDraftKpiItem, journeyKpiItems]);

  const stageJourneyFollowupToggleItem = useCallback((sessionId, kpiItem, paused, options = {}) => {
    if (!sessionId || !kpiItem || !kpiItem.id) return;
    const serverPaused = Boolean(kpiItem.paused_from_followup);
    const suppressAnnotation = Boolean(options && options.suppressAnnotation);
    const shouldCloseMenu = options && options.closeMenu !== undefined ? Boolean(options.closeMenu) : true;
    const existing = baselineGoalStaged[sessionId] || [];
    const existingIndex = existing.findIndex((item) => item && (item.action === 'pause' || item.action === 'resume') && String(item.edit && item.edit.id) === String(kpiItem.id));
    let nextItems = existing;

    if (paused === serverPaused) {
      if (existingIndex < 0) {
        if (shouldCloseMenu) closeJourneyActionMenu();
        return;
      }
      nextItems = existing.filter((_, index) => index !== existingIndex);
    } else {
      const toggleItem = {
        id: `followup-${kpiItem.id}`,
        mode: kpiItem.kpi_type === 'function_goal' ? 'goal' : 'baseline',
        action: paused ? 'pause' : 'resume',
        edit: {
          id: kpiItem.id,
          kpi_type: kpiItem.kpi_type,
          kpi_subtype: kpiItem.kpi_subtype,
          question_identifier: kpiItem.question_identifier,
          paused_from_followup: paused,
          is_deleted: false,
        },
        lineText: paused ? getJourneyPauseLineText(kpiItem) : getJourneyResumeLineText(kpiItem),
        suppressAnnotation,
      };

      if (existingIndex < 0) {
        nextItems = [...existing, toggleItem];
      } else {
        nextItems = existing.slice();
        nextItems[existingIndex] = toggleItem;
      }
    }

    setBaselineGoalStaged((prev) => {
      const out = { ...prev };
      if (nextItems.length === 0) delete out[sessionId];
      else out[sessionId] = nextItems;
      return out;
    });
    replaceJourneyBaselineGoalDraftNote(sessionId, existing, nextItems);

    if (shouldCloseMenu) closeJourneyActionMenu();
  }, [baselineGoalStaged, closeJourneyActionMenu, replaceJourneyBaselineGoalDraftNote]);

  const currentJourneyBaselineItems = useMemo(() => {
    if (!journeyActionMenu.sessionId) return [];
    return getJourneyKpiItemsByMode(journeyActionMenu.sessionId, 'baseline');
  }, [getJourneyKpiItemsByMode, journeyActionMenu.sessionId]);

  const currentJourneyGoalItems = useMemo(() => {
    if (!journeyActionMenu.sessionId) return [];
    return getJourneyKpiItemsByMode(journeyActionMenu.sessionId, 'goal');
  }, [getJourneyKpiItemsByMode, journeyActionMenu.sessionId]);

  const findJourneyKpiItemForPersistedFollowupToggle = useCallback((parsedToggle) => {
    if (!parsedToggle || !parsedToggle.label) return null;
    const expectedType = parsedToggle.mode === 'goal' ? 'function_goal' : 'pain_baseline';
    const normalizedLabel = parsedToggle.label.replace(/\s+/g, ' ').trim().toLowerCase();
    return (journeyKpiItems || []).find((item) => {
      if (!item || item.is_deleted || item.kpi_type !== expectedType) return false;
      const itemLabel = getJourneyKpiMenuLabel(item).replace(/\s+/g, ' ').trim().toLowerCase();
      return itemLabel === normalizedLabel;
    }) || null;
  }, [journeyKpiItems]);

  const handleRemovePersistedFollowupToggleLine = useCallback((sessionId, lineText, noteFallback = '') => {
    if (!sessionId || !lineText) return;

    const parsedToggle = parsePersistedFollowupToggleLine(lineText);

    setEditNoteValue((prev) => {
      const hasDraft = Object.prototype.hasOwnProperty.call(prev, sessionId);
      const current = hasDraft ? (prev[sessionId] || '') : (noteFallback || '');
      const next = removeLineFromBaselineGoalSection(current, lineText);
      if (next === current && hasDraft) return prev;
      return { ...prev, [sessionId]: next };
    });

    const matchedItem = findJourneyKpiItemForPersistedFollowupToggle(parsedToggle);
    if (!matchedItem) return;
    stageJourneyFollowupToggleItem(sessionId, matchedItem, parsedToggle.reversePaused, { suppressAnnotation: true, closeMenu: false });
  }, [findJourneyKpiItemForPersistedFollowupToggle, stageJourneyFollowupToggleItem]);

  const currentUserId = authUser?.id || authUser?.user_id || authUser?.uuid || null;
  const currentUserNames = useMemo(() => {
    const candidates = [
      authUser?.email,
      authUser?.username,
      authUser?.name,
      authUser?.full_name,
      [authUser?.first_name, authUser?.last_name].filter(Boolean).join(' ').trim(),
    ];
    return candidates
      .map((value) => String(value || '').trim())
      .filter(Boolean);
  }, [authUser]);

  const isCurrentUsersNote = useCallback((note) => {
    if (!note) {
      return false;
    }
    if (currentUserId && note.author != null && String(note.author) === String(currentUserId)) {
      return true;
    }
    return currentUserNames.includes(note.author_name);
  }, [currentUserId, currentUserNames]);

  // Author dropdown state per session — values are user IDs (string)
  const [clinicStaff, setClinicStaff] = useState([]); // [{ userId, firstName, lastName, initials, email }]
  const [selectedAuthors, setSelectedAuthors] = useState({}); // { [sessionId]: userId }
  const [newNoteAuthors, setNewNoteAuthors] = useState({}); // { [sessionId]: userId } empty by default for new notes

  // Fetch clinic staff once token is available (real first/last name + initials)
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/user/list_staff/`, {
          headers: { Authorization: `Token ${token}`, Accept: 'application/json' },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const mapped = (data.staff || []).map(s => {
          const fn = s.first_name || '';
          const ln = s.last_name || '';
          const fallbackInitials = `${fn ? fn[0] : ''}${ln ? ln[0] : ''}`.toUpperCase();
          return {
            userId: s.user_id,
            email: s.email || '',
            firstName: fn,
            lastName: ln,
            initials: (s.initials || fallbackInitials || (s.email || '').slice(0, 2).toUpperCase()),
          };
        });
        if (!cancelled) setClinicStaff(mapped);
      } catch (err) {
        console.warn('Failed to fetch clinic staff for journal author dropdown', err);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Helpers to display staff in dropdowns
  const getStaffById = useCallback((userId) => {
    if (!userId) return null;
    return clinicStaff.find(s => String(s.userId) === String(userId)) || null;
  }, [clinicStaff]);
  const getStaffByAuthorMeta = useCallback((userId, authorName = '') => {
    const directMatch = getStaffById(userId);
    if (directMatch) return directMatch;

    const normalizedAuthorName = String(authorName || '').trim().toLowerCase();
    if (!normalizedAuthorName) return null;

    return clinicStaff.find((staff) => {
      const fullName = `${staff.firstName || ''} ${staff.lastName || ''}`.trim().toLowerCase();
      return (
        String(staff.email || '').trim().toLowerCase() === normalizedAuthorName ||
        fullName === normalizedAuthorName ||
        String(staff.initials || '').trim().toLowerCase() === normalizedAuthorName
      );
    }) || null;
  }, [clinicStaff, getStaffById]);
  const getStaffInitialsById = useCallback((userId) => {
    const s = getStaffById(userId);
    return s ? s.initials : '';
  }, [getStaffById]);
  const getStaffInitialsByAuthor = useCallback((userId, authorName = '') => {
    const staff = getStaffByAuthorMeta(userId, authorName);
    return staff ? staff.initials : '';
  }, [getStaffByAuthorMeta]);
  const getStaffFullNameById = useCallback((userId) => {
    const s = getStaffById(userId);
    if (!s) return '';
    return `${s.firstName} ${s.lastName}`.trim() || s.email || s.initials;
  }, [getStaffById]);
  const getStaffFullNameByAuthor = useCallback((userId, authorName = '') => {
    const staff = getStaffByAuthorMeta(userId, authorName);
    if (!staff) return '';
    return `${staff.firstName} ${staff.lastName}`.trim() || staff.email || staff.initials;
  }, [getStaffByAuthorMeta]);

  useEffect(() => {
    if (Array.isArray(libraryExercises) && libraryExercises.length > 0) {
      setJournalLibraryExercises(libraryExercises);
      return;
    }
    if (!token) return;

    let cancelled = false;
    const fetchExerciseLibrary = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/treatment/exercise/`, {
          headers: { Authorization: `Token ${token}` },
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled) {
          setJournalLibraryExercises(Array.isArray(data.exercises) ? data.exercises : []);
        }
      } catch (error) {
        console.warn('Failed to fetch exercise library in journal view', error);
      }
    };

    fetchExerciseLibrary();
    return () => {
      cancelled = true;
    };
  }, [libraryExercises, token]);

  const fetchAdviceLibraryDraftItems = useCallback(async () => {
    if (Array.isArray(adviceLibraryDraftCacheRef.current)) {
      return adviceLibraryDraftCacheRef.current;
    }
    if (!token || (typeof token === 'string' && token.trim() === '')) {
      return [];
    }

    try {
      const response = await fetch(`${apiBaseUrl}/treatment/all_advices/`, {
        headers: { Authorization: `Token ${token}` },
        method: 'GET',
      });
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      const adviceList = Array.isArray(data) ? data : (Array.isArray(data?.advices) ? data.advices : []);
      adviceLibraryDraftCacheRef.current = adviceList;
      return adviceList;
    } catch (error) {
      console.warn('Failed to fetch advice library for journal restore', error);
      return [];
    }
  }, [token]);

  const findAdviceLibraryRestoreItem = useCallback(async (title) => {
    const normalizedTitle = String(title || '').trim().toLowerCase();
    if (!normalizedTitle) {
      return null;
    }

    const adviceList = await fetchAdviceLibraryDraftItems();
    return adviceList.find((advice) => String(advice?.title || '').trim().toLowerCase() === normalizedTitle) || null;
  }, [fetchAdviceLibraryDraftItems]);

  const refreshAdjustmentLogs = useCallback(async () => {
    const resolvedTreatmentId = treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id;
    if (!resolvedTreatmentId || !selectedPatient?.user_id || !token) {
      setAdjustmentLogs([]);
      return [];
    }

    try {
      const response = await fetch(`${apiBaseUrl}/treatment/exercise-adjustments/${selectedPatient.user_id}/${resolvedTreatmentId}/`, {
        headers: { Authorization: `Token ${token}` },
      });
      if (!response.ok) {
        return [];
      }
      const data = await response.json();
      setAdjustmentLogs(Array.isArray(data) ? data : []);
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.warn('Failed to fetch exercise adjustment logs in journal view', error);
      return [];
    }
  }, [treatmentId, selectedPatient?.treatment_id, diagnosis?.treatment_id, selectedPatient?.user_id, token]);

  useEffect(() => {
    refreshAdjustmentLogs();
  }, [refreshAdjustmentLogs]);

  useEffect(() => {
    const resolvedTreatmentId = treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id;
    if (!resolvedTreatmentId || !token) return;
    let cancelled = false;

    const fetchSettings = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/treatment/treatments/${resolvedTreatmentId}/settings/`, {
          headers: { Authorization: `Token ${token}` },
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (!cancelled) {
          setAdjustmentMode(sanitizeJourneyAdjustmentMode(data.exercise_adjustment_mode));
        }
      } catch (error) {
        console.warn('Failed to fetch treatment settings in journal view', error);
      }
    };

    fetchSettings();
    return () => {
      cancelled = true;
    };
  }, [diagnosis?.treatment_id, sanitizeJourneyAdjustmentMode, selectedPatient?.treatment_id, token, treatmentId]);

  // Sync selectedAuthors with completedSessionsList — default to the note's author
  // (or the currently authenticated user) once we know it. Stored as user_id.
  useEffect(() => {
    if (!completedSessionsList.length) return;
    setSelectedAuthors(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      completedSessionsList.forEach(session => {
        const notes = fetchedNotes[session.id] || [];
        const noteIndex = noteIndices[session.id] || 0;
        const currentNote = notes[noteIndex] || null;
        const noteAuthor = currentNote?.author ? String(currentNote.author) : '';
        const matchedStaff = getStaffByAuthorMeta(noteAuthor, currentNote?.author_name || '');
        const fallback = noteAuthor || (matchedStaff ? String(matchedStaff.userId) : '') || (!notes.length && currentUserId ? String(currentUserId) : '');
        if (fallback && String(updated[session.id] || '') !== String(fallback)) {
          updated[session.id] = String(fallback);
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });
  }, [completedSessionsList, currentUserId, fetchedNotes, getStaffByAuthorMeta, noteIndices]);

  // Sync journalNotes state with backend value when fetchedNotes changes
  useEffect(() => {
    if (!completedSessionsList.length) return;
    setJournalNotes(prev => {
      const updated = { ...prev };
      let hasChanges = false;
      completedSessionsList.forEach(session => {
        const notes = fetchedNotes[session.id] || [];
        const myNote = notes.find(note => isCurrentUsersNote(note));
        // Only update if not currently editing (or if local note is empty)
        if (
          myNote &&
          (!prev[session.id] || prev[session.id].note === undefined || prev[session.id].note === "")
        ) {
          updated[session.id] = { note: myNote.note, noteId: myNote.id };
          hasChanges = true;
        }
        // If no backend note, initialize if not present
        if (!myNote && (!prev[session.id] || prev[session.id].note === undefined)) {
          updated[session.id] = { note: "", noteId: null };
          hasChanges = true;
        }
      });
      return hasChanges ? updated : prev;
    });
  }, [fetchedNotes, completedSessionsList, isCurrentUsersNote]);

  // Pre-load exercise drafts for all sessions so prescription details
  // are visible immediately without requiring the program editor to open.
  useEffect(() => {
    if (!completedSessionsList.length) return;
    const treatmentPhaseIds = new Set((phases || []).map((phase) => String(phase.id)));
    completedSessionsList.slice(-INITIAL_VISIBLE_JOURNAL_SESSION_COUNT).forEach(session => {
      if (!treatmentPhaseIds.has(String(session.phase_id))) return;
      if (!session.exercise_feedback || session.exercise_feedback.length === 0) return;
      if (sessionExerciseDrafts[session.id]) return; // already loaded
      ensureSessionExerciseDraft(session).catch(() => {});
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedSessionsList]);

  // Batch fetch all journal notes for multiple sessions in ONE request
  const fetchAllJournalNotesBatch = async (sessionIds, context = {}) => {
    if (!sessionIds || sessionIds.length === 0) return {};
    
    const userId = context.userId || selectedPatient?.user_id || '';
    const treatmentId = (context.treatmentId !== undefined && context.treatmentId !== null)
      ? context.treatmentId
      : resolvedJourneyTreatmentId;
    
    if (!userId || !treatmentId) return {};
    
    // Build batch key to deduplicate the entire batch
    const batchKey = `batch_${userId}_${treatmentId}_${sessionIds.sort().join(',')}`;
    if (fetchedNotesRef.current.has(batchKey)) {
      return {};
    }
    
    // Mark batch as initiated
    fetchedNotesRef.current.add(batchKey);
    
    try {
      // Make single batch request with all session IDs
      const url = `${apiBaseUrl}/treatment/session/journal_notes/batch/`;
      const response = await dedupedFetch(url, {
        method: 'POST',
        headers: { 
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ session_ids: sessionIds, treatment_id: treatmentId })
      });
      
      if (!response.ok) return {};
      
      const batchData = await response.json();
      // batchData is { [sessionId]: [notes...], ... }
      const allNotesMap = {};
      const pendingJournalUpdates = {};
      
      // Process each session's notes
      Object.entries(batchData || {}).forEach(([sId, notes]) => {
        const scopedKey = `${userId}_${treatmentId}_${sId}`;
        fetchedNotesRef.current.add(scopedKey);
        const myNote = Array.isArray(notes) ? notes.find(note => isCurrentUsersNote(note)) : null;

        allNotesMap[sId] = notes || [];
        pendingJournalUpdates[sId] = { note: myNote ? myNote.note : "", noteId: myNote ? myNote.id : null };
      });

      // Apply all journal note initializations in one state commit.
      setJournalNotes(prev => {
        let hasChanges = false;
        const updated = { ...prev };
        Object.entries(pendingJournalUpdates).forEach(([sId, noteState]) => {
          const current = prev[sId];
          if (!current || current.note === undefined || current.note === "") {
            updated[sId] = noteState;
            hasChanges = true;
          }
        });
        return hasChanges ? updated : prev;
      });
      
      return allNotesMap;
    } catch (err) {
      console.error('[fetchAllJournalNotesBatch] Error fetching batch:', err);
      return {};
    }
  };
  
  // Legacy single-session fetch (for save operations that need to refetch one session)
  const fetchJournalNotes = async (sessionId, force = false, context = {}) => {
    const userId = context.userId || selectedPatient?.user_id || '';
    const treatmentId = (context.treatmentId !== undefined && context.treatmentId !== null)
      ? context.treatmentId
      : (selectedPatient?.treatment_id || diagnosis?.treatment_id || '');
    if (!userId || !treatmentId || !sessionId) return [];

    // Patient-scope synthetic id uses the shared loader so concurrent initial
    // loads share one request and forced post-save refreshes stay fresh.
    if (typeof sessionId === 'string' && sessionId.startsWith('patient-')) {
      return fetchPatientJournalNotes(force);
    }

    const scopedKey = `${userId}_${treatmentId}_${sessionId}`;
    
    if (!force && fetchedNotesRef.current.has(scopedKey)) {
      return fetchedNotes[sessionId] || [];
    }
    
    fetchedNotesRef.current.add(scopedKey);

    const response = await dedupedFetch(`${apiBaseUrl}/treatment/session/journal_note/${sessionId}/?treatment_id=${encodeURIComponent(treatmentId)}`, {
      headers: { "Authorization": `Token ${token}` }
    });
    if (!response.ok) return [];
    const notes = await response.json();
    const myNote = notes.find(note => isCurrentUsersNote(note)) || null;
    setJournalNotes(prev => {
      const current = prev[sessionId];
      if (!current || current.note === undefined || current.note === "") {
        return { ...prev, [sessionId]: { note: myNote ? myNote.note : "", noteId: myNote ? myNote.id : null } };
      }
      return prev;
    });
    return notes;
  };

  const resolveNotesAfterSave = useCallback((refetchedNotes, savedResponse, previousNotes = []) => {
    if (Array.isArray(refetchedNotes) && refetchedNotes.length > 0) {
      return refetchedNotes;
    }

    const savedNote = savedResponse && typeof savedResponse === 'object'
      ? (savedResponse.id
        ? savedResponse
        : (savedResponse.note && typeof savedResponse.note === 'object' && savedResponse.note.id
          ? savedResponse.note
          : null))
      : null;

    if (!savedNote) {
      return Array.isArray(refetchedNotes) ? refetchedNotes : (Array.isArray(previousNotes) ? previousNotes : []);
    }

    const existingNotes = Array.isArray(previousNotes) ? previousNotes : [];
    return [
      savedNote,
      ...existingNotes.filter((note) => String(note?.id || '') !== String(savedNote.id || '')),
    ];
  }, []);

  const handleConfirmExternalProgramUpdate = async () => {
    const pending = pendingExternalProgramUpdate;
    if (!pending || !token || savingExternalProgramUpdate) return;

    if (pending.contextKey !== journeyTreatmentContextKey) {
      setPendingExternalProgramUpdate(null);
      return;
    }

    const targetSessionId = pending.targetSessionId;
    const journalLines = pending.journalLines || [];
    if (!targetSessionId || journalLines.length === 0) {
      setPendingExternalProgramUpdate(null);
      return;
    }

    setSavingExternalProgramUpdate(true);
    try {
      const isPatientScope = isPatientScopeSessionId(targetSessionId);
      const freshNotes = isPatientScope
        ? await fetchPatientJournalNotes(true)
        : await fetchJournalNotes(targetSessionId, true);
      const existingNotes = Array.isArray(freshNotes) ? freshNotes : (fetchedNotes[targetSessionId] || []);
      const latestNote = existingNotes[0]?.note || '';
      let nextNote = mergeTreatmentAdjustmentsSection(latestNote, journalLines);

      if (canAutoSyncGeneratedPlanSection(nextNote)) {
        nextNote = mergePlanUntilNextTimeSection(
          nextNote,
          buildPlanText('mixed', [], { programLines: journalLines })
        );
        nextNote = upsertPatientAcceptSection(nextNote);
      }

      const savedResponse = await saveJournalNoteToBackend(targetSessionId, nextNote, token);
      const refreshedNotes = isPatientScope
        ? await fetchPatientJournalNotes(true)
        : await fetchJournalNotes(targetSessionId, true);

      setFetchedNotes((prev) => ({
        ...prev,
        [targetSessionId]: resolveNotesAfterSave(refreshedNotes, savedResponse, prev[targetSessionId] || []),
      }));
      setNoteIndices((prev) => ({ ...prev, [targetSessionId]: 0 }));
      setJournalNotes((prev) => ({ ...prev, [targetSessionId]: { note: nextNote, noteId: savedResponse?.id || null } }));

      const latestUpdate = readLatestExerciseProgramUpdate();
      const latestUpdateKey = latestUpdate
        ? `${latestUpdate.ts || '0'}::${latestUpdate.sessionId || ''}::${latestUpdate.phaseId || ''}::${latestUpdate.source || 'program'}`
        : null;
      if (latestUpdateKey === pending.updateKey) {
        clearLatestExerciseProgramUpdate({
          treatmentId: pending.treatmentId,
          phaseId: pending.phaseId,
          sessionId: pending.payloadSessionId,
        });
      }

      setPendingExternalProgramUpdate(null);
      setSnackbarMsg('Program changes added as a new journal note.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      setPendingExternalProgramUpdate((current) => (current ? { ...current, autoConfirm: false } : current));
      setSnackbarMsg(error.message || 'Unable to add the program changes to the journal.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSavingExternalProgramUpdate(false);
    }
  };


  // Handler for saving journal note per session.
  // Session notes are append-only so previous versions remain navigable.
  const handleSaveJournalNote = async (sessionId) => {
    const noteObj = journalNotes[sessionId] || { note: "", noteId: null };
    const note = noteObj.note || "";
    if (!note.trim()) return;
    const session = completedSessionsList.find(item => String(item.id) === String(sessionId));
    const noteToSave = buildNoteWithLatestAdjustments(sessionId, note);
    setSavingNotes(prev => ({ ...prev, [sessionId]: true }));
    try {
      await persistSessionAdviceDraft(sessionId);
      await applySuggestionDecisions(sessionId);
      const authorId = selectedAuthors[sessionId] || null;
      const savedResponse = await saveJournalNoteToBackend(sessionId, noteToSave, token, authorId);
      // Re-fetch notes with force=true to get the updated list after save
      const notes = await fetchJournalNotes(sessionId, true);
      setFetchedNotes(prev => ({
        ...prev,
        [sessionId]: resolveNotesAfterSave(notes, savedResponse, prev[sessionId] || []),
      }));
      setSuggestionDecisions(prev => ({ ...prev, [sessionId]: {} }));
      clearSessionProgramChangeSummary(sessionId, session?.phase_id);
      // Persist any staged baseline/goal items now that the journal note saved.
      const staged = await persistStagedBaselineGoal(sessionId);
      if (staged.persisted > 0) {
        clearBaselineGoalStaged(sessionId);
      }
      setSnackbarMsg('Note saved successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      alert("Failed to save note: " + e.message);
    }
    setSavingNotes(prev => ({ ...prev, [sessionId]: false }));
  };


  // === Patient-scoped journal notes (no completed sessions yet) ===
  // Synthetic session id used to render patient-scope notes through the same
  // session-row UI. Format: 'patient-<user_id>'.
  const buildPatientSyntheticSessionId = (userId) => (userId ? `patient-${userId}` : null);
  const isPatientScopeSessionId = (sessionId) => typeof sessionId === 'string' && sessionId.startsWith('patient-');
  const extractPatientIdFromSessionId = (sessionId) => (isPatientScopeSessionId(sessionId) ? sessionId.slice('patient-'.length) : null);

  const buildPatientScopeSession = useCallback((patientId) => {
    if (!patientId) return null;
    const phase = phases.find(p => String(p.id) === String(selectedPhaseId));
    return {
      id: `patient-${patientId}`,
      is_patient_scope: true,
      patient_user_id: patientId,
      completed_date: null,
      phase: phase?.name || '',
      phase_id: selectedPhaseId || phase?.id || null,
      phase_index: null,
      exercise_feedback: [],
      session_feedback: [],
    };
  }, [phases, selectedPhaseId]);

  // Fetch all patient-scoped notes for the selected patient.
  const fetchPatientJournalNotes = useCallback((force = false) => {
    const patientId = selectedPatient?.user_id;
    if (!patientId || !resolvedJourneyTreatmentId || !token) return Promise.resolve([]);
    const cachedContext = journeyContextCache.get(journeyTreatmentContextKey);
    if (!force
      && cachedContext
      && Array.isArray(cachedContext.patientJournalNotes)
      && Date.now() - cachedContext.fetchedAt < JOURNEY_CONTEXT_CACHE_TTL_MS) {
      const synthId = buildPatientSyntheticSessionId(patientId);
      setPatientJournalNotes(cachedContext.patientJournalNotes);
      if (synthId) {
        setFetchedNotes(prev => ({ ...prev, [synthId]: cachedContext.patientJournalNotes }));
      }
      return Promise.resolve(cachedContext.patientJournalNotes);
    }

    if (!force) {
      const inFlightRequest = journeyPatientNotesInFlight.get(journeyTreatmentContextKey);
      if (inFlightRequest) {
        return inFlightRequest;
      }
    }

    const request = (async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/treatment/patient/journal_notes/${patientId}/?treatment_id=${encodeURIComponent(resolvedJourneyTreatmentId)}`,
          { headers: { 'Authorization': `Token ${token}` } }
        );
        if (!response.ok) return [];
        const notes = await response.json();
        const list = Array.isArray(notes) ? notes : [];
        setPatientJournalNotes(list);
        // Also hydrate fetchedNotes under the synthetic session id so the
        // session-row UI (versions, edit, etc.) picks them up directly.
        const synthId = buildPatientSyntheticSessionId(patientId);
        if (synthId) {
          setFetchedNotes(prev => ({ ...prev, [synthId]: list }));
        }
        journeyContextCache.set(journeyTreatmentContextKey, {
          ...(cachedContext || {}),
          fetchedAt: Date.now(),
          patientJournalNotes: list,
        });
        return list;
      } catch (e) {
        // Silent — patient-scoped notes are optional and the empty-state
        // editor will simply start blank.
        return [];
      }
    })();

    if (!force) {
      journeyPatientNotesInFlight.set(journeyTreatmentContextKey, request);
      request.finally(() => {
        if (journeyPatientNotesInFlight.get(journeyTreatmentContextKey) === request) {
          journeyPatientNotesInFlight.delete(journeyTreatmentContextKey);
        }
      });
    }

    return request;
  }, [journeyTreatmentContextKey, resolvedJourneyTreatmentId, selectedPatient?.user_id, token]);

  // Load on patient change.
  useEffect(() => {
    setPatientJournalNotes([]);
    setPatientNoteDraft('');
    setPatientNoteEditingId(null);
    fetchPatientJournalNotes();
  }, [fetchPatientJournalNotes]);

  // Synthetic patient-scope session: when no real sessions are completed yet,
  // we still want the full journal-row UI (toolbar, Auto-generate, baseline/goal,
  // follow-up, exercise program editor, etc.) so the user can author a
  // patient-level journal note and edit the program before any session exists.
  // IMPORTANT: this memo must be declared BEFORE any useEffect that references it.
  const syntheticPatientSession = useMemo(() => {
    if (!selectedPatient?.user_id) return null;
    if (completedSessionsList.length > 0) return null;
    return buildPatientScopeSession(selectedPatient.user_id);
  }, [buildPatientScopeSession, completedSessionsList.length, selectedPatient?.user_id]);

  const persistentPatientScopeSession = useMemo(() => {
    if (!selectedPatient?.user_id) return null;
    if (completedSessionsList.length === 0) return syntheticPatientSession;
    if (!patientJournalNotes.length) return null;
    return buildPatientScopeSession(selectedPatient.user_id);
  }, [buildPatientScopeSession, completedSessionsList.length, patientJournalNotes.length, selectedPatient?.user_id, syntheticPatientSession]);

  // When the synthetic patient-scope session is in use (no real sessions yet),
  // proactively load its exercise draft so the right-hand "Current exercise
  // program:" panel renders the patient's actual program (driven by
  // selectedPhaseId) and the program editor sidebar opens with real data.
  // We deliberately depend only on the synthetic id and phase so this fires
  // once per (patient, phase) and not on unrelated state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!syntheticPatientSession) return;
    if (!syntheticPatientSession.phase_id) return;
    if (sessionExerciseDrafts[syntheticPatientSession.id]) return;
    ensureSessionExerciseDraft(syntheticPatientSession).catch(() => { /* silent */ });
  }, [syntheticPatientSession?.id, syntheticPatientSession?.phase_id]);

  // Save a patient-scoped note. Creates a new note (POST with patient
  // and no session) or updates an existing one (PATCH on its pk).
  const handleSavePatientJournalNote = async () => {
    const patientId = selectedPatient?.user_id;
    const note = (patientNoteDraft || '').trim();
    if (!patientId || !token || !note) return;
    setPatientNoteSaving(true);
    try {
      let response;
      if (patientNoteEditingId) {
        response = await fetch(
          `${apiBaseUrl}/treatment/session/journal_note/update/${patientNoteEditingId}/`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
            body: JSON.stringify({ note, treatment_id: resolvedJourneyTreatmentId }),
          }
        );
      } else {
        response = await fetch(
          `${apiBaseUrl}/treatment/session/journal_note/`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Token ${token}` },
            body: JSON.stringify({ patient: patientId, note, treatment_id: resolvedJourneyTreatmentId }),
          }
        );
      }
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err || 'Failed to save patient note');
      }
      setPatientNoteDraft('');
      setPatientNoteEditingId(null);
      await fetchPatientJournalNotes(true);
      setSnackbarMsg('Note saved successfully.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (e) {
      alert('Failed to save note: ' + e.message);
    } finally {
      setPatientNoteSaving(false);
    }
  };


  // Save a journal note to the backend (POST). Optional authorId overrides default author.
  const saveJournalNoteToBackend = async (sessionId, note, token, authorId = null) => {
    const isPatientScope = typeof sessionId === 'string' && sessionId.startsWith('patient-');
    const payload = isPatientScope
      ? { patient: sessionId.slice('patient-'.length), note, treatment_id: resolvedJourneyTreatmentId }
      : { session: sessionId, note, treatment_id: resolvedJourneyTreatmentId };
    if (authorId) payload.author_id = authorId;
    const response = await fetch(
      `${apiBaseUrl}/treatment/session/journal_note/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Token ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );
    if (response.status === 401) throw new Error("Authentication required. Please log in again.");
    if (response.status === 403) throw new Error("You do not have permission to add a note.");
    if (!response.ok) {
      let errMsg = "Failed to save note";
      try {
        const err = await response.json();
        errMsg = err.detail || JSON.stringify(err) || errMsg;
      } catch {}
      throw new Error(errMsg);
    }
    return await response.json();
  };

  const normalizeExerciseKey = useCallback((value) => (value || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, ''), []);

  const getDraftExerciseKey = useCallback((exercise) => exercise?.id || exercise?.uniqueKey || exercise?.identifier || exercise?.title || '', []);

  const normalizeExerciseComparisonValue = useCallback((value) => (value || '').toString().trim().toLowerCase(), []);

  const getExerciseAdjustmentAddLine = useCallback((exercise) => {
    return formatAddedExerciseProgramJournalLine(exercise || {});
  }, []);

  const isDraftAddedExercise = useCallback((draft, exercise) => {
    if (!draft || !Array.isArray(draft.originalExercises) || !exercise) {
      return false;
    }

    const targetKey = getDraftExerciseKey(exercise);
    const targetIdentifier = (exercise?.identifier || '').toString().trim();
    const targetTitle = normalizeExerciseComparisonValue(exercise?.title);

    return !draft.originalExercises.some((originalExercise) => {
      const originalKey = getDraftExerciseKey(originalExercise);
      const originalIdentifier = (originalExercise?.identifier || '').toString().trim();
      const originalTitle = normalizeExerciseComparisonValue(originalExercise?.title);

      if (targetKey && originalKey && targetKey === originalKey) {
        return true;
      }
      if (targetIdentifier && originalIdentifier && targetIdentifier === originalIdentifier) {
        return true;
      }
      if (!targetIdentifier && targetTitle && !originalIdentifier && targetTitle === originalTitle) {
        return true;
      }

      return false;
    });
  }, [getDraftExerciseKey, normalizeExerciseComparisonValue]);

  const matchesLogicalDraftExercise = useCallback((leftExercise, rightExercise) => {
    if (!leftExercise || !rightExercise) {
      return false;
    }

    const leftIdentifier = (leftExercise?.identifier || '').toString().trim();
    const rightIdentifier = (rightExercise?.identifier || '').toString().trim();
    if (leftIdentifier && rightIdentifier) {
      return leftIdentifier === rightIdentifier;
    }

    const leftTitle = normalizeExerciseComparisonValue(leftExercise?.title || leftExercise?.identifier || '');
    const rightTitle = normalizeExerciseComparisonValue(rightExercise?.title || rightExercise?.identifier || '');
    if (leftTitle && rightTitle) {
      return leftTitle === rightTitle;
    }

    const leftKey = getDraftExerciseKey(leftExercise);
    const rightKey = getDraftExerciseKey(rightExercise);
    return Boolean(leftKey && rightKey && leftKey === rightKey);
  }, [getDraftExerciseKey, normalizeExerciseComparisonValue]);

  const findRestorableOriginalExercise = useCallback((draft, exercise) => {
    if (!draft || !Array.isArray(draft.originalExercises) || !Array.isArray(draft.exercises) || !exercise) {
      return null;
    }

    const originalMatch = draft.originalExercises.find((originalExercise) => matchesLogicalDraftExercise(originalExercise, exercise));
    if (!originalMatch) {
      return null;
    }

    const activeMatch = draft.exercises.some((currentExercise) => matchesLogicalDraftExercise(currentExercise, exercise));
    return activeMatch ? null : originalMatch;
  }, [matchesLogicalDraftExercise]);

  const stripDraftAddedExerciseArtifacts = useCallback((sessionId, exercise) => {
    if (!sessionId || !exercise) {
      return;
    }

    const addedLine = getExerciseAdjustmentAddLine(exercise);
    const targetIdentifier = (exercise?.identifier || '').toString().trim();
    const targetTitle = normalizeExerciseComparisonValue(exercise?.title || exercise?.identifier || 'Exercise');

    setEditNoteValue((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }

      const nextDraft = removeLineFromAdjustmentsSection(currentDraft, addedLine);
      if (nextDraft === currentDraft) {
        return prev;
      }

      return {
        ...prev,
        [sessionId]: nextDraft,
      };
    });

    setProgramChangeSummaries((prev) => {
      const currentSummary = prev[sessionId];
      const currentJournalSummary = getJournalProgramSummaryPart(currentSummary);
      if (!currentJournalSummary) {
        return prev;
      }

      const nextAdded = (currentJournalSummary.added || []).filter((item) => {
        const itemIdentifier = (item?.identifier || '').toString().trim();
        const itemTitle = normalizeExerciseComparisonValue(item?.title || item?.identifier || 'Exercise');

        if (targetIdentifier && itemIdentifier) {
          return itemIdentifier !== targetIdentifier;
        }

        return itemTitle !== targetTitle;
      });
      const nextJournalLines = (currentJournalSummary.journalLines || []).filter((line) => line !== addedLine);

      if (nextAdded.length === (currentJournalSummary.added || []).length && nextJournalLines.length === (currentJournalSummary.journalLines || []).length) {
        return prev;
      }

      const remainingJournalChanges = (currentJournalSummary.replacements?.length || 0)
        + (currentJournalSummary.updated?.length || 0)
        + (currentJournalSummary.removed?.length || 0)
        + nextAdded.length;
      const hasChanges = remainingJournalChanges > 0 || (currentJournalSummary.reordered?.length || 0) > 0;
      const nextJournalSummary = hasChanges
        ? {
            ...currentJournalSummary,
            added: nextAdded,
            journalLines: nextJournalLines,
            hasChanges,
          }
        : null;
      const nextSummary = mergeProgramChangeSummaryEntries({
        journalSummary: nextJournalSummary,
        externalSummary: getExternalProgramSummaryPart(currentSummary),
      });

      if (!nextSummary) {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      }

      return {
        ...prev,
        [sessionId]: nextSummary,
      };
    });
  }, [getExerciseAdjustmentAddLine, normalizeExerciseComparisonValue]);

  const cloneExerciseForDraft = useCallback((exercise, fallbackIndex = 1) => ({
    ...exercise,
    uniqueKey: exercise?.uniqueKey || exercise?.id || uuid(),
    index: exercise?.index ?? fallbackIndex,
    sets: exercise?.sets ?? 3,
    repetitions: exercise?.repetitions ?? 10,
    time: exercise?.time ?? 30,
    pause: exercise?.pause ?? 15,
    frequencyType: normalizeScheduleType(exercise?.frequencyType || 'daily'),
    frequency: exercise?.frequency ?? 1,
    daysInbetween: normalizeScheduleType(exercise?.frequencyType || 'daily') === 'weekly'
      ? (exercise?.daysInbetween ?? exercise?.daysInBetween ?? exercise?.frequency ?? 1)
      : '',
    description: exercise?.description || '',
    title: exercise?.title || '',
    img: exercise?.img || exercise?.picture || exercise?.image_url || null,
    video: exercise?.video || exercise?.video_url || null,
    is_paused: Boolean(exercise?.is_paused),
  }), []);

  const reindexDraftExercises = useCallback((exerciseList) => exerciseList.map((exercise, index) => ({
    ...exercise,
    index: index + 1,
  })), []);

  const updateSuggestionDecision = useCallback((sessionId, issueKey, patch) => {
    setSuggestionDecisions(prev => ({
      ...prev,
      [sessionId]: {
        ...(prev[sessionId] || {}),
        [issueKey]: {
          ...((prev[sessionId] || {})[issueKey] || {}),
          ...patch,
        },
      },
    }));
  }, []);

  const fetchPhaseExercisesForDraft = useCallback(async (phaseId, { force = false } = {}) => {
    const resolvedTreatmentId = treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id;
    if (!phaseId || !resolvedTreatmentId || !selectedPatient?.user_id || !token) {
      return [];
    }

    const cacheKey = `${selectedPatient.user_id}::${resolvedTreatmentId}::${phaseId}`;
    const cached = journeyPhaseExerciseCache.get(cacheKey);
    const isFresh = cached && Date.now() - cached.fetchedAt < JOURNEY_CONTEXT_CACHE_TTL_MS;

    if (!force && isFresh && cached.exercises) {
      return cached.exercises.map((exercise, index) => cloneExerciseForDraft(exercise, index + 1));
    }

    if (cached?.promise) {
      const exercises = await cached.promise;
      return exercises.map((exercise, index) => cloneExerciseForDraft(exercise, index + 1));
    }

    const promise = fetch(`${apiBaseUrl}/treatment/exercises/phase/${phaseId}/${selectedPatient.user_id}/?treatment_id=${resolvedTreatmentId}`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then(async (response) => {
        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || 'Failed to fetch phase exercises');
        }
        const data = await response.json();
        return Array.isArray(data.exercises) ? data.exercises : [];
      })
      .then((exerciseList) => {
        journeyPhaseExerciseCache.set(cacheKey, { fetchedAt: Date.now(), exercises: exerciseList });
        return exerciseList;
      })
      .catch((error) => {
        journeyPhaseExerciseCache.delete(cacheKey);
        throw error;
      });

    journeyPhaseExerciseCache.set(cacheKey, { fetchedAt: Date.now(), promise });
    const exercises = await promise;
    return exercises.map((exercise, index) => cloneExerciseForDraft(exercise, index + 1));
  }, [treatmentId, selectedPatient?.treatment_id, diagnosis?.treatment_id, selectedPatient?.user_id, token, cloneExerciseForDraft]);

  const deriveSessionIssues = useCallback((session, adjustments = adjustmentLogs, librarySource = availableLibraryExercises, options = {}) => {
    if (!session) {
      return [];
    }

    /**
     * REGULATORY COMPLIANCE ANCHOR: CLASS I / NON-MDR SAFEGUARD
     *
     * The pending / suggest_only inference path below is retained only as a
     * dormant compatibility branch for historical note rendering and legacy
     * issue shaping. Backend CDS generation is disabled and current UI mode
     * sanitization prevents live suggestive execution.
     */
    const allowInference = options.allowInference !== false;
    const inferredDisposition = options.inferredDisposition || null;
    const applied = adjustments.filter(adjustment => adjustment.status === 'applied');
    const pending = adjustments.filter(adjustment => adjustment.status === 'pending');
    const adjByFeedback = {};
    const adjBySessionExercise = {};

    for (const adjustment of [...applied, ...pending]) {
      if (adjustment.feedback) {
        adjByFeedback[String(adjustment.feedback)] = adjustment;
      }
      const key = [adjustment.session || '', adjustment.original_identifier || '', (adjustment.original_title || '').toLowerCase()].join('::');
      adjBySessionExercise[key] = adjustment;
    }

    const issues = [];
    for (const feedback of session.exercise_feedback || []) {
      const name = feedback.exercise_title || 'Exercise';
      const feedbackId = feedback.id != null ? String(feedback.id) : '';
      const originalIdentifier = feedback.exercise_id || '';
      const pain = feedback.painLevel != null ? parseFloat(feedback.painLevel) : null;
      const reasonDetails = getFeedbackReasonDetails({ pain, difficulty: feedback.difficulty });
      const primaryReason = reasonDetails[0] || null;
      let issue = null;

      if (primaryReason) {
        issue = {
          name,
          reason: joinReasonTexts(reasonDetails.map(detail => detail.reason)),
          reasonKey: primaryReason.reasonKey,
          reasonKeys: reasonDetails.map(detail => detail.reasonKey),
        };
      }

      if (!issue) {
        continue;
      }

      issue.feedbackId = feedbackId;
      issue.originalIdentifier = originalIdentifier;
      issue.issueKey = `${feedbackId || originalIdentifier || name}::${(issue.reasonKeys || [issue.reasonKey]).join('+')}`;
      issue.originalPrescription = {
        sets: feedback?.prescribed_sets,
        repetitions: feedback?.prescribed_repetitions,
        time: feedback?.prescribed_time,
        pause: feedback?.prescribed_pause,
        doseType: ((feedback?.prescribed_repetitions ?? 0) > 0 && (feedback?.prescribed_time ?? 0) === 0) ? 'repetitions' : 'time',
        frequencyType: feedback?.prescribed_frequency_type,
        frequency: feedback?.prescribed_frequency,
        daysInbetween: feedback?.prescribed_days_inbetween,
        daysInBetween: feedback?.prescribed_days_inbetween,
      };
      const sessionExerciseKey = [session.id || '', feedback.exercise_id || '', name.toLowerCase()].join('::');
      const adjustment = adjByFeedback[feedbackId] || adjBySessionExercise[sessionExerciseKey];

      if (adjustment && adjustment.status === 'applied') {
        issue.modeAtTime = getLoggedAdjustmentModeAtTime(adjustment);
        issue.modeSourceAtTime = getLoggedAdjustmentModeSourceAtTime(adjustment);
        issue.isSystemGenerated = isLoggedAdjustmentSystemGenerated(adjustment);
        issue.replacement = adjustment.replacement_title || adjustment.replacement_identifier;
        issue.replacementIdentifier = adjustment.replacement_identifier;
        issue.adjusted = true;
        issue.adjId = adjustment.id;
      } else if (adjustment && adjustment.status === 'pending') {
        issue.modeAtTime = getLoggedAdjustmentModeAtTime(adjustment);
        issue.modeSourceAtTime = getLoggedAdjustmentModeSourceAtTime(adjustment);
        issue.isSystemGenerated = false;
        issue.replacement = adjustment.replacement_title || adjustment.replacement_identifier;
        issue.replacementIdentifier = adjustment.replacement_identifier;
        issue.pending = true;
        issue.adjId = adjustment.id;
      } else if (allowInference) {
        const normalizedIdentifier = normalizeExerciseKey(originalIdentifier);
        const normalizedName = normalizeExerciseKey(name);
        const originalExercise = librarySource.find(exercise => {
          const exerciseIdentifier = normalizeExerciseKey(exercise.identifier);
          const exerciseTitle = normalizeExerciseKey(exercise.title);
          return (normalizedIdentifier && exerciseIdentifier === normalizedIdentifier) ||
            (normalizedName && (exerciseTitle === normalizedName || exerciseIdentifier === normalizedName));
        });
        const alternativeOptions = originalExercise?.[issue.reasonKey] || originalExercise?.alternatives?.[issue.reasonKey] || [];
        const firstAlternative = Array.isArray(alternativeOptions) ? alternativeOptions[0] : alternativeOptions;
        const inferredIdentifier = typeof firstAlternative === 'string'
          ? firstAlternative
          : firstAlternative?.identifier || null;
        const inferredExercise = inferredIdentifier
          ? librarySource.find(exercise => exercise.identifier === inferredIdentifier)
          : null;
        if (inferredExercise) {
          issue.replacement = inferredExercise.title;
          issue.replacementIdentifier = inferredExercise.identifier;
          issue.inferred = true;
          issue.modeAtTime = inferredDisposition === 'adjusted'
            ? 'auto_replace'
            : inferredDisposition === 'pending'
              ? 'suggest_only'
              : null;
          issue.modeSourceAtTime = null;
          issue.isSystemGenerated = inferredDisposition === 'adjusted';
          if (inferredDisposition === 'adjusted') {
            issue.adjusted = true;
          } else if (inferredDisposition === 'pending') {
            issue.pending = true;
          }
        }
      }

      issues.push(issue);
    }

    return issues;
  }, [adjustmentLogs, availableLibraryExercises, normalizeExerciseKey]);

  const buildIssueFromLoggedAdjustment = useCallback((adjustment) => {
    const reasonKey = adjustment?.reason || null;
    return {
      name: adjustment?.original_title || adjustment?.original_identifier || 'Exercise',
      reason: getLoggedAdjustmentReasonText(adjustment),
      reasonKey,
      reasonKeys: reasonKey ? [reasonKey] : [],
      feedbackId: adjustment?.feedback != null ? String(adjustment.feedback) : '',
      originalIdentifier: adjustment?.original_identifier || '',
      issueKey: adjustment?.id ? `log:${adjustment.id}` : `log:${adjustment?.session || ''}:${adjustment?.original_identifier || ''}:${reasonKey || ''}`,
      replacement: adjustment?.replacement_title || adjustment?.replacement_identifier || '',
      replacementIdentifier: adjustment?.replacement_identifier || '',
      adjusted: adjustment?.status === 'applied',
      pending: adjustment?.status === 'pending',
      adjId: adjustment?.id,
      modeAtTime: getLoggedAdjustmentModeAtTime(adjustment),
      modeSourceAtTime: getLoggedAdjustmentModeSourceAtTime(adjustment),
      isSystemGenerated: isLoggedAdjustmentSystemGenerated(adjustment),
    };
  }, []);

  const getSessionLoggedAdjustmentIssues = useCallback((session) => {
    if (!session?.id) {
      return [];
    }

    return adjustmentLogs
      .filter((adjustment) => {
        if (!adjustment || adjustment.status === 'rejected') {
          return false;
        }
        return String(adjustment.session || '') === String(session.id);
      })
      .map((adjustment) => ({
        ...buildIssueFromLoggedAdjustment(adjustment),
        sessionId: session.id,
      }));
  }, [adjustmentLogs, buildIssueFromLoggedAdjustment]);

  const ensureSessionExerciseDraft = useCallback(async (session, options = {}) => {
    const phaseId = session.phase_id || selectedPhaseId;
    if (!phaseId) {
      throw new Error('No phase available for this session');
    }

    const existingDraft = sessionExerciseDrafts[session.id];
    if (existingDraft && existingDraft.phaseId === phaseId && !options.forceReload) {
      return existingDraft;
    }

    const draftExercises = await fetchPhaseExercisesForDraft(phaseId, { force: Boolean(options.forceReload) });
    const nextDraft = {
      phaseId,
      phaseName: session.phase || phases.find(phase => String(phase.id) === String(phaseId))?.name || 'Selected phase',
      originalExercises: draftExercises,
      exercises: draftExercises,
      dirty: false,
      version: (existingDraft?.version || 0) + 1,
    };

    setSessionExerciseDrafts(prev => ({
      ...prev,
      [session.id]: nextDraft,
    }));

    return nextDraft;
  }, [selectedPhaseId, sessionExerciseDrafts, fetchPhaseExercisesForDraft, phases]);

  const findDraftTargetExercise = useCallback((draftExercises, issue) => {
    const normalizedIssueName = normalizeExerciseKey(issue.name);

    if (issue.adjusted && issue.replacementIdentifier) {
      const currentReplacement = draftExercises.find(exercise =>
        exercise.identifier === issue.replacementIdentifier ||
        normalizeExerciseKey(exercise.title) === normalizeExerciseKey(issue.replacement)
      );
      if (currentReplacement) {
        return currentReplacement;
      }
    }

    return draftExercises.find(exercise =>
      (issue.originalIdentifier && exercise.identifier === issue.originalIdentifier) ||
      normalizeExerciseKey(exercise.title) === normalizedIssueName
    ) || null;
  }, [normalizeExerciseKey]);

  const buildConfiguredDraftExercise = useCallback((selectedExercise, targetExercise, fallbackIndex) => {
    const cloned = cloneExerciseForDraft(selectedExercise, targetExercise?.index || fallbackIndex);
    return {
      ...cloned,
      id: targetExercise && targetExercise.identifier === selectedExercise.identifier ? targetExercise.id : undefined,
      uniqueKey: targetExercise?.uniqueKey || cloned.uniqueKey,
      index: targetExercise?.index ?? cloned.index,
      sets: selectedExercise.sets ?? targetExercise?.sets ?? cloned.sets,
      repetitions: selectedExercise.repetitions ?? targetExercise?.repetitions ?? cloned.repetitions,
      time: selectedExercise.time ?? targetExercise?.time ?? cloned.time,
      pause: selectedExercise.pause ?? targetExercise?.pause ?? cloned.pause,
      frequencyType: normalizeScheduleType(selectedExercise.frequencyType || targetExercise?.frequencyType || cloned.frequencyType),
      frequency: selectedExercise.frequency ?? targetExercise?.frequency ?? cloned.frequency,
      daysInbetween: normalizeScheduleType(selectedExercise.frequencyType || targetExercise?.frequencyType || cloned.frequencyType) === 'weekly'
        ? (selectedExercise.daysInbetween ?? targetExercise?.daysInbetween ?? cloned.daysInbetween ?? selectedExercise.frequency ?? targetExercise?.frequency ?? cloned.frequency ?? 1)
        : '',
      description: selectedExercise.description || targetExercise?.description || cloned.description,
      title: selectedExercise.title || targetExercise?.title || cloned.title,
    };
  }, [cloneExerciseForDraft]);

  const upsertExerciseInDraft = useCallback((exerciseList, selectedExercise, targetExercise = null) => {
    const configuredExercise = buildConfiguredDraftExercise(selectedExercise, targetExercise, exerciseList.length + 1);
    if (!targetExercise) {
      return reindexDraftExercises([...exerciseList, configuredExercise]);
    }

    const targetKey = getDraftExerciseKey(targetExercise);
    const targetIndex = exerciseList.findIndex(exercise => getDraftExerciseKey(exercise) === targetKey);
    if (targetIndex === -1) {
      return reindexDraftExercises([...exerciseList, configuredExercise]);
    }

    const nextExercises = [...exerciseList];
    nextExercises[targetIndex] = configuredExercise;
    return reindexDraftExercises(nextExercises);
  }, [buildConfiguredDraftExercise, getDraftExerciseKey, reindexDraftExercises]);

  const stageIssueExerciseEdit = useCallback((sessionId, issue, configuredExercise) => {
    setSessionExerciseDrafts(prev => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }
      const targetExercise = findDraftTargetExercise(currentDraft.exercises, issue);
      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          exercises: upsertExerciseInDraft(currentDraft.exercises, configuredExercise, targetExercise),
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });

    updateSuggestionDecision(sessionId, issue.issueKey, {
      action: 'edit',
      customExercise: configuredExercise.identifier,
      configuredExercise,
    });
  }, [findDraftTargetExercise, upsertExerciseInDraft, updateSuggestionDecision]);

  const stageProgramExerciseAdd = useCallback((sessionId, configuredExercise) => {
    setSessionExerciseDrafts(prev => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }

       const restorableOriginal = findRestorableOriginalExercise(currentDraft, configuredExercise);
       if (restorableOriginal) {
         const restoreIndex = Math.max(
           0,
           Math.min(
             currentDraft.exercises.length,
             (currentDraft.originalExercises.findIndex((originalExercise) => matchesLogicalDraftExercise(originalExercise, restorableOriginal)) >= 0
               ? currentDraft.originalExercises.findIndex((originalExercise) => matchesLogicalDraftExercise(originalExercise, restorableOriginal))
               : ((restorableOriginal.index || 1) - 1))
           )
         );
         const nextExercises = currentDraft.exercises.slice();
         nextExercises.splice(restoreIndex, 0, restorableOriginal);
         return {
           ...prev,
           [sessionId]: {
             ...currentDraft,
             exercises: reindexDraftExercises(nextExercises),
             dirty: true,
             version: (currentDraft.version || 0) + 1,
           },
         };
       }

      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          exercises: upsertExerciseInDraft(currentDraft.exercises, configuredExercise),
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, [findRestorableOriginalExercise, matchesLogicalDraftExercise, reindexDraftExercises, upsertExerciseInDraft]);

  const stageProgramExerciseReplace = useCallback((sessionId, targetExercise, configuredExercise) => {
    setSessionExerciseDrafts(prev => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }
      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          exercises: upsertExerciseInDraft(currentDraft.exercises, configuredExercise, targetExercise),
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, [upsertExerciseInDraft]);

  const removeProgramDraftExercise = useCallback((sessionId, targetExercise) => {
    const currentDraft = sessionExerciseDrafts[sessionId];
    if (isDraftAddedExercise(currentDraft, targetExercise)) {
      stripDraftAddedExerciseArtifacts(sessionId, targetExercise);
    }

    setSessionExerciseDrafts(prev => {
      const draftForSession = prev[sessionId];
      if (!draftForSession) {
        return prev;
      }
      const targetKey = getDraftExerciseKey(targetExercise);
      const nextExercises = reindexDraftExercises(draftForSession.exercises.filter(exercise => getDraftExerciseKey(exercise) !== targetKey));
      return {
        ...prev,
        [sessionId]: {
          ...draftForSession,
          exercises: nextExercises,
          dirty: true,
          version: (draftForSession.version || 0) + 1,
        },
      };
    });
  }, [getDraftExerciseKey, isDraftAddedExercise, reindexDraftExercises, sessionExerciseDrafts, stripDraftAddedExerciseArtifacts]);

  // Move a draft exercise to a new 1-based position. Used by both the
  // drag-and-drop handler and the inline index text field in the
  // Journal program editor sidebar.
  const reorderProgramDraftExercise = useCallback((sessionId, fromIndex, toIndex) => {
    setSessionExerciseDrafts(prev => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) return prev;
      // Always operate on a sort-by-index view so the from/to positions
      // produced by the rendered (sorted) list match the splice targets,
      // regardless of the underlying array order.
      const list = (currentDraft.exercises || [])
        .slice()
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
      const len = list.length;
      if (len < 2) return prev;
      const from = Math.max(0, Math.min(len - 1, fromIndex));
      const to = Math.max(0, Math.min(len - 1, toIndex));
      if (from === to) return prev;
      const next = list.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          exercises: reindexDraftExercises(next),
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, [reindexDraftExercises]);

  const openSessionProgramEditor = useCallback(async (session) => {
    setProgramEditorLoading(true);
    try {
      const draft = await ensureSessionExerciseDraft(session);
      setProgramEditorContext({
        sessionId: session.id,
        phaseId: draft.phaseId,
        phaseName: draft.phaseName,
      });
      setProgramEditorOpen(true);
    } catch (error) {
      alert(error.message || 'Failed to open program editor');
    } finally {
      setProgramEditorLoading(false);
    }
  }, [ensureSessionExerciseDraft]);

  const getExerciseSeedBodyRegion = useCallback((exercise, fallbackExercise = null) => {
    const regionSources = [exercise, fallbackExercise].filter(Boolean);

    for (const source of regionSources) {
      const candidate = source?.bodyRegion ?? source?.body_region ?? source?.region ?? source?.bodyPart ?? source?.body_part ?? null;
      if (Array.isArray(candidate)) {
        const match = candidate.find((region) => typeof region === 'string' && region.trim());
        if (match) {
          return match.trim();
        }
      }
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    return '';
  }, []);

  const buildLibraryInitialFilters = useCallback((targetExercise, options = {}) => {
    const { levelDelta = 0 } = options;
    const fallbackExercise = availableLibraryExercises.find((exercise) => (
      targetExercise?.identifier && String(exercise?.identifier || '') === String(targetExercise.identifier)
    ) || (
      normalizeExerciseKey(exercise?.title) && normalizeExerciseKey(exercise?.title) === normalizeExerciseKey(targetExercise?.title)
    )) || null;
    const filterBodyRegion = getExerciseSeedBodyRegion(targetExercise, fallbackExercise) || selectedTreatmentExerciseRegion;
    const parsedLevel = Number(targetExercise?.exerciseLevel);

    return {
      searchInput: '',
      filterType: typeof targetExercise?.exerciseType === 'string' ? targetExercise.exerciseType.trim() : '',
      filterLevel: Number.isFinite(parsedLevel) && parsedLevel > 0
        ? String(Math.max(1, parsedLevel + levelDelta))
        : '',
      filterEquipment: '',
      filterBodyRegion,
    };
  }, [availableLibraryExercises, getExerciseSeedBodyRegion, normalizeExerciseKey, selectedTreatmentExerciseRegion]);

  const buildIssueLibraryInitialFilters = useCallback((issue, targetExercise) => {
    const reasonKeys = Array.isArray(issue?.reasonKeys) && issue.reasonKeys.length > 0
      ? issue.reasonKeys.filter(Boolean)
      : (issue?.reasonKey ? [issue.reasonKey] : []);
    let levelDelta = 0;

    if (reasonKeys.includes('too_hard')) {
      levelDelta = -1;
    } else if (reasonKeys.includes('too_easy')) {
      levelDelta = 1;
    }

    return buildLibraryInitialFilters(targetExercise, { levelDelta });
  }, [buildLibraryInitialFilters]);

  const openEditSuggestionDrawer = useCallback(async (session, issue) => {
    setProgramEditorLoading(true);
    try {
      const draft = await ensureSessionExerciseDraft(session);
      const targetExercise = findDraftTargetExercise(draft?.exercises || [], issue)
        || findDraftTargetExercise(availableLibraryExercises, issue);
      setEditingSuggestionContext({
        type: 'issue',
        sessionId: session.id,
        issueKey: issue.issueKey,
        issue,
        libraryStateKey: uuid(),
        libraryInitialFilters: buildIssueLibraryInitialFilters(issue, targetExercise),
      });
      setLibraryDrawerOpen(true);
    } catch (error) {
      alert(error.message || 'Failed to load exercise editor');
    } finally {
      setProgramEditorLoading(false);
    }
  }, [availableLibraryExercises, buildIssueLibraryInitialFilters, ensureSessionExerciseDraft, findDraftTargetExercise]);

  const openProgramLibraryDrawer = useCallback((context) => {
    let libraryInitialFilters = context?.libraryInitialFilters || null;

    if (!libraryInitialFilters && context?.type === 'program-replace') {
      const targetExercise = sessionExerciseDrafts[context.sessionId]?.exercises.find((exercise) => (
        getDraftExerciseKey(exercise) === context.targetExerciseKey
      )) || null;

      if (targetExercise) {
        libraryInitialFilters = buildLibraryInitialFilters(targetExercise);
      }
    }

    if (!libraryInitialFilters && context?.type === 'program-add') {
      libraryInitialFilters = buildLibraryInitialFilters(null);
    }

    setEditingSuggestionContext({
      ...context,
      libraryStateKey: uuid(),
      libraryInitialFilters,
    });
    setLibraryDrawerOpen(true);
  }, [buildLibraryInitialFilters, getDraftExerciseKey, sessionExerciseDrafts]);

  const openDraftExerciseEditDialog = useCallback((sessionId, exercise) => {
    setDraftExerciseEditContext({ sessionId, exerciseKey: getDraftExerciseKey(exercise) });
    setDraftEditedExerciseParams(buildExerciseEditParams(exercise));
    setDraftExerciseDialogOpen(true);
  }, [getDraftExerciseKey]);

  const openCustomDraftExerciseDialog = useCallback((sessionId) => {
    const exerciseKey = uuid();
    const customExercise = {
      uniqueKey: exerciseKey,
      identifier: null,
      exerciseType: 'custom',
      isNewCustomExercise: true,
      title: '',
      description: '',
      img: null,
      video: null,
    };

    setSessionExerciseDrafts((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }
      const nextExercises = reindexDraftExercises([
        ...currentDraft.exercises,
        {
          ...buildConfiguredDraftExercise(customExercise, null, currentDraft.exercises.length + 1),
          uniqueKey: exerciseKey,
          identifier: null,
          exerciseType: 'custom',
          isNewCustomExercise: true,
          title: '',
          description: '',
          img: null,
          video: null,
        },
      ]);
      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          exercises: nextExercises,
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });

    setDraftExerciseEditContext({ sessionId, exerciseKey, isNew: true, isNewCustomExercise: true, previousDirty: Boolean(sessionExerciseDrafts[sessionId]?.dirty) });
    setDraftEditedExerciseParams(buildExerciseEditParams(customExercise));
    setDraftExerciseDialogOpen(true);
  }, [buildConfiguredDraftExercise, reindexDraftExercises, sessionExerciseDrafts]);

  const closeDraftExerciseDialog = useCallback(() => {
    if (draftExerciseEditContext?.isNew) {
      const draftExercise = sessionExerciseDrafts[draftExerciseEditContext.sessionId]?.exercises.find(
        (exercise) => getDraftExerciseKey(exercise) === draftExerciseEditContext.exerciseKey
      );
      stripDraftAddedExerciseArtifacts(draftExerciseEditContext.sessionId, draftExercise);

      setSessionExerciseDrafts((prev) => {
        const currentDraft = prev[draftExerciseEditContext.sessionId];
        if (!currentDraft) {
          return prev;
        }
        const nextExercises = reindexDraftExercises(
          currentDraft.exercises.filter((exercise) => getDraftExerciseKey(exercise) !== draftExerciseEditContext.exerciseKey)
        );
        return {
          ...prev,
          [draftExerciseEditContext.sessionId]: {
            ...currentDraft,
            exercises: nextExercises,
            dirty: Boolean(draftExerciseEditContext.previousDirty),
            version: (currentDraft.version || 0) + 1,
          },
        };
      });
    }
    setDraftExerciseDialogOpen(false);
    setDraftExerciseEditContext(null);
  }, [draftExerciseEditContext, getDraftExerciseKey, reindexDraftExercises, sessionExerciseDrafts, stripDraftAddedExerciseArtifacts]);

  const applyJourneyBulkEditToSession = useCallback((sessionId, field, value) => {
    setSessionExerciseDrafts((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }
      const normalizedValue = ['sets', 'repetitions', 'time', 'pause', 'frequency'].includes(field)
        ? (parseInt(value, 10) || 0)
        : value;
      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          exercises: currentDraft.exercises.map((exercise) => ({
            ...exercise,
            [field]: normalizedValue,
          })),
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, []);

  const renderJourneyInlineAddPlaceholder = (label, hint, onClick) => (
    <Box
      role="button"
      tabIndex={0}
      className={classes.journeyInlineAddPlaceholder}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <Box>
        <Typography component="span" className={classes.journeyInlineAddPlaceholderPlus}>+</Typography>
        <Typography className={classes.journeyInlineAddPlaceholderLabel}>{label}</Typography>
        <Typography className={classes.journeyInlineAddPlaceholderHint}>{hint}</Typography>
      </Box>
    </Box>
  );

  const renderJourneyInlineAddPlaceholderRow = (items) => (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 'clamp(8px, 0.78vw, 12px)',
        width: '100%',
      }}
    >
      {items.map((item) => (
        <React.Fragment key={item.label}>
          {renderJourneyInlineAddPlaceholder(item.label, item.hint, item.onClick)}
        </React.Fragment>
      ))}
    </Box>
  );

  // Open the per-exercise ExerciseEditDialog when the user clicks the Edit
  // pencil on a Program adjustment annotation line. Lines look like
  // "Updated [Prone Back Lifts]: sets 3 to 4" or "Replaced [A] with [B]";
  // we extract the first bracketed title and resolve it against the
  // session's staged exercise draft. Falls back to the program editor
  // drawer when the exercise can no longer be found (e.g. it was removed).
  const editProgramAdjustmentLineExercise = useCallback(async (session, line) => {
    const text = String(line || '');
    const m = text.match(/\[([^\]]+)\]/);
    const title = m ? m[1].trim() : '';
    let draft = sessionExerciseDrafts[session.id];
    if (!draft) {
      try {
        draft = await ensureSessionExerciseDraft(session);
      } catch (e) {
        await openSessionProgramEditor(session);
        return;
      }
    }
    const exercises = (draft && draft.exercises) || [];
    const norm = (s) => String(s || '').trim().toLowerCase();
    const target = exercises.find(ex => norm(ex.title) === norm(title));
    if (target) {
      openDraftExerciseEditDialog(session.id, target);
      return;
    }
    await openSessionProgramEditor(session);
  }, [sessionExerciseDrafts, ensureSessionExerciseDraft, openDraftExerciseEditDialog, openSessionProgramEditor]);

  const handleDraftExerciseParamChange = useCallback((field, value) => {
    if (field === 'time' || field === 'pause' || field === 'daysInbetween') {
      setDraftEditedExerciseParams(prev => ({ ...prev, [field]: parseInt(value, 10) || 0 }));
      return;
    }
    if (field === 'doseType') {
      setDraftEditedExerciseParams(prev => (
        value === 'repetitions'
          ? { ...prev, doseType: value, time: 0 }
          : { ...prev, doseType: value, repetitions: 0 }
      ));
      return;
    }
    if (field === 'frequencyType') {
      setDraftEditedExerciseParams(prev => ({
        ...prev,
        frequencyType: value,
        daysInbetween: value === 'weekly' ? (prev.daysInbetween || prev.frequency || 1) : '',
      }));
      return;
    }
    setDraftEditedExerciseParams(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleConfirmDraftExerciseEdit = useCallback(() => {
    if (!draftExerciseEditContext) {
      return;
    }

    const normalizedParams = normalizeEditedExerciseParams(draftEditedExerciseParams);
    setSessionExerciseDrafts(prev => {
      const currentDraft = prev[draftExerciseEditContext.sessionId];
      if (!currentDraft) {
        return prev;
      }
      const nextExercises = currentDraft.exercises.map(exercise => (
        getDraftExerciseKey(exercise) === draftExerciseEditContext.exerciseKey
          ? { ...exercise, ...normalizedParams }
          : exercise
      ));
      return {
        ...prev,
        [draftExerciseEditContext.sessionId]: {
          ...currentDraft,
          exercises: nextExercises,
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
    setDraftExerciseDialogOpen(false);
    setDraftExerciseEditContext(null);
  }, [draftExerciseEditContext, draftEditedExerciseParams, getDraftExerciseKey]);

  const persistSessionExerciseDraft = useCallback(async (sessionId) => {
    const draft = sessionExerciseDrafts[sessionId];
    const resolvedTreatmentId = treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id;
    if (!draft?.dirty || !resolvedTreatmentId || !selectedPatient?.user_id || !token) {
      return draft;
    }

    await persistExerciseProgramChanges({
      originalExercises: draft.persistBaseline || draft.originalExercises,
      nextExercises: draft.exercises,
      userId: selectedPatient.user_id,
      treatmentId: resolvedTreatmentId,
      phaseId: draft.phaseId,
      token,
      changeSource: 'journal',
      sessionId,
    });

    const persistedExercises = draft.exercises.map(exercise => ({ ...exercise }));
    setSessionExerciseDrafts(prev => ({
      ...prev,
      [sessionId]: {
        ...prev[sessionId],
        originalExercises: persistedExercises,
        exercises: persistedExercises,
        persistBaseline: undefined,
        dirty: false,
        version: ((prev[sessionId]?.version || draft.version || 0) + 1),
      },
    }));

    if (typeof fetchExercisesForPhase === 'function' && draft.phaseId && String(draft.phaseId) === String(selectedPhaseId)) {
      fetchExercisesForPhase(draft.phaseId).catch((error) => {
        console.warn('Failed to refresh phase exercises after journal program save', error);
      });
    }

    return persistedExercises;
  }, [sessionExerciseDrafts, treatmentId, selectedPatient?.treatment_id, diagnosis?.treatment_id, selectedPatient?.user_id, token, fetchPhaseExercisesForDraft, fetchExercisesForPhase, selectedPhaseId]);

  const getIssueAdjustmentLines = useCallback((sessionId, issues = generatedIssuesBySession[sessionId] || []) => {
    return issues
      .filter(issue => (issue.pending || issue.adjusted) && issue.replacementIdentifier)
      .map(issue => {
        const decision = (suggestionDecisions[sessionId] || {})[issue.issueKey] || {};
        const selectedReplacement = decision.configuredExercise?.title || issue.replacement;
        return formatIssueAdjustmentLine(issue, selectedReplacement);
      });
  }, [generatedIssuesBySession, suggestionDecisions]);

  const getSessionCompletedAt = useCallback((session) => {
    const candidateTimestamps = [
      session?.completed_date,
      ...((session?.exercise_feedback || []).map(feedback => feedback.created_at)),
      ...((session?.session_feedback || []).map(feedback => feedback.created_at)),
    ].filter(Boolean);

    const parsed = candidateTimestamps
      .map(value => Date.parse(value))
      .filter(value => !Number.isNaN(value));

    if (parsed.length === 0) {
      return null;
    }

    return Math.max(...parsed);
  }, []);

  const getPreviousManualJournalEntryTime = useCallback((session, options = {}) => {
    const sessionCompletedAt = getSessionCompletedAt(session);
    if (sessionCompletedAt == null) {
      return null;
    }

    const notesBySession = options.notesBySession || fetchedNotes;

    let latestManualNoteTime = null;

    completedSessionsList.forEach(candidateSession => {
      if (String(candidateSession.id) === String(session.id)) {
        return;
      }

      const candidateCompletedAt = getSessionCompletedAt(candidateSession);
      if (candidateCompletedAt == null || candidateCompletedAt >= sessionCompletedAt) {
        return;
      }

      const notes = notesBySession[candidateSession.id] || [];
      notes.forEach(note => {
        if (!note || note.is_system || note.author == null || !note.created_at) {
          return;
        }
        const noteTime = Date.parse(note.created_at);
        if (Number.isNaN(noteTime)) {
          return;
        }
        if (latestManualNoteTime == null || noteTime > latestManualNoteTime) {
          latestManualNoteTime = noteTime;
        }
      });
    });

    return latestManualNoteTime;
  }, [completedSessionsList, fetchedNotes, getSessionCompletedAt]);

  const getConsultationSessions = useCallback((session, options = {}) => {
    const upperBound = getSessionCompletedAt(session);
    const lowerBound = getPreviousManualJournalEntryTime(session, options);

    return [...completedSessionsList]
      .filter(candidateSession => {
        const candidateCompletedAt = getSessionCompletedAt(candidateSession);
        if (candidateCompletedAt == null) {
          return false;
        }
        if (upperBound != null && candidateCompletedAt > upperBound) {
          return false;
        }
        if (String(candidateSession.id) === String(session.id)) {
          return true;
        }
        return lowerBound == null ? true : candidateCompletedAt > lowerBound;
      })
      .sort((left, right) => (getSessionCompletedAt(left) || 0) - (getSessionCompletedAt(right) || 0));
  }, [completedSessionsList, getPreviousManualJournalEntryTime, getSessionCompletedAt]);

  const getConsultationSessionSummary = useCallback((session, options = {}) => {
    const consultationSessions = getConsultationSessions(session, options);
    const painValues = consultationSessions.flatMap(candidateSession => (
      (candidateSession?.exercise_feedback || [])
        .map(feedback => (feedback?.painLevel != null ? parseFloat(feedback.painLevel) : null))
        .filter(value => Number.isFinite(value))
    ));
    const averagePain = painValues.length > 0
      ? (painValues.reduce((sum, value) => sum + value, 0) / painValues.length)
      : null;

    return {
      sessionCount: consultationSessions.length,
      averagePain,
    };
  }, [getConsultationSessions]);

  const getConsultationIssues = useCallback((session) => {
    const upperBound = getSessionCompletedAt(session);
    const lowerBound = getPreviousManualJournalEntryTime(session);

    const relevantLogs = adjustmentLogs.filter(log => {
      if (!log || log.status === 'rejected' || !log.created_at) {
        return false;
      }
      const createdAt = Date.parse(log.created_at);
      if (Number.isNaN(createdAt)) {
        return false;
      }
      if (upperBound != null && createdAt > upperBound) {
        return false;
      }
      if (lowerBound != null && createdAt <= lowerBound) {
        return false;
      }
      return true;
    });

    const consultationSessions = getConsultationSessions(session);

    const issues = [];

    consultationSessions.forEach(candidateSession => {
      const sessionIssues = deriveSessionIssues(candidateSession, relevantLogs, availableLibraryExercises, { allowInference: false });
      sessionIssues.forEach(issue => {
        if ((issue.pending || issue.adjusted) && issue.replacementIdentifier) {
          issues.push({
            ...issue,
            sessionId: candidateSession.id,
          });
        }
      });
    });

    return issues;
  }, [adjustmentLogs, availableLibraryExercises, deriveSessionIssues, getConsultationSessions, getPreviousManualJournalEntryTime, getSessionCompletedAt]);

  const getConsultationIssueLines = useCallback((session, options = {}) => {
    const upperBound = getSessionCompletedAt(session);
    const lowerBound = getPreviousManualJournalEntryTime(session, options);
    const consultationSessions = getConsultationSessions(session, options);
    const relevantLogs = adjustmentLogs.filter(log => {
      if (!log || log.status === 'rejected' || !log.created_at) {
        return false;
      }
      const createdAt = Date.parse(log.created_at);
      if (Number.isNaN(createdAt)) {
        return false;
      }
      if (upperBound != null && createdAt > upperBound) {
        return false;
      }
      if (lowerBound != null && createdAt <= lowerBound) {
        return false;
      }
      return true;
    });

    const feedbackHistoryByExercise = new Map();
    consultationSessions.forEach((candidateSession, sessionIndex) => {
      const fallbackTime = getSessionCompletedAt(candidateSession) || 0;
      (candidateSession.exercise_feedback || []).forEach(feedback => {
        const exerciseName = feedback.exercise_title || 'Exercise';
        const exerciseKey = feedback.exercise_id
          ? `id:${feedback.exercise_id}`
          : `title:${normalizeExerciseKey(exerciseName)}`;
        const parsedFeedbackTime = Date.parse(feedback.created_at);
        const feedbackTime = Number.isNaN(parsedFeedbackTime) ? fallbackTime : parsedFeedbackTime;
        const history = feedbackHistoryByExercise.get(exerciseKey) || [];
        history.push({
          exerciseKey,
          exerciseName,
          sessionId: candidateSession.id,
          sessionIndex,
          feedbackTime,
          feedback,
        });
        feedbackHistoryByExercise.set(exerciseKey, history);
      });
    });

    const flaggedEntriesByExercise = new Map();

    consultationSessions.forEach((candidateSession, sessionIndex) => {
      const fallbackTime = getSessionCompletedAt(candidateSession) || 0;
      const sessionIssues = deriveSessionIssues(candidateSession, relevantLogs, availableLibraryExercises);
      const replacementByFlagKey = new Map();

      sessionIssues.forEach(issue => {
        if (!(issue.pending || issue.adjusted) || !issue.replacementIdentifier) {
          return;
        }
        const exerciseKey = issue.originalIdentifier
          ? `id:${issue.originalIdentifier}`
          : `title:${normalizeExerciseKey(issue.name)}`;
        const decision = (suggestionDecisions[candidateSession.id] || {})[issue.issueKey] || {};
        const selectedReplacement = decision.configuredExercise?.title || issue.replacement || null;
        (issue.reasonKeys || [issue.reasonKey]).forEach(reasonKey => {
          if (!['too_painful', 'too_hard', 'too_easy', 'no_equipment'].includes(reasonKey)) {
            return;
          }
          replacementByFlagKey.set(`${exerciseKey}::${reasonKey}`, {
            title: selectedReplacement,
            identifier: issue.replacementIdentifier || null,
            disposition: issue.adjusted ? 'adjusted' : 'pending',
            isSystemGenerated: Boolean(issue.isSystemGenerated),
          });
        });
      });

      (candidateSession.exercise_feedback || []).forEach(feedback => {
        const exerciseName = feedback.exercise_title || 'Exercise';
        const exerciseKey = feedback.exercise_id
          ? `id:${feedback.exercise_id}`
          : `title:${normalizeExerciseKey(exerciseName)}`;
        const parsedFeedbackTime = Date.parse(feedback.created_at);
        const feedbackTime = Number.isNaN(parsedFeedbackTime) ? fallbackTime : parsedFeedbackTime;
        const painValue = feedback.painLevel != null ? parseFloat(feedback.painLevel) : null;
        const reasonDetails = getFeedbackReasonDetails({ pain: painValue, difficulty: feedback.difficulty })
          .filter(detail => ['too_painful', 'too_hard', 'too_easy', 'no_equipment'].includes(detail.reasonKey));

        reasonDetails.forEach(detail => {
          const flagKey = `${exerciseKey}::${detail.reasonKey}`;
          const replacementInfo = replacementByFlagKey.get(flagKey) || null;
          const nextEntry = {
            exerciseKey,
            exerciseName,
            reasonKey: detail.reasonKey,
            reasonText: detail.reason,
            replacementTitle: replacementInfo?.title || null,
            replacementIdentifier: replacementInfo?.identifier || null,
            replacementDisposition: replacementInfo?.disposition || null,
            isSystemGenerated: Boolean(replacementInfo?.isSystemGenerated),
            flaggedSessionIndex: sessionIndex,
            flaggedSessionId: candidateSession.id,
            flaggedFeedbackTime: feedbackTime,
            flaggedFeedback: feedback,
          };

          const existingExerciseEntry = flaggedEntriesByExercise.get(exerciseKey) || {
            exerciseKey,
            exerciseName,
            flaggedEvents: [],
          };

          existingExerciseEntry.flaggedEvents.push(nextEntry);
          flaggedEntriesByExercise.set(exerciseKey, existingExerciseEntry);
        });
      });
    });

    const roundMetric = (value) => {
      const numeric = Number.parseFloat(value);
      return Number.isFinite(numeric) ? `${Math.round(numeric * 10) / 10}` : null;
    };

    const formatSessionsAgo = (sessionsAgo) => {
      if (sessionsAgo <= 0) {
        return 'at latest session';
      }
      if (sessionsAgo === 1) {
        return '1 session ago';
      }
      return `${sessionsAgo} sessions ago`;
    };

    const getExerciseFeedbackHistory = ({ exerciseKey = null, identifier = null, title = null } = {}) => {
      const historyEntries = [];
      const seenEntries = new Set();
      const appendEntries = (key) => {
        if (!key) {
          return;
        }
        (feedbackHistoryByExercise.get(key) || []).forEach((item) => {
          const dedupeKey = [
            item.sessionId || '',
            item.feedbackTime || '',
            item.exerciseName || '',
            item.feedback?.created_at || '',
          ].join('::');
          if (!seenEntries.has(dedupeKey)) {
            seenEntries.add(dedupeKey);
            historyEntries.push(item);
          }
        });
      };

      appendEntries(exerciseKey);
      appendEntries(identifier ? `id:${identifier}` : null);
      appendEntries(title ? `title:${normalizeExerciseKey(title)}` : null);

      return historyEntries;
    };

    const sortFlaggedEntries = (left, right) => {
      if (left.flaggedSessionIndex !== right.flaggedSessionIndex) {
        return left.flaggedSessionIndex - right.flaggedSessionIndex;
      }
      if (left.flaggedFeedbackTime !== right.flaggedFeedbackTime) {
        return left.flaggedFeedbackTime - right.flaggedFeedbackTime;
      }
      if (left.reasonKey !== right.reasonKey) {
        return String(left.reasonKey || '').localeCompare(String(right.reasonKey || ''));
      }
      return left.exerciseName.localeCompare(right.exerciseName);
    };

    const getFlaggedValueText = (entry) => {
      if (entry.reasonKey === 'too_hard' || entry.reasonKey === 'too_easy') {
        return roundMetric(entry.flaggedFeedback?.difficulty);
      }
      return null;
    };

    const getFlaggedReasonText = (entry) => {
      const flaggedValue = getFlaggedValueText(entry);
      const baseReasonText = String(entry?.reasonText || '').trim();
      if (!flaggedValue || baseReasonText.includes('(')) {
        return baseReasonText;
      }
      return `${baseReasonText} (${flaggedValue})`;
    };

    const buildFlagTimelineSegment = (entries) => {
      const groupedEntries = Array.isArray(entries) ? entries : [entries];
      const referenceEntry = groupedEntries[0] || {};
      const flaggedSessionsAgo = Math.max(0, consultationSessions.length - 1 - (referenceEntry.flaggedSessionIndex || 0));
      const reasonTexts = [];
      const seenReasonTexts = new Set();

      groupedEntries.forEach((entry) => {
        const reasonText = getFlaggedReasonText(entry);
        const reasonIdentity = `${entry?.reasonKey || ''}::${reasonText}`;
        if (!reasonText || seenReasonTexts.has(reasonIdentity)) {
          return;
        }
        seenReasonTexts.add(reasonIdentity);
        reasonTexts.push(reasonText);
      });

      let segment = `flagged ${joinReasonTexts(reasonTexts) || getFlaggedReasonText(referenceEntry)}`;
      segment += ` - ${formatSessionsAgo(flaggedSessionsAgo)}`;
      return segment;
    };

    return [...flaggedEntriesByExercise.values()]
      .map((exerciseEntry) => ({
        ...exerciseEntry,
        flaggedEvents: [...(exerciseEntry.flaggedEvents || [])].sort(sortFlaggedEntries),
      }))
      .sort((left, right) => {
        const leftFirst = left.flaggedEvents[0] || {};
        const rightFirst = right.flaggedEvents[0] || {};
        const orderedByFirstEvent = sortFlaggedEntries(leftFirst, rightFirst);
        if (orderedByFirstEvent !== 0) {
          return orderedByFirstEvent;
        }
        return left.exerciseName.localeCompare(right.exerciseName);
      })
      .map(exerciseEntry => {
        const latestEntry = exerciseEntry.flaggedEvents[exerciseEntry.flaggedEvents.length - 1];
        const groupedFlaggedEvents = exerciseEntry.flaggedEvents.reduce((groups, entry) => {
          const previousGroup = groups[groups.length - 1];
          const previousEntry = previousGroup?.[previousGroup.length - 1] || null;
          const isSameFeedbackMoment = previousEntry
            && String(previousEntry.flaggedSessionId || '') === String(entry.flaggedSessionId || '')
            && Number(previousEntry.flaggedFeedbackTime || 0) === Number(entry.flaggedFeedbackTime || 0);

          if (isSameFeedbackMoment) {
            previousGroup.push(entry);
            return groups;
          }

          groups.push([entry]);
          return groups;
        }, []);
        const originalHistory = getExerciseFeedbackHistory({ exerciseKey: exerciseEntry.exerciseKey, title: exerciseEntry.exerciseName });
        const replacementHistory = latestEntry?.replacementDisposition === 'adjusted'
          ? getExerciseFeedbackHistory({
              identifier: latestEntry.replacementIdentifier,
              title: latestEntry.replacementTitle,
            })
          : [];
        const preferredHistory = replacementHistory.length > 0 ? replacementHistory : originalHistory;
        const latestFeedbackEntry = preferredHistory.reduce((latest, current) => {
          if (!latest || current.feedbackTime > latest.feedbackTime) {
            return current;
          }
          return latest;
        }, null);
        const latestPainText = latestFeedbackEntry
          ? (() => {
              const rounded = roundMetric(latestFeedbackEntry.feedback?.painLevel);
              return rounded ? `pain: ${rounded}/100` : null;
            })()
          : null;
        const latestDifficultyText = latestFeedbackEntry
          ? (() => {
              const difficultyLabel = latestFeedbackEntry.feedback?.difficulty != null
                ? getDifficultyDisplayLabel(latestFeedbackEntry.feedback.difficulty)
                : null;
              return difficultyLabel ? `difficulty: ${difficultyLabel}` : null;
            })()
          : null;
        const replacementText = latestEntry?.replacementTitle && latestEntry?.replacementDisposition === 'adjusted'
          ? (latestEntry.isSystemGenerated
            ? `*system replaced it with [${latestEntry.replacementTitle}]`
            : `*replaced with [${latestEntry.replacementTitle}]`)
          : null;

        const distinctReasonCount = new Set(
          exerciseEntry.flaggedEvents
            .map((entry) => entry.reasonKey)
            .filter(Boolean)
        ).size;
        const hasMixedResponse = distinctReasonCount > 1 && groupedFlaggedEvents.length > 1;
        const timelineText = groupedFlaggedEvents
          .map((groupedEntries) => buildFlagTimelineSegment(groupedEntries))
          .join(' --> ');
        const latestGroupedEntries = groupedFlaggedEvents[groupedFlaggedEvents.length - 1] || [];
        const latestGroupedEntry = latestGroupedEntries[latestGroupedEntries.length - 1] || null;
        const latestGroupMatchesLatestFeedback = latestGroupedEntry && latestFeedbackEntry
          ? String(latestGroupedEntry.flaggedSessionId || '') === String(latestFeedbackEntry.sessionId || '')
          : false;
        const latestGroupReasonKeys = new Set(latestGroupedEntries.map((entry) => entry.reasonKey).filter(Boolean));

        let line = `[${exerciseEntry.exerciseName}]: `;
        if (hasMixedResponse) {
          line += `mixed response - ${timelineText}`;
        } else {
          line += timelineText;
        }

        const latestSummaryParts = [
          latestGroupMatchesLatestFeedback && latestGroupReasonKeys.has('too_painful') ? null : latestPainText,
          latestGroupMatchesLatestFeedback && ['too_hard', 'too_easy', 'no_equipment'].some((reasonKey) => latestGroupReasonKeys.has(reasonKey)) ? null : latestDifficultyText,
        ].filter(Boolean);
        if (replacementText) {
          line += `; ${replacementText}`;
        }
        if (latestSummaryParts.length > 0) {
          line += `; latest feedback: ${latestSummaryParts.join(' | ')}`;
        }

        return line;
      });
  }, [adjustmentLogs, availableLibraryExercises, deriveSessionIssues, getConsultationSessions, getPreviousManualJournalEntryTime, getSessionCompletedAt, normalizeExerciseKey, suggestionDecisions]);

  const getSessionAdjustmentIssues = useCallback((session, options = {}) => {
    if (!session) {
      return [];
    }

    const derivedIssues = deriveSessionIssues(session, adjustmentLogs, availableLibraryExercises, {
      allowInference: false,
      ...options,
    }).map(issue => ({
      ...issue,
      sessionId: session.id,
    }));

    const mergedIssues = new Map();
    derivedIssues.forEach((issue) => {
      mergedIssues.set(issue.adjId || issue.issueKey, issue);
    });

    getSessionLoggedAdjustmentIssues(session).forEach((issue) => {
      const issueKey = issue.adjId || issue.issueKey;
      const existingIssue = mergedIssues.get(issueKey);
      mergedIssues.set(issueKey, existingIssue ? mergeAdjustmentIssues(existingIssue, issue) : issue);
    });

    return Array.from(mergedIssues.values());
  }, [deriveSessionIssues, adjustmentLogs, availableLibraryExercises, getSessionLoggedAdjustmentIssues]);

  const isSystemGeneratedIssueStillApplied = useCallback((session, issue) => {
    if (!issue?.isSystemGenerated || !issue.adjusted || !issue.replacementIdentifier) {
      return true;
    }

    const draftExercises = sessionExerciseDrafts[session?.id]?.exercises || [];
    const normalizedReplacementTitle = normalizeExerciseKey(issue.replacement);
    const normalizedOriginalTitle = normalizeExerciseKey(issue.name);

    if (Array.isArray(draftExercises) && draftExercises.length > 0) {
      const replacementExistsInDraft = draftExercises.some((exercise) => (
        issue.replacementIdentifier && String(exercise?.identifier || '') === String(issue.replacementIdentifier)
      ) || (
        normalizedReplacementTitle && normalizeExerciseKey(exercise?.title) === normalizedReplacementTitle
      ));
      const originalStillExistsInDraft = draftExercises.some((exercise) => (
        issue.originalIdentifier && String(exercise?.identifier || '') === String(issue.originalIdentifier)
      ) || (
        normalizedOriginalTitle && normalizeExerciseKey(exercise?.title) === normalizedOriginalTitle
      ));
      return replacementExistsInDraft && !originalStillExistsInDraft;
    }

    const hasDraftNoteValue = Object.prototype.hasOwnProperty.call(editNoteValue, session?.id);
    const sessionNotes = fetchedNotes[session?.id] || [];
    const noteIndex = noteIndices[session?.id] || 0;
    const noteSource = hasDraftNoteValue
      ? (editNoteValue[session?.id] || '')
      : (sessionNotes[noteIndex]?.note || '');

    if (!noteSource) {
      return false;
    }

    const issueIdentity = getProgramAdjustmentDisplayIdentity(formatIssueAdjustmentLine(issue, issue.replacement));
    if (!issueIdentity) {
      return false;
    }

    return getSavedAdjustmentLinesFromNote(noteSource).some((line) => (
      getProgramAdjustmentDisplayIdentity(line) === issueIdentity
    ));
  }, [editNoteValue, fetchedNotes, normalizeExerciseKey, noteIndices, sessionExerciseDrafts]);

  const getLatestRealCompletedSessionId = useCallback(() => {
    const latestRealSession = [...completedSessionsList]
      .filter((candidateSession) => !(typeof candidateSession?.id === 'string' && candidateSession.id.startsWith('patient-')))
      .reduce((latestSession, candidateSession) => {
        if (!candidateSession) {
          return latestSession;
        }
        const candidateTime = getSessionCompletedAt(candidateSession) || 0;
        const latestTime = latestSession ? (getSessionCompletedAt(latestSession) || 0) : -Infinity;
        return candidateTime >= latestTime ? candidateSession : latestSession;
      }, null);

    return latestRealSession?.id || null;
  }, [completedSessionsList, getSessionCompletedAt]);

  const getSessionJournalAdjustmentLines = useCallback((session) => {
    const latestRealSessionId = getLatestRealCompletedSessionId();
    const removedKeySet = removedHistoricalLines[session?.id] || new Set();
    const sessionIssues = getSessionAdjustmentIssues(session)
      .filter(issue => (issue.pending || issue.adjusted) && issue.replacementIdentifier)
      .filter(issue => !issue.isSystemGenerated || String(session?.id) === String(latestRealSessionId))
      .filter(issue => isSystemGeneratedIssueStillApplied(session, issue))
      .map(issue => {
        const decision = (suggestionDecisions[issue.sessionId] || {})[issue.issueKey] || {};
        const selectedReplacement = decision.configuredExercise?.title || issue.replacement;
        return formatIssueAdjustmentLine(issue, selectedReplacement, { includeMarker: false });
      })
      .filter((line) => !removedKeySet.has(normalizeProgramSummaryLineKey(line)));

    const programSummary = programChangeSummaries[session?.id] || null;
    const adviceSummaryLines = getAdviceSummaryJournalLinesForSession(session?.id);
    return dedupeProgramAdjustmentDisplayLines([
      ...sessionIssues,
      ...((programSummary && Array.isArray(programSummary.journalLines)) ? programSummary.journalLines : []),
      ...adviceSummaryLines,
    ].filter(Boolean)).filter((line) => !removedKeySet.has(normalizeProgramSummaryLineKey(line)));
  }, [getLatestRealCompletedSessionId, getSessionAdjustmentIssues, isSystemGeneratedIssueStillApplied, suggestionDecisions, programChangeSummaries, sessionAdviceDrafts, removedHistoricalLines]);

  const buildSessionProgramAdjustmentLines = useCallback((session) => {
    const latestRealSessionId = getLatestRealCompletedSessionId();
    const removedKeySet = removedHistoricalLines[session?.id] || new Set();
    const appliedIssues = getSessionAdjustmentIssues(session)
      .filter(issue => issue.adjusted && issue.replacementIdentifier)
      .filter(issue => !issue.isSystemGenerated || String(session?.id) === String(latestRealSessionId))
      .filter(issue => isSystemGeneratedIssueStillApplied(session, issue));
    const programSummary = programChangeSummaries[session?.id] || null;

    const lines = appliedIssues.map(issue => {
      const decision = (suggestionDecisions[issue.sessionId] || {})[issue.issueKey] || {};
      const selectedReplacement = decision.configuredExercise?.title || issue.replacement;
      return formatIssueAdjustmentLine(issue, selectedReplacement);
    });

    return dedupeProgramAdjustmentDisplayLines([
      ...lines,
      ...((programSummary && Array.isArray(programSummary.journalLines)) ? programSummary.journalLines : []),
    ].filter(Boolean)).filter((line) => !removedKeySet.has(normalizeProgramSummaryLineKey(line)));
  }, [getLatestRealCompletedSessionId, getSessionAdjustmentIssues, isSystemGeneratedIssueStillApplied, programChangeSummaries, suggestionDecisions, removedHistoricalLines]);

  const getProgramSummaryJournalLines = useCallback((sessionId, overrideProgramSummary = null) => {
    const programSummary = overrideProgramSummary || programChangeSummaries[sessionId] || null;
    return Array.isArray(programSummary?.journalLines)
      ? programSummary.journalLines.filter(Boolean)
      : [];
  }, [programChangeSummaries]);

  function getAdviceSummaryJournalLinesForSession(sessionId) {
    const draft = sessionAdviceDrafts[sessionId];
    if (!draft?.dirty) {
      return [];
    }

    const getAdviceKey = (advice) => advice?.uniqueKey || advice?.id || advice?.identifier || `${advice?.title || ''}:${advice?.importance || ''}`;
    const originalAdvices = draft.originalAdvices || [];
    const nextAdvices = draft.advices || [];
    const originalByKey = new Map(originalAdvices.map((advice) => [getAdviceKey(advice), advice]));
    const nextByKey = new Map(nextAdvices.map((advice) => [getAdviceKey(advice), advice]));
    const replacedLines = [];
    const updatedLines = [];
    const removedLines = [];
    const addedLines = [];

    originalAdvices.forEach((originalAdvice) => {
      const key = getAdviceKey(originalAdvice);
      const nextAdvice = nextByKey.get(key);
      const originalTitle = String(originalAdvice?.title || '').trim() || `Advice ${originalAdvice?.importance || ''}`.trim();
      if (!nextAdvice) {
        removedLines.push(`Removed advice [${originalTitle}] from the advice plan.`);
        return;
      }

      const nextTitle = String(nextAdvice?.title || '').trim() || `Advice ${nextAdvice?.importance || ''}`.trim();
      const replacedFromTitle = String(nextAdvice?.__journeyReplaceFromTitle || '').trim();
      if (replacedFromTitle) {
        replacedLines.push(`Replaced [${replacedFromTitle}] with [${nextTitle}].`);
        return;
      }
      const changes = [];
      if (originalTitle !== nextTitle) {
        changes.push(`title to [${nextTitle}]`);
      }
      if (String(originalAdvice?.advice || '').trim() !== String(nextAdvice?.advice || '').trim()) {
        changes.push('content updated');
      }
      if (changes.length > 0) {
        updatedLines.push(`Updated advice [${originalTitle}]: ${changes.join(', ')}.`);
      }
    });

    nextAdvices.forEach((nextAdvice) => {
      const key = getAdviceKey(nextAdvice);
      if (originalByKey.has(key)) {
        return;
      }
      const nextTitle = String(nextAdvice?.title || '').trim() || `Advice ${nextAdvice?.importance || ''}`.trim();
      addedLines.push(`Added advice [${nextTitle}] to the advice plan.`);
    });

    return [...replacedLines, ...updatedLines, ...removedLines, ...addedLines].filter(Boolean);
  }

  const getCurrentDraftProgramSummary = useCallback((sessionId) => {
    const draft = sessionExerciseDrafts[sessionId];
    if (!draft) {
      return programChangeSummaries[sessionId] || null;
    }

    const journalSummary = summarizeExerciseProgramChanges({
      originalExercises: draft.originalExercises,
      nextExercises: draft.exercises,
    });
    const currentSummary = programChangeSummaries[sessionId];
    return mergeProgramChangeSummaryEntries({
      journalSummary: journalSummary.hasChanges
        ? { ...journalSummary, phaseId: draft.phaseId, source: 'journal' }
        : null,
      externalSummary: getExternalProgramSummaryPart(currentSummary),
    });
  }, [programChangeSummaries, sessionExerciseDrafts]);

  const buildNoteWithLatestAdjustments = useCallback((sessionId, noteHtml, options = {}) => {
    const programLines = getProgramSummaryJournalLines(sessionId, options.programSummary || null);
    const adviceLines = getAdviceSummaryJournalLinesForSession(sessionId);
    const baselineGoalLines = (baselineGoalStaged[sessionId] || [])
      .filter((item) => !(item && item.suppressAnnotation))
      .map((item) => item.lineText)
      .filter(Boolean);
    const session = completedSessionsList.find(item => String(item.id) === String(sessionId));
    let merged = mergeTreatmentAdjustmentsSection(noteHtml || '', [...programLines, ...adviceLines]);
    merged = mergeBaselineGoalSection(merged, baselineGoalLines);

    if (!session) {
      return upsertPatientAcceptSection(merged, JOURNAL_PATIENT_ACCEPT_DEFAULT_TEXT);
    }

    return upsertPatientAcceptSection(
      mergePlanUntilNextTimeSection(
        merged,
        buildPlanText(null, getSessionAdjustmentIssues(session), { programLines })
      ),
      JOURNAL_PATIENT_ACCEPT_DEFAULT_TEXT
    );
  }, [baselineGoalStaged, completedSessionsList, getProgramSummaryJournalLines, getSessionAdjustmentIssues, sessionAdviceDrafts]);

  const syncAdjustmentSectionForSession = useCallback((sessionId, options = {}) => {
    const programLines = getProgramSummaryJournalLines(sessionId, options.programSummary || null);
    const adviceLines = getAdviceSummaryJournalLinesForSession(sessionId);
    const session = completedSessionsList.find(item => String(item.id) === String(sessionId));

    // Drop any line the user has explicitly removed via the per-line remove
    // icon. Without this filter, an edit that re-derives the program lines
    // (e.g. from saved adjustment-log issues, or via the chain-consolidation
    // pass) could re-emit the exact text of a historical line the user has
    // already revoked, which would re-introduce it on the next merge.
    const removedKeySet = removedHistoricalLines[sessionId] || new Set();
    const lcKey = (s) => (s || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();
    const filteredAdjustmentLines = (removedKeySet.size > 0
      ? [...programLines, ...adviceLines].filter(l => !removedKeySet.has(lcKey(l)))
      : [...programLines, ...adviceLines]);

    const planText = buildPlanText(null, getSessionAdjustmentIssues(session), { programLines });
    const shouldSyncAdjustments = filteredAdjustmentLines.length > 0 || removedKeySet.size > 0;

    // Read editNoteValue inside the updater (via `prev`) and editingNote via its
    // stable ref so this callback does not re-create on every keystroke, which
    // would trigger the sync useEffect on every character typed.
    setEditNoteValue(prev => {
      const hasDraftValue = Object.prototype.hasOwnProperty.call(prev, sessionId);
      // Only modify an existing draft (or one that the user is actively
      // editing). Do NOT preemptively create a draft for sessions with no
      // saved notes — doing so would race with auto-generate (which sets
      // generatedIssuesBySession before setEditNoteValue) and overwrite the
      // generated text with a bare "Plan until next time: Continue as
      // planned." stub.
      const shouldSyncDraft = editingNoteRef.current[sessionId] || hasDraftValue;

      if (!shouldSyncDraft) {
        return prev;
      }

      const currentDraft = prev[sessionId] || '';
      const sessionNotes = fetchedNotes[sessionId] || [];
      const noteIdx = noteIndices[sessionId] || 0;
      const savedNoteText = sessionNotes[noteIdx]?.note || '';
      const baseAdjustmentNote = (currentDraft && currentDraft.trim()) ? currentDraft : savedNoteText;
      const noteWithAdjustments = mergeTreatmentAdjustmentsSection(baseAdjustmentNote, filteredAdjustmentLines);
      let mergedDraft = noteWithAdjustments;
      const canAutoSyncPlan = canAutoSyncGeneratedPlanSection(baseAdjustmentNote);

      if (canAutoSyncPlan && shouldSyncAdjustments) {
        // Only rewrite the Plan section when we actually have something to
        // reflect (program lines or adjustment issues). Otherwise sync would
        // collapse a richly auto-generated Plan paragraph (built with the
        // real symptom trend at generate-time) into the trivial "Continue as
        // planned." fallback every time some unrelated state change fires
        // this effect (e.g. opening the program editor re-derives issues).
        mergedDraft = mergePlanUntilNextTimeSection(noteWithAdjustments, planText);
      }

      mergedDraft = upsertPatientAcceptSection(mergedDraft, JOURNAL_PATIENT_ACCEPT_DEFAULT_TEXT);

      if (mergedDraft === currentDraft) {
        return prev;
      }

      return {
        ...prev,
        [sessionId]: mergedDraft,
      };
    });
  }, [completedSessionsList, editingNoteRef, fetchedNotes, getProgramSummaryJournalLines, getSessionAdjustmentIssues, noteIndices, removedHistoricalLines, sessionAdviceDrafts]);

  const clearSessionProgramChangeSummary = useCallback((sessionId, phaseId) => {
    setProgramChangeSummaries(prev => {
      if (!prev[sessionId]) {
        return prev;
      }
      const nextSummary = mergeProgramChangeSummaryEntries({
        journalSummary: null,
        externalSummary: getExternalProgramSummaryPart(prev[sessionId]),
      });
      if (!nextSummary) {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      }
      return {
        ...prev,
        [sessionId]: nextSummary,
      };
    });

    const resolvedTreatmentId = treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id;
    clearLatestExerciseProgramUpdate({
      treatmentId: resolvedTreatmentId,
      phaseId,
    });
  }, [treatmentId, selectedPatient?.treatment_id, diagnosis?.treatment_id]);

  const removeProgramAdjustmentLine = useCallback(async (sessionId, lineText, options = {}) => {
    // If the change is staged in the exercise draft (source: 'journal'),
    // revert the underlying draft so the "Current exercise program" list
    // reflects the rollback (re-add replaced/removed exercises, drop added
    // ones, restore updated prescriptions). The draft -> summary effect will
    // then auto-strip the line from journalLines, and syncAdjustmentSection
    // will remove it from the editNoteValue text.
    //
    // For HISTORICAL lines (already saved into the note text), the draft's
    // originalExercises already reflects the post-replacement state — the
    // "before" exercise A is no longer there. In that case we identify A
    // from (in priority order):
    //   1) draft.originalExercises (staged scenario)
    //   2) session.exercise_feedback (session-time program — confirms A was
    //      part of the patient's executed session)
    //   3) availableLibraryExercises (catalogue lookup)
    // and synthesise a reverse replacement so the journal program editor
    // sees a fresh "Replaced [B] with [A]" stage.
    const sessionForLookup = options.session || completedSessionsList.find((session) => String(session.id) === String(sessionId)) || null;
    let loadedDraft = null;
    if (sessionForLookup) {
      try {
        loadedDraft = await ensureSessionExerciseDraft(sessionForLookup);
      } catch (error) {
        setSnackbarMsg('Unable to load the current program to stage this undo.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
    }

    setSessionExerciseDrafts(prev => {
      const draft = prev[sessionId] || loadedDraft;
      if (!draft || !Array.isArray(draft.originalExercises) || !Array.isArray(draft.exercises)) {
        return prev;
      }
      const original = draft.originalExercises;
      let nextEx = [...draft.exercises];
      let mutated = false;
      const labelOf = (e) => ((e?.title || e?.identifier || '') + '').trim();
      const lc = (s) => (s || '').toString().trim().toLowerCase();

      const replacedMatch = lineText.match(/^Replaced \[([^\]]+)\] with \[([^\]]+)\]/i)
        || lineText.match(/^\[([^\]]+)\]:.*-->\s*\[([^\]]+)\]\*?$/i);
      const removedMatch = lineText.match(/^Removed \[([^\]]+)\] from the exercise program/i);
      const addedMatch = lineText.match(/^Added \[([^\]]+)\](?:\s+•.*?)?\s+to the exercise program/i);
      const updatedMatch = lineText.match(/^Updated \[([^\]]+)\]:/i);

      if (replacedMatch) {
        const fromLabel = replacedMatch[1].trim();
        const toLabel = replacedMatch[2].trim();
        const draftIdx = nextEx.findIndex(e => labelOf(e) === toLabel);
        if (draftIdx >= 0) {
          // 1) Staged: A is in originalExercises with its full prior prescription.
          const origItem = original.find(e => labelOf(e) === fromLabel);
          if (origItem) {
            nextEx[draftIdx] = origItem;
            mutated = true;
          } else {
            // 2/3) Historical: A is no longer in originalExercises. Try
            // session.exercise_feedback first to identify A, then use the
            // library entry (with its catalogue defaults — closest match to
            // A's natural prescription) to build a reverse replacement.
            const feedbackEntry = sessionForLookup?.exercise_feedback?.find(
              ef => lc(ef.exercise_title) === lc(fromLabel)
            );
            const libA = availableLibraryExercises.find(
              lib => lc(lib?.title) === lc(fromLabel)
                || (feedbackEntry?.exercise_id && lc(lib?.identifier) === lc(feedbackEntry.exercise_id))
            );
            const targetB = nextEx[draftIdx];
            let restored = null;
            if (libA) {
              const replacement = buildConfiguredDraftExercise(
                libA,
                { ...targetB, sets: undefined, repetitions: undefined, time: undefined, pause: undefined, frequencyType: undefined, frequency: undefined, daysInbetween: undefined },
                draftIdx + 1
              );
              restored = { ...replacement, id: undefined };
            } else if (feedbackEntry) {
              const stub = buildConfiguredDraftExercise(
                {
                  identifier: feedbackEntry.exercise_id || fromLabel,
                  title: feedbackEntry.exercise_title || fromLabel,
                },
                targetB,
                draftIdx + 1
              );
              restored = { ...stub, id: undefined };
            }
            if (restored) {
              nextEx[draftIdx] = restored;
              mutated = true;
              // For historical reverts, also rewrite originalExercises so
              // the visible diff (Program adjustments box / generated
              // journal lines) collapses to "no change". The TRUE backend
              // baseline is preserved on draft.persistBaseline so that on
              // save we still PATCH the rollback to the server.
              const newOriginal = original.slice();
              const origDraftIdx = newOriginal.findIndex(e => labelOf(e) === toLabel);
              if (origDraftIdx >= 0) {
                newOriginal[origDraftIdx] = restored;
                const persistBaseline = draft.persistBaseline || original;
                return {
                  ...prev,
                  [sessionId]: {
                    ...draft,
                    exercises: nextEx,
                    originalExercises: newOriginal,
                    persistBaseline,
                    dirty: true,
                    version: (draft.version || 0) + 1,
                  },
                };
              }
            } else {
              nextEx.splice(draftIdx, 1);
              mutated = true;
              const newOriginal = original.filter(e => labelOf(e) !== toLabel);
              const persistBaseline = draft.persistBaseline || original;
              return {
                ...prev,
                [sessionId]: {
                  ...draft,
                  exercises: nextEx,
                  originalExercises: newOriginal,
                  persistBaseline,
                  dirty: true,
                  version: (draft.version || 0) + 1,
                },
              };
            }
          }
        }
      } else if (removedMatch) {
        const label = removedMatch[1].trim();
        const origIdx = original.findIndex(e => lc(labelOf(e)) === lc(label));
        if (origIdx >= 0 && !nextEx.some(e => lc(labelOf(e)) === lc(label))) {
          const restoredExercises = nextEx.slice();
          restoredExercises.splice(Math.min(origIdx, restoredExercises.length), 0, original[origIdx]);
          nextEx = reindexDraftExercises(restoredExercises);
          mutated = true;
        } else if (origIdx < 0 && !nextEx.some(e => lc(labelOf(e)) === lc(label))) {
          const feedbackEntry = sessionForLookup?.exercise_feedback?.find(
            ef => lc(ef.exercise_title) === lc(label)
          ) || null;
          const feedbackIndex = sessionForLookup?.exercise_feedback?.findIndex(
            ef => lc(ef.exercise_title) === lc(label)
          );
          const libExercise = availableLibraryExercises.find(
            lib => lc(lib?.title) === lc(label)
              || (feedbackEntry?.exercise_id && lc(lib?.identifier) === lc(feedbackEntry.exercise_id))
          );
          const desiredIndex = Number.isFinite(feedbackIndex) && feedbackIndex >= 0
            ? Math.max(0, Math.min(nextEx.length, feedbackIndex))
            : nextEx.length;
          const restored = libExercise
            ? { ...buildConfiguredDraftExercise(libExercise, null, desiredIndex + 1), id: undefined }
            : (feedbackEntry
              ? {
                  ...buildConfiguredDraftExercise({
                    identifier: feedbackEntry.exercise_id || label,
                    title: feedbackEntry.exercise_title || label,
                  }, null, desiredIndex + 1),
                  id: undefined,
                }
              : null);
          if (restored) {
            const restoredExercises = nextEx.slice();
            restoredExercises.splice(desiredIndex, 0, restored);
            nextEx = reindexDraftExercises(restoredExercises);
            mutated = true;
            const persistBaseline = draft.persistBaseline || original;
            const newOriginalList = original.slice();
            newOriginalList.splice(desiredIndex, 0, restored);
            return {
              ...prev,
              [sessionId]: {
                ...draft,
                exercises: nextEx,
                originalExercises: reindexDraftExercises(newOriginalList),
                persistBaseline,
                dirty: true,
                version: (draft.version || 0) + 1,
              },
            };
          }
        }
      } else if (addedMatch) {
        const label = addedMatch[1].trim();
        const before = nextEx.length;
        nextEx = nextEx.filter(e => labelOf(e) !== label);
        if (nextEx.length !== before) mutated = true;
      } else if (updatedMatch) {
        const label = updatedMatch[1].trim();
        const origItem = original.find(e => labelOf(e) === label);
        const draftIdx = nextEx.findIndex(e => labelOf(e) === label);
        if (origItem && draftIdx >= 0) {
          const updatedExercise = { ...nextEx[draftIdx] };
          const changeText = lineText.slice(lineText.indexOf(':') + 1);
          const setsMatch = changeText.match(/sets\s+(\d+)\s+to\s+\d+/i);
          const pauseMatch = changeText.match(/pause\s+(\d+)s\s+to\s+\d+s/i);
          const timeMatch = changeText.match(/time\s+(\d+)s\s+to\s+\d+s/i);
          const doseTypeMatch = changeText.match(/dose type\s+([a-z_]+)\s+to\s+[a-z_]+/i);
          const repetitionsMatch = changeText.match(/dose\s+(\d+)\s+repetitions\s+to/i);
          const rmMatch = changeText.match(/dose\s+(\d+)\s+RM\s+to/i);
          const weightMatch = changeText.match(/dose\s+(\d+(?:\.\d+)?)\s+(total kg|kg|total lbs|lbs)\s+to/i);

          if (setsMatch) updatedExercise.sets = Number(setsMatch[1]);
          if (pauseMatch) updatedExercise.pause = Number(pauseMatch[1]);
          if (timeMatch) {
            updatedExercise.doseType = 'time';
            updatedExercise.time = Number(timeMatch[1]);
          }
          if (doseTypeMatch) updatedExercise.doseType = doseTypeMatch[1].toLowerCase();
          if (repetitionsMatch) {
            updatedExercise.doseType = 'repetitions';
            updatedExercise.repetitions = Number(repetitionsMatch[1]);
          }
          if (rmMatch) {
            updatedExercise.doseType = 'repetition_maximum';
            updatedExercise.repetitionMaximum = Number(rmMatch[1]);
          }
          if (weightMatch) {
            const value = Number(weightMatch[1]);
            const unit = weightMatch[2].toLowerCase();
            const fieldByUnit = {
              'kg': ['weight_kg', 'weightKg'],
              'total kg': ['weight_total_kg', 'weightTotalKg'],
              'lbs': ['weight_lbs', 'weightLbs'],
              'total lbs': ['weight_total_lbs', 'weightTotalLbs'],
            };
            const [doseType, field] = fieldByUnit[unit];
            updatedExercise.doseType = doseType;
            updatedExercise[field] = value;
          }
          nextEx[draftIdx] = updatedExercise;
          mutated = true;
        }
      }

      if (!mutated) return prev;
      return {
        ...prev,
        [sessionId]: {
          ...draft,
          exercises: nextEx,
          dirty: true,
          version: (draft.version || 0) + 1,
        },
      };
    });

    // Also remove the line from non-staged (e.g. program-sourced) summaries
    // and from the journal draft text directly. For staged journal-sourced
    // summaries the draft->summary effect will overwrite this anyway, so
    // these are no-ops in that path.
    setProgramChangeSummaries(prev => {
      const summary = prev[sessionId];
      if (!summary || !Array.isArray(summary.journalLines)) return prev;
      const nextJournalSummary = removeLineFromProgramSummaryPart(getJournalProgramSummaryPart(summary), lineText);
      const nextExternalSummary = removeLineFromProgramSummaryPart(getExternalProgramSummaryPart(summary), lineText);
      const nextSummary = mergeProgramChangeSummaryEntries({
        journalSummary: nextJournalSummary,
        externalSummary: nextExternalSummary,
      });
      if (buildProgramSummarySignature(nextSummary) === buildProgramSummarySignature(summary)) return prev;
      if (!nextSummary) {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      }
      return { ...prev, [sessionId]: nextSummary };
    });
    setEditNoteValue(prev => {
      const hasDraft = Object.prototype.hasOwnProperty.call(prev, sessionId);
      // If the user has not yet started editing the note text, seed the
      // draft from the currently displayed saved note so this strip is
      // actually persisted. Otherwise the strip is a no-op against an
      // empty draft, and a later syncAdjustmentSectionForSession will
      // re-seed the draft from the saved note (which still contains the
      // historical line), causing the line to reappear.
      let cur;
      if (hasDraft) {
        cur = prev[sessionId] || '';
      } else {
        const sessionNotes = fetchedNotes[sessionId] || [];
        const noteIdx = noteIndices[sessionId] || 0;
        cur = sessionNotes[noteIdx]?.note || '';
      }
      const stripped = removeLineFromAdjustmentsSection(cur, lineText);
      if (stripped === cur && hasDraft) return prev;
      return { ...prev, [sessionId]: stripped };
    });

    // Record the removal so later re-derivations from the saved note text
    // (persistedProgramLines), from adjustment-log issues, or from
    // mergeTreatmentAdjustmentsSection cannot re-introduce the line until
    // the note is saved or editing is cancelled.
    const normalizedKey = (lineText || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();
    const activeSummary = programChangeSummaries[sessionId];
    const activeJournalSummary = getJournalProgramSummaryPart(activeSummary);
    const isStagedJournalLine = Array.isArray(activeJournalSummary?.journalLines)
      && activeJournalSummary.journalLines.some(
        (line) => (line || '').toString().replace(/\s+/g, ' ').trim().toLowerCase() === normalizedKey
      );
    if (normalizedKey) {
      setRemovedHistoricalLines(prev => {
        const existing = prev[sessionId] || new Set();
        if (isStagedJournalLine) {
          if (!existing.has(normalizedKey)) {
            return prev;
          }
          const next = new Set(existing);
          next.delete(normalizedKey);
          if (next.size === 0) {
            const nextState = { ...prev };
            delete nextState[sessionId];
            return nextState;
          }
          return { ...prev, [sessionId]: next };
        }
        if (existing.has(normalizedKey)) return prev;
        const next = new Set(existing);
        next.add(normalizedKey);
        return { ...prev, [sessionId]: next };
      });
    }
  }, [availableLibraryExercises, buildConfiguredDraftExercise, completedSessionsList, ensureSessionExerciseDraft, fetchedNotes, noteIndices, programChangeSummaries, reindexDraftExercises]);

  // ---- Staged baseline/goal helpers ------------------------------------
  const getBaselineGoalStagedLines = useCallback((sessionId) => {
    const items = baselineGoalStaged[sessionId] || [];
    return items.filter(item => !(item && item.suppressAnnotation)).map(item => item.lineText).filter(Boolean);
  }, [baselineGoalStaged]);

  const getVisibleBaselineGoalStagedItems = useCallback((sessionId) => {
    const items = baselineGoalStaged[sessionId] || [];
    return items.filter(item => item && !item.suppressAnnotation && item.lineText);
  }, [baselineGoalStaged]);

  const addBaselineGoalStaged = useCallback((sessionId, item) => {
    if (!sessionId || !item) return;
    const existing = baselineGoalStaged[sessionId] || [];
    const nextItems = [...existing, {
      id: `${item.mode}-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
      mode: item.mode,
      action: item.action || 'add',
      edit: item.edit,
      lineText: item.lineText,
      replaceLineText: item.replaceLineText || '',
      suppressAnnotation: Boolean(item.suppressAnnotation),
    }];
    setBaselineGoalStaged(prev => ({ ...prev, [sessionId]: nextItems }));
    replaceJourneyBaselineGoalDraftNote(sessionId, existing, nextItems);
  }, [baselineGoalStaged, replaceJourneyBaselineGoalDraftNote]);

  const removeBaselineGoalStaged = useCallback((sessionId, itemId) => {
    const existing = baselineGoalStaged[sessionId] || [];
    const nextItems = existing.filter(it => it.id !== itemId);
    setBaselineGoalStaged(prev => {
      const out = { ...prev };
      if (nextItems.length === 0) delete out[sessionId];
      else out[sessionId] = nextItems;
      return out;
    });
    replaceJourneyBaselineGoalDraftNote(sessionId, existing, nextItems);
  }, [baselineGoalStaged, replaceJourneyBaselineGoalDraftNote]);

  const updateBaselineGoalStaged = useCallback((sessionId, itemId, item) => {
    if (!sessionId || !itemId || !item) return;
    const existing = baselineGoalStaged[sessionId] || [];
    const idx = existing.findIndex(it => it.id === itemId);
    const nextItem = {
      id: itemId,
      mode: item.mode,
      action: item.action || 'add',
      edit: item.edit,
      lineText: item.lineText,
      replaceLineText: item.replaceLineText || '',
      suppressAnnotation: Boolean(item.suppressAnnotation),
    };
    const nextItems = idx < 0
      ? [...existing, nextItem]
      : existing.slice();
    if (idx >= 0) {
      nextItems[idx] = nextItem;
    }
    setBaselineGoalStaged(prev => ({ ...prev, [sessionId]: nextItems }));
    if (idx < 0 && item.replaceLineText) {
      replaceJourneyBaselineGoalDraftNote(sessionId, [...existing, { lineText: item.replaceLineText }], nextItems);
      return;
    }
    replaceJourneyBaselineGoalDraftNote(sessionId, existing, nextItems);
  }, [baselineGoalStaged, replaceJourneyBaselineGoalDraftNote]);

  const handleRemoveJourneyDraftItem = useCallback((sessionId, itemId) => {
    if (!sessionId || !itemId) return;
    removeBaselineGoalStaged(sessionId, itemId);
    setJourneyActionMenu((prev) => {
      if (!prev.inlineEditor || prev.inlineEditor.editingItemId !== itemId) return prev;
      return { ...prev, inlineEditor: null };
    });
  }, [removeBaselineGoalStaged]);

  const stageJourneyInlineEditorDraft = useCallback(() => {
    const sessionId = journeyActionMenu.sessionId;
    const inlineEditor = journeyActionMenu.inlineEditor;
    if (!sessionId || !inlineEditor) return;

    const score = parseInt(inlineEditor.score, 10);
    const scoreMax = inlineEditor.mode === 'goal'
      ? 100
      : (inlineEditor.baselineType === 'frequency' ? 7 : 100);

    if (inlineEditor.mode === 'goal' && !String(inlineEditor.goalText || '').trim()) {
      updateJourneyInlineEditor({ error: 'Please enter the goal description.' });
      return;
    }

    if (inlineEditor.mode === 'baseline' && inlineEditor.baselineType === 'custom' && !String(inlineEditor.customMetric || '').trim()) {
      updateJourneyInlineEditor({ error: 'Please enter the custom metric name.' });
      return;
    }

    if (!inlineEditor.score || Number.isNaN(score)) {
      updateJourneyInlineEditor({ error: 'Please enter a numeric score.' });
      return;
    }

    if (score < 0 || score > scoreMax) {
      updateJourneyInlineEditor({ error: `Score must be between 0 and ${scoreMax}.` });
      return;
    }

    if (inlineEditor.mode === 'goal') {
      const goalText = String(inlineEditor.goalText || '').trim();
      const baseIdentifier = 'painAnamnesis_SelfRatedFunction_MainGoal';
      const edit = {
        id: inlineEditor.editingEditId || `creator-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
        theme: `${baseIdentifier}_manual`,
        subgroup: `${baseIdentifier}_manual`,
        metaJournalPhrase: 'Patient described functional goal (PSFS):',
        journalPhrase: goalText,
        answer_text: '',
        answer_text_role: 'goal_text',
        numerical_value: String(score || 0),
        numeric_value: score || 0,
        mapped_key: baseIdentifier,
        identifier: baseIdentifier,
        display_group: 'Patient goal for treatment:',
        kpi_type: 'function_goal',
        kpi_subtype: `${baseIdentifier}_manual`,
        mode: 'create_kpi',
        is_deleted: false,
        paused_from_followup: false,
      };
      if (inlineEditor.editingItemId) {
        updateBaselineGoalStaged(sessionId, inlineEditor.editingItemId, {
          mode: 'goal',
          edit,
          lineText: `Added function goal: ${goalText} = ${score}/100`,
          replaceLineText: inlineEditor.sourceLineText,
        });
      } else {
        addBaselineGoalStaged(sessionId, {
          mode: 'goal',
          edit,
          lineText: `Added function goal: ${goalText} = ${score}/100`,
        });
      }
      closeJourneyInlineEditor();
      return;
    }

    const themeInfo = JOURNEY_INLINE_THEME_MAP[inlineEditor.baselineType] || JOURNEY_INLINE_THEME_MAP.current;
    const base = JOURNEY_INLINE_BASELINE_LABELS[inlineEditor.baselineType] || 'Patient reported:';
    const suffix = inlineEditor.baselineType === 'custom'
      ? (inlineEditor.customMetric ? ` ${String(inlineEditor.customMetric).trim()}` : '')
      : ` ${JOURNEY_INLINE_BASELINE_SUFFIX[inlineEditor.baselineType] || ''}`;
    const baseHasTerminator = /[:\u2014\u2013-]\s*$/.test(base);
    const joinedBase = (suffix.trim() && !baseHasTerminator) ? `${base}:` : base;
    const labelText = `${joinedBase}${suffix}`.replace(/\s{2,}/g, ' ').trim();
    const edit = {
      id: inlineEditor.editingEditId || `creator-${Date.now()}-${Math.floor(Math.random() * 10000)}`,
      theme: themeInfo.theme,
      subgroup: themeInfo.subgroup,
      metaJournalPhrase: '',
      journalPhrase: labelText,
      answer_text: '',
      numerical_value: String(score || 0),
      mapped_key: themeInfo.theme,
      display_group: 'Pain baseline levels:',
      kpi_type: 'pain_baseline',
      kpi_subtype: themeInfo.subgroup,
      mode: 'create_kpi',
      is_deleted: false,
      paused_from_followup: false,
    };
    if (inlineEditor.editingItemId) {
      updateBaselineGoalStaged(sessionId, inlineEditor.editingItemId, {
        mode: 'baseline',
        edit,
        lineText: `Added pain baseline: ${labelText} = ${score}${inlineEditor.baselineType === 'frequency' ? '/7d' : '/100'}`,
        replaceLineText: inlineEditor.sourceLineText,
      });
    } else {
      addBaselineGoalStaged(sessionId, {
        mode: 'baseline',
        edit,
        lineText: `Added pain baseline: ${labelText} = ${score}${inlineEditor.baselineType === 'frequency' ? '/7d' : '/100'}`,
      });
    }
    closeJourneyInlineEditor();
  }, [addBaselineGoalStaged, closeJourneyInlineEditor, journeyActionMenu.inlineEditor, journeyActionMenu.sessionId, updateBaselineGoalStaged, updateJourneyInlineEditor]);

  const clearBaselineGoalStaged = useCallback((sessionId) => {
    setBaselineGoalStaged(prev => {
      if (!prev[sessionId]) return prev;
      const out = { ...prev };
      delete out[sessionId];
      return out;
    });
  }, []);

  const persistStagedBaselineGoal = useCallback(async (sessionId) => {
    const items = baselineGoalStaged[sessionId] || [];
    if (items.length === 0) return { ok: true, persisted: 0 };
    const resolvedTreatmentId = treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id;
    if (!resolvedTreatmentId || !token) {
      return { ok: false, persisted: 0, error: 'Missing treatment or token' };
    }
    const patientUserId = selectedPatient?.user_id || selectedPatient?.id;

    // POST a single journal version containing only the staged new edits.
    // The backend `?in_latest_journal=1` filter accumulates edit keys
    // across ALL prior versions, so adding a single new baseline/goal
    // here does NOT drop the others. Re-asserting existing KPIs as
    // `override_baseline` would corrupt the audit chain (each override
    // writes a new KpiMeasurement at the current value, collapsing
    // baseline == latest and flat-lining the chart / 0% improvement).
    const newEdits = items.map((it) => it.edit).filter(Boolean);

    let ok = false;
    try {
      const body = {
        treatment_id: resolvedTreatmentId,
        user_id: patientUserId,
        note: items.length === 1
          ? (items[0].action === 'pause'
            ? (items[0].mode === 'goal'
              ? 'Paused function goal from journal tab'
              : 'Paused pain baseline from journal tab')
            : items[0].action === 'resume'
              ? (items[0].mode === 'goal'
                ? 'Resumed function goal from journal tab'
                : 'Resumed pain baseline from journal tab')
            : (items[0].mode === 'goal'
              ? 'Added function goal from journal tab'
              : 'Added pain baseline from journal tab'))
          : 'Updated baseline/goal items from journal tab',
        payload: { edits: newEdits },
      };
      const res = await fetch(
        `${apiBaseUrl}/treatment/treatments/${resolvedTreatmentId}/journal_versions/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Token ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      ok = res.ok;
      if (ok) {
        await fetchJourneyKpiItems();
      }
    } catch (e) {
      ok = false;
    }

    // Notify other tabs / panels of new KPI rows
    try {
      const updateDetail = { treatmentId: resolvedTreatmentId, userId: patientUserId, ts: Date.now() };
      localStorage.setItem('vp_latest_journal_update', JSON.stringify(updateDetail));
      window.dispatchEvent(new CustomEvent('vp-journal-updated', { detail: updateDetail }));
    } catch (e) { /* ignore */ }
    return { ok, persisted: ok ? items.length : 0 };
  }, [baselineGoalStaged, treatmentId, selectedPatient, diagnosis, token, fetchJourneyKpiItems]);

  useEffect(() => {
    setProgramChangeSummaries(prev => {
      let hasChanges = false;
      const next = { ...prev };

      Object.entries(sessionExerciseDrafts).forEach(([sessionId, draft]) => {
        const changeSummary = summarizeExerciseProgramChanges({
          originalExercises: draft.originalExercises,
          nextExercises: draft.exercises,
        });

        // === Consolidate transitive replacements with the saved note ===
        // If the saved note already contains "Replaced [A] with [B]" and
        // we just staged "Replaced [B] with [C]", collapse to a single
        // "Replaced [A] with [C]". The historical [A→B] line will then
        // be evicted by mergeTreatmentAdjustmentsSection because the
        // consolidated line shares its identity (replaced:a).
        const sessionNotes = fetchedNotes[sessionId] || [];
        const noteIdx = noteIndices[sessionId] || 0;
        const savedNoteText = sessionNotes[noteIdx]?.note || '';
        const savedHistoricalReps = [];
        if (savedNoteText) {
          const re = /Replaced \[([^\]]+)\] with \[([^\]]+)\]/g;
          let m;
          while ((m = re.exec(savedNoteText)) !== null) {
            savedHistoricalReps.push({ from: m[1].trim(), to: m[2].trim() });
          }
        }
        if (changeSummary.hasChanges && savedHistoricalReps.length > 0) {
          const lc = (s) => (s || '').toString().trim().toLowerCase();
          changeSummary.replacements = changeSummary.replacements.map(r => {
            // Walk the chain: while there is a historical X->r.from, rewrite r.from to X.
            let from = r.from;
            let fromIdentifier = r.fromIdentifier;
            const guard = new Set();
            // eslint-disable-next-line no-constant-condition
            while (true) {
              const match = savedHistoricalReps.find(h => lc(h.to) === lc(from));
              if (!match || guard.has(lc(match.from))) break;
              guard.add(lc(match.from));
              from = match.from;
              fromIdentifier = null; // historical "from" identifier is unknown
            }
            if (lc(from) === lc(r.from)) return r;
            return { ...r, from, fromIdentifier };
          });
          // Also drop trivial replacements where the consolidation makes from===to
          changeSummary.replacements = changeSummary.replacements.filter(r => lc(r.from) !== lc(r.to));
          // Rebuild journalLines with the consolidated replacements
          changeSummary.journalLines = [
            ...changeSummary.replacements.map(c => formatReplacedExerciseProgramJournalLine(c.from, c.nextExercise || { title: c.to, identifier: c.toIdentifier })),
            ...changeSummary.updated.map(c => `Updated [${c.title}]: ${c.fieldChanges.join(', ')}.`),
            ...changeSummary.removed.map(c => `Removed [${c.title}] from the exercise program.`),
            ...changeSummary.added.map(c => formatAddedExerciseProgramJournalLine(c.exercise || { title: c.title, identifier: c.identifier })),
          ];
          changeSummary.hasChanges = changeSummary.journalLines.length > 0;
        }

        if (changeSummary.hasChanges) {
          const previousSummary = prev[sessionId];
          const nextJournalSummary = {
            ...changeSummary,
            phaseId: draft.phaseId,
            source: 'journal',
          };
          const nextSummary = mergeProgramChangeSummaryEntries({
            journalSummary: nextJournalSummary,
            externalSummary: getExternalProgramSummaryPart(previousSummary),
          });

          if (buildProgramSummarySignature(previousSummary) !== buildProgramSummarySignature(nextSummary)) {
            next[sessionId] = nextSummary;
            hasChanges = true;
          }
        } else {
          const previousSummary = prev[sessionId];
          const nextSummary = mergeProgramChangeSummaryEntries({
            journalSummary: null,
            externalSummary: getExternalProgramSummaryPart(previousSummary),
          });
          if (buildProgramSummarySignature(previousSummary) === buildProgramSummarySignature(nextSummary)) {
            return;
          }
          if (nextSummary) {
            next[sessionId] = nextSummary;
          } else {
            delete next[sessionId];
          }
          hasChanges = true;
        }
      });

      return hasChanges ? next : prev;
    });
  }, [sessionExerciseDrafts, fetchedNotes, noteIndices]);

  const applyExternalProgramUpdate = useCallback((payload) => {
    const resolvedTreatmentId = treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id;
    if (!payload?.changeSummary?.hasChanges || !resolvedTreatmentId || !selectedPatient?.user_id) {
      return;
    }
    if (String(payload.treatmentId) !== String(resolvedTreatmentId)) {
      return;
    }
    if (payload.userId && String(payload.userId) !== String(selectedPatient.user_id)) {
      return;
    }

    const updateKey = `${payload.ts || '0'}::${payload.sessionId || ''}::${payload.phaseId || ''}::${payload.source || 'program'}`;
    if (handledProgramUpdateKeysRef.current.has(updateKey)) {
      return;
    }

    const orderedSessions = [...completedSessionsList].reverse();
    const targetSession = payload.sessionId
      ? orderedSessions.find(session => String(session.id) === String(payload.sessionId))
      : orderedSessions.find(session => String(session.phase_id || selectedPhaseId || '') === String(payload.phaseId || ''))
        || persistentPatientScopeSession
        || syntheticPatientSession;

    if (!targetSession) {
      return;
    }

    handledProgramUpdateKeysRef.current.add(updateKey);

    const journalLines = dedupeProgramSummaryLines(payload.changeSummary.journalLines || []);
    if (journalLines.length > 0) {
      setPendingExternalProgramUpdate({
        contextKey: journeyTreatmentContextKey,
        updateKey,
        targetSessionId: targetSession.id,
        treatmentId: payload.treatmentId,
        phaseId: payload.phaseId,
        payloadSessionId: payload.sessionId || null,
        journalLines,
        autoConfirm: Boolean(payload.journalConfirmed),
      });

      if (payload.reviewInJournal) {
        setProgramChangeSummaries((prev) => ({
          ...prev,
          [targetSession.id]: mergeProgramChangeSummaryEntries({
            journalSummary: getJournalProgramSummaryPart(prev[targetSession.id]),
            externalSummary: {
              ...payload.changeSummary,
              phaseId: payload.phaseId,
              source: payload.source || 'program',
            },
          }),
        }));
        setEditingNote((prev) => ({ ...prev, [targetSession.id]: true }));
        setEditNoteValue((prev) => {
          if (Object.prototype.hasOwnProperty.call(prev, targetSession.id)) {
            return prev;
          }
          const noteIndex = noteIndices[targetSession.id] || 0;
          return {
            ...prev,
            [targetSession.id]: (fetchedNotes[targetSession.id] || [])[noteIndex]?.note || '',
          };
        });
      }
    }

    // The persisted change (e.g. from selectedPatientProgram.js — including
    // exercise reorders that bump Exercise.index) must invalidate the
    // cached draft for any session whose phase matches; otherwise the
    // journal "Current exercise program:" list keeps sorting by stale
    // indices until the user opens the program editor or refreshes.
    const phaseIdForUpdate = String(payload.phaseId || '');
    if (phaseIdForUpdate) {
      completedSessionsList.forEach(session => {
        const sessionPhaseId = String(session.phase_id || selectedPhaseId || '');
        if (sessionPhaseId !== phaseIdForUpdate) return;
        ensureSessionExerciseDraft(session, { forceReload: true }).catch(() => {});
      });
    } else {
      ensureSessionExerciseDraft(targetSession, { forceReload: true }).catch(() => {});
    }

  }, [completedSessionsList, selectedPhaseId, treatmentId, selectedPatient?.treatment_id, diagnosis?.treatment_id, selectedPatient?.user_id, ensureSessionExerciseDraft, fetchedNotes, journeyTreatmentContextKey, noteIndices, persistentPatientScopeSession, syntheticPatientSession]);

  useEffect(() => {
    if (!pendingExternalProgramUpdate?.autoConfirm || savingExternalProgramUpdate) {
      return;
    }
    handleConfirmExternalProgramUpdate();
  }, [pendingExternalProgramUpdate?.autoConfirm, savingExternalProgramUpdate]);

  useEffect(() => {
    const latestProgramUpdate = readLatestExerciseProgramUpdate();
    if (latestProgramUpdate) {
      applyExternalProgramUpdate(latestProgramUpdate);
    }

    const handleProgramUpdate = (event) => {
      applyExternalProgramUpdate(event.detail);
    };

    window.addEventListener('vp-program-updated', handleProgramUpdate);
    return () => {
      window.removeEventListener('vp-program-updated', handleProgramUpdate);
    };
  }, [applyExternalProgramUpdate]);

  useEffect(() => {
    const handleJournalUpdated = (detail) => {
      try {
        const evt = detail && detail.detail ? detail.detail : detail;
        const evtUser = evt && evt.userId ? String(evt.userId) : null;
        const evtTreatment = evt && evt.treatmentId ? String(evt.treatmentId) : null;
        const currentUser = selectedPatient && selectedPatient.user_id ? String(selectedPatient.user_id) : null;
        const currentTreatment = String(treatmentId || selectedPatient?.treatment_id || diagnosis?.treatment_id || '');
        if (!currentUser) return;
        if (evtUser && evtUser !== currentUser) return;
        if (evtTreatment && currentTreatment && evtTreatment !== currentTreatment) return;

        journeyContextCache.delete(`${currentUser}::${currentTreatment}`);
        fetchJourneyKpiItems().catch(() => {});
        fetchedNotesRef.current = new Set();
        fetchPatientJournalNotes().catch(() => {});

        const sessionIds = completedSessionsList
          .map((session) => session && session.id)
          .filter(Boolean);
        if (sessionIds.length > 0) {
          fetchAllJournalNotesBatch(sessionIds, {
            userId: selectedPatient && selectedPatient.user_id,
            treatmentId: currentTreatment || undefined,
          })
            .then((batchNotesMap) => {
              if (batchNotesMap && Object.keys(batchNotesMap).length) {
                setFetchedNotes((prev) => ({ ...prev, ...batchNotesMap }));
              }
            })
            .catch(() => {});
        }
      } catch (e) {}
    };

    const handleStorage = (event) => {
      if (!event || event.key !== 'vp_latest_journal_update' || !event.newValue) return;
      try {
        const parsed = JSON.parse(event.newValue);
        handleJournalUpdated(parsed);
      } catch (e) {}
    };

    window.addEventListener('vp-journal-updated', handleJournalUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('vp-journal-updated', handleJournalUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [completedSessionsList, diagnosis, fetchAllJournalNotesBatch, fetchJourneyKpiItems, fetchPatientJournalNotes, selectedPatient, treatmentId]);

  useEffect(() => {
    const sessionIds = Array.from(new Set([
      ...Object.keys(programChangeSummaries),
      ...Object.keys(sessionAdviceDrafts),
      ...Object.keys(generatedIssuesBySession),
    ]));

    sessionIds.forEach(sessionId => {
      syncAdjustmentSectionForSession(sessionId);
    });
  }, [programChangeSummaries, sessionAdviceDrafts, generatedIssuesBySession, suggestionDecisions, syncAdjustmentSectionForSession]);

  const handleLibraryExerciseSelect = useCallback((exercise) => {
    if (!editingSuggestionContext) {
      setLibraryDrawerOpen(false);
      return;
    }

    if (editingSuggestionContext.type === 'issue') {
      updateSuggestionDecision(editingSuggestionContext.sessionId, editingSuggestionContext.issue.issueKey, {
        action: 'edit',
        customExercise: exercise.identifier,
        configuredExercise: exercise,
      });
    } else if (editingSuggestionContext.type === 'program-add') {
      stageProgramExerciseAdd(editingSuggestionContext.sessionId, exercise);
    } else if (editingSuggestionContext.type === 'program-replace') {
      const draft = sessionExerciseDrafts[editingSuggestionContext.sessionId];
      const targetExercise = draft?.exercises.find(existingExercise => getDraftExerciseKey(existingExercise) === editingSuggestionContext.targetExerciseKey);
      if (targetExercise) {
        stageProgramExerciseReplace(editingSuggestionContext.sessionId, targetExercise, exercise);
      }
    }

    setLibraryDrawerOpen(false);
    setEditingSuggestionContext(null);
  }, [editingSuggestionContext, updateSuggestionDecision, stageProgramExerciseAdd, sessionExerciseDrafts, getDraftExerciseKey, stageProgramExerciseReplace]);

  const resolveSuggestionReplacementExercise = useCallback((issue, decision = {}) => {
    if (decision.configuredExercise) {
      return decision.configuredExercise;
    }

    const normalizedReplacementTitle = normalizeExerciseKey(issue?.replacement);
    return availableLibraryExercises.find((exercise) => (
      issue?.replacementIdentifier && String(exercise?.identifier || '') === String(issue.replacementIdentifier)
    ) || (
      normalizedReplacementTitle && normalizeExerciseKey(exercise?.title) === normalizedReplacementTitle
    )) || null;
  }, [availableLibraryExercises, normalizeExerciseKey]);

  const handleAcceptSuggestionIssue = useCallback(async (targetSession, issue) => {
    if (!targetSession) {
      return;
    }

    setProgramEditorLoading(true);
    try {
      await ensureSessionExerciseDraft(targetSession);
      const targetSessionId = targetSession.id;
      const decision = (suggestionDecisions[targetSessionId] || {})[issue.issueKey] || {};
      const configuredExercise = resolveSuggestionReplacementExercise(issue, decision);

      if (!configuredExercise) {
        await openEditSuggestionDrawer(targetSession, issue);
        return;
      }

      stageIssueExerciseEdit(targetSessionId, issue, configuredExercise);
      updateSuggestionDecision(targetSessionId, issue.issueKey, {
        action: 'accept',
        customExercise: configuredExercise.identifier,
        configuredExercise,
      });
    } catch (error) {
      alert(error.message || 'Failed to accept exercise suggestion');
    } finally {
      setProgramEditorLoading(false);
    }
  }, [ensureSessionExerciseDraft, suggestionDecisions, resolveSuggestionReplacementExercise, openEditSuggestionDrawer, stageIssueExerciseEdit, updateSuggestionDecision]);

  const handleDeclineSuggestionIssue = useCallback((targetSessionId, issueKey) => {
    updateSuggestionDecision(targetSessionId, issueKey, {
      action: 'decline',
      customExercise: undefined,
      configuredExercise: undefined,
    });
  }, [updateSuggestionDecision]);

  // --- Auto-generate journal text ---
  const [generatingJournal, setGeneratingJournal] = useState({});

  const generateJournalText = useCallback(async (session) => {
    if (!token || !diagnosis || !selectedPatient) return;
    setGeneratingJournal(prev => ({ ...prev, [session.id]: true }));

    const diagIdent = diagnosis?.identifier || diagnosis?.diagnosis_identifier || diagnosis?.diagnosis || '';
    const userId = selectedPatient.user_id;
    const treatmentId = selectedPatient.treatment_id || diagnosis?.treatment_id || '';

    try {
      // 1. Fetch symptom comparison
      let comparisonData = null;
      try {
        const res = await fetch(
          `${apiBaseUrl}/question-handler/improvements/${diagIdent}/${userId}/`,
          { headers: { 'Authorization': `Token ${token}` } }
        );
        if (res.ok) comparisonData = await res.json();
      } catch (e) { console.warn('Failed to fetch improvements', e); }

      // 2. Fetch exercise adjustments
      let adjustments = adjustmentLogs;
      if (treatmentId) {
        try {
          const adjRes = await fetch(
            `${apiBaseUrl}/treatment/exercise-adjustments/${userId}/${treatmentId}/`,
            { headers: { 'Authorization': `Token ${token}` } }
          );
          if (adjRes.ok) adjustments = await adjRes.json();
        } catch (e) { console.warn('Failed to fetch adjustments', e); }
      }

      let inferenceLibraryExercises = availableLibraryExercises;
      if (!inferenceLibraryExercises.length) {
        try {
          const libraryRes = await fetch(`${apiBaseUrl}/treatment/exercise/`, {
            headers: { Authorization: `Token ${token}` },
          });
          if (libraryRes.ok) {
            const libraryData = await libraryRes.json();
            inferenceLibraryExercises = Array.isArray(libraryData.exercises) ? libraryData.exercises : [];
            if (inferenceLibraryExercises.length > 0) {
              setJournalLibraryExercises(inferenceLibraryExercises);
            }
          }
        } catch (e) { console.warn('Failed to fetch exercise library for journal inference', e); }
      }

      // --- Build compact template ---
      const lines = [];
      const generatedSymptomParagraphs = [];

      // Helper: convert identifier like "PSFS_SelfRatedPain_neck_current" → "Pain neck (current)"
      const formatLabel = (id) => {
        if (!id) return id;
        if (/^PSFS_SelfRatedFunction/i.test(id)) {
          return 'Patient specific functional score (PSFS)';
        }
        if (/^SelfRated_?NPS/i.test(id)) {
          return 'NPS';
        }
        let s = id
          .replace(/^PSFS_/i, '')
          .replace(/SelfRatedPain/i, 'Pain')
          .replace(/SelfRatedFunction/i, 'Function')
          .replace(/SelfRated_?NPS/i, 'NPS')
          .replace(/Frequency_?of_?symptoms/i, 'Symptom frequency')
          .replace(/MainGoal/i, 'Main goal')
          .replace(/_/g, ' ')
          .trim();
        // Capitalize first letter, format trailing qualifier in parentheses
        const parts = s.split(' ').filter(Boolean);
        const qualifiers = ['current', 'atBest', 'atWorst', 'at best', 'at worst'];
        const last = parts[parts.length - 1]?.toLowerCase();
        if (parts.length > 1 && qualifiers.some(q => q.toLowerCase() === last)) {
          const qual = last === 'atbest' ? 'best' : last === 'atworst' ? 'worst' : last;
          return parts.slice(0, -1).join(' ') + ' (' + qual + ')';
        }
        return parts.join(' ');
      };

      const classifySymptomTrend = (comparisons) => {
        let improved = 0;
        let worsened = 0;

        comparisons.forEach((comparison) => {
          const baseline = comparison.baseline_value;
          const followup = comparison.followup_value;
          if (baseline == null || followup == null) return;

          const qid = comparison.question_identifier || '';
          const delta = followup - baseline;
          const higherIsBetter = /^PSFS_SelfRatedFunction/i.test(qid);

          if ((higherIsBetter && delta > 0) || (!higherIsBetter && delta < 0)) {
            improved += 1;
          } else if ((higherIsBetter && delta < 0) || (!higherIsBetter && delta > 0)) {
            worsened += 1;
          }
        });

        if (improved > 0 && worsened === 0) {
          return 'improved';
        }
        if (worsened > 0 && improved === 0) {
          return 'worsened';
        }
        if (improved > 0 && worsened > 0) {
          return 'mixed';
        }
        return 'stable';
      };

      const summarizeSymptomTrend = (comparisons) => {
        const trend = classifySymptomTrend(comparisons);
        if (trend === 'improved') {
          return 'Patient has seen an improvement of symptoms and function.';
        }
        if (trend === 'worsened') {
          return 'Patient has seen a worsening of symptoms and function.';
        }
        if (trend === 'mixed') {
          return 'Patient has had a mixed change in symptoms and function.';
        }
        return 'No clear change in symptoms or function since last consultation.';
      };

      // === Section 1: Symptom development ===
      lines.push(JOURNAL_SYMPTOM_HEADING);
      const symptomTrend = comparisonData?.comparisons?.length > 0
        ? classifySymptomTrend(comparisonData.comparisons)
        : 'stable';
      if (comparisonData?.comparisons?.length > 0) {
        generatedSymptomParagraphs.push(`<p>${summarizeSymptomTrend(comparisonData.comparisons)}</p>`);
        const items = [];
        for (const c of comparisonData.comparisons) {
          if (c.baseline_value == null || c.followup_value == null) {
            continue;
          }
          const label = formatLabel(c.question_identifier);
          const bv = c.baseline_value ?? 'N/A';
          const fv = c.followup_value ?? 'N/A';
          if (c.absolute_change === 0) {
            items.push(`${label}: no change (${fv})`);
          } else {
            const arrow = c.absolute_change < 0 ? '↓' : '↑';
            items.push(`${label}: ${bv} → ${fv} (${arrow}${Math.abs(c.absolute_change)})`);
          }
        }
        if (items.length > 0) {
          generatedSymptomParagraphs.push(`<p>${items.join('<br>')}</p>`);
        }
      } else {
        generatedSymptomParagraphs.push('<p>No follow-up assessment data available.</p>');
      }

      const derivedIssues = deriveSessionIssues(session, adjustments, inferenceLibraryExercises, {
        allowInference: false,
      }).map((issue) => ({
        ...issue,
        sessionId: session.id,
      }));
      const mergedIssues = new Map();
      derivedIssues.forEach((issue) => {
        mergedIssues.set(issue.adjId || issue.issueKey, issue);
      });
      adjustments
        .filter((adjustment) => adjustment && adjustment.status !== 'rejected' && String(adjustment.session || '') === String(session.id))
        .map((adjustment) => ({
          ...buildIssueFromLoggedAdjustment(adjustment),
          sessionId: session.id,
        }))
        .forEach((issue) => {
          const issueKey = issue.adjId || issue.issueKey;
          const existingIssue = mergedIssues.get(issueKey);
          mergedIssues.set(issueKey, existingIssue ? mergeAdjustmentIssues(existingIssue, issue) : issue);
        });
      const issues = Array.from(mergedIssues.values());
      const consultationSessionIds = completedSessionsList
        .map((candidateSession) => candidateSession && candidateSession.id)
        .filter(Boolean);
      let consultationNotesBySession = fetchedNotes;
      const missingConsultationNotes = consultationSessionIds.some((sessionId) => (
        !Object.prototype.hasOwnProperty.call(consultationNotesBySession, sessionId)
      ));

      if (consultationSessionIds.length > 0 && missingConsultationNotes) {
        try {
          const notesResponse = await fetch(`${apiBaseUrl}/treatment/session/journal_notes/batch/`, {
            method: 'POST',
            headers: {
              Authorization: `Token ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ session_ids: consultationSessionIds, treatment_id: resolvedJourneyTreatmentId }),
          });
          if (notesResponse.ok) {
            const batchNotesMap = await notesResponse.json();
            consultationNotesBySession = { ...consultationNotesBySession, ...(batchNotesMap || {}) };
            if (batchNotesMap && Object.keys(batchNotesMap).length > 0) {
              setFetchedNotes((prev) => ({ ...prev, ...batchNotesMap }));
            }
          }
        } catch (e) {
          console.warn('Failed to fetch journal notes for consultation window', e);
        }
      }

      const consultationResponseLines = getConsultationIssueLines(session, {
        includeMarker: false,
        notesBySession: consultationNotesBySession,
      });
      const sessionAdjustmentLines = getSessionJournalAdjustmentLines(session);
      const consultationSessionSummary = getConsultationSessionSummary(session, {
        notesBySession: consultationNotesBySession,
      });
      const formattedAveragePain = consultationSessionSummary.averagePain == null
        ? 'N/A'
        : `${Math.round(consultationSessionSummary.averagePain * 10) / 10}`;
      setGeneratedIssuesBySession(prev => ({ ...prev, [session.id]: issues }));

      generatedSymptomParagraphs.push(`<p>${escapeHtml(`Patient has completed ${consultationSessionSummary.sessionCount} sessions since last consultation.`)}</p>`);
      generatedSymptomParagraphs.push(`<p>${escapeHtml(`Average pain per exercise: ${formattedAveragePain}`)}</p>`);

      if (consultationResponseLines.length > 0) {
        generatedSymptomParagraphs.push('<p>Exercise response ok since last consultation, except for:</p>');
        generatedSymptomParagraphs.push(`<p>${consultationResponseLines.map(line => `- ${escapeHtml(line)}`).join('<br>')}</p>`);
      } else {
        generatedSymptomParagraphs.push('<p>Exercise response since last consultation: ok</p>');
      }

      lines.push(buildGeneratedSymptomHtml(generatedSymptomParagraphs));

      // === Section 2: Plan until next time ===
      lines.push('<hr style="border:none;border-top:1px solid #cfd8dc;margin:8px 0;">');
      lines.push(JOURNAL_ADJUSTMENTS_HEADING);
      lines.push(buildAdjustmentsSectionHtml(sessionAdjustmentLines));

      // === Section 3: Plan until next time ===
      lines.push('<hr style="border:none;border-top:1px solid #cfd8dc;margin:8px 0;">');
      lines.push(JOURNAL_PLAN_HEADING);
      lines.push(buildGeneratedPlanHtml(buildPlanText(symptomTrend, issues, { programLines: getProgramSummaryJournalLines(session.id) })));

      const generatedHtml = upsertPatientAcceptSection(lines.join(''), JOURNAL_PATIENT_ACCEPT_DEFAULT_TEXT);
      const hasDraftValue = Object.prototype.hasOwnProperty.call(editNoteValue, session.id);
      const savedSessionNotes = fetchedNotes[session.id] || [];
      const baseNoteHtml = hasDraftValue
        ? (editNoteValue[session.id] || '')
        : (journalNotes[session.id]?.note || savedSessionNotes[0]?.note || '');

      const html = baseNoteHtml && baseNoteHtml.trim()
        ? upsertPatientAcceptSection(mergePlanUntilNextTimeSection(
            mergeTreatmentAdjustmentsSection(
              mergeSymptomDevelopmentSection(baseNoteHtml, buildGeneratedSymptomHtml(generatedSymptomParagraphs)),
              sessionAdjustmentLines,
            ),
            buildPlanText(symptomTrend, issues, { programLines: getProgramSummaryJournalLines(session.id) })
          ), JOURNAL_PATIENT_ACCEPT_DEFAULT_TEXT)
        : generatedHtml;

      // Set generated text into editor
      setJournalNotes(prev => ({
        ...prev,
        [session.id]: { ...prev[session.id], note: html }
      }));
      setEditNoteValue(prev => ({ ...prev, [session.id]: html }));

      const newEditorRef = editorRefsMap.current[`${session.id}_new`];
      if (newEditorRef) {
        if (newEditorRef.setContent) newEditorRef.setContent(html);
      } else if (editorRefsMap.current[session.id]) {
        const editorEl = editorRefsMap.current[session.id];
        if (editorEl.setContent) editorEl.setContent(html);
      }

    } catch (e) {
      console.error('Failed to generate journal text', e);
    }
    setGeneratingJournal(prev => ({ ...prev, [session.id]: false }));
  }, [token, diagnosis, selectedPatient, treatmentId, availableLibraryExercises, adjustmentMode, adjustmentLogs, completedSessionsList, deriveSessionIssues, buildIssueFromLoggedAdjustment, getConsultationIssueLines, getConsultationSessionSummary, getSessionJournalAdjustmentLines, editNoteValue, fetchedNotes, journalNotes, getProgramSummaryJournalLines]);


  // Track last fetch to prevent duplicate fetches
  const lastFetchRef = useRef(null);
  const editorRefsMap = useRef({}); // { [sessionId]: RichNoteEditor ref }
  const fetchedNotesRef = useRef(new Set()); // Track which sessions already have notes fetched
  const batchFetchInitiatedRef = useRef(null); // Track if we've already started fetching notes for this context
  const initialJournalEditBootstrapRef = useRef(null); // Track whether the initial last-session auto-open has already been evaluated for the current patient/treatment context
  const sessionRowCacheRef = useRef({}); // { [sessionId]: { signature..., element } }
  const [visibleSessionCount, setVisibleSessionCount] = useState(INITIAL_VISIBLE_JOURNAL_SESSION_COUNT);
  const loadMoreSentinelRef = useRef(null);

  // Request-level deduplication: include POST payloads so distinct note batches
  // never share a response merely because they use the same endpoint.
  const inFlightRequestsRef = useRef({}); // { [requestKey]: { promise, timestamp } }
  
  const dedupedFetch = useCallback((url, options = {}) => {
    const now = Date.now();
    const requestKey = `${options.method || 'GET'}::${url}::${options.body || ''}`;
    const cached = inFlightRequestsRef.current[requestKey];
    
    // If request is in-flight and within 2000ms window, return cached promise
    if (cached && (now - cached.timestamp < 2000)) {
      return cached.promise;
    }
    
    // New request: create promise and cache it
    const promise = fetch(url, options).finally(() => {
      // Clean up after 2000ms
      setTimeout(() => {
        delete inFlightRequestsRef.current[requestKey];
      }, 2000);
    });
    
    inFlightRequestsRef.current[requestKey] = { promise, timestamp: now };
    return promise;
  }, []);

  // Create a single composite key to prevent effect thrashing when diagnosis object reference changes
  const resolvedDiagnosisIdentifier = diagnosis?.identifier || diagnosis?.diagnosis_identifier || diagnosis?.diagnosis || '';
  const dataContextKey = selectedPatient && diagnosis && resolvedDiagnosisIdentifier
    ? `${selectedPatient.user_id}-${selectedPatient?.treatment_id || diagnosis?.treatment_id || ''}-${resolvedDiagnosisIdentifier}`
    : null;
  
  // Use a memoized version to prevent re-running effect on every render
  const memoizedContextKey = useMemo(() => dataContextKey, [dataContextKey]);

  useEffect(() => {
    if (!dataContextKey) {
      return;
    }

    if (completedSessionsList.length === 0 && !syntheticPatientSession) {
      return;
    }

    if (initialJournalEditBootstrapRef.current === dataContextKey) {
      return;
    }

    let isCancelled = false;

    const bootstrapLatestSessionEditMode = async () => {
      let notesBySession = fetchedNotes;
      let bootstrapSession = null;

      if (completedSessionsList.length > 0) {
        bootstrapSession = completedSessionsList.reduce((latest, candidate) => {
          if (!candidate) {
            return latest;
          }
          if (!latest) {
            return candidate;
          }
          return (getSessionCompletedAt(candidate) || 0) > (getSessionCompletedAt(latest) || 0)
            ? candidate
            : latest;
        }, null);
        if (!bootstrapSession || !Object.prototype.hasOwnProperty.call(notesBySession, bootstrapSession.id)) {
          return;
        }
      } else {
        bootstrapSession = syntheticPatientSession;
        const syntheticSessionId = syntheticPatientSession?.id;
        const needsPatientNotes = syntheticSessionId
          && !Object.prototype.hasOwnProperty.call(notesBySession, syntheticSessionId);

        if (needsPatientNotes) {
          try {
            const patientNotes = await fetchPatientJournalNotes();
            notesBySession = { ...notesBySession, [syntheticSessionId]: patientNotes };
          } catch (error) {
            console.warn('Failed to fetch patient-scoped journal notes for initial edit mode bootstrap', error);
          }
        }
      }

      if (isCancelled || !bootstrapSession || bootstrapSession.id == null) {
        return;
      }

      const bootstrapSessionHasManualNote = (notesBySession[bootstrapSession.id] || []).some((note) => (
        note
        && !note.is_system
        && note.author != null
        && String(note.note || '').trim() !== ''
      ));

      initialJournalEditBootstrapRef.current = dataContextKey;

      if (bootstrapSessionHasManualNote || Object.values(editingNoteRef.current || {}).some(Boolean)) {
        return;
      }

      setEditingNote((prev) => ({
        ...prev,
        [bootstrapSession.id]: true,
      }));
      setEditNoteValue((prev) => {
        if (Object.prototype.hasOwnProperty.call(prev, bootstrapSession.id)) {
          return prev;
        }

        const latestSessionNotes = notesBySession[bootstrapSession.id] || [];
        const noteIndex = noteIndices[bootstrapSession.id] || 0;
        const currentNote = latestSessionNotes[noteIndex] || {};

        return {
          ...prev,
          [bootstrapSession.id]: currentNote.note || '',
        };
      });
    };

    bootstrapLatestSessionEditMode();

    return () => {
      isCancelled = true;
    };
  }, [completedSessionsList, dataContextKey, ensureSessionExerciseDraft, fetchPatientJournalNotes, fetchedNotes, getSessionCompletedAt, noteIndices, syntheticPatientSession, token]);

  useEffect(() => {
    if (completedSessionsList.length === 0) {
      return;
    }

    const latestSession = completedSessionsList.reduce((latest, candidate) => {
      if (!candidate) {
        return latest;
      }
      if (!latest) {
        return candidate;
      }
      return (getSessionCompletedAt(candidate) || 0) > (getSessionCompletedAt(latest) || 0)
        ? candidate
        : latest;
    }, null);

    if (!latestSession || latestSession.id == null) {
      return;
    }

    const latestSessionNotes = fetchedNotes[latestSession.id] || [];
    const latestSessionHasManualNote = latestSessionNotes.some((note) => (
      note
      && !note.is_system
      && note.author != null
      && String(note.note || '').trim() !== ''
    ));

    if (latestSessionHasManualNote || editingNoteRef.current[latestSession.id] || Object.values(editingNoteRef.current || {}).some(Boolean)) {
      return;
    }

    setEditingNote((prev) => ({
      ...prev,
      [latestSession.id]: true,
    }));
    setEditNoteValue((prev) => {
      if (Object.prototype.hasOwnProperty.call(prev, latestSession.id)) {
        return prev;
      }

      const noteIndex = noteIndices[latestSession.id] || 0;
      const currentNote = latestSessionNotes[noteIndex] || {};

      return {
        ...prev,
        [latestSession.id]: currentNote.note || '',
      };
    });
  }, [completedSessionsList, ensureSessionExerciseDraft, fetchedNotes, getSessionCompletedAt, noteIndices]);

  useEffect(() => {
    let isActive = true;

    const fetchAllSessionsData = async () => {
      if (isActive) setIsJournalLoading(true);
      try {
        if (!selectedPatient || !selectedPatient.user_id) {
          setCompletedSessionsList([]);
          setPlannedSessionsPerDayOverall(null);
          return;
        }

        // Support identifier, diagnosis_identifier, and diagnosis fields
        const diagnosisIdentifier = diagnosis?.identifier || diagnosis?.diagnosis_identifier || diagnosis?.diagnosis;
        if (!diagnosis || !diagnosisIdentifier) {
          setCompletedSessionsList([]);
          setPlannedSessionsPerDayOverall(null);
          return;
        }

        // Get treatment_id from diagnosis object - MUST use selectedPatient.treatment_id if available
        const treatmentId = selectedPatient?.treatment_id || diagnosis?.treatment_id || '';

        // GUARD: Do not fetch if treatmentId is empty (prevents stale requests during state transitions)
        if (!treatmentId) {
          setPlannedSessionsPerDayOverall(null);
          return;
        }

        // Prevent duplicate fetches with same parameters
        const fetchKey = `${selectedPatient.user_id}-${diagnosisIdentifier}-${treatmentId}`;
        if (lastFetchRef.current === fetchKey) {
          return;
        }
        lastFetchRef.current = fetchKey;
        const sessionsUrl = `${apiBaseUrl}/treatment/sessions/all/${selectedPatient.user_id}/${diagnosisIdentifier}/?treatment_id=${treatmentId}`;
        const cachedContext = journeyContextCache.get(journeyTreatmentContextKey);
        if (JOURNEY_CONTEXT_RESTORE_ENABLED
          && cachedContext
          && Date.now() - cachedContext.fetchedAt < JOURNEY_CONTEXT_CACHE_TTL_MS) {
          const cachedSessions = cachedContext.sessions || [];
          const cachedNotes = cachedContext.fetchedNotes || {};
          setProgress(cachedContext.progress || 0);
          setTitle(cachedContext.title || '');
          setPlannedSessionsPerDayOverall(cachedContext.plannedSessionsPerDayOverall ?? null);
          setCompletedSessionsList(cachedSessions);
          setFetchedNotes(cachedNotes);
          batchFetchInitiatedRef.current = dataContextKey;

          // Restore cached notes immediately, then revalidate just the rows
          // on screen. This keeps the latest session correct after a tab
          // return without waiting for the entire historic journal.
          const visibleSessionIds = cachedSessions
            .slice(-INITIAL_VISIBLE_JOURNAL_SESSION_COUNT)
            .map((session) => session.id)
            .filter(Boolean);
          if (visibleSessionIds.length > 0) {
            window.setTimeout(() => {
              if (!isActive) return;
              fetchAllJournalNotesBatch(visibleSessionIds, {
                userId: selectedPatient.user_id,
                treatmentId,
              }).then((batchNotesMap) => {
                if (!isActive || !batchNotesMap || Object.keys(batchNotesMap).length === 0) return;
                setFetchedNotes((previousNotes) => ({ ...previousNotes, ...batchNotesMap }));
                const currentCache = journeyContextCache.get(journeyTreatmentContextKey);
                if (currentCache) {
                  journeyContextCache.set(journeyTreatmentContextKey, {
                    ...currentCache,
                    fetchedAt: Date.now(),
                    fetchedNotes: { ...(currentCache.fetchedNotes || {}), ...batchNotesMap },
                  });
                }
              });
            }, 0);
          }

          return;
        }
        // Don't clear fetchedNotesRef - let fetchJournalNotes handle deduplication per session

        // Session feedback is the first-paint priority. Start phase metadata at
        // the same time, but do not make the exercise cards wait for it.
        const phaseDataPromise = fetch(
          `${apiBaseUrl}/treatment/phase/progress/user/${selectedPatient.user_id}/${diagnosisIdentifier}/?treatment_id=${treatmentId}`,
          { headers: { 'Authorization': `Token ${token}` }, method: 'GET' }
        )
          .then(async (response) => (response.ok ? response.json() : null))
          .catch(() => null);

        // Fetch all completed sessions and feedback for all phases for the selected patient and diagnosis.
        const allSessionsRes = await fetch(sessionsUrl, {
          headers: { 'Authorization': `Token ${token}` },
          method: 'GET',
        });
        if (allSessionsRes.ok) {
          const allSessionsData = await allSessionsRes.json();
          if (!isActive) return;
          const plannedSessionsPerDay = (typeof allSessionsData.planned_sessions_per_day_overall === 'number' && allSessionsData.planned_sessions_per_day_overall > 0)
            ? Number(allSessionsData.planned_sessions_per_day_overall)
            : 3;
          const mappedSessions = (allSessionsData.sessions || []).map(session => ({
            id: session.id,
            completed_date: session.completed_date,
            phase: session.phase,
            phase_index: session.phase_index,
            phase_id: session.phase_id,
            exercise_feedback: session.exercise_feedback,
            session_feedback: session.session_feedback,
          }));
          journeyContextCache.set(journeyTreatmentContextKey, {
            fetchedAt: Date.now(),
            progress: 0,
            title: '',
            plannedSessionsPerDayOverall: plannedSessionsPerDay,
            sessions: mappedSessions,
            fetchedNotes: {},
            patientJournalNotes: journeyContextCache.get(journeyTreatmentContextKey)?.patientJournalNotes || [],
          });
          setPlannedSessionsPerDayOverall(plannedSessionsPerDay);
          setCompletedSessionsList(mappedSessions);
          phaseDataPromise.then((phaseData) => {
            if (!isActive || !phaseData) return;
            setProgress(phaseData.progress || 0);
            setTitle(phaseData.name || '');
            const currentCache = journeyContextCache.get(journeyTreatmentContextKey);
            if (currentCache) {
              journeyContextCache.set(journeyTreatmentContextKey, {
                ...currentCache,
                fetchedAt: Date.now(),
                progress: phaseData.progress || 0,
                title: phaseData.name || '',
              });
            }
          });
          // Keep the first Journal paint lightweight. The latest sessions are
          // immediately useful; older notes are loaded as their rows appear.
          if (batchFetchInitiatedRef.current !== dataContextKey) {
            batchFetchInitiatedRef.current = dataContextKey;
            const initialSessions = [...mappedSessions]
              .sort((left, right) => (
                new Date(left.completed_date || 0).getTime() - new Date(right.completed_date || 0).getTime()
              ))
              .slice(-INITIAL_JOURNAL_NOTE_COUNT);
            const latestSession = initialSessions[initialSessions.length - 1];
            const historicalSessionIds = initialSessions
              .slice(0, -1)
              .map((session) => session.id)
              .filter(Boolean);
            const applyFetchedNotes = (batchNotesMap) => {
              if (!isActive || !batchNotesMap || Object.keys(batchNotesMap).length === 0) return;
              setFetchedNotes((previousNotes) => ({ ...previousNotes, ...batchNotesMap }));
              const currentCache = journeyContextCache.get(journeyTreatmentContextKey);
              if (currentCache) {
                journeyContextCache.set(journeyTreatmentContextKey, {
                  ...currentCache,
                  fetchedAt: Date.now(),
                  fetchedNotes: { ...(currentCache.fetchedNotes || {}), ...batchNotesMap },
                });
              }
            };

            if (latestSession?.id) {
              const phaseBelongsToTreatment = (phases || []).some((phase) => (
                String(phase.id) === String(latestSession.phase_id)
              ));
              if (phaseBelongsToTreatment) {
                ensureSessionExerciseDraft(latestSession).catch(() => {});
              }

              fetchAllJournalNotesBatch([latestSession.id], {
                userId: selectedPatient.user_id,
                treatmentId,
              }).then((batchNotesMap) => {
                applyFetchedNotes(batchNotesMap);
                if (!isActive || historicalSessionIds.length === 0) return;
                fetchAllJournalNotesBatch(historicalSessionIds, {
                  userId: selectedPatient.user_id,
                  treatmentId,
                }).then(applyFetchedNotes);
              });
            } else if (historicalSessionIds.length > 0) {
              fetchAllJournalNotesBatch(historicalSessionIds, {
                userId: selectedPatient.user_id,
                treatmentId,
              }).then(applyFetchedNotes);
            }
          }
        } else {
          setCompletedSessionsList([]);
          setPlannedSessionsPerDayOverall(null);
        }
      } finally {
        if (isActive) setIsJournalLoading(false);
      }
    };
    fetchAllSessionsData();
    return () => {
      isActive = false;
    };
  }, [token, memoizedContextKey]);

  const reversedSessions = useMemo(() => {
    if (completedSessionsList.length === 0 && syntheticPatientSession) {
      return [syntheticPatientSession];
    }
    const realSessions = [...completedSessionsList].reverse();
    if (persistentPatientScopeSession) {
      return [persistentPatientScopeSession, ...realSessions];
    }
    return realSessions;
  }, [completedSessionsList, persistentPatientScopeSession, syntheticPatientSession]);

  const hasExerciseTrendData = useMemo(() => (
    reversedSessions.some((session) => (
      !session?.is_patient_scope
      && Array.isArray(session?.exercise_feedback)
      && session.exercise_feedback.length > 0
    ))
  ), [reversedSessions]);

  /**
   * REGULATORY COMPLIANCE ANCHOR: CLASS I / NON-MDR SAFEGUARD
   *
   * This retained suggest_only / pending decision-support pipeline is kept as
   * an explicitly dormant branch for legacy compatibility and controlled
   * rollback review. The backend action endpoints are disabled and journey
   * mode sanitization prevents this branch from becoming active in Class I use.
   */
  const latestSuggestiveSessionId = useMemo(() => {
    if (adjustmentMode !== 'suggest_only') {
      return null;
    }
    const latestRealSession = reversedSessions.find((session) => !isPatientScopeSessionId(session?.id));
    return latestRealSession?.id || reversedSessions[0]?.id || null;
  }, [adjustmentMode, reversedSessions]);

  const getSuggestiveDecisionSupportIssues = useCallback((session, options = {}) => {
    if (adjustmentMode !== 'suggest_only' || !session || String(session.id) !== String(latestSuggestiveSessionId)) {
      return [];
    }

    const includeResolved = options.includeResolved === true;
    const decisions = suggestionDecisions[session.id] || {};

    return getConsultationSessions(session)
      .flatMap((candidateSession) => {
        const sessionCompletedAt = getSessionCompletedAt(candidateSession) || 0;
        return deriveSessionIssues(candidateSession, adjustmentLogs, availableLibraryExercises, {
          inferredDisposition: 'pending',
        })
          .filter((issue) => issue.pending && issue.replacementIdentifier)
          .map((issue) => ({
            ...issue,
            sessionId: candidateSession.id,
            sessionCompletedAt,
          }));
      })
      .filter((issue) => {
        if (includeResolved) {
          return true;
        }
        const decision = decisions[issue.issueKey] || {};
        return decision.action !== 'accept' && decision.action !== 'decline';
      })
      .sort((left, right) => (right.sessionCompletedAt || 0) - (left.sessionCompletedAt || 0));
  }, [adjustmentLogs, adjustmentMode, availableLibraryExercises, deriveSessionIssues, getConsultationSessions, getSessionCompletedAt, latestSuggestiveSessionId, suggestionDecisions]);

  const applySuggestionDecisions = useCallback(async (sessionId) => {
    const session = reversedSessions.find((item) => String(item.id) === String(sessionId));
    const issues = session ? getSuggestiveDecisionSupportIssues(session, { includeResolved: true }) : [];
    const decisions = suggestionDecisions[sessionId] || {};

    const hasSameReplacement = (decision, issue) => {
      if (!decision?.configuredExercise) {
        return false;
      }
      if (issue?.replacementIdentifier && decision.configuredExercise.identifier) {
        return String(decision.configuredExercise.identifier) === String(issue.replacementIdentifier);
      }
      return normalizeExerciseKey(decision.configuredExercise.title) === normalizeExerciseKey(issue?.replacement);
    };

    for (const issue of issues) {
      const decision = decisions[issue.issueKey];
      if (!decision || !decision.action) continue;

      if (decision.action === 'decline') {
        if (issue.adjId) {
          await fetch(`${apiBaseUrl}/treatment/exercise-adjustment/${issue.adjId}/`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Token ${token}`,
            },
            body: JSON.stringify({ action: 'reject' }),
          });
        }
        continue;
      }

      if (decision.action === 'accept' && issue.pending && issue.adjId && (!decision.configuredExercise || hasSameReplacement(decision, issue))) {
        await fetch(`${apiBaseUrl}/treatment/exercise-adjustment/${issue.adjId}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ action: 'accept' }),
        });
        continue;
      }

      if ((decision.action === 'edit' || decision.action === 'accept') && issue.pending && issue.adjId) {
        await fetch(`${apiBaseUrl}/treatment/exercise-adjustment/${issue.adjId}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${token}`,
          },
          body: JSON.stringify({ action: 'reject' }),
        });
      }
    }

    await refreshAdjustmentLogs();
  }, [reversedSessions, getSuggestiveDecisionSupportIssues, suggestionDecisions, normalizeExerciseKey, token, refreshAdjustmentLogs]);

  const adviceItemsByPhaseId = useMemo(() => {
    return allPhaseAdvices.reduce((phaseMap, phaseEntry) => {
      const phaseId = phaseEntry?.phase_id;
      if (!phaseId) {
        return phaseMap;
      }

      phaseMap[String(phaseId)] = Array.isArray(phaseEntry.advices) ? phaseEntry.advices : [];
      return phaseMap;
    }, {});
  }, [allPhaseAdvices]);

  const getJourneyAdvicesForSession = useCallback((session) => {
    const phaseId = session?.phase_id || selectedPhaseId;
    const unsortedAdvices = adviceItemsByPhaseId[String(phaseId)] || [];

    return [...unsortedAdvices].sort((firstAdvice, secondAdvice) => {
      const firstImportance = Number.isFinite(firstAdvice?.importance) ? firstAdvice.importance : Number.MAX_SAFE_INTEGER;
      const secondImportance = Number.isFinite(secondAdvice?.importance) ? secondAdvice.importance : Number.MAX_SAFE_INTEGER;
      return firstImportance - secondImportance;
    });
  }, [adviceItemsByPhaseId, selectedPhaseId]);

  const normalizeJourneyAdviceDraft = useCallback((advices = []) => {
    return [...advices]
      .sort((firstAdvice, secondAdvice) => {
        const firstImportance = Number.isFinite(firstAdvice?.importance) ? firstAdvice.importance : Number.MAX_SAFE_INTEGER;
        const secondImportance = Number.isFinite(secondAdvice?.importance) ? secondAdvice.importance : Number.MAX_SAFE_INTEGER;
        return firstImportance - secondImportance;
      })
      .map((advice, index) => ({
        ...advice,
        uniqueKey: advice?.uniqueKey || advice?.id || advice?.identifier || uuid(),
        title: advice?.title || '',
        advice: advice?.advice || '',
        importance: index + 1,
      }));
  }, []);

  const reindexJourneyAdviceDraft = useCallback((advices = []) => {
    return [...advices].map((advice, index) => ({
      ...advice,
      uniqueKey: advice?.uniqueKey || advice?.id || advice?.identifier || uuid(),
      title: advice?.title || '',
      advice: advice?.advice || '',
      importance: index + 1,
    }));
  }, []);

  const getJourneyAdviceDraftKey = useCallback((advice) => {
    return advice?.uniqueKey || advice?.id || advice?.identifier || `${advice?.title || ''}:${advice?.importance || ''}`;
  }, []);

  const ensureSessionAdviceDraft = useCallback(async (session, options = {}) => {
    const phaseId = session.phase_id || selectedPhaseId;
    if (!phaseId) {
      throw new Error('No phase available for this session');
    }

    const existingDraft = sessionAdviceDrafts[session.id];
    if (existingDraft && existingDraft.phaseId === phaseId && !options.forceReload) {
      return existingDraft;
    }

    const draftAdvices = normalizeJourneyAdviceDraft(getJourneyAdvicesForSession(session));
    const nextDraft = {
      phaseId,
      phaseName: session.phase || phases.find((phase) => String(phase.id) === String(phaseId))?.name || 'Selected phase',
      originalAdvices: draftAdvices,
      advices: draftAdvices,
      dirty: false,
      version: (existingDraft?.version || 0) + 1,
    };

    setSessionAdviceDrafts((prev) => ({
      ...prev,
      [session.id]: nextDraft,
    }));

    return nextDraft;
  }, [selectedPhaseId, sessionAdviceDrafts, normalizeJourneyAdviceDraft, getJourneyAdvicesForSession, phases]);

  const toggleJourneyAdviceVisibility = useCallback((sessionId) => {
    setJourneyAdviceVisibility((previousVisibility) => ({
      ...previousVisibility,
      [sessionId]: !previousVisibility[sessionId],
    }));
  }, []);

  // When a journal note enters edit mode from the generic note controls,
  // ensure the advice draft exists so inline advice actions render without
  // requiring the dedicated advice drawer to be opened first.
  useEffect(() => {
    if (!completedSessionsList.length) return;
    completedSessionsList.forEach((session) => {
      if (!editingNote[session.id]) return;
      if (sessionAdviceDrafts[session.id]) return;
      ensureSessionAdviceDraft(session).catch(() => {});
    });
  }, [completedSessionsList, editingNote, sessionAdviceDrafts, ensureSessionAdviceDraft]);

  const openSessionAdviceEditor = useCallback(async (session) => {
    setEditingNote(prev => ({ ...prev, [session.id]: true }));
    setEditNoteValue(prev => {
      if (Object.prototype.hasOwnProperty.call(prev, session.id)) {
        return prev;
      }
      const notes = fetchedNotes[session.id] || [];
      const noteIndex = noteIndices[session.id] || 0;
      const currentNote = notes[noteIndex] || {};
      return { ...prev, [session.id]: currentNote.note || '' };
    });
    setAdviceEditorLoading(true);
    try {
      const draft = await ensureSessionAdviceDraft(session);
      setAdviceEditorContext({
        sessionId: session.id,
        phaseId: draft.phaseId,
        phaseName: draft.phaseName,
      });
      setAdviceEditorOpen(true);
    } catch (error) {
      alert(error.message || 'Failed to open advice editor');
    } finally {
      setAdviceEditorLoading(false);
    }
  }, [ensureSessionAdviceDraft, fetchedNotes, noteIndices]);

  const openAdviceLibraryDrawer = useCallback((context = {}) => {
    setAdviceLibraryContext(context);
    setAdviceLibraryDrawerOpen(true);
  }, []);

  const addJourneyDraftAdvice = useCallback((sessionId, advice) => {
    setSessionAdviceDrafts((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }

      const normalizedTitle = String(advice?.title || '').trim().toLowerCase();
      const duplicateExists = currentDraft.advices.some((existingAdvice) => (
        (advice?.id && existingAdvice.id === advice.id) ||
        (advice?.identifier && existingAdvice.identifier === advice.identifier) ||
        (normalizedTitle && String(existingAdvice.title || '').trim().toLowerCase() === normalizedTitle)
      ));
      if (duplicateExists) {
        return prev;
      }

      const nextAdvices = normalizeJourneyAdviceDraft([
        ...currentDraft.advices,
        {
          ...advice,
          uniqueKey: advice?.uniqueKey || advice?.id || advice?.identifier || uuid(),
          title: advice?.title || '',
          advice: advice?.advice || '',
          importance: currentDraft.advices.length + 1,
        },
      ]);

      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          advices: nextAdvices,
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, [normalizeJourneyAdviceDraft]);

  const replaceJourneyDraftAdvice = useCallback((sessionId, targetAdvice, replacementAdvice) => {
    setSessionAdviceDrafts((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }

      const targetKey = getJourneyAdviceDraftKey(targetAdvice);
      const normalizedReplacementTitle = String(replacementAdvice?.title || '').trim().toLowerCase();
      const duplicateExists = currentDraft.advices.some((existingAdvice) => (
        getJourneyAdviceDraftKey(existingAdvice) !== targetKey && (
          (replacementAdvice?.id && existingAdvice.id === replacementAdvice.id) ||
          (replacementAdvice?.identifier && existingAdvice.identifier === replacementAdvice.identifier) ||
          (normalizedReplacementTitle && String(existingAdvice.title || '').trim().toLowerCase() === normalizedReplacementTitle)
        )
      ));
      if (duplicateExists) {
        return prev;
      }

      const nextAdvices = normalizeJourneyAdviceDraft(
        currentDraft.advices.map((existingAdvice) => (
          getJourneyAdviceDraftKey(existingAdvice) === targetKey
            ? {
                ...replacementAdvice,
                uniqueKey: targetAdvice?.uniqueKey || targetKey,
                title: replacementAdvice?.title || '',
                advice: replacementAdvice?.advice || '',
                importance: existingAdvice.importance || targetAdvice?.importance || 1,
                __journeyReplaceFromTitle: String(targetAdvice?.title || '').trim(),
                __journeyReplaceFromKey: targetKey,
              }
            : existingAdvice
        ))
      );

      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          advices: nextAdvices,
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, [getJourneyAdviceDraftKey, normalizeJourneyAdviceDraft]);

  const removeJourneyDraftAdvice = useCallback((sessionId, advice) => {
    setSessionAdviceDrafts((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }

      const targetKey = getJourneyAdviceDraftKey(advice);
      const nextAdvices = normalizeJourneyAdviceDraft(
        currentDraft.advices.filter((entry) => getJourneyAdviceDraftKey(entry) !== targetKey)
      );

      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          advices: nextAdvices,
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, [getJourneyAdviceDraftKey, normalizeJourneyAdviceDraft]);

  const reorderJourneyDraftAdvice = useCallback((sessionId, fromIndex, toIndex) => {
    setSessionAdviceDrafts((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) return prev;
      const list = currentDraft.advices
        .slice()
        .sort((firstAdvice, secondAdvice) => (firstAdvice.importance ?? 0) - (secondAdvice.importance ?? 0));
      const length = list.length;
      if (length < 2) return prev;
      const from = Math.max(0, Math.min(length - 1, fromIndex));
      const to = Math.max(0, Math.min(length - 1, toIndex));
      if (from === to) return prev;
      const next = list.slice();
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          advices: reindexJourneyAdviceDraft(next),
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });
  }, [reindexJourneyAdviceDraft]);

  const openDraftAdviceEditDialog = useCallback((sessionId, advice) => {
    setDraftAdviceEditContext({ sessionId, adviceKey: getJourneyAdviceDraftKey(advice) });
    setDraftEditedAdviceParams({
      title: advice?.title || '',
      advice: advice?.advice || '',
      importance: advice?.importance || 1,
    });
    setDraftAdviceDialogOpen(true);
  }, [getJourneyAdviceDraftKey]);

  const openCustomDraftAdviceDialog = useCallback((sessionId) => {
    const adviceKey = uuid();
    const customAdvice = {
      uniqueKey: adviceKey,
      title: '',
      advice: '',
      importance: (sessionAdviceDrafts[sessionId]?.advices || []).length + 1,
    };

    setSessionAdviceDrafts((prev) => {
      const currentDraft = prev[sessionId];
      if (!currentDraft) {
        return prev;
      }
      const nextAdvices = normalizeJourneyAdviceDraft([
        ...currentDraft.advices,
        customAdvice,
      ]);
      return {
        ...prev,
        [sessionId]: {
          ...currentDraft,
          advices: nextAdvices,
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });

    setDraftAdviceEditContext({ sessionId, adviceKey, isNew: true, previousDirty: Boolean(sessionAdviceDrafts[sessionId]?.dirty) });
    setDraftEditedAdviceParams({
      title: '',
      advice: '',
      importance: customAdvice.importance,
    });
    setDraftAdviceDialogOpen(true);
  }, [normalizeJourneyAdviceDraft, sessionAdviceDrafts]);

  const closeDraftAdviceDialog = useCallback(() => {
    if (draftAdviceEditContext?.isNew) {
      setSessionAdviceDrafts((prev) => {
        const currentDraft = prev[draftAdviceEditContext.sessionId];
        if (!currentDraft) {
          return prev;
        }
        const nextAdvices = normalizeJourneyAdviceDraft(
          currentDraft.advices.filter((advice) => getJourneyAdviceDraftKey(advice) !== draftAdviceEditContext.adviceKey)
        );
        return {
          ...prev,
          [draftAdviceEditContext.sessionId]: {
            ...currentDraft,
            advices: nextAdvices,
            dirty: Boolean(draftAdviceEditContext.previousDirty),
            version: (currentDraft.version || 0) + 1,
          },
        };
      });
    }
    setDraftAdviceDialogOpen(false);
    setDraftAdviceEditContext(null);
  }, [draftAdviceEditContext, getJourneyAdviceDraftKey, normalizeJourneyAdviceDraft]);

  const handleDraftAdviceParamChange = useCallback((field, value) => {
    setDraftEditedAdviceParams((prev) => ({
      ...prev,
      [field]: field === 'importance' ? (parseInt(value, 10) || 1) : value,
    }));
  }, []);

  const handleConfirmDraftAdviceEdit = useCallback(() => {
    if (!draftAdviceEditContext) {
      return;
    }

    setSessionAdviceDrafts((prev) => {
      const currentDraft = prev[draftAdviceEditContext.sessionId];
      if (!currentDraft) {
        return prev;
      }

      const nextAdvices = normalizeJourneyAdviceDraft(
        currentDraft.advices.map((advice) => (
          getJourneyAdviceDraftKey(advice) === draftAdviceEditContext.adviceKey
            ? { ...advice, ...draftEditedAdviceParams }
            : advice
        ))
      );

      return {
        ...prev,
        [draftAdviceEditContext.sessionId]: {
          ...currentDraft,
          advices: nextAdvices,
          dirty: true,
          version: (currentDraft.version || 0) + 1,
        },
      };
    });

    setDraftAdviceDialogOpen(false);
    setDraftAdviceEditContext(null);
  }, [draftAdviceEditContext, draftEditedAdviceParams, getJourneyAdviceDraftKey, normalizeJourneyAdviceDraft]);

  const editAdviceAdjustmentLine = useCallback(async (session, lineText) => {
    const text = String(lineText || '');
    const match = text.match(/\[([^\]]+)\]/);
    const title = match ? match[1].trim() : '';
    let draft = sessionAdviceDrafts[session.id];
    if (!draft) {
      try {
        draft = await ensureSessionAdviceDraft(session);
      } catch (error) {
        await openSessionAdviceEditor(session);
        return;
      }
    }

    const normalizedTitle = String(title || '').trim().toLowerCase();
    const originalAdvice = (draft.originalAdvices || []).find((advice) => String(advice?.title || '').trim().toLowerCase() === normalizedTitle);
    const originalKey = originalAdvice ? getJourneyAdviceDraftKey(originalAdvice) : null;
    const targetAdvice = (draft.advices || []).find((advice) => {
      if (originalKey && getJourneyAdviceDraftKey(advice) === originalKey) {
        return true;
      }
      return String(advice?.title || '').trim().toLowerCase() === normalizedTitle;
    });

    if (targetAdvice) {
      openDraftAdviceEditDialog(session.id, targetAdvice);
      return;
    }

    await openSessionAdviceEditor(session);
  }, [sessionAdviceDrafts, ensureSessionAdviceDraft, openSessionAdviceEditor, getJourneyAdviceDraftKey, openDraftAdviceEditDialog]);

  const removeAdviceAdjustmentLine = useCallback(async (sessionId, lineText, options = {}) => {
    const session = options.session || completedSessionsList.find((item) => String(item.id) === String(sessionId)) || null;
    let draft = sessionAdviceDrafts[sessionId];
    const replacedMatch = lineText.match(/^Replaced \[([^\]]+)\] with \[([^\]]+)\]\.?/i);
    const removedMatch = lineText.match(/^Removed advice \[([^\]]+)\] from the advice plan/i);
    const updatedMatch = lineText.match(/^Updated advice \[([^\]]+)\]:/i);
    const addedMatch = lineText.match(/^Added advice \[([^\]]+)\] to the advice plan/i);
    if (!draft && session) {
      try {
        draft = await ensureSessionAdviceDraft(session);
      } catch (error) {
        draft = null;
      }
    }

    let restoreAdviceTemplate = null;
    if (draft && Array.isArray(draft.originalAdvices)) {
      const lc = (value) => String(value || '').trim().toLowerCase();
      const needsReplacedRestore = replacedMatch && !draft.originalAdvices.some((advice) => lc(advice?.title) === lc(replacedMatch[1]));
      const needsRemovedRestore = removedMatch && !draft.originalAdvices.some((advice) => lc(advice?.title) === lc(removedMatch[1]));
      const needsUpdatedRestore = updatedMatch && !draft.originalAdvices.some((advice) => lc(advice?.title) === lc(updatedMatch[1]));
      if (needsReplacedRestore) {
        restoreAdviceTemplate = await findAdviceLibraryRestoreItem(replacedMatch[1].trim());
      } else if (needsRemovedRestore) {
        restoreAdviceTemplate = await findAdviceLibraryRestoreItem(removedMatch[1].trim());
      } else if (needsUpdatedRestore) {
        restoreAdviceTemplate = await findAdviceLibraryRestoreItem(updatedMatch[1].trim());
      }
    }

    if (draft && Array.isArray(draft.originalAdvices) && Array.isArray(draft.advices)) {
      const titleOf = (advice) => String(advice?.title || '').trim();
      const lc = (value) => String(value || '').trim().toLowerCase();

      setSessionAdviceDrafts((prev) => {
        const currentDraft = prev[sessionId];
        if (!currentDraft || !Array.isArray(currentDraft.originalAdvices) || !Array.isArray(currentDraft.advices)) {
          return prev;
        }

        const originalAdvices = currentDraft.originalAdvices;
        let nextAdvices = [...currentDraft.advices];
        let mutated = false;

        if (replacedMatch) {
          const replacedTitle = replacedMatch[1].trim();
          const replacementTitle = replacedMatch[2].trim();
          const originalAdvice = originalAdvices.find((advice) => lc(titleOf(advice)) === lc(replacedTitle));
          const originalKey = originalAdvice ? getJourneyAdviceDraftKey(originalAdvice) : null;
          const restoredAdvice = originalAdvice || (restoreAdviceTemplate
            ? {
                ...restoreAdviceTemplate,
                uniqueKey: originalKey || restoreAdviceTemplate?.uniqueKey || restoreAdviceTemplate?.id || restoreAdviceTemplate?.identifier || uuid(),
                title: restoreAdviceTemplate?.title || replacedTitle,
                advice: restoreAdviceTemplate?.advice || '',
              }
            : null);
          const targetIndex = nextAdvices.findIndex((advice) => {
            if (originalKey && getJourneyAdviceDraftKey(advice) === originalKey) {
              return true;
            }
            return lc(titleOf(advice)) === lc(replacementTitle);
          });
          if (restoredAdvice && targetIndex >= 0) {
            nextAdvices[targetIndex] = restoredAdvice;
            nextAdvices = normalizeJourneyAdviceDraft(nextAdvices);
            mutated = true;
          }
        } else if (removedMatch) {
          const removedTitle = removedMatch[1].trim();
          const originalAdvice = originalAdvices.find((advice) => lc(titleOf(advice)) === lc(removedTitle));
          const originalIndex = originalAdvices.findIndex((advice) => lc(titleOf(advice)) === lc(removedTitle));
          const restoredAdvice = originalAdvice || (restoreAdviceTemplate
            ? {
                ...restoreAdviceTemplate,
                uniqueKey: restoreAdviceTemplate?.uniqueKey || restoreAdviceTemplate?.id || restoreAdviceTemplate?.identifier || uuid(),
                title: restoreAdviceTemplate?.title || removedTitle,
                advice: restoreAdviceTemplate?.advice || '',
              }
            : null);
          if (restoredAdvice && !nextAdvices.some((advice) => lc(titleOf(advice)) === lc(removedTitle))) {
            const desiredIndex = originalIndex >= 0
              ? originalIndex
              : (Number.isFinite(restoredAdvice?.importance)
                ? Math.max(0, Math.min(nextAdvices.length, restoredAdvice.importance - 1))
                : nextAdvices.length);
            const restoredAdvices = nextAdvices.slice();
            restoredAdvices.splice(desiredIndex, 0, restoredAdvice);
            nextAdvices = reindexJourneyAdviceDraft(restoredAdvices);
            mutated = true;
            if (!originalAdvice) {
              const persistBaseline = currentDraft.persistBaseline || originalAdvices;
              const newOriginalAdvicesList = originalAdvices.slice();
              newOriginalAdvicesList.splice(desiredIndex, 0, restoredAdvice);
              const newOriginalAdvices = reindexJourneyAdviceDraft(newOriginalAdvicesList);
              return {
                ...prev,
                [sessionId]: {
                  ...currentDraft,
                  advices: nextAdvices,
                  originalAdvices: newOriginalAdvices,
                  persistBaseline,
                  dirty: true,
                  version: (currentDraft.version || 0) + 1,
                },
              };
            }
          }
        } else if (addedMatch) {
          const addedTitle = addedMatch[1].trim();
          const filteredAdvices = nextAdvices.filter((advice) => lc(titleOf(advice)) !== lc(addedTitle));
          if (filteredAdvices.length !== nextAdvices.length) {
            nextAdvices = normalizeJourneyAdviceDraft(filteredAdvices);
            mutated = true;
          }
        } else if (updatedMatch) {
          const updatedTitle = updatedMatch[1].trim();
          const originalAdvice = originalAdvices.find((advice) => lc(titleOf(advice)) === lc(updatedTitle));
          const restoredAdvice = originalAdvice || (restoreAdviceTemplate
            ? {
                ...restoreAdviceTemplate,
                uniqueKey: restoreAdviceTemplate?.uniqueKey || restoreAdviceTemplate?.id || restoreAdviceTemplate?.identifier || uuid(),
                title: restoreAdviceTemplate?.title || updatedTitle,
                advice: restoreAdviceTemplate?.advice || '',
              }
            : null);
          const originalKey = restoredAdvice ? getJourneyAdviceDraftKey(restoredAdvice) : null;
          const targetIndex = nextAdvices.findIndex((advice) => {
            if (originalKey && getJourneyAdviceDraftKey(advice) === originalKey) {
              return true;
            }
            return lc(titleOf(advice)) === lc(updatedTitle);
          });
          if (restoredAdvice && targetIndex >= 0) {
            nextAdvices[targetIndex] = restoredAdvice;
            nextAdvices = normalizeJourneyAdviceDraft(nextAdvices);
            mutated = true;
          }
        }

        if (!mutated) {
          return prev;
        }

        return {
          ...prev,
          [sessionId]: {
            ...currentDraft,
            advices: nextAdvices,
            dirty: true,
            version: (currentDraft.version || 0) + 1,
          },
        };
      });
    }

    setEditNoteValue((prev) => {
      const hasDraft = Object.prototype.hasOwnProperty.call(prev, sessionId);
      let currentDraft = '';
      if (hasDraft) {
        currentDraft = prev[sessionId] || '';
      } else {
        const sessionNotes = fetchedNotes[sessionId] || [];
        const noteIdx = noteIndices[sessionId] || 0;
        currentDraft = sessionNotes[noteIdx]?.note || '';
      }
      const stripped = removeLineFromAdjustmentsSection(currentDraft, lineText);
      if (stripped === currentDraft && hasDraft) {
        return prev;
      }
      return { ...prev, [sessionId]: stripped };
    });

    const normalizedKey = (lineText || '').toString().replace(/\s+/g, ' ').trim().toLowerCase();
    if (normalizedKey) {
      setRemovedHistoricalLines((prev) => {
        const existing = prev[sessionId] || new Set();
        if (existing.has(normalizedKey)) {
          return prev;
        }
        const next = new Set(existing);
        next.add(normalizedKey);
        return { ...prev, [sessionId]: next };
      });
    }
  }, [completedSessionsList, sessionAdviceDrafts, ensureSessionAdviceDraft, normalizeJourneyAdviceDraft, reindexJourneyAdviceDraft, getJourneyAdviceDraftKey, fetchedNotes, noteIndices, findAdviceLibraryRestoreItem]);

  const persistSessionAdviceDraft = useCallback(async (sessionId) => {
    const draft = sessionAdviceDrafts[sessionId];
    if (!draft?.dirty) {
      return draft;
    }
    if (typeof onSaveJourneyAdvices !== 'function') {
      throw new Error('Advice saving is unavailable from the journal');
    }

    setAdviceEditorSaving(true);
    try {
      const persistableAdvices = (draft.advices || []).map((advice) => {
        const { __journeyReplaceFromTitle, __journeyReplaceFromKey, ...persistableAdvice } = advice || {};
        return persistableAdvice;
      });
      const savedAdvices = await onSaveJourneyAdvices({
        phaseId: draft.phaseId,
        advices: persistableAdvices,
        phaseName: draft.phaseName,
        successMessage: 'Journey advice changes saved!',
      });
      const normalizedSavedAdvices = normalizeJourneyAdviceDraft(savedAdvices || persistableAdvices);
      const nextDraft = {
        ...draft,
        originalAdvices: normalizedSavedAdvices,
        advices: normalizedSavedAdvices,
        dirty: false,
        version: (draft.version || 0) + 1,
      };
      setSessionAdviceDrafts((prev) => ({
        ...prev,
        [sessionId]: nextDraft,
      }));
      return nextDraft;
    } finally {
      setAdviceEditorSaving(false);
    }
  }, [sessionAdviceDrafts, onSaveJourneyAdvices, normalizeJourneyAdviceDraft]);

  useEffect(() => {
    if (!reversedSessions.length) {
      sessionRowCacheRef.current = {};
    }
    setVisibleSessionCount(INITIAL_VISIBLE_JOURNAL_SESSION_COUNT);
  }, [reversedSessions.length]);

  // IntersectionObserver: load the next session page and its notes on demand.
  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const nextVisibleCount = Math.min(
            visibleSessionCount + INITIAL_JOURNAL_NOTE_COUNT,
            reversedSessions.length
          );
          const sessionIds = reversedSessions
            .slice(visibleSessionCount, nextVisibleCount)
            .map((session) => session?.id)
            .filter((sessionId) => sessionId && !Object.prototype.hasOwnProperty.call(fetchedNotes, sessionId));

          setVisibleSessionCount(nextVisibleCount);

          if (!sessionIds.length || !selectedPatient?.user_id || !resolvedJourneyTreatmentId) {
            return;
          }

          fetchAllJournalNotesBatch(sessionIds, {
            userId: selectedPatient.user_id,
            treatmentId: resolvedJourneyTreatmentId,
          }).then((batchNotesMap) => {
            if (!batchNotesMap || Object.keys(batchNotesMap).length === 0) return;
            setFetchedNotes((previousNotes) => ({ ...previousNotes, ...batchNotesMap }));
            const cachedContext = journeyContextCache.get(journeyTreatmentContextKey);
            if (cachedContext) {
              journeyContextCache.set(journeyTreatmentContextKey, {
                ...cachedContext,
                fetchedAt: Date.now(),
                fetchedNotes: { ...(cachedContext.fetchedNotes || {}), ...batchNotesMap },
              });
            }
          });
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  // The local batch helper is recreated by the legacy component on each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSessionCount, reversedSessions, fetchedNotes, selectedPatient?.user_id, resolvedJourneyTreatmentId, journeyTreatmentContextKey]);

  const formatSessionPhaseForClipboard = (session) => {
    const rawPhase = (session?.phase || '').toString().trim();
    if (!rawPhase) {
      return '';
    }

    const normalizedPhase = rawPhase.replace(/\s+/g, ' ').trim();
    if (/^Phase\s+\d+\s*:/i.test(normalizedPhase)) {
      return /[.!?]$/.test(normalizedPhase) ? normalizedPhase : `${normalizedPhase}.`;
    }

    const parsedPhaseIndex = Number(session?.phase_index);
    const phasePrefix = Number.isFinite(parsedPhaseIndex)
      ? `Phase ${parsedPhaseIndex + 1}`
      : 'Phase';
    return `${phasePrefix}: '${normalizedPhase}'.`;
  };

  const buildClipboardProgramExercises = (session) => {
    const normalizeExerciseMatchValue = (value) => (value || '').toString().trim().toLowerCase();
    const feedbackList = Array.isArray(session?.exercise_feedback) ? session.exercise_feedback : [];
    const draftExercises = (sessionExerciseDrafts[session?.id]?.exercises || [])
      .slice()
      .sort((left, right) => (left?.index ?? 0) - (right?.index ?? 0));

    if (draftExercises.length > 0) {
      return draftExercises.map((exercise) => {
        const normalizedTitle = normalizeExerciseMatchValue(exercise?.title || exercise?.identifier);
        const matchingFeedback = feedbackList.find((feedback) => (
          exercise?.identifier && String(feedback?.exercise_id || '') === String(exercise.identifier)
        ) || (
          normalizedTitle && normalizeExerciseMatchValue(feedback?.exercise_title) === normalizedTitle
        ));

        return {
          title: (exercise?.title || exercise?.identifier || 'Exercise').toString().trim(),
          sets: exercise?.sets,
          repetitions: exercise?.repetitions,
          time: exercise?.time,
          pause: exercise?.pause,
          doseType: exercise?.doseType,
          frequencyType: exercise?.frequencyType,
          frequency: exercise?.frequency,
          daysInbetween: exercise?.daysInbetween ?? exercise?.daysInBetween,
          painLevel: matchingFeedback?.painLevel,
          difficulty: matchingFeedback?.difficulty,
        };
      });
    }

    return feedbackList.map((feedback) => ({
      title: (feedback?.exercise_title || feedback?.exercise_id || 'Exercise').toString().trim(),
      sets: feedback?.prescribed_sets,
      repetitions: feedback?.prescribed_repetitions,
      time: feedback?.prescribed_time,
      pause: feedback?.prescribed_pause,
      doseType: ((feedback?.prescribed_repetitions ?? 0) > 0 && (feedback?.prescribed_time ?? 0) === 0) ? 'repetitions' : 'time',
      frequencyType: feedback?.prescribed_frequency_type,
      frequency: feedback?.prescribed_frequency,
      daysInbetween: feedback?.prescribed_days_inbetween,
      painLevel: feedback?.painLevel,
      difficulty: feedback?.difficulty,
    }));
  };

  const buildCurrentExerciseProgramSectionText = (session) => {
    const phaseLine = formatSessionPhaseForClipboard(session);
    const programExercises = buildClipboardProgramExercises(session);
    const lines = [];

    if (phaseLine) {
      lines.push(phaseLine);
    }

    if (phaseLine && programExercises.length > 0) {
      lines.push('');
    }

    programExercises.forEach((exercise, index) => {
      const title = (exercise?.title || 'Exercise').toString().trim();

      lines.push(title);
      lines.push(formatSharedExercisePrescriptionSummary(exercise));
      lines.push(`- Pain: ${exercise?.painLevel ?? '-'} | Difficulty: ${exercise?.difficulty != null ? getDifficultyDisplayLabel(exercise.difficulty) : '-'}`);

      if (index < programExercises.length - 1) {
        lines.push('');
      }
    });

    return normalizeClipboardText(lines.join('\n'));
  };

  const formatSessionForClipboard = (session, journalNote) => {
    const sections = [];
    const formatClipboardSectionHeading = (heading) => `** ${heading} **`;
    const pushSection = (heading, bodyText) => {
      const normalizedBody = normalizeClipboardText(bodyText);
      if (!heading || !normalizedBody) {
        return;
      }
      sections.push({ heading, body: normalizedBody });
    };

    if (journalNote && !isHtmlEmpty(journalNote)) {
      pushSection(JOURNAL_SYMPTOM_HEADING_TEXT, extractJournalSectionText(journalNote, JOURNAL_SYMPTOM_HEADING_TEXT));
      pushSection(JOURNAL_ADJUSTMENTS_HEADING_TEXT, extractJournalSectionText(journalNote, JOURNAL_ADJUSTMENTS_HEADING_TEXT));
      pushSection(JOURNAL_PLAN_HEADING_TEXT, extractJournalSectionText(journalNote, JOURNAL_PLAN_HEADING_TEXT));
      pushSection(JOURNAL_PATIENT_ACCEPT_HEADING_TEXT, extractJournalSectionText(journalNote, JOURNAL_PATIENT_ACCEPT_HEADING_TEXT));

      if (sections.length === 0) {
        pushSection('Journal note:', htmlToClipboardText(journalNote));
      }
    }

    pushSection(JOURNAL_CURRENT_PROGRAM_HEADING_TEXT, buildCurrentExerciseProgramSectionText(session));

    const text = sections
      .map((section) => `${formatClipboardSectionHeading(section.heading)}\n${section.body}`)
      .join('\n\n');
    const html = sections
      .map((section) => `<p><strong>${escapeHtml(formatClipboardSectionHeading(section.heading))}</strong></p><p>${escapeHtml(section.body).replace(/\n/g, '<br>')}</p>`)
      .join('');

    return { text, html };
  };

  const handleCopySessionText = (session, journalNote) => {
    const { text, html } = formatSessionForClipboard(session, journalNote);
    const markCopied = () => {
      setCopiedSessions(prev => ({ ...prev, [session.id]: true }));
      setTimeout(() => setCopiedSessions(prev => ({ ...prev, [session.id]: false })), 2500);
    };
    const fallbackCopyText = () => {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(markCopied).catch(markCopied);
        return;
      }
      // fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      markCopied();
    };

    if (navigator.clipboard?.write && typeof window !== 'undefined' && window.ClipboardItem && html) {
      const clipboardItem = new window.ClipboardItem({
        'text/plain': new Blob([text], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      });
      navigator.clipboard.write([clipboardItem]).then(markCopied).catch(fallbackCopyText);
    } else {
      fallbackCopyText();
    }
  };

  const activeProgramDraft = programEditorContext
    ? sessionExerciseDrafts[programEditorContext.sessionId] || null
    : null;
  const activeAdviceDraft = adviceEditorContext
    ? sessionAdviceDrafts[adviceEditorContext.sessionId] || null
    : null;
  const activeDraftExercise = draftExerciseEditContext
    ? sessionExerciseDrafts[draftExerciseEditContext.sessionId]?.exercises.find(exercise => getDraftExerciseKey(exercise) === draftExerciseEditContext.exerciseKey) || null
    : null;
  const activeDraftAdvice = draftAdviceEditContext
    ? (sessionAdviceDrafts[draftAdviceEditContext.sessionId]?.advices || []).find((advice) => getJourneyAdviceDraftKey(advice) === draftAdviceEditContext.adviceKey) || null
    : null;
  const allowDraftExercisePhoneUpload = Boolean(
    !activeDraftExercise?.id && (activeDraftExercise?.isNewCustomExercise || draftExerciseEditContext?.isNewCustomExercise)
  );
  const libraryDrawerTitle = editingSuggestionContext?.type === 'program-add'
    ? 'Add exercise to journal draft'
    : editingSuggestionContext?.type === 'program-replace'
      ? 'Replace exercise in journal draft'
      : 'Select replacement exercise';

  return (
    <>
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert elevation={6} variant="filled" severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMsg}
        </MuiAlert>
      </Snackbar>
    <Card
      className={clsx(classes.root, className)}
      {...rest}
    >
        {isJournalLoading && (
          <Box className={classes.loadingOverlay}>
            <CircularProgress size={44} thickness={4.8} />
          </Box>
        )}
        {/* Table for completed sessions, exercises, and feedback */}
        <Table style={{ marginBottom: 0, borderCollapse: 'collapse', width: '100%', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,45,85,0.08)' }}>
          <TableHead >
            <TableRow className={classes.sectionHeaderRow}>
              <TableCell align="left" colSpan={responsiveColSpan(7)} className={classes.sectionHeaderCell} style={{ borderRadius: 0, width: '61.33%' }}>
                <span className={classes.sectionHeaderTitle}>Journal Note</span>
              </TableCell>
              <TableCell align="left" colSpan={responsiveColSpan(5)} className={classes.sectionHeaderCell} style={{ width: '38.67%' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)' }}>
                  <span className={classes.sectionHeaderTitle}>Exercises & Feedback</span>
                  <Tooltip title={hasExerciseTrendData ? 'View exercise trend graph' : 'No completed session feedback yet'} arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setExerciseTrendDialogOpen(true)}
                        disabled={!hasExerciseTrendData}
                        style={{
                          padding: 'clamp(1.5px, 0.22vw, 3px)',
                          color: hasExerciseTrendData ? '#002D55' : '#9aa8b5',
                          backgroundColor: '#ffffff',
                          border: '1px solid rgba(0,45,85,0.12)',
                          borderRadius: 10,
                        }}
                      >
                        <BarChartIcon style={{ fontSize: 'clamp(14px, 1.38vw, 18px)' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody >
            {reversedSessions.length > 0 ? (
              reversedSessions.slice(0, visibleSessionCount).map((session, idx) => {
                const notes = fetchedNotes[session.id] || [];
                const noteIndex = noteIndices[session.id] || 0;
                const currentNote = notes[noteIndex] || {};
                const journalNote = currentNote.note || '';
                const sessionKey = session.id || idx;
                const sessionIsEditing = editingNote[session.id] || false;
                const sessionShowsHistoricalVersion = !sessionIsEditing && noteIndex > 0;
                const sessionEditValue = editNoteValue[session.id] || "";
                const sessionIsSaving = savingNotes[session.id] || false;
                const sessionIsCopied = copiedSessions[session.id] || false;
                const sessionSelectedAuthor = selectedAuthors[session.id] || '';
                const sessionNewNoteAuthor = newNoteAuthors[session.id] || '';
                const sessionDraftVersion = sessionExerciseDrafts[session.id]?.version || 0;
                const sessionPrescriptionSignature = idx === 0 && Array.isArray(exercises)
                  ? exercises.map((exercise) => `${exercise.id || exercise.identifier || exercise.title || ''}:${exercise.index || ''}`).join('|')
                  : '';
                const sessionAdviceDraftVersion = sessionAdviceDrafts[session.id]?.version || 0;
                const sessionIssueSignature = (generatedIssuesBySession[session.id] || []).map(issue => `${issue.issueKey}:${issue.replacementIdentifier || ''}:${issue.adjusted ? 'a' : ''}${issue.pending ? 'p' : ''}`).join('|');
                const sessionProgramSummarySignature = (programChangeSummaries[session.id]?.journalLines || []).join('|');
                const sessionAdviceSummarySignature = getAdviceSummaryJournalLinesForSession(session.id).join('|');
                const sessionConsultationAdjustmentSignature = buildSessionProgramAdjustmentLines(session).join('|');
                const sessionDirectAdjustmentSignature = getSessionJournalAdjustmentLines(session).join('|');
                const sessionBaselineGoalSignature = (baselineGoalStaged[session.id] || []).map(it => `${it.id}:${it.action || ''}:${it.lineText || ''}:${it.suppressAnnotation ? 'hidden' : 'shown'}`).join('|');
                const sessionAdviceMode = journeyAdviceVisibility[session.id] ? 'advices' : 'exercises';
                const sessionBulkEditSignature = sessionIsEditing && sessionExerciseDrafts[session.id]
                  ? `${journeyBulkEditParam}:${String(journeyBulkEditValue)}`
                  : '';
                const renderedSessionAdvices = sessionAdviceDrafts[session.id]?.advices || getJourneyAdvicesForSession(session);
                const sessionAdviceSignature = renderedSessionAdvices
                  .map((advice) => `${advice.id || advice.identifier || advice.title || ''}:${advice.importance || ''}:${advice.title || ''}:${advice.advice || ''}:${advice.__journeyReplaceFromTitle || ''}:${advice.__journeyReplaceFromKey || ''}`)
                  .join('|');
                const sessionSuggestionSignature = getSuggestiveDecisionSupportIssues(session)
                  .map((issue) => {
                    const decision = (suggestionDecisions[session.id] || {})[issue.issueKey] || {};
                    return `${issue.sessionId}:${issue.issueKey}:${decision.action || ''}:${decision.configuredExercise?.identifier || decision.configuredExercise?.title || ''}`;
                  })
                  .join('|');
                const sessionInlineProgramDragSignature = inlineProgramDragSessionId === session.id
                  ? `${inlineProgramDragIndex ?? ''}:${inlineProgramDragOverIndex ?? ''}`
                  : '';
                const sessionInlineAdviceDragSignature = inlineAdviceDragSessionId === session.id
                  ? `${inlineAdviceDragIndex ?? ''}:${inlineAdviceDragOverIndex ?? ''}`
                  : '';
                const sessionExpandedAdviceSignature = Object.keys(expandedJourneyAdviceCards)
                  .filter((key) => key.startsWith(`${session.id}::`) && expandedJourneyAdviceCards[key])
                  .sort()
                  .join('|');

                const cachedRow = sessionRowCacheRef.current[sessionKey];
                if (
                  cachedRow &&
                  cachedRow.sessionRef === session &&
                  cachedRow.notesRef === notes &&
                  cachedRow.noteIndex === noteIndex &&
                  cachedRow.isEditing === sessionIsEditing &&
                  cachedRow.editValue === sessionEditValue &&
                  cachedRow.isSaving === sessionIsSaving &&
                  cachedRow.isCopied === sessionIsCopied &&
                  cachedRow.selectedAuthor === sessionSelectedAuthor &&
                  cachedRow.newNoteAuthor === sessionNewNoteAuthor &&
                  cachedRow.draftVersion === sessionDraftVersion &&
                  cachedRow.prescriptionSignature === sessionPrescriptionSignature &&
                  cachedRow.adviceDraftVersion === sessionAdviceDraftVersion &&
                  cachedRow.issueSignature === sessionIssueSignature &&
                  cachedRow.programSummarySignature === sessionProgramSummarySignature &&
                  cachedRow.adviceSummarySignature === sessionAdviceSummarySignature &&
                  cachedRow.consultationAdjustmentSignature === sessionConsultationAdjustmentSignature &&
                  cachedRow.directAdjustmentSignature === sessionDirectAdjustmentSignature &&
                  cachedRow.suggestionSignature === sessionSuggestionSignature &&
                  cachedRow.baselineGoalSignature === sessionBaselineGoalSignature &&
                  cachedRow.adviceMode === sessionAdviceMode &&
                  cachedRow.bulkEditSignature === sessionBulkEditSignature &&
                  cachedRow.adviceSignature === sessionAdviceSignature &&
                  cachedRow.inlineProgramDragSignature === sessionInlineProgramDragSignature &&
                  cachedRow.inlineAdviceDragSignature === sessionInlineAdviceDragSignature &&
                  cachedRow.expandedAdviceSignature === sessionExpandedAdviceSignature
                ) {
                  return cachedRow.element;
                }

                const rowElement = (
                  <TableRow key={session.id || idx} style={{ backgroundColor: "#F5F5F5" }}>
                    <TableCell align="left" colSpan={responsiveColSpan(7)} style={{ verticalAlign: 'top', padding: 'clamp(10px, 1.2vw, 16px)', border: '1px solid #e0e0e0', minWidth: 'clamp(240px, 34vw, 380px)', background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)', borderRadius: 0, boxShadow: '0 2px 8px rgba(0,45,85,0.04)', width: '58.33%', position: 'relative' }}>
                      {(() => {
                        const notes = fetchedNotes[session.id] || [];
                        const noteIndex = noteIndices[session.id] || 0;
                        const currentNote = notes[noteIndex] || {};
                        const isAuthor = isCurrentUsersNote(currentNote);
                        const isEditing = editingNote[session.id] || false;
                        const hasNote = notes.length > 0;
                        const noteVersionLabel = notes.length > 0 ? `Version ${Math.max(notes.length - noteIndex, 1)} of ${notes.length}` : '';
                        const noteCreatedLabel = currentNote.created_at ? moment(currentNote.created_at).format("YYYY-MM-DD HH:mm") : '';
                        const sessionAdjustmentIssues = getSessionAdjustmentIssues(session);
                        const suggestionAdjustmentIssues = getSuggestiveDecisionSupportIssues(session);
                        const appliedAdjustmentIssues = sessionAdjustmentIssues.filter(issue => issue.adjusted && issue.replacementIdentifier);
                        const systemGeneratedAdjustmentIssues = appliedAdjustmentIssues.filter(issue => issue.isSystemGenerated);
                        const programAdjustmentLines = buildSessionProgramAdjustmentLines(session);
                        // Annotation persistence: also surface program /
                        // baseline-goal lines that have already been saved
                        // into the currently-displayed note. Without this,
                        // both annotation boxes vanish the moment the
                        // staged in-memory state is cleared on save —
                        // even though the note text retains the lines.
                        // In editing mode derive from editNoteValue so that
                        // stripping a line immediately hides it and newly
                        // merged-in lines appear without needing a save.
                        const _noteSourceForPersisted = isEditing
                          ? (editNoteValue[session.id] || currentNote.note || '')
                          : (currentNote.note || '');
                        const savedAdjustmentLines = getSavedAdjustmentLinesFromNote(_noteSourceForPersisted);
                        const savedProgramLines = savedAdjustmentLines.filter(line => !isAdviceAdjustmentLine(line));
                        const savedAdviceLines = savedAdjustmentLines.filter(line => isAdviceAdjustmentLine(line));
                        const savedBaselineGoalLines = getSavedBaselineGoalLinesFromNote(_noteSourceForPersisted);
                        const _normalizeLineKey = (line) => normalizeProgramSummaryLineKey(line);
                        const _visibleStagedBaselineGoalItems = getVisibleBaselineGoalStagedItems(session.id);
                        const stagedAdviceLines = getAdviceSummaryJournalLinesForSession(session.id);
                        const _stagedKeys = new Set(_visibleStagedBaselineGoalItems.map(it => _normalizeLineKey(it.lineText)));
                        const _stagedProgramKeys = new Set(programAdjustmentLines.map(_normalizeLineKey));
                        const _stagedAdviceKeys = new Set(stagedAdviceLines.map(_normalizeLineKey));
                        const _removedHistoricalKeys = new Set(Array.from(removedHistoricalLines[session.id] || []).map(_normalizeLineKey));
                        // Only show saved lines that aren't already present
                        // in the live staged set (avoid double-rendering a
                        // line that is both saved and being re-edited),
                        // and exclude lines the user has explicitly removed
                        // via the per-line remove icon.
                        const persistedProgramLines = savedProgramLines.filter(l => {
                          const key = _normalizeLineKey(l);
                          return !_stagedProgramKeys.has(key) && !_removedHistoricalKeys.has(key);
                        });
                        // Also drop user-removed lines from the live staged
                        // set so adjustment-log derived issues that re-emit
                        // the same line stay hidden.
                        const _filteredProgramAdjustmentLines = programAdjustmentLines.filter(l => !_removedHistoricalKeys.has(_normalizeLineKey(l)));
                        const persistedAdviceLines = savedAdviceLines.filter(line => !_stagedAdviceKeys.has(_normalizeLineKey(line)));
                        const persistedBaselineGoalLines = savedBaselineGoalLines.filter(l => !_stagedKeys.has(_normalizeLineKey(l)));
                        // Show Program adjustments box only when there are
                        // staged changes, or when editing (so persisted lines
                        // can be edited/removed). Never show an empty box in
                        // read-only mode with no pending changes.
                        const showProgramAdjustments = (
                          programAdjustmentLines.length > 0
                          || (isEditing && persistedProgramLines.length > 0)
                        );
                        const showBaselineGoalSection = (
                          _visibleStagedBaselineGoalItems.length > 0
                          || (isEditing && persistedBaselineGoalLines.length > 0)
                        );
                        const showAdviceAdjustmentSection = (
                          stagedAdviceLines.length > 0
                          || (isEditing && persistedAdviceLines.length > 0)
                        );
                        if (hasNote) {
                          // Show the existing note (with edit/save/cancel if author)
                          return (
                            <Box>
                              {/* Top bar: Copy | formatting toolbar (edit mode) | action buttons (edit mode) | Save/Cancel (edit mode) | Edit button (read mode) */}
                              <Box display="flex" alignItems="center" mb={1} style={{ flexWrap: 'wrap', gap: 'clamp(2px, 0.2vw, 4px)' }}>
                                <Tooltip
                                  title={copiedSessions[session.id] ? "Journal & exercise feedback copied!" : "Copy as text"}
                                  placement="top"
                                  open={copiedSessions[session.id] ? true : undefined}
                                >
                                  <span
                                    style={{ cursor: 'pointer', fontWeight: 700, fontSize: 'clamp(0.72rem, 0.76vw, 0.875rem)', marginRight: 'clamp(6px, 0.52vw, 8px)', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, transition: 'color 0.2s' }}
                                    onClick={() => handleCopySessionText(session, journalNote)}
                                    tabIndex={0}
                                    role="button"
                                    onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') handleCopySessionText(session, journalNote); }}
                                    aria-label="Copy as text"
                                  >
                                    {copiedSessions[session.id] ? (
                                      <>
                                        <span style={{ color: '#43ea7a', fontSize: 'clamp(0.72rem, 0.76vw, 0.875rem)' }}>Copied!</span>
                                        <CheckCircleIcon style={{ color: '#43ea7a', fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', transition: 'all 0.2s', transform: 'scale(1.15)' }} />
                                      </>
                                    ) : (
                                      <span style={{ color: '#FF7F27' }}>Copy 📝</span>
                                    )}
                                  </span>
                                </Tooltip>
                                <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 700, fontSize: 'clamp(0.72rem, 0.76vw, 0.875rem)', userSelect: 'none' }}>
                                  {getStaffInitialsByAuthor(selectedAuthors[session.id] || (currentNote.author ? String(currentNote.author) : ''), currentNote.author_name) || (currentNote.author_name ? currentNote.author_name.split(' ').map(p => p[0]).join('').slice(0, 3).toUpperCase() : '')}
                                </Typography>
                                {isEditing && (
                                  <>
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'bold')} title="Bold" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                      <FormatBoldIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                    </IconButton>
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'italic')} title="Italic" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                      <FormatItalicIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                    </IconButton>
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'underline')} title="Underline" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                      <FormatUnderlinedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                    </IconButton>
                                    <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'insertUnorderedList')} title="Bullet List" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                      <FormatListBulletedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                    </IconButton>
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'insertOrderedList')} title="Numbered List" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                      <FormatListNumberedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                    </IconButton>
                                    <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'formatBlock', '<h1>')} title="H1" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                      <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H1</span>
                                    </IconButton>
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'formatBlock', '<h2>')} title="H2" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                      <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H2</span>
                                    </IconButton>
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'formatBlock', '<h3>')} title="H3" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                      <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H3</span>
                                    </IconButton>
                                    <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                    <IconButton size="small" onMouseDown={e => editorRefsMap.current[session.id]?.execCmd(e, 'formatBlock', '<p>')} title="Normal text" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                      <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 400, lineHeight: 1 }}>¶</span>
                                    </IconButton>
                                    <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      disabled={generatingJournal[session.id]}
                                      onClick={() => generateJournalText(session)}
                                      style={{ fontSize: 'clamp(0.6rem, 0.72vw, 0.75rem)', textTransform: 'none', borderColor: '#FF7F27', color: '#FF7F27', borderRadius: 8, padding: '1px 8px', minWidth: 0, lineHeight: 1.4 }}
                                    >
                                      {generatingJournal[session.id] ? '...' : '✨ Auto-generate'}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => { setFollowupInitialStep(0); setFollowupDialogOpen(true); }}
                                      style={{ fontSize: 'clamp(0.6rem, 0.72vw, 0.75rem)', textTransform: 'none', borderColor: '#002D55', color: '#002D55', borderRadius: 8, padding: '1px 8px', minWidth: 0, lineHeight: 1.4 }}
                                    >
                                      📊 Follow-up
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => openJourneyActionMenu(session.id)}
                                      style={{ fontSize: 'clamp(0.6rem, 0.72vw, 0.75rem)', textTransform: 'none', borderColor: '#fb741a', color: '#fb741a', borderRadius: 8, padding: '1px 8px', minWidth: 0, lineHeight: 1.4, fontWeight: 700 }}
                                    >
                                      + KPI
                                    </Button>
                                  </>
                                )}
                              </Box>
                              <div style={currentNote.is_system ? { background: '#f5f5f5', borderLeft: '3px solid #cfd8dc', borderRadius: 4, padding: '2px 0', marginBottom: 4 } : undefined}>
                                <RichNoteEditor
                                  ref={el => { editorRefsMap.current[session.id] = el; }}
                                  label="Journal note"
                                  value={isEditing ? (editNoteValue[session.id] ?? currentNote.note ?? "") : (currentNote.note || "")}
                                  onChange={isEditing ? (html => setEditNoteValue(prev => ({ ...prev, [session.id]: html }))) : undefined}
                                  readOnly={!isEditing}
                                  hideToolbar={true}
                                />
                              </div>
                              {/* Below-field metadata */}
                              <Box display="flex" alignItems="center" mb={'clamp(6px, 0.52vw, 8px)'} mt={'clamp(2px, 0.2vw, 4px)'} style={{ flexWrap: 'wrap', gap: 'clamp(2px, 0.2vw, 4px)' }}>
                                <Typography variant="caption" color="textSecondary" style={{ userSelect: 'none' }}>Created by:</Typography>
                                <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4, fontWeight: 600, userSelect: 'none' }}>
                                  {getStaffFullNameByAuthor(selectedAuthors[session.id] || currentNote.author, currentNote.author_name) || currentNote.author_name || '—'}
                                </Typography>
                                {(noteCreatedLabel || noteVersionLabel) && (
                                  <>
                                    <span style={{ width: 1, height: 'clamp(12px, 1vw, 16px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                    <Typography variant="caption" color="textSecondary" style={{ userSelect: 'none' }}>
                                      {noteCreatedLabel ? `${noteCreatedLabel}` : ''}
                                    </Typography>
                                  </>
                                )}
                                <span style={{ width: 1, height: 'clamp(12px, 1vw, 16px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                <Button
                                  size="small"
                                  onClick={() => setNoteIndices(prev => ({ ...prev, [session.id]: Math.max((prev[session.id] || 0) - 1, 0) }))}
                                  disabled={noteIndex === 0 || isEditing}
                                  style={{ minWidth: 0, padding: '0 4px' }}
                                >
                                  {"<"}
                                </Button>
                                <span style={{ minWidth: 'clamp(28px, 2.2vw, 36px)', textAlign: 'center', fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)', color: '#546e7a' }}>
                                  {notes.length ? `${Math.max(notes.length - noteIndex, 1)} / ${notes.length}` : "0 / 0"}
                                </span>
                                <Button
                                  size="small"
                                  onClick={() => setNoteIndices(prev => ({ ...prev, [session.id]: Math.min((prev[session.id] || 0) + 1, notes.length - 1) }))}
                                  disabled={noteIndex >= notes.length - 1 || isEditing}
                                  style={{ minWidth: 0, padding: '0 4px' }}
                                >
                                  {">"}
                                </Button>
                              </Box>
                              {showProgramAdjustments && (
                                <Box style={{ marginTop: 'clamp(6px, 0.52vw, 8px)', marginBottom: 'clamp(6px, 0.52vw, 8px)', padding: 'clamp(8px, 0.78vw, 12px)', borderRadius: 12, background: '#f7faff', border: '1px solid #d0e4f7' }}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                    Program adjustments
                                  </Typography>
                                  {_filteredProgramAdjustmentLines.map(line => (
                                    <Box key={`existing-summary-${session.id}-${line}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `existing-summary-${session.id}-${line}`)}
                                      </Typography>
                                      <IconButton size="small" onClick={() => editProgramAdjustmentLineExercise(session, line)} title="Edit exercise">
                                        <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => removeProgramAdjustmentLine(session.id, line, { session })} title="Remove">
                                        <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {persistedProgramLines.map(line => (
                                    <Box key={`existing-saved-${session.id}-${line}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `existing-saved-${session.id}-${line}`)}
                                      </Typography>
                                      {isEditing && (
                                        <>
                                          <IconButton size="small" onClick={() => editProgramAdjustmentLineExercise(session, line)} title="Edit exercise">
                                            <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                          </IconButton>
                                          <IconButton size="small" onClick={() => removeProgramAdjustmentLine(session.id, line, { session })} title="Remove">
                                            <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                          </IconButton>
                                        </>
                                      )}
                                    </Box>
                                  ))}
                                  {systemGeneratedAdjustmentIssues.length > 0 && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 'clamp(4px, 0.39vw, 6px)' }}>
                                      {SYSTEM_GENERATED_HINT}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {showAdviceAdjustmentSection && (
                                <Box style={{ marginTop: 'clamp(6px, 0.52vw, 8px)', marginBottom: 'clamp(6px, 0.52vw, 8px)', padding: 'clamp(8px, 0.78vw, 12px)', borderRadius: 12, background: '#fff7ee', border: '1px solid #ffd7a8' }}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                    Advice adjustment
                                  </Typography>
                                  {stagedAdviceLines.map((line) => (
                                    <Box key={`existing-staged-advice-${session.id}-${line}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `existing-staged-advice-${session.id}-${line}`)}
                                      </Typography>
                                      <IconButton size="small" onClick={() => editAdviceAdjustmentLine(session, line)} title="Edit advice">
                                        <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => removeAdviceAdjustmentLine(session.id, line, { session })} title="Remove">
                                        <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {persistedAdviceLines.map((line, idx) => (
                                    <Box key={`existing-saved-advice-${session.id}-${idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `existing-saved-advice-${session.id}-${idx}`)}
                                      </Typography>
                                      {isEditing && (
                                        <>
                                          <IconButton size="small" onClick={() => editAdviceAdjustmentLine(session, line)} title="Edit advice">
                                            <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                          </IconButton>
                                          <IconButton size="small" onClick={() => removeAdviceAdjustmentLine(session.id, line, { session })} title="Remove">
                                            <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                          </IconButton>
                                        </>
                                      )}
                                    </Box>
                                  ))}
                                  {stagedAdviceLines.length > 0 && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 'clamp(4px, 0.39vw, 6px)' }}>
                                      Saved on next journal save.
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {showBaselineGoalSection && (
                                <Box style={{ marginTop: 'clamp(6px, 0.52vw, 8px)', marginBottom: 'clamp(6px, 0.52vw, 8px)', padding: 'clamp(8px, 0.78vw, 12px)', borderRadius: 12, background: '#fff7ee', border: '1px solid #ffd7a8' }}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                    Baseline / Goal adjustment
                                  </Typography>
                                  {getVisibleBaselineGoalStagedItems(session.id).map(item => (
                                    <Box key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {item.lineText}
                                      </Typography>
                                      {item.action !== 'pause' && (
                                        <IconButton size="small" onClick={() => openJourneyActionMenuForEdit(session.id, item)} title="Edit">
                                          <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                        </IconButton>
                                      )}
                                      <IconButton size="small" onClick={() => removeBaselineGoalStaged(session.id, item.id)} title="Remove">
                                        <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {persistedBaselineGoalLines.map((line, idx) => (
                                    <Box key={`existing-bg-saved-${session.id}-${idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {line}
                                      </Typography>
                                      {isEditing && (() => {
                                        const isPauseLine = isPersistedFollowupToggleBaselineGoalLine(line);
                                        const parsedItem = isPauseLine ? null : parsePersistedBaselineGoalLine(line);
                                        return (
                                          <>
                                            {parsedItem && (
                                              <IconButton size="small" onClick={() => openJourneyActionMenuForEdit(session.id, parsedItem)} title="Edit">
                                                <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                              </IconButton>
                                            )}
                                            {isPauseLine ? (
                                              <IconButton size="small" onClick={() => handleRemovePersistedFollowupToggleLine(session.id, line, currentNote.note || '')} title="Remove">
                                                <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                              </IconButton>
                                            ) : (
                                              <IconButton size="small" onClick={() => setEditNoteValue(prev => { const cur = prev[session.id] || ''; const next = removeLineFromBaselineGoalSection(cur, line); return next === cur ? prev : { ...prev, [session.id]: next }; })} title="Remove">
                                                <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                              </IconButton>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </Box>
                                  ))}
                                  {getVisibleBaselineGoalStagedItems(session.id).length > 0 && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 'clamp(4px, 0.39vw, 6px)' }}>
                                      Saved on next journal save.
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {/* Edit/Save/Cancel buttons */}
                              <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} style={{ flexWrap: 'wrap', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                {!isEditing && (
                                  <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setEditingNote(prev => ({ ...prev, [session.id]: true })) || setEditNoteValue(prev => ({ ...prev, [session.id]: isAuthor ? (currentNote.note || "") : (prev[session.id] || notes[0]?.note || currentNote.note || "") }))}
                                    className={classes.confirmButton}
                                    style={{ marginRight: 'clamp(6px, 0.52vw, 8px)' }}
                                  >
                                    Edit
                                  </Button>
                                )}
                                {isEditing && (
                                  <>
                                    <Box display="flex" alignItems="center" style={{ gap: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography variant="caption" color="textSecondary" style={{ userSelect: 'none', fontWeight: 600 }}>
                                        Author:
                                      </Typography>
                                      <TextField
                                        select
                                        value={selectedAuthors[session.id] || (currentNote.author ? String(currentNote.author) : '')}
                                        onChange={e => setSelectedAuthors(prev => ({ ...prev, [session.id]: e.target.value }))}
                                        variant="standard"
                                        size="small"
                                        style={{
                                          minWidth: 'clamp(120px, 11vw, 180px)',
                                          minHeight: 'clamp(28px, 2.1vw, 34px)',
                                          fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)',
                                          maxHeight: 'clamp(28px, 2.1vw, 34px)',
                                          background: 'transparent',
                                          border: 'none',
                                          boxShadow: 'none',
                                          outline: 'none',
                                          padding: 0,
                                        }}
                                        InputProps={{
                                          disableUnderline: true,
                                          style: {
                                            fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)',
                                            background: 'transparent',
                                            border: 'none',
                                            boxShadow: 'none',
                                            fontFamily: 'inherit',
                                            outline: 'none',
                                            padding: '0 clamp(8px, 0.72vw, 12px)',
                                            height: 'clamp(28px, 2.1vw, 34px)',
                                            borderRadius: 50,
                                            display: 'flex',
                                            alignItems: 'center',
                                          }
                                        }}
                                        SelectProps={{
                                          classes: { root: 'author-dropdown-select' },
                                          renderValue: (val) => {
                                            const resolvedAuthorId = val || (currentNote.author ? String(currentNote.author) : '');
                                            const fullName = getStaffFullNameByAuthor(resolvedAuthorId, currentNote.author_name);
                                            const initials = getStaffInitialsByAuthor(resolvedAuthorId, currentNote.author_name);
                                            if (fullName) return `${fullName}${initials ? ` (${initials})` : ''}`;
                                            return currentNote.author_name || '';
                                          },
                                          MenuProps: {
                                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                                            getContentAnchorEl: null,
                                            PaperProps: { style: { paddingTop: 'clamp(2px, 0.26vw, 4px)', borderRadius: 14 } },
                                          }
                                        }}
                                        InputLabelProps={{ style: { fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)' } }}
                                      >
                                        {clinicStaff.map(staff => (
                                          <MenuItem
                                            key={staff.userId}
                                            value={staff.userId}
                                            style={{ fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)', background: 'transparent', textAlign: 'left', width: '100%' }}
                                            className="author-dropdown-menuitem"
                                          >
                                            <span style={{ display: 'inline-block', minWidth: 'clamp(28px, 2.5vw, 36px)', fontWeight: 700 }}>{staff.initials}</span>
                                            <span style={{ marginLeft: 8, color: '#546e7a' }}>{`${staff.firstName} ${staff.lastName}`.trim()}</span>
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Box>
                                    <Button
                                      variant="contained"
                                      color="primary"
                                      size="small"
                                      startIcon={<SaveIcon />}
                                      onClick={async () => {
                                        const newVal = editNoteValue[session.id] || "";
                                        if (isHtmlEmpty(newVal)) return;
                                        const noteToSave = buildNoteWithLatestAdjustments(session.id, newVal, {
                                          programSummary: getCurrentDraftProgramSummary(session.id),
                                        });
                                        setSavingNotes(prev => ({ ...prev, [session.id]: true }));
                                        try {
                                          await persistSessionAdviceDraft(session.id);
                                          await persistSessionExerciseDraft(session.id);
                                          await applySuggestionDecisions(session.id);
                                          const savedResponse = await saveJournalNoteToBackend(session.id, noteToSave, token, selectedAuthors[session.id] || null);
                                          // Re-fetch notes with force=true and reset navigation to latest
                                          const notes = await fetchJournalNotes(session.id, true);
                                          setFetchedNotes(prev => ({
                                            ...prev,
                                            [session.id]: resolveNotesAfterSave(notes, savedResponse, prev[session.id] || []),
                                          }));
                                          setNoteIndices(prev => ({ ...prev, [session.id]: 0 }));
                                          setEditNoteValue(prev => ({ ...prev, [session.id]: "" }));
                                          setEditingNote(prev => ({ ...prev, [session.id]: false }));
                                          setSuggestionDecisions(prev => ({ ...prev, [session.id]: {} }));
                                          clearSessionProgramChangeSummary(session.id, session.phase_id);
                                          setRemovedHistoricalLines(prev => { if (!prev[session.id]) return prev; const next = { ...prev }; delete next[session.id]; return next; });
                                          const stagedBG = await persistStagedBaselineGoal(session.id);
                                          if (stagedBG.persisted > 0) clearBaselineGoalStaged(session.id);
                                          // Reload draft so the exercise list reflects the saved program
                                          ensureSessionExerciseDraft(session, { forceReload: true }).catch(() => {});
                                          setSnackbarMsg('Note saved successfully.');
                                          setSnackbarSeverity('success');
                                          setSnackbarOpen(true);
                                        } catch (e) {
                                          alert("Failed to save note: " + e.message);
                                        }
                                        setSavingNotes(prev => ({ ...prev, [session.id]: false }));
                                      }}
                                      disabled={savingNotes[session.id] || isHtmlEmpty(editNoteValue[session.id])}
                                      className={classes.confirmButton}
                                      style={{ marginRight: 'clamp(6px, 0.52vw, 8px)' }}
                                    >
                                      {savingNotes[session.id] ? "Saving..." : "Save"}
                                    </Button>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      onClick={() => {
                                        setEditingNote(prev => ({ ...prev, [session.id]: false }));
                                        setEditNoteValue(prev => ({ ...prev, [session.id]: currentNote.note || "" }));
                                        // Revert staged program changes
                                        clearSessionProgramChangeSummary(session.id, session.phase_id);
                                        // Drop any user-removed historical line markers
                                        setRemovedHistoricalLines(prev => { if (!prev[session.id]) return prev; const next = { ...prev }; delete next[session.id]; return next; });
                                        // Revert staged baseline/goal annotations
                                        clearBaselineGoalStaged(session.id);
                                        // Revert exercise draft to original (incl. any historical revert)
                                        setSessionExerciseDrafts(prev => {
                                          const existing = prev[session.id];
                                          if (!existing) return prev;
                                          const trueOriginal = existing.persistBaseline || existing.originalExercises;
                                          return { ...prev, [session.id]: { ...existing, exercises: trueOriginal, originalExercises: trueOriginal, persistBaseline: undefined, dirty: false } };
                                        });
                                        setSessionAdviceDrafts(prev => {
                                          const existing = prev[session.id];
                                          if (!existing) return prev;
                                          const trueOriginal = existing.persistBaseline || existing.originalAdvices;
                                          return { ...prev, [session.id]: { ...existing, advices: trueOriginal, originalAdvices: trueOriginal, persistBaseline: undefined, dirty: false } };
                                        });
                                      }}
                                      className={classes.cancelButton}
                                      style={{ marginRight: 'clamp(6px, 0.52vw, 8px)' }}
                                    >
                                      Cancel
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </Box>
                          );
                        } else {
                          // Show the "Add a new note" TextField in place
                          return (
                            <Box>
                              <Box display="flex" alignItems="center" mb={1} style={{ flexWrap: 'wrap', gap: 'clamp(2px, 0.2vw, 4px)' }}>
                                <span style={{ color: '#f5a62f', fontWeight: 700, fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', marginRight: 'clamp(6px, 0.52vw, 8px)', userSelect: 'none', display: 'inline-block' }}>
                                  📝
                                </span>
                                <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'bold')} title="Bold" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                  <FormatBoldIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                </IconButton>
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'italic')} title="Italic" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                  <FormatItalicIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                </IconButton>
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'underline')} title="Underline" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                  <FormatUnderlinedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                </IconButton>
                                <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'insertUnorderedList')} title="Bullet List" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                  <FormatListBulletedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                </IconButton>
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'insertOrderedList')} title="Numbered List" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55' }}>
                                  <FormatListNumberedIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />
                                </IconButton>
                                <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'formatBlock', '<h1>')} title="H1" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                  <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H1</span>
                                </IconButton>
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'formatBlock', '<h2>')} title="H2" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                  <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H2</span>
                                </IconButton>
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'formatBlock', '<h3>')} title="H3" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                  <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 700, lineHeight: 1 }}>H3</span>
                                </IconButton>
                                <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                <IconButton size="small" onMouseDown={e => editorRefsMap.current[`${session.id}_new`]?.execCmd(e, 'formatBlock', '<p>')} title="Normal text" style={{ borderRadius: 6, border: '1px solid #cfd8dc', padding: 'clamp(2px, 0.26vw, 4px)', color: '#002D55', minWidth: 0 }}>
                                  <span style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', fontWeight: 400, lineHeight: 1 }}>¶</span>
                                </IconButton>
                                <span style={{ width: 1, height: 'clamp(16px, 1.3vw, 20px)', background: '#cfd8dc', margin: '0 clamp(2px, 0.2vw, 4px)' }} />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={generatingJournal[session.id]}
                                  onClick={() => generateJournalText(session)}
                                  style={{ fontSize: 'clamp(0.6rem, 0.72vw, 0.75rem)', textTransform: 'none', borderColor: '#FF7F27', color: '#FF7F27', borderRadius: 8, padding: '1px 8px', minWidth: 0, lineHeight: 1.4 }}
                                >
                                  {generatingJournal[session.id] ? '...' : '✨ Auto-generate'}
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => { setFollowupInitialStep(0); setFollowupDialogOpen(true); }}
                                  style={{ fontSize: 'clamp(0.6rem, 0.72vw, 0.75rem)', textTransform: 'none', borderColor: '#002D55', color: '#002D55', borderRadius: 8, padding: '1px 8px', minWidth: 0, lineHeight: 1.4, marginLeft: 4 }}
                                >
                                  📊 Follow-up
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openJourneyActionMenu(session.id)}
                                  style={{ fontSize: 'clamp(0.6rem, 0.72vw, 0.75rem)', textTransform: 'none', borderColor: '#fb741a', color: '#fb741a', borderRadius: 8, padding: '1px 8px', minWidth: 0, lineHeight: 1.4, marginLeft: 4, fontWeight: 700 }}
                                >
                                  + KPI
                                </Button>
                              </Box>
                              <RichNoteEditor
                                ref={el => { editorRefsMap.current[`${session.id}_new`] = el; }}
                                label="Add a new note"
                                value={editNoteValue[session.id] || ""}
                                onChange={html => {
                                  setEditingNote(prev => ({ ...prev, [session.id]: true }));
                                  setEditNoteValue(prev => ({ ...prev, [session.id]: html }));
                                }}
                                readOnly={false}
                                hideToolbar={true}
                              />
                              {showProgramAdjustments && (
                                <Box style={{ marginTop: 'clamp(6px, 0.52vw, 8px)', marginBottom: 'clamp(6px, 0.52vw, 8px)', padding: 'clamp(8px, 0.78vw, 12px)', borderRadius: 12, background: '#f7faff', border: '1px solid #d0e4f7' }}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                    Program adjustments
                                  </Typography>
                                  {_filteredProgramAdjustmentLines.map(line => (
                                    <Box key={`new-summary-${session.id}-${line}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `new-summary-${session.id}-${line}`)}
                                      </Typography>
                                      <IconButton size="small" onClick={() => editProgramAdjustmentLineExercise(session, line)} title="Edit exercise">
                                        <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => removeProgramAdjustmentLine(session.id, line, { session })} title="Remove">
                                        <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {persistedProgramLines.map(line => (
                                    <Box key={`new-saved-${session.id}-${line}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `new-saved-${session.id}-${line}`)}
                                      </Typography>
                                      {isEditing && (
                                        <>
                                          <IconButton size="small" onClick={() => editProgramAdjustmentLineExercise(session, line)} title="Edit exercise">
                                            <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                          </IconButton>
                                          <IconButton size="small" onClick={() => removeProgramAdjustmentLine(session.id, line, { session })} title="Remove">
                                            <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#546e7a' }} />
                                          </IconButton>
                                        </>
                                      )}
                                    </Box>
                                  ))}
                                  {systemGeneratedAdjustmentIssues.length > 0 && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 'clamp(4px, 0.39vw, 6px)' }}>
                                      {SYSTEM_GENERATED_HINT}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {showAdviceAdjustmentSection && (
                                <Box style={{ marginTop: 'clamp(6px, 0.52vw, 8px)', marginBottom: 'clamp(6px, 0.52vw, 8px)', padding: 'clamp(8px, 0.78vw, 12px)', borderRadius: 12, background: '#fff7ee', border: '1px solid #ffd7a8' }}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                    Advice adjustment
                                  </Typography>
                                  {stagedAdviceLines.map((line) => (
                                    <Box key={`new-staged-advice-${session.id}-${line}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `new-staged-advice-${session.id}-${line}`)}
                                      </Typography>
                                      <IconButton size="small" onClick={() => editAdviceAdjustmentLine(session, line)} title="Edit advice">
                                        <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                      </IconButton>
                                      <IconButton size="small" onClick={() => removeAdviceAdjustmentLine(session.id, line, { session })} title="Remove">
                                        <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {persistedAdviceLines.map((line, idx) => (
                                    <Box key={`new-saved-advice-${session.id}-${idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {renderProgramAdjustmentText(line, `new-saved-advice-${session.id}-${idx}`)}
                                      </Typography>
                                      {isEditing && (
                                        <>
                                          <IconButton size="small" onClick={() => editAdviceAdjustmentLine(session, line)} title="Edit advice">
                                            <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                          </IconButton>
                                          <IconButton size="small" onClick={() => removeAdviceAdjustmentLine(session.id, line, { session })} title="Remove">
                                            <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                          </IconButton>
                                        </>
                                      )}
                                    </Box>
                                  ))}
                                  {stagedAdviceLines.length > 0 && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 'clamp(4px, 0.39vw, 6px)' }}>
                                      Saved on next journal save.
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {showBaselineGoalSection && (
                                <Box style={{ marginTop: 'clamp(6px, 0.52vw, 8px)', marginBottom: 'clamp(6px, 0.52vw, 8px)', padding: 'clamp(8px, 0.78vw, 12px)', borderRadius: 12, background: '#fff7ee', border: '1px solid #ffd7a8' }}>
                                  <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                    Baseline / Goal adjustment
                                  </Typography>
                                  {getVisibleBaselineGoalStagedItems(session.id).map(item => (
                                    <Box key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {item.lineText}
                                      </Typography>
                                      {item.action !== 'pause' && (
                                        <IconButton size="small" onClick={() => openJourneyActionMenuForEdit(session.id, item)} title="Edit">
                                          <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                        </IconButton>
                                      )}
                                      <IconButton size="small" onClick={() => {
                                        removeBaselineGoalStaged(session.id, item.id);
                                        setEditNoteValue(prev => {
                                          const cur = prev[session.id] || '';
                                          const stripped = removeLineFromBaselineGoalSection(cur, item.lineText);
                                          return stripped === cur ? prev : { ...prev, [session.id]: stripped };
                                        });
                                      }} title="Remove">
                                        <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                      </IconButton>
                                    </Box>
                                  ))}
                                  {persistedBaselineGoalLines.map((line, idx) => (
                                    <Box key={`new-bg-saved-${session.id}-${idx}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'clamp(8px, 0.78vw, 12px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                      <Typography style={{ color: '#002D55', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', flex: 1 }}>
                                        {line}
                                      </Typography>
                                      {isEditing && (() => {
                                        const isPauseLine = isPersistedFollowupToggleBaselineGoalLine(line);
                                        const parsedItem = isPauseLine ? null : parsePersistedBaselineGoalLine(line);
                                        return (
                                          <>
                                            {parsedItem && (
                                              <IconButton size="small" onClick={() => openJourneyActionMenuForEdit(session.id, parsedItem)} title="Edit">
                                                <EditIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                              </IconButton>
                                            )}
                                            {isPauseLine ? (
                                              <IconButton size="small" onClick={() => handleRemovePersistedFollowupToggleLine(session.id, line, currentNote.note || '')} title="Remove">
                                                <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                              </IconButton>
                                            ) : (
                                              <IconButton size="small" onClick={() => setEditNoteValue(prev => { const cur = prev[session.id] || ''; const next = removeLineFromBaselineGoalSection(cur, line); return next === cur ? prev : { ...prev, [session.id]: next }; })} title="Remove">
                                                <CloseIcon style={{ fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)', color: '#fb741a' }} />
                                              </IconButton>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </Box>
                                  ))}
                                  {getVisibleBaselineGoalStagedItems(session.id).length > 0 && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 'clamp(4px, 0.39vw, 6px)' }}>
                                      Saved on next journal save.
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              <Box display="flex" justifyContent="flex-end" alignItems="center" mt={1} style={{ flexWrap: 'wrap', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                <Box display="flex" alignItems="center" style={{ gap: 'clamp(4px, 0.39vw, 6px)' }}>
                                  <Typography variant="caption" color="textSecondary" style={{ userSelect: 'none', fontWeight: 600 }}>
                                    Author:
                                  </Typography>
                                  <TextField
                                    select
                                    value={newNoteAuthors[session.id] || ''}
                                    onChange={e => setNewNoteAuthors(prev => ({ ...prev, [session.id]: e.target.value }))}
                                    variant="outlined"
                                    size="small"
                                    required
                                    style={{ minWidth: 'clamp(100px, 10.4vw, 160px)', minHeight: 'clamp(28px, 2.1vw, 34px)' }}
                                    InputProps={{
                                      style: {
                                        fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)',
                                        height: 'clamp(28px, 2.1vw, 34px)',
                                        borderRadius: 50,
                                        paddingRight: 8,
                                        overflow: 'hidden',
                                        background: '#fff',
                                      }
                                    }}
                                    inputProps={{
                                      style: { fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)' }
                                    }}
                                    SelectProps={{
                                      displayEmpty: true,
                                      renderValue: (val) => {
                                        if (!val) return <span style={{ color: '#aaa', fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)' }}>— select —</span>;
                                        const fullName = getStaffFullNameById(val);
                                        const initials = getStaffInitialsById(val);
                                        return <span style={{ fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)' }}>{`${fullName}${initials ? ` (${initials})` : ''}`}</span>;
                                      },
                                      MenuProps: {
                                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                                        getContentAnchorEl: null,
                                        PaperProps: { style: { borderRadius: 14 } },
                                      },
                                    }}
                                  >
                                    <MenuItem value="" disabled style={{ fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)' }}>
                                      <em>— select —</em>
                                    </MenuItem>
                                    {clinicStaff.map(staff => (
                                      <MenuItem
                                        key={staff.userId}
                                        value={staff.userId}
                                        style={{ fontSize: 'clamp(0.625rem, 0.65vw, 0.75rem)' }}
                                      >
                                        <span style={{ display: 'inline-block', minWidth: 'clamp(28px, 2.5vw, 36px)', fontWeight: 700 }}>{staff.initials}</span>
                                        <span style={{ marginLeft: 8, color: '#546e7a' }}>{`${staff.firstName} ${staff.lastName}`.trim()}</span>
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  {!newNoteAuthors[session.id] && (
                                    <Typography variant="caption" style={{ color: '#d32f2f' }}>
                                      Required to save
                                    </Typography>
                                  )}
                                </Box>
                                <Button
                                  variant="contained"
                                  color="primary"
                                  startIcon={<SaveIcon />}
                                  onClick={async () => {
                                    const note = editNoteValue[session.id] || "";
                                    if (isHtmlEmpty(note)) return;
                                    const authorId = newNoteAuthors[session.id];
                                    if (!authorId) return;
                                    const noteToSave = buildNoteWithLatestAdjustments(session.id, note, {
                                      programSummary: getCurrentDraftProgramSummary(session.id),
                                    });
                                    setSavingNotes(prev => ({ ...prev, [session.id]: true }));
                                    try {
                                      await persistSessionAdviceDraft(session.id);
                                      await persistSessionExerciseDraft(session.id);
                                      await applySuggestionDecisions(session.id);
                                      const savedResponse = await saveJournalNoteToBackend(session.id, noteToSave, token, authorId);
                                      // Re-fetch notes with force=true and reset navigation to latest
                                      const notes = await fetchJournalNotes(session.id, true);
                                      setFetchedNotes(prev => ({
                                        ...prev,
                                        [session.id]: resolveNotesAfterSave(notes, savedResponse, prev[session.id] || []),
                                      }));
                                      setNoteIndices(prev => ({ ...prev, [session.id]: 0 }));
                                      setEditNoteValue(prev => ({ ...prev, [session.id]: "" }));
                                      setEditingNote(prev => ({ ...prev, [session.id]: false }));
                                      setNewNoteAuthors(prev => ({ ...prev, [session.id]: '' }));
                                      setSuggestionDecisions(prev => ({ ...prev, [session.id]: {} }));
                                      clearSessionProgramChangeSummary(session.id, session.phase_id);
                                      setRemovedHistoricalLines(prev => { if (!prev[session.id]) return prev; const next = { ...prev }; delete next[session.id]; return next; });
                                      const stagedBG = await persistStagedBaselineGoal(session.id);
                                      if (stagedBG.persisted > 0) clearBaselineGoalStaged(session.id);
                                      // Reload draft so the exercise list reflects the saved program
                                      ensureSessionExerciseDraft(session, { forceReload: true }).catch(() => {});
                                    } catch (e) {
                                      alert("Failed to save note: " + e.message);
                                    }
                                    setSavingNotes(prev => ({ ...prev, [session.id]: false }));
                                  }}
                                  disabled={savingNotes[session.id] || isHtmlEmpty(editNoteValue[session.id]) || !newNoteAuthors[session.id]}
                                  className={classes.confirmButton}
                                  style={{ marginRight: 'clamp(6px, 0.52vw, 8px)' }}
                                  size="small"
                                >
                                  {savingNotes[session.id] ? "Saving..." : "Save"}
                                </Button>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    setEditNoteValue(prev => ({ ...prev, [session.id]: "" }));
                                    setNewNoteAuthors(prev => ({ ...prev, [session.id]: '' }));
                                    setEditingNote(prev => ({ ...prev, [session.id]: false }));
                                  }}
                                  className={classes.cancelButton}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            </Box>
                          );
                        }
                      })()}
                      {/* Copy functionality moved to 📝 icon */}
                    </TableCell>
                    {/* Exercises & Feedback Nested Structure */}
                    <TableCell align="left" colSpan={responsiveColSpan(5)} style={{ verticalAlign: 'top', padding: 'clamp(3px, 0.33vw, 5px)', width: '41.67%', background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)' }}>
                      {((session.exercise_feedback && session.exercise_feedback.length > 0)
                        || (session.session_feedback && session.session_feedback.length > 0)
                        || (idx === 0 && Array.isArray(exercises) && exercises.length > 0)
                        || session.is_patient_scope) ? (
                        <Table size="small" style={{ width: '100%', background: 'none', boxShadow: 'none' }}>
                          <TableBody>
                            {idx === 0 && (() => {
                              const sessionAdvices = sessionAdviceDrafts[session.id]?.advices || getJourneyAdvicesForSession(session);
                              const advicesVisible = Boolean(journeyAdviceVisibility[session.id]);
                              return (
                                <>
                                  <TableRow style={{ background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)' }}>
                                    <TableCell colSpan={responsiveColSpan(2)} style={{ border: 'none', padding: '0 0 clamp(4px, 0.39vw, 6px) 0', background: 'none' }}>
                                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)', color: '#002D55', gap: 'clamp(8px, 0.78vw, 12px)', fontWeight: 700 }}>
                                        <Box display="flex" alignItems="center" style={{ gap: 'clamp(8px, 0.78vw, 12px)' }}>
                                          <Box className={classes.journeyProgramModeToggle}>
                                            <Tabs
                                              value={advicesVisible ? 1 : 0}
                                              onChange={(event, nextValue) => {
                                                const nextShowsAdvices = nextValue === 1;
                                                if (nextShowsAdvices !== advicesVisible) {
                                                  toggleJourneyAdviceVisibility(session.id);
                                                }
                                              }}
                                              className={classes.journeyProgramTabsWrapper}
                                              textColor="inherit"
                                              variant="standard"
                                              classes={{
                                                indicator: classes.journeyProgramTabsIndicator,
                                                flexContainer: classes.journeyProgramTabsFlexContainer,
                                              }}
                                            >
                                              <Tab
                                                label="Exercises"
                                                disableRipple
                                                classes={{ root: classes.journeyProgramTab, selected: classes.journeyProgramTabSelected }}
                                              />
                                              <Tab
                                                label="Advices"
                                                disableRipple
                                                classes={{ root: classes.journeyProgramTab, selected: classes.journeyProgramTabSelected }}
                                              />
                                            </Tabs>
                                          </Box>
                                        </Box>
                                        <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ marginLeft: 'auto', gap: 'clamp(4px, 0.39vw, 6px)' }}>
                                          {!advicesVisible && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              startIcon={<EditIcon />}
                                              className={classes.cancelButton}
                                              style={{ minWidth: 'auto', padding: '2px 10px' }}
                                              onClick={() => {
                                                openSessionProgramEditor(session);
                                                setEditingNote(prev => ({ ...prev, [session.id]: true }));
                                                setEditNoteValue(prev => {
                                                  if (Object.prototype.hasOwnProperty.call(prev, session.id)) {
                                                    return prev;
                                                  }
                                                  const notes = fetchedNotes[session.id] || [];
                                                  const noteIndex = noteIndices[session.id] || 0;
                                                  const currentNote = notes[noteIndex] || {};
                                                  return { ...prev, [session.id]: currentNote.note || '' };
                                                });
                                              }}
                                            >
                                              Edit
                                            </Button>
                                          )}
                                          {advicesVisible && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              startIcon={<EditIcon />}
                                              className={classes.cancelButton}
                                              style={{ minWidth: 'auto', padding: '2px 10px' }}
                                              onClick={() => openSessionAdviceEditor(session)}
                                            >
                                              Edit
                                            </Button>
                                          )}
                                          {!advicesVisible && sessionExerciseDrafts[session.id]?.dirty && (
                                            <Chip
                                              label="Program changes staged"
                                              size="small"
                                              style={{ backgroundColor: '#fff3e0', color: '#8a5b00', fontWeight: 700 }}
                                            />
                                          )}
                                          {advicesVisible && sessionAdviceDrafts[session.id]?.dirty && (
                                            <Chip
                                              label="Advice changes staged"
                                              size="small"
                                              style={{ backgroundColor: '#fff3e0', color: '#8a5b00', fontWeight: 700 }}
                                            />
                                          )}
                                        </Box>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                  {advicesVisible && (
                                    <TableRow style={{ background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)' }}>
                                      <TableCell colSpan={responsiveColSpan(2)} style={{ border: 'none', padding: '0 0 clamp(6px, 0.52vw, 8px) 0', background: 'none' }}>
                                        <Box display="flex" flexDirection="column" style={{ gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                          <Box style={{ borderRadius: 14, padding: 'clamp(8px, 0.69vw, 12px)', background: 'transparent', border: '1px solid transparent' }}>
                                            {(() => {
                                              const adviceDraft = sessionAdviceDrafts[session.id] || null;
                                              const originalAdviceList = adviceDraft?.originalAdvices || sessionAdvices;
                                              const currentAdviceList = adviceDraft?.advices || sessionAdvices;
                                              const normalizeAdviceTitle = (value) => String(value || '').trim().toLowerCase();
                                              const adviceNoteText = (sessionIsEditing || sessionShowsHistoricalVersion)
                                                ? (() => {
                                                    const liveNote = editNoteValue[session.id] || '';
                                                    if (liveNote) {
                                                      return liveNote;
                                                    }
                                                    const savedNotes = fetchedNotes[session.id] || [];
                                                    const savedIdx = noteIndices[session.id] || 0;
                                                    return (savedNotes[savedIdx] && savedNotes[savedIdx].note) || '';
                                                  })()
                                                : '';
                                              const historicalReplacementByTargetTitle = new Map();
                                              const historicalRemovedTitleMap = new Map();
                                              if (adviceNoteText) {
                                                const replacedPat = /Replaced \[([^\]]+)\] with \[([^\]]+)\]/g;
                                                const removedPat = /Removed advice \[([^\]]+)\] from the advice plan/g;
                                                let match;
                                                while ((match = replacedPat.exec(adviceNoteText)) !== null) {
                                                  const fromTitle = String(match[1] || '').trim();
                                                  const toTitle = String(match[2] || '').trim();
                                                  const normalizedToTitle = normalizeAdviceTitle(toTitle);
                                                  const targetLooksLikeAdvice = currentAdviceList.some((advice) => normalizeAdviceTitle(advice?.title) === normalizedToTitle)
                                                    || originalAdviceList.some((advice) => normalizeAdviceTitle(advice?.title) === normalizedToTitle);
                                                  if (targetLooksLikeAdvice && normalizedToTitle) {
                                                    historicalReplacementByTargetTitle.set(normalizedToTitle, fromTitle);
                                                  }
                                                }
                                                while ((match = removedPat.exec(adviceNoteText)) !== null) {
                                                  const removedTitle = String(match[1] || '').trim();
                                                  const normalizedRemovedTitle = normalizeAdviceTitle(removedTitle);
                                                  if (removedTitle && normalizedRemovedTitle) {
                                                    historicalRemovedTitleMap.set(normalizedRemovedTitle, removedTitle);
                                                  }
                                                }
                                              }
                                              const currentAdviceByKey = new Map(
                                                currentAdviceList.map((advice) => [getJourneyAdviceDraftKey(advice), advice])
                                              );
                                              const renderedAdviceRows = [];
                                              const renderedAdviceKeys = new Set();

                                              originalAdviceList.forEach((originalAdvice, originalIndex) => {
                                                const originalKey = getJourneyAdviceDraftKey(originalAdvice);
                                                const matchingAdvice = currentAdviceByKey.get(originalKey) || null;
                                                const originalTitle = String(originalAdvice?.title || '').trim();
                                                const historicalReplacementSourceTitle = matchingAdvice
                                                  ? String(historicalReplacementByTargetTitle.get(normalizeAdviceTitle(matchingAdvice?.title)) || '').trim()
                                                  : '';
                                                const replacementAdvice = matchingAdvice && String(matchingAdvice?.__journeyReplaceFromTitle || '').trim()
                                                  ? matchingAdvice
                                                  : null;

                                                if (replacementAdvice) {
                                                  renderedAdviceRows.push({
                                                    advice: originalAdvice,
                                                    renderKey: `advice-replaced-original-${session.id}-${originalKey}-${originalIndex}`,
                                                    status: 'replaced-original',
                                                  });
                                                  renderedAdviceRows.push({
                                                    advice: replacementAdvice,
                                                    renderKey: `advice-replacement-${session.id}-${originalKey}-${originalIndex}`,
                                                    status: 'active',
                                                  });
                                                  renderedAdviceKeys.add(originalKey);
                                                  return;
                                                }

                                                if (matchingAdvice && historicalReplacementSourceTitle && normalizeAdviceTitle(originalTitle) !== normalizeAdviceTitle(historicalReplacementSourceTitle)) {
                                                  return;
                                                }

                                                if (matchingAdvice) {
                                                  renderedAdviceRows.push({
                                                    advice: matchingAdvice,
                                                    renderKey: `advice-active-${session.id}-${originalKey}-${originalIndex}`,
                                                    status: 'active',
                                                  });
                                                  renderedAdviceKeys.add(originalKey);
                                                  return;
                                                }

                                                if ((sessionIsEditing || sessionShowsHistoricalVersion) && historicalRemovedTitleMap.has(normalizeAdviceTitle(originalTitle))) {
                                                  renderedAdviceRows.push({
                                                    advice: originalAdvice,
                                                    renderKey: `advice-removed-${session.id}-${originalKey}-${originalIndex}`,
                                                    status: 'removed',
                                                  });
                                                }
                                              });

                                              currentAdviceList.forEach((advice, adviceIndex) => {
                                                const adviceKey = getJourneyAdviceDraftKey(advice);
                                                if (renderedAdviceKeys.has(adviceKey)) {
                                                  return;
                                                }

                                                const historicalReplacementSourceTitle = String(
                                                  historicalReplacementByTargetTitle.get(normalizeAdviceTitle(advice?.title)) || ''
                                                ).trim();
                                                const sourceExistsInOriginal = historicalReplacementSourceTitle
                                                  ? originalAdviceList.some((originalAdvice) => normalizeAdviceTitle(originalAdvice?.title) === normalizeAdviceTitle(historicalReplacementSourceTitle))
                                                  : false;

                                                if ((sessionIsEditing || sessionShowsHistoricalVersion) && historicalReplacementSourceTitle && !sourceExistsInOriginal) {
                                                  renderedAdviceRows.push({
                                                    advice: {
                                                      title: historicalReplacementSourceTitle,
                                                      advice: '',
                                                      importance: advice?.importance || adviceIndex + 1,
                                                    },
                                                    renderKey: `advice-historical-replaced-${session.id}-${adviceKey}-${adviceIndex}`,
                                                    status: 'replaced-original',
                                                  });
                                                }

                                                renderedAdviceRows.push({
                                                  advice,
                                                  renderKey: `advice-added-${session.id}-${adviceKey}-${adviceIndex}`,
                                                  status: 'active',
                                                });
                                                renderedAdviceKeys.add(adviceKey);
                                              });

                                              if ((sessionIsEditing || sessionShowsHistoricalVersion) && historicalRemovedTitleMap.size > 0) {
                                                historicalRemovedTitleMap.forEach((removedTitle, removedTitleNorm) => {
                                                  const alreadyRendered = renderedAdviceRows.some((entry) => normalizeAdviceTitle(entry?.advice?.title) === removedTitleNorm);
                                                  const existsInCurrent = currentAdviceList.some((advice) => normalizeAdviceTitle(advice?.title) === removedTitleNorm);
                                                  if (alreadyRendered || existsInCurrent) {
                                                    return;
                                                  }

                                                  renderedAdviceRows.push({
                                                    advice: {
                                                      title: removedTitle,
                                                      advice: '',
                                                      importance: renderedAdviceRows.length + 1,
                                                    },
                                                    renderKey: `advice-historical-removed-${session.id}-${removedTitleNorm}`,
                                                    status: 'removed',
                                                  });
                                                });
                                              }

                                              if (renderedAdviceRows.length === 0) {
                                                return (
                                              <Box display="flex" flexDirection="column" style={{ gap: 'clamp(8px, 0.69vw, 12px)' }}>
                                                <Typography style={{ fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', color: '#6b7a8c', lineHeight: 1.45 }}>
                                                  No current advices for this phase.
                                                </Typography>
                                                {sessionIsEditing && renderJourneyInlineAddPlaceholderRow([
                                                  {
                                                    label: 'Add advice',
                                                    hint: 'Open library for this phase',
                                                    onClick: () => openAdviceLibraryDrawer({ type: 'add', sessionId: session.id }),
                                                  },
                                                  {
                                                    label: 'Add custom advice',
                                                    hint: 'Create advice from scratch',
                                                    onClick: () => openCustomDraftAdviceDialog(session.id),
                                                  },
                                                ])}
                                              </Box>
                                                );
                                              }

                                              return (
                                              <Box display="flex" flexDirection="column" style={{ gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                                {renderedAdviceRows.map(({ advice, renderKey, status }, adviceIndex) => {
                                                  const adviceKey = getJourneyAdviceDraftKey(advice);
                                                  const adviceCardKey = `${session.id}::${renderKey}`;
                                                  const isDimmedAdvice = status === 'removed' || status === 'replaced-original';
                                                  const activeDraftAdvice = sessionIsEditing && !isDimmedAdvice
                                                    ? ((sessionAdviceDrafts[session.id]?.advices || []).find((entry) => getJourneyAdviceDraftKey(entry) === adviceKey) || null)
                                                    : null;
                                                  const hasAdviceBody = Boolean(String(advice.advice || '').trim());
                                                  const isAdviceExpanded = !isDimmedAdvice && Boolean(expandedJourneyAdviceCards[adviceCardKey]);
                                                  const renderAdviceCardIcon = (title) => {
                                                    const iconData = getAdviceIcon(title);
                                                    if (iconData?.type === 'image' && iconData?.value) {
                                                      return (
                                                        <Avatar
                                                          variant="rounded"
                                                          src={iconData.value}
                                                          style={{ width: 38, height: 38, backgroundColor: '#eef5fb', border: '1px solid #dce7f2' }}
                                                        />
                                                      );
                                                    }
                                                    const IconComponent = iconData?.value;
                                                    return (
                                                      <Avatar
                                                        variant="rounded"
                                                        style={{ width: 38, height: 38, backgroundColor: '#eef5fb', color: '#002D55', border: '1px solid #dce7f2' }}
                                                      >
                                                        {IconComponent ? <IconComponent style={{ fontSize: 22 }} /> : null}
                                                      </Avatar>
                                                    );
                                                  };
                                                  const renderInlineDraftAdviceActions = (draftAdvice) => {
                                                    if (!sessionIsEditing || !draftAdvice) {
                                                      return null;
                                                    }
                                                    const adviceKey = getJourneyAdviceDraftKey(draftAdvice);
                                                    const stopCardDrag = (event) => {
                                                      event.stopPropagation();
                                                    };
                                                    return (
                                                      <Box
                                                        display="flex"
                                                        justifyContent="flex-end"
                                                        alignItems="center"
                                                        style={{ gap: 'clamp(4px, 0.39vw, 6px)', flexWrap: 'wrap', marginLeft: 'auto' }}
                                                        draggable={false}
                                                        onMouseDown={stopCardDrag}
                                                        onClick={stopCardDrag}
                                                        onDragStart={(event) => {
                                                          event.preventDefault();
                                                          event.stopPropagation();
                                                        }}
                                                      >
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          className={classes.cancelButton}
                                                          style={{ minWidth: 'auto' }}
                                                          draggable={false}
                                                          onClick={() => openDraftAdviceEditDialog(session.id, draftAdvice)}
                                                        >
                                                          Edit
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          className={classes.cancelButton}
                                                          style={{ minWidth: 'auto' }}
                                                          draggable={false}
                                                          onClick={() => openAdviceLibraryDrawer({ type: 'replace', sessionId: session.id, targetAdviceKey: adviceKey })}
                                                        >
                                                          Replace
                                                        </Button>
                                                        <Button
                                                          size="small"
                                                          variant="outlined"
                                                          className={classes.cancelButton}
                                                          style={{ minWidth: 'auto' }}
                                                          draggable={false}
                                                          onClick={() => removeJourneyDraftAdvice(session.id, draftAdvice)}
                                                        >
                                                          Remove
                                                        </Button>
                                                      </Box>
                                                    );
                                                  };
                                                  const getInlineAdviceCardOptions = (draftAdvice) => {
                                                    if (!sessionIsEditing || !draftAdvice) {
                                                      return {};
                                                    }
                                                    const listIndex = Math.max(0, (draftAdvice.importance ?? (adviceIndex + 1)) - 1);
                                                    const sameSession = inlineAdviceDragSessionId === session.id;
                                                    const isDragSource = sameSession && inlineAdviceDragIndex === listIndex;
                                                    const isDragOver = sameSession && inlineAdviceDragOverIndex === listIndex && inlineAdviceDragIndex !== null && inlineAdviceDragIndex !== listIndex;
                                                    return {
                                                      draggable: true,
                                                      onDragStart: (event) => {
                                                        setInlineAdviceDragSessionId(session.id);
                                                        setInlineAdviceDragIndex(listIndex);
                                                        setInlineAdviceDragOverIndex(null);
                                                        try {
                                                          event.dataTransfer.effectAllowed = 'move';
                                                          event.dataTransfer.setData('text/plain', String(listIndex));
                                                        } catch (error) {
                                                          // noop
                                                        }
                                                      },
                                                      onDragOver: (event) => {
                                                        if (inlineAdviceDragSessionId !== session.id || inlineAdviceDragIndex === null) {
                                                          return;
                                                        }
                                                        event.preventDefault();
                                                        try {
                                                          event.dataTransfer.dropEffect = 'move';
                                                        } catch (error) {
                                                          // noop
                                                        }
                                                        if (inlineAdviceDragOverIndex !== listIndex) {
                                                          setInlineAdviceDragOverIndex(listIndex);
                                                        }
                                                      },
                                                      onDragLeave: () => {
                                                        if (inlineAdviceDragSessionId === session.id && inlineAdviceDragOverIndex === listIndex) {
                                                          setInlineAdviceDragOverIndex(null);
                                                        }
                                                      },
                                                      onDrop: (event) => {
                                                        if (inlineAdviceDragSessionId !== session.id) {
                                                          return;
                                                        }
                                                        event.preventDefault();
                                                        const from = inlineAdviceDragIndex;
                                                        setInlineAdviceDragSessionId(null);
                                                        setInlineAdviceDragIndex(null);
                                                        setInlineAdviceDragOverIndex(null);
                                                        if (from === null || from === listIndex) {
                                                          return;
                                                        }
                                                        reorderJourneyDraftAdvice(session.id, from, listIndex);
                                                      },
                                                      onDragEnd: () => {
                                                        setInlineAdviceDragSessionId(null);
                                                        setInlineAdviceDragIndex(null);
                                                        setInlineAdviceDragOverIndex(null);
                                                      },
                                                      style: {
                                                        border: isDragOver ? '2px dashed #1976d2' : '1px solid #dce7f2',
                                                        opacity: isDragSource ? 0.5 : 1,
                                                        cursor: 'grab',
                                                        userSelect: 'none',
                                                        WebkitUserSelect: 'none',
                                                      },
                                                    };
                                                  };
                                                  return (
                                                    <Box
                                                      key={renderKey}
                                                      role={hasAdviceBody && !isDimmedAdvice ? 'button' : undefined}
                                                      tabIndex={hasAdviceBody && !isDimmedAdvice ? 0 : undefined}
                                                      onClick={hasAdviceBody ? (() => {
                                                        if (isDimmedAdvice) {
                                                          return;
                                                        }
                                                        setExpandedJourneyAdviceCards((prev) => ({
                                                          ...prev,
                                                          [adviceCardKey]: !prev[adviceCardKey],
                                                        }));
                                                      }) : undefined}
                                                      onKeyDown={hasAdviceBody ? ((event) => {
                                                        if (isDimmedAdvice) {
                                                          return;
                                                        }
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                          event.preventDefault();
                                                          setExpandedJourneyAdviceCards((prev) => ({
                                                            ...prev,
                                                            [adviceCardKey]: !prev[adviceCardKey],
                                                          }));
                                                        }
                                                      }) : undefined}
                                                      style={{
                                                        borderRadius: 14,
                                                        padding: 'clamp(10px, 0.9vw, 14px)',
                                                        background: '#ffffff',
                                                        border: '1px solid #dce7f2',
                                                        boxShadow: '0 2px 8px rgba(0,45,85,0.05)',
                                                        opacity: isDimmedAdvice ? 0.45 : 1,
                                                        cursor: sessionIsEditing && activeDraftAdvice ? 'grab' : ((hasAdviceBody && !isDimmedAdvice) ? 'pointer' : 'default'),
                                                        ...(getInlineAdviceCardOptions(activeDraftAdvice).style || {}),
                                                      }}
                                                      draggable={Boolean(getInlineAdviceCardOptions(activeDraftAdvice).draggable)}
                                                      onDragStart={getInlineAdviceCardOptions(activeDraftAdvice).onDragStart}
                                                      onDragOver={getInlineAdviceCardOptions(activeDraftAdvice).onDragOver}
                                                      onDragLeave={getInlineAdviceCardOptions(activeDraftAdvice).onDragLeave}
                                                      onDrop={getInlineAdviceCardOptions(activeDraftAdvice).onDrop}
                                                      onDragEnd={getInlineAdviceCardOptions(activeDraftAdvice).onDragEnd}
                                                    >
                                                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                                        <Box display="flex" alignItems="flex-start" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flex: 1, minWidth: 160 }}>
                                                          {renderAdviceCardIcon(advice.title || `Advice ${adviceIndex + 1}`)}
                                                          <Box style={{ flex: 1, minWidth: 0 }}>
                                                          <Typography style={{ color: isDimmedAdvice ? '#888' : '#002D55', fontWeight: 700, fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)', whiteSpace: 'pre-line', wordBreak: 'break-word', textDecoration: isDimmedAdvice ? 'line-through' : 'none' }}>
                                                            {advice.title || `Advice ${adviceIndex + 1}`}
                                                          </Typography>
                                                          {status === 'replaced-original' && (
                                                            <Typography component="span" style={{ fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', color: '#c0392b', fontWeight: 700, marginTop: 4, display: 'inline-block' }}>
                                                              Replaced
                                                            </Typography>
                                                          )}
                                                          {status === 'removed' && (
                                                            <Typography component="span" style={{ fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', color: '#c0392b', fontWeight: 700, marginTop: 4, display: 'inline-block' }}>
                                                              Removed
                                                            </Typography>
                                                          )}
                                                          {hasAdviceBody && !isDimmedAdvice && (
                                                            <Typography style={{ color: '#7a8ea5', fontSize: 'clamp(0.6rem, 0.64vw, 0.75rem)', marginTop: 4, fontWeight: 600 }}>
                                                              {isAdviceExpanded ? 'Hide details' : 'Show details'}
                                                            </Typography>
                                                          )}
                                                          </Box>
                                                        </Box>
                                                        <Box display="flex" alignItems="center" style={{ gap: 'clamp(4px, 0.39vw, 6px)', flexWrap: 'wrap', marginLeft: 'auto' }}>
                                                          <Chip
                                                            label={`#${advice.importance || adviceIndex + 1}`}
                                                            size="small"
                                                            style={{ backgroundColor: '#e3eef9', color: '#002D55', fontWeight: 700, marginTop: 2 }}
                                                          />
                                                          {renderInlineDraftAdviceActions(activeDraftAdvice)}
                                                        </Box>
                                                      </Box>
                                                      {isAdviceExpanded && hasAdviceBody && (
                                                        <Typography style={{ color: isDimmedAdvice ? '#888' : '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginTop: 8, whiteSpace: 'pre-line', lineHeight: 1.45, wordBreak: 'break-word', textDecoration: isDimmedAdvice ? 'line-through' : 'none' }}>
                                                          {advice.advice}
                                                        </Typography>
                                                      )}
                                                    </Box>
                                                  );
                                                })}
                                                {sessionIsEditing && renderJourneyInlineAddPlaceholderRow([
                                                  {
                                                    label: 'Add advice',
                                                    hint: 'Open library for this phase',
                                                    onClick: () => openAdviceLibraryDrawer({ type: 'add', sessionId: session.id }),
                                                  },
                                                  {
                                                    label: 'Add custom advice',
                                                    hint: 'Create advice from scratch',
                                                    onClick: () => openCustomDraftAdviceDialog(session.id),
                                                  },
                                                ])}
                                              </Box>
                                              );
                                            })()}
                                          </Box>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </>
                              );
                            })()}
                            {/* Discreet session info above first exercise */}
                            {!Boolean(journeyAdviceVisibility[session.id]) && (
                            <TableRow style={{ background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)' }}>
                              <TableCell colSpan={responsiveColSpan(2)} style={{ border: 'none', padding: '2px 0 clamp(4px, 0.39vw, 6px) 0', background: 'none' }}>
                                <Box style={{ padding: '0 clamp(8px, 0.69vw, 12px)' }}>
                                  <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                    <Box display="flex" alignItems="center" style={{ gap: 'clamp(8px, 0.78vw, 12px)' }}>
                                      {session.phase && (
                                        <span style={{ color: '#888', fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', fontWeight: 'bold' }}>
                                          {typeof session.phase_index === 'number'
                                            ? `Phase ${session.phase_index + 1}: '${session.phase}'.`
                                            : session.phase}
                                        </span>
                                      )}
                                    </Box>
                                    {sessionIsEditing && Boolean(sessionExerciseDrafts[session.id]) && (
                                      <Box display="flex" alignItems="center" style={{ gap: 6, flexWrap: 'wrap', marginLeft: 'auto' }}>
                                        <Typography style={{ fontSize: 'clamp(0.66rem, 0.88vw, 0.82rem)', color: '#002D55', fontWeight: 600, whiteSpace: 'nowrap', marginRight: 4 }}>
                                          Apply to all:
                                        </Typography>
                                        <Select
                                          disableUnderline
                                          value={journeyBulkEditParam}
                                          onChange={(event) => {
                                            const nextParam = event.target.value;
                                            setJourneyBulkEditParam(nextParam);
                                            setJourneyBulkEditValue(journeyBulkEditParamDefaults[nextParam]);
                                          }}
                                          className={classes.orangeSelect}
                                          style={{ minWidth: 112 }}
                                          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}
                                        >
                                          <MenuItem value="sets" className={classes.menuItem}>Sets</MenuItem>
                                          <MenuItem value="repetitions" className={classes.menuItem}>Rep(s)</MenuItem>
                                          <MenuItem value="time" className={classes.menuItem}>Time (s)</MenuItem>
                                          <MenuItem value="doseType" className={classes.menuItem}>Dose Type</MenuItem>
                                          <MenuItem value="pause" className={classes.menuItem}>Pause (s)</MenuItem>
                                          <MenuItem value="frequency" className={classes.menuItem}>Freq.</MenuItem>
                                          <MenuItem value="frequencyType" className={classes.menuItem}>Schedule</MenuItem>
                                        </Select>
                                        {['sets', 'repetitions', 'time', 'pause', 'frequency'].includes(journeyBulkEditParam) ? (
                                          <input
                                            type="number"
                                            min={0}
                                            value={journeyBulkEditValue}
                                            onChange={(event) => setJourneyBulkEditValue(event.target.value)}
                                            style={{
                                              width: 'clamp(55px, 3.75vw, 72px)',
                                              background: '#fff',
                                              color: '#002D55',
                                              fontWeight: 500,
                                              fontSize: 'clamp(0.66rem, 0.88vw, 0.82rem)',
                                              border: '1px solid #ccc',
                                              borderRadius: 14,
                                              padding: '3.5px 10px',
                                              outline: 'none',
                                              cursor: 'text',
                                              fontFamily: 'inherit',
                                              transition: 'border-color 0.2s',
                                              marginRight: 6,
                                            }}
                                            onFocus={(event) => { event.target.style.borderColor = '#FF7F27'; }}
                                            onBlur={(event) => { event.target.style.borderColor = '#ccc'; }}
                                          />
                                        ) : journeyBulkEditParam === 'doseType' ? (
                                          <Select
                                            disableUnderline
                                            value={journeyBulkEditValue}
                                            onChange={(event) => setJourneyBulkEditValue(event.target.value)}
                                            className={classes.orangeSelect}
                                            style={{ minWidth: 80 }}
                                            MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}
                                          >
                                            <MenuItem value="time" className={classes.menuItem}>Time</MenuItem>
                                            <MenuItem value="repetitions" className={classes.menuItem}>Repetitions</MenuItem>
                                          </Select>
                                        ) : (
                                          <Select
                                            disableUnderline
                                            value={journeyBulkEditValue}
                                            onChange={(event) => setJourneyBulkEditValue(event.target.value)}
                                            className={classes.orangeSelect}
                                            style={{ minWidth: 90 }}
                                            MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 14, marginTop: 8 } } }}
                                          >
                                            <MenuItem value="daily" className={classes.menuItem}>Daily</MenuItem>
                                            <MenuItem value="weekly" className={classes.menuItem}>Weekly</MenuItem>
                                          </Select>
                                        )}
                                        <Tooltip title="Apply to all" arrow>
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() => applyJourneyBulkEditToSession(session.id, journeyBulkEditParam, journeyBulkEditValue)}
                                              style={{ padding: 2 }}
                                            >
                                              <CheckCircleIcon style={{ color: '#0fbb51', fontSize: 'clamp(18px, 1.6vw, 24px)' }} />
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Tooltip title="Cancel" arrow>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              setJourneyBulkEditParam('sets');
                                              setJourneyBulkEditValue(journeyBulkEditParamDefaults.sets);
                                            }}
                                            style={{ padding: 2 }}
                                          >
                                            <CloseIcon style={{ color: '#e53935', fontSize: 'clamp(18px, 1.6vw, 24px)' }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                            )}
                            {!Boolean(journeyAdviceVisibility[session.id]) && (
                            <TableRow style={{ background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)' }}>
                              <TableCell colSpan={responsiveColSpan(2)} style={{ border: 'none', padding: '0', background: 'none', margin: 'clamp(6px, 0.52vw, 8px) 0' }}>
                                <Box style={{ padding: '0 clamp(8px, 0.69vw, 12px)' }}>
                                  <span style={{ fontSize: 'clamp(0.67rem, 0.7vw, 0.8125rem)', color: '#888', display: 'flex', alignItems: 'center', gap: 'clamp(8px, 0.78vw, 12px)' }}>
                                    {session.completed_date && (
                                      <span>
                                        <span role="img" aria-label="calendar">Patient completed exercises at: 📅</span> {moment(session.completed_date).format("MMM D, YYYY [at] HH:mm")}
                                      </span>
                                    )}
                                  </span>
                                </Box>
                              </TableCell>
                            </TableRow>
                            )}
                            {!Boolean(journeyAdviceVisibility[session.id]) && (() => {
                              // === Parse note text for historical changes ===
                              // Always parse — both view mode (so replaced/removed
                              // exercises are correctly hidden / re-indexed) and
                              // editing mode (so the dimmed "Replaced" rows render).
                              // In editing mode we prefer the live draft only when a
                              // saved journal note already exists. For sessions with no
                              // saved note yet, the draft text is just a transient staging
                              // surface; parsing it as historical note text causes stale
                              // Replaced/Removed lines to keep rows dimmed after an undo.
                              let noteText = '';
                              if (sessionIsEditing) {
                                noteText = notes.length > 0
                                  ? (editNoteValue[session.id] || journalNote || '')
                                  : '';
                              } else {
                                const savedNotes = fetchedNotes[session.id] || [];
                                const savedIdx = noteIndices[session.id] || 0;
                                noteText = (savedNotes[savedIdx] && savedNotes[savedIdx].note) || '';
                              }
                              const historicalReplacements = [];
                              const historicalRemoved = [];
                              const historicalAdded = [];
                              if (noteText) {
                                const replacedPat = /Replaced \[([^\]]+)\] with \[([^\]]+)\]/g;
                                const removedPat = /Removed \[([^\]]+)\] from/g;
                                const addedPat = /Added \[([^\]]+)\](?:\s+•[^\n\r]*?)?\s+to/g;
                                let hm;
                                // Trim captured bracket contents — saved note text occasionally
                                // carries trailing whitespace inside the brackets, which would
                                // otherwise prevent exact-string matches against
                                // ex.exercise_title further down (pushing the row into the
                                // orphan path that cannot recover Pain/Difficulty).
                                while ((hm = replacedPat.exec(noteText)) !== null) historicalReplacements.push({ from: hm[1].trim(), to: hm[2].trim(), fromIdentifier: null, toIdentifier: null });
                                while ((hm = removedPat.exec(noteText)) !== null) historicalRemoved.push({ title: hm[1].trim(), identifier: null });
                                while ((hm = addedPat.exec(noteText)) !== null) historicalAdded.push({ title: hm[1].trim(), identifier: null });
                              }
                              const stagedReplacements = programChangeSummaries[session.id]?.replacements || [];
                              const stagedRemoved = programChangeSummaries[session.id]?.removed || [];
                              const stagedAddedList = programChangeSummaries[session.id]?.added || [];
                              const hiddenProgramAdjustmentIdentities = new Set(
                                Array.from(removedHistoricalLines[session.id] || [])
                                  .map((line) => getProgramAdjustmentDisplayIdentity(line))
                                  .filter(Boolean)
                              );
                              const hasReplacementIdentity = (collection, entry) => collection.some((candidate) => (
                                (entry?.fromIdentifier && candidate?.fromIdentifier && String(candidate.fromIdentifier) === String(entry.fromIdentifier))
                                || ((candidate?.from || '').toString().trim().toLowerCase() === (entry?.from || '').toString().trim().toLowerCase())
                              ));
                              const appliedAdjustmentReplacements = getSessionAdjustmentIssues(session)
                                .filter((issue) => issue.adjusted && issue.replacementIdentifier)
                                .map((issue) => {
                                  const decision = (suggestionDecisions[issue.sessionId] || {})[issue.issueKey] || {};
                                  const selectedReplacement = decision.configuredExercise?.title || issue.replacement;
                                  return {
                                    from: issue.name,
                                    to: selectedReplacement,
                                    fromIdentifier: issue.originalIdentifier || null,
                                    toIdentifier: issue.replacementIdentifier || null,
                                    nextExercise: selectedReplacement
                                      ? { title: selectedReplacement, identifier: issue.replacementIdentifier || null }
                                      : null,
                                    isSystemGenerated: Boolean(issue.isSystemGenerated),
                                  };
                                })
                                .filter((entry) => !hiddenProgramAdjustmentIdentities.has(`replaced:${(entry?.from || '').toString().trim().toLowerCase()}`));
                              const allReplacements = [
                                ...stagedReplacements,
                                ...appliedAdjustmentReplacements.filter((entry) => !hasReplacementIdentity(stagedReplacements, entry)),
                                ...historicalReplacements.filter((entry) => !hasReplacementIdentity([...stagedReplacements, ...appliedAdjustmentReplacements], entry)),
                              ];
                              const allRemoved = [...stagedRemoved, ...historicalRemoved.filter(h => !stagedRemoved.some(s => s.title === h.title))];
                              const allAdded = [...stagedAddedList, ...historicalAdded.filter(h => !stagedAddedList.some(s => s.title === h.title))];
                              const allReplacementToTitles = new Set(allReplacements.map(r => r.to));
                              const allAddedTitles = new Set(allAdded.map(a => a.title));
                              const getJourneyExerciseThumbnailUrl = (exercise) => {
                                const thumbnailUrl = exercise?.img || exercise?.picture || exercise?.image_url || '';
                                return resolveExerciseImageUrl(thumbnailUrl);
                              };
                              const renderJourneyExerciseViewCardRow = (key, content, options = {}) => (
                                <TableRow key={key} style={{ background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)' }}>
                                  <TableCell colSpan={responsiveColSpan(2)} style={{ border: 'none', padding: '0 clamp(8px, 0.69vw, 12px) clamp(6px, 0.52vw, 8px)', background: 'none' }}>
                                    <Box
                                      draggable={Boolean(options.draggable)}
                                      onDragStart={options.onDragStart}
                                      onDragOver={options.onDragOver}
                                      onDragLeave={options.onDragLeave}
                                      onDrop={options.onDrop}
                                      onDragEnd={options.onDragEnd}
                                      title={options.title}
                                      style={{
                                        borderRadius: 14,
                                        padding: 'clamp(8px, 0.78vw, 12px)',
                                        background: '#ffffff',
                                        border: '1px solid #dce7f2',
                                        boxShadow: '0 2px 8px rgba(0,45,85,0.05)',
                                        opacity: options.opacity != null ? options.opacity : (options.dimmed ? 0.45 : 1),
                                        cursor: options.draggable ? 'grab' : 'default',
                                        ...options.style,
                                      }}
                                    >
                                      {options.thumbnailUrl ? (
                                        <Box display="flex" alignItems="flex-start" style={{ gap: 'clamp(10px, 0.9vw, 14px)' }}>
                                          <Box
                                            style={{
                                              width: 'clamp(54px, 4.8vw, 72px)',
                                              height: 'clamp(54px, 4.8vw, 72px)',
                                              flexShrink: 0,
                                              borderRadius: 12,
                                              overflow: 'hidden',
                                              border: '1px solid #dce7f2',
                                              background: '#eef5fb',
                                            }}
                                          >
                                            <ExerciseImage
                                              imageUrl={options.thumbnailUrl}
                                              alt={options.thumbnailAlt || 'Exercise thumbnail'}
                                              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            />
                                          </Box>
                                          <Box style={{ flex: 1, minWidth: 0 }}>
                                            {content}
                                          </Box>
                                        </Box>
                                      ) : content}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                              const renderInlineDraftExerciseActions = (exercise) => {
                                if (!sessionIsEditing || !exercise) {
                                  return null;
                                }
                                const exerciseKey = getDraftExerciseKey(exercise);
                                const stopCardDrag = (event) => {
                                  event.stopPropagation();
                                };
                                return (
                                  <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    style={{ gap: 'clamp(4px, 0.39vw, 6px)', flexWrap: 'wrap', marginLeft: 'auto' }}
                                    draggable={false}
                                    onMouseDown={stopCardDrag}
                                    onClick={stopCardDrag}
                                    onDragStart={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                    }}
                                  >
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={classes.cancelButton}
                                      style={{ minWidth: 'auto' }}
                                      draggable={false}
                                      onClick={() => openDraftExerciseEditDialog(session.id, exercise)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={classes.cancelButton}
                                      style={{ minWidth: 'auto' }}
                                      draggable={false}
                                      onClick={() => openProgramLibraryDrawer({ type: 'program-replace', sessionId: session.id, targetExerciseKey: exerciseKey })}
                                    >
                                      Replace
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      className={classes.cancelButton}
                                      style={{ minWidth: 'auto' }}
                                      draggable={false}
                                      onClick={() => removeProgramDraftExercise(session.id, exercise)}
                                    >
                                      Remove
                                    </Button>
                                  </Box>
                                );
                              };
                              const getInlineDraftCardOptions = (exercise) => {
                                if (!sessionIsEditing || !exercise) {
                                  return {};
                                }
                                const listIndex = Math.max(0, (exercise.index ?? 1) - 1);
                                const sameSession = inlineProgramDragSessionId === session.id;
                                const isDragSource = sameSession && inlineProgramDragIndex === listIndex;
                                const isDragOver = sameSession && inlineProgramDragOverIndex === listIndex && inlineProgramDragIndex !== null && inlineProgramDragIndex !== listIndex;
                                return {
                                  draggable: true,
                                  title: 'Drag the card to reorder',
                                  onDragStart: (event) => {
                                    setInlineProgramDragSessionId(session.id);
                                    setInlineProgramDragIndex(listIndex);
                                    setInlineProgramDragOverIndex(null);
                                    try {
                                      event.dataTransfer.effectAllowed = 'move';
                                      event.dataTransfer.setData('text/plain', String(listIndex));
                                    } catch (error) {
                                      // noop
                                    }
                                  },
                                  onDragOver: (event) => {
                                    if (inlineProgramDragSessionId !== session.id || inlineProgramDragIndex === null) {
                                      return;
                                    }
                                    event.preventDefault();
                                    try {
                                      event.dataTransfer.dropEffect = 'move';
                                    } catch (error) {
                                      // noop
                                    }
                                    if (inlineProgramDragOverIndex !== listIndex) {
                                      setInlineProgramDragOverIndex(listIndex);
                                    }
                                  },
                                  onDragLeave: () => {
                                    if (inlineProgramDragSessionId === session.id && inlineProgramDragOverIndex === listIndex) {
                                      setInlineProgramDragOverIndex(null);
                                    }
                                  },
                                  onDrop: (event) => {
                                    if (inlineProgramDragSessionId !== session.id) {
                                      return;
                                    }
                                    event.preventDefault();
                                    const from = inlineProgramDragIndex;
                                    setInlineProgramDragSessionId(null);
                                    setInlineProgramDragIndex(null);
                                    setInlineProgramDragOverIndex(null);
                                    if (from === null || from === listIndex) {
                                      return;
                                    }
                                    reorderProgramDraftExercise(session.id, from, listIndex);
                                  },
                                  onDragEnd: () => {
                                    setInlineProgramDragSessionId(null);
                                    setInlineProgramDragIndex(null);
                                    setInlineProgramDragOverIndex(null);
                                  },
                                  opacity: isDragSource ? 0.5 : 1,
                                  style: {
                                    border: isDragOver ? '2px dashed #1976d2' : '1px solid #dce7f2',
                                  },
                                };
                              };

                              // === Detect consolidated chains: staged "Replaced [A] with [C]"
                              // absorbs historical "Replaced [A] with [B]". The intermediate
                              // exercise B (historical.to) should NOT render its own feedback
                              // row — it is implicit to the chain. The consolidated [A→C] is
                              // shown via the orphan-replacement-rows section instead.
                              const lcEq = (a, b) => (a || '').toString().trim().toLowerCase() === (b || '').toString().trim().toLowerCase();
                              const consolidatedIntermediateTitles = new Set();
                              historicalReplacements.forEach(h => {
                                const consumed = stagedReplacements.find(s => lcEq(s.from, h.from) && !lcEq(s.to, h.to));
                                if (consumed) consolidatedIntermediateTitles.add((h.to || '').trim());
                              });

                              // === exercise_feedback rows ===
                              // Sort feedback rows by the current program
                              // exercise index so reorder edits saved via the
                              // Journal program editor (or selectedPatientProgram)
                              // are reflected in view mode. Falls back to the
                              // feedback array order when no matching draft
                              // exercise is found (e.g. removed exercises).
                              const draftExercisesForSort = sessionExerciseDrafts[session.id]?.exercises || [];
                              const sessionPrescriptionExercises = draftExercisesForSort.length > 0
                                ? draftExercisesForSort
                                : (idx === 0 && String(session.phase_id || '') === String(selectedPhaseId || '')
                                  ? exercises
                                  : []);
                              const hasSavedJournalNote = (fetchedNotes[session.id] || []).length > 0;
                              const shouldRenderEditingCurrentProgram = sessionIsEditing
                                && !sessionShowsHistoricalVersion
                                && hasSavedJournalNote
                                && draftExercisesForSort.length > 0;
                              const shouldRenderReadOnlyCurrentProgram = !sessionIsEditing
                                && !sessionShowsHistoricalVersion
                                && hasSavedJournalNote
                                && draftExercisesForSort.length > 0;
                              const draftIndexById = new Map();
                              const draftIndexByTitleNorm = new Map();
                              draftExercisesForSort.forEach(de => {
                                if (de.identifier) draftIndexById.set(de.identifier, de.index ?? 0);
                                const t = (de.title || '').toString().trim().toLowerCase();
                                if (t) draftIndexByTitleNorm.set(t, de.index ?? 0);
                              });
                              const readOnlyCurrentProgramRows = shouldRenderReadOnlyCurrentProgram
                                ? draftExercisesForSort
                                  .slice()
                                  .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
                                  .map((exercise, exerciseIdx) => {
                                    const exerciseTitleNorm = (exercise.title || '').toString().trim().toLowerCase();
                                    const matchingFeedback = session.exercise_feedback.find((feedback) => (
                                      exercise.identifier && String(feedback?.exercise_id || '') === String(exercise.identifier)
                                    ) || (
                                      exerciseTitleNorm && (feedback?.exercise_title || '').toString().trim().toLowerCase() === exerciseTitleNorm
                                    )) || null;
                                    const completedBits = [];
                                    if (matchingFeedback?.completed_sets != null && matchingFeedback?.prescribed_sets != null && matchingFeedback.completed_sets < matchingFeedback.prescribed_sets) {
                                      completedBits.push(`${matchingFeedback.completed_sets}/${matchingFeedback.prescribed_sets} sets`);
                                    }
                                    if (matchingFeedback?.completed_repetitions != null && matchingFeedback?.prescribed_repetitions != null && matchingFeedback.completed_repetitions < matchingFeedback.prescribed_repetitions) {
                                      completedBits.push(`${matchingFeedback.completed_repetitions}/${matchingFeedback.prescribed_repetitions} reps`);
                                    }
                                    if (matchingFeedback?.completed_time != null && matchingFeedback?.prescribed_time != null && matchingFeedback.completed_time < matchingFeedback.prescribed_time) {
                                      completedBits.push(`${matchingFeedback.completed_time}/${matchingFeedback.prescribed_time}s`);
                                    }

                                    return {
                                      sortIndex: exercise.index ?? exerciseIdx,
                                      node: renderJourneyExerciseViewCardRow(
                                        `view-current-program-${session.id}-${getDraftExerciseKey(exercise) || exerciseIdx}`,
                                        <>
                                          <Chip
                                            label={exercise.title || exercise.identifier || '-'}
                                            style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                            size="small"
                                          />
                                          <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                              {formatSharedExercisePrescriptionSummary(exercise)}
                                            </Typography>
                                            {completedBits.length > 0 && (
                                              <Typography style={{ color: '#c0392b', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', fontWeight: 600 }}>
                                                {`(completed: ${completedBits.join(', ')})`}
                                              </Typography>
                                            )}
                                          </div>
                                          {(matchingFeedback?.painLevel || matchingFeedback?.difficulty) && (
                                            <div style={{ marginTop: 'clamp(4px, 0.39vw, 6px)', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                              <span style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
                                                <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)', paddingLeft: 'clamp(8px, 0.65vw, 10px)' }}>Pain:</span>
                                                <Chip
                                                  label={matchingFeedback.painLevel ?? '-'}
                                                  style={{
                                                    marginRight: 'clamp(6px, 0.52vw, 8px)', marginBottom: 2, height: 'clamp(14px, 1.17vw, 18px)',
                                                    backgroundColor:
                                                      matchingFeedback.painLevel === undefined || matchingFeedback.painLevel === null ? 'rgba(255,243,224,0.75)'
                                                      : matchingFeedback.painLevel < 35 ? 'rgba(67,234,122,0.75)'
                                                      : matchingFeedback.painLevel >= 35 && matchingFeedback.painLevel <= 49 ? 'rgba(255,224,102,0.75)'
                                                      : matchingFeedback.painLevel >= 50 && matchingFeedback.painLevel <= 60 ? 'rgba(255,179,71,0.75)'
                                                      : matchingFeedback.painLevel > 70 ? 'rgba(255,77,79,0.75)'
                                                      : 'rgba(255,243,224,0.75)',
                                                    color: '#002D55',
                                                    fontWeight: 'bold'
                                                  }}
                                                  size="small"
                                                />
                                              </span>
                                              <span style={{ color: '#002D55', fontWeight: 'bold', margin: '0 clamp(6px, 0.52vw, 8px)' }}>|</span>
                                              <span style={{ display: 'flex', alignItems: 'center', marginRight: 'clamp(8px, 0.78vw, 12px)' }}>
                                                <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)' }}>Difficulty:</span>
                                                {getDifficultyReasonKey(matchingFeedback.difficulty) === 'good' ? (
                                                  <Chip
                                                    icon={<CheckCircleIcon style={{ color: '#43ea7a', fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />}
                                                    label={'Good.'}
                                                    style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(18px, 1.43vw, 22px)', backgroundColor: 'rgba(67,234,122,0)', color: '#002D55', fontWeight: 'bold' }}
                                                    size="small"
                                                  />
                                                ) : (
                                                  <Chip
                                                    label={getDifficultyDisplayLabel(matchingFeedback.difficulty)}
                                                    style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(16px, 1.3vw, 20px)', backgroundColor: 'rgba(255,179,71,0.75)', color: '#002D55', fontWeight: 'bold' }}
                                                    size="small"
                                                  />
                                                )}
                                              </span>
                                            </div>
                                          )}
                                        </>,
                                        {
                                          thumbnailUrl: getJourneyExerciseThumbnailUrl(exercise),
                                          thumbnailAlt: exercise.title || exercise.identifier || 'Exercise thumbnail',
                                        }
                                      ),
                                    };
                                  })
                                : null;
                              const editingCurrentProgramRows = shouldRenderEditingCurrentProgram
                                ? draftExercisesForSort
                                  .slice()
                                  .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
                                  .map((exercise, exerciseIdx) => {
                                    const exerciseTitleNorm = (exercise.title || '').toString().trim().toLowerCase();
                                    const matchingFeedback = session.exercise_feedback.find((feedback) => (
                                      exercise.identifier && String(feedback?.exercise_id || '') === String(exercise.identifier)
                                    ) || (
                                      exerciseTitleNorm && (feedback?.exercise_title || '').toString().trim().toLowerCase() === exerciseTitleNorm
                                    )) || null;
                                    const completedBits = [];
                                    if (matchingFeedback?.completed_sets != null && matchingFeedback?.prescribed_sets != null && matchingFeedback.completed_sets < matchingFeedback.prescribed_sets) {
                                      completedBits.push(`${matchingFeedback.completed_sets}/${matchingFeedback.prescribed_sets} sets`);
                                    }
                                    if (matchingFeedback?.completed_repetitions != null && matchingFeedback?.prescribed_repetitions != null && matchingFeedback.completed_repetitions < matchingFeedback.prescribed_repetitions) {
                                      completedBits.push(`${matchingFeedback.completed_repetitions}/${matchingFeedback.prescribed_repetitions} reps`);
                                    }
                                    if (matchingFeedback?.completed_time != null && matchingFeedback?.prescribed_time != null && matchingFeedback.completed_time < matchingFeedback.prescribed_time) {
                                      completedBits.push(`${matchingFeedback.completed_time}/${matchingFeedback.prescribed_time}s`);
                                    }

                                    return {
                                      sortIndex: exercise.index ?? exerciseIdx,
                                      node: renderJourneyExerciseViewCardRow(
                                        `edit-current-program-${session.id}-${getDraftExerciseKey(exercise) || exerciseIdx}`,
                                        <>
                                          <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                            <Box style={{ flex: 1, minWidth: 0 }}>
                                              <Chip
                                                label={exercise.title || exercise.identifier || '-'}
                                                style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                                size="small"
                                              />
                                            </Box>
                                            {renderInlineDraftExerciseActions(exercise)}
                                          </Box>
                                          <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                              {formatSharedExercisePrescriptionSummary(exercise)}
                                            </Typography>
                                            {completedBits.length > 0 && (
                                              <Typography style={{ color: '#c0392b', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', fontWeight: 600 }}>
                                                {`(completed: ${completedBits.join(', ')})`}
                                              </Typography>
                                            )}
                                          </div>
                                          {(matchingFeedback?.painLevel || matchingFeedback?.difficulty) && (
                                            <div style={{ marginTop: 'clamp(4px, 0.39vw, 6px)', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                              <span style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
                                                <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)', paddingLeft: 'clamp(8px, 0.65vw, 10px)' }}>Pain:</span>
                                                <Chip
                                                  label={matchingFeedback.painLevel ?? '-'}
                                                  style={{
                                                    marginRight: 'clamp(6px, 0.52vw, 8px)', marginBottom: 2, height: 'clamp(14px, 1.17vw, 18px)',
                                                    backgroundColor:
                                                      matchingFeedback.painLevel === undefined || matchingFeedback.painLevel === null ? 'rgba(255,243,224,0.75)'
                                                      : matchingFeedback.painLevel < 35 ? 'rgba(67,234,122,0.75)'
                                                      : matchingFeedback.painLevel >= 35 && matchingFeedback.painLevel <= 49 ? 'rgba(255,224,102,0.75)'
                                                      : matchingFeedback.painLevel >= 50 && matchingFeedback.painLevel <= 60 ? 'rgba(255,179,71,0.75)'
                                                      : matchingFeedback.painLevel > 70 ? 'rgba(255,77,79,0.75)'
                                                      : 'rgba(255,243,224,0.75)',
                                                    color: '#002D55',
                                                    fontWeight: 'bold'
                                                  }}
                                                  size="small"
                                                />
                                              </span>
                                              <span style={{ color: '#002D55', fontWeight: 'bold', margin: '0 clamp(6px, 0.52vw, 8px)' }}>|</span>
                                              <span style={{ display: 'flex', alignItems: 'center', marginRight: 'clamp(8px, 0.78vw, 12px)' }}>
                                                <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)' }}>Difficulty:</span>
                                                {getDifficultyReasonKey(matchingFeedback.difficulty) === 'good' ? (
                                                  <Chip
                                                    icon={<CheckCircleIcon style={{ color: '#43ea7a', fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />}
                                                    label={'Good.'}
                                                    style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(18px, 1.43vw, 22px)', backgroundColor: 'rgba(67,234,122,0)', color: '#002D55', fontWeight: 'bold' }}
                                                    size="small"
                                                  />
                                                ) : (
                                                  <Chip
                                                    label={getDifficultyDisplayLabel(matchingFeedback.difficulty)}
                                                    style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(16px, 1.3vw, 20px)', backgroundColor: 'rgba(255,179,71,0.75)', color: '#002D55', fontWeight: 'bold' }}
                                                    size="small"
                                                  />
                                                )}
                                              </span>
                                            </div>
                                          )}
                                        </>,
                                        {
                                          ...getInlineDraftCardOptions(exercise),
                                          thumbnailUrl: getJourneyExerciseThumbnailUrl(exercise),
                                          thumbnailAlt: exercise.title || exercise.identifier || 'Exercise thumbnail',
                                        }
                                      ),
                                    };
                                  })
                                : null;
                              const sortedFeedback = session.exercise_feedback.slice().map((ex, originalIdx) => {
                                const titleNorm = (ex.exercise_title || '').toString().trim().toLowerCase();
                                // If this feedback row was replaced, the original
                                // exercise has been soft-deleted and is no longer
                                // in the active draft. Without special handling
                                // it would fall through to the (1000 + originalIdx)
                                // tail and the visible replacement chip would
                                // cluster at the bottom regardless of where the
                                // replacement now sits in the program index. Use
                                // the replacement's draft index instead so the
                                // visible slot follows reorder edits.
                                const repForSort = allReplacements.find(
                                  rep => (rep.fromIdentifier && rep.fromIdentifier === ex.exercise_id)
                                    || (rep.from || '').toString().trim().toLowerCase() === titleNorm
                                );
                                let lookedUp;
                                if (repForSort) {
                                  const toNorm = (repForSort.to || '').toString().trim().toLowerCase();
                                  const byId = repForSort.toIdentifier ? draftIndexById.get(repForSort.toIdentifier) : undefined;
                                  const byTitle = draftIndexByTitleNorm.get(toNorm);
                                  lookedUp = byId != null ? byId : byTitle;
                                } else {
                                  lookedUp = draftIndexById.get(ex.exercise_id);
                                }
                                const fallback = draftIndexByTitleNorm.get(titleNorm);
                                const sortIndex = (lookedUp != null ? lookedUp : (fallback != null ? fallback : (1000 + originalIdx)));
                                return { ex, originalIdx, sortIndex };
                              }).sort((a, b) => a.sortIndex - b.sortIndex || a.originalIdx - b.originalIdx);

                              // Match historical/staged change titles to feedback rows
                              // case-insensitively and ignoring leading/trailing whitespace,
                              // so a saved-note bracket like "[Elastic Band Shoulder A-Pulls]"
                              // still classifies the corresponding feedback row as
                              // Replaced/Removed (dimmed) — which is the path that renders
                              // Pain/Difficulty for the original exercise.
                              const feedbackRows = sortedFeedback.map(({ ex, originalIdx: exIdx, sortIndex: rowSortIndex }) => {
                              const exTitleNorm = (ex.exercise_title || '').toString().trim().toLowerCase();
                              if (consolidatedIntermediateTitles.has((ex.exercise_title || '').trim())) {
                                return null;
                              }
                              const activeDraftExercise = (sessionExerciseDrafts[session.id]?.exercises || []).find(
                                e => (ex.exercise_id && e.identifier === ex.exercise_id)
                                  || (e.title || '').toString().trim().toLowerCase() === exTitleNorm
                              ) || null;
                              const isReplaced = allReplacements.some(
                                rep => (rep.fromIdentifier && rep.fromIdentifier === ex.exercise_id)
                                  || (rep.from || '').toString().trim().toLowerCase() === exTitleNorm
                              );
                              const isRemoved = !isReplaced && !activeDraftExercise && allRemoved.some(
                                r => (r.identifier && r.identifier === ex.exercise_id)
                                  || (r.title || '').toString().trim().toLowerCase() === exTitleNorm
                              );
                              // In view mode, replaced/removed exercises should
                              // not appear in their dimmed form (they are no
                              // longer part of the active program). For
                              // "Removed" rows, hide the slot entirely. For
                              // "Replaced" rows, render the replacement
                              // exercise in the original's position so it
                              // inherits the same index in the visible list.
                              if (!sessionIsEditing && !sessionShowsHistoricalVersion && isRemoved) {
                                return null;
                              }
                              if (!sessionIsEditing && !sessionShowsHistoricalVersion && isReplaced) {
                                const repEntryView = allReplacements.find(
                                  rep => (rep.fromIdentifier && rep.fromIdentifier === ex.exercise_id)
                                    || (rep.from || '').toString().trim().toLowerCase() === exTitleNorm
                                );
                                if (!repEntryView) return null;
                                const replacementDraftExView = (sessionExerciseDrafts[session.id]?.exercises || []).find(
                                  e => (repEntryView.toIdentifier && e.identifier === repEntryView.toIdentifier) || e.title === repEntryView.to
                                );
                                return {
                                  sortIndex: rowSortIndex,
                                  node: renderJourneyExerciseViewCardRow(
                                    `view-rep-${session.id}-${exIdx}`,
                                    <>
                                      <Chip
                                        label={repEntryView.to}
                                        style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                        size="small"
                                      />
                                      {replacementDraftExView && (
                                        <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                          <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                            {` • ${replacementDraftExView.sets ?? 0} sets • ${replacementDraftExView.doseType === 'repetitions' || ((replacementDraftExView.repetitions ?? 0) > 0 && (replacementDraftExView.time ?? 0) === 0) ? `${replacementDraftExView.repetitions ?? 0} reps` : `${replacementDraftExView.time ?? 0}s`} • pause ${replacementDraftExView.pause ?? 0}s • ${formatExerciseScheduleLabel(replacementDraftExView)}`}
                                          </Typography>
                                        </div>
                                      )}
                                    </>,
                                    {
                                      thumbnailUrl: getJourneyExerciseThumbnailUrl(replacementDraftExView),
                                      thumbnailAlt: repEntryView.to || 'Exercise thumbnail',
                                    }
                                  ),
                                };
                              }
                              const isDimmed = isReplaced || isRemoved;
                              const repEntry = isReplaced ? allReplacements.find(
                                rep => (rep.fromIdentifier && rep.fromIdentifier === ex.exercise_id)
                                  || (rep.from || '').toString().trim().toLowerCase() === exTitleNorm
                              ) : null;
                              const replacementDraftEx = repEntry ? (sessionExerciseDrafts[session.id]?.exercises || []).find(
                                e => (repEntry.toIdentifier && e.identifier === repEntry.toIdentifier) || e.title === repEntry.to
                              ) : null;
                              if (!sessionIsEditing && !sessionShowsHistoricalVersion) {
                                return {
                                  sortIndex: rowSortIndex,
                                  node: renderJourneyExerciseViewCardRow(
                                    `view-exercise-${session.id}-${exIdx}`,
                                    <>
                                      <Chip
                                        label={ex.exercise_title ? ex.exercise_title : (ex.exercise_id || "-")}
                                        style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                        size="small"
                                      />
                                      {ex.note && ex.note.trim() !== "" ? (
                                        <span style={{ marginLeft: 'clamp(6px, 0.52vw, 8px)', color: '#002D55' }}>{ex.note}</span>
                                      ) : null}
                                      {(() => {
                                        const draftEx = activeDraftExercise;
                                        const sets = draftEx?.sets ?? ex.prescribed_sets ?? 0;
                                        const reps = draftEx?.repetitions ?? ex.prescribed_repetitions ?? 0;
                                        const time = draftEx?.time ?? ex.prescribed_time ?? 0;
                                        const pause = draftEx?.pause ?? ex.prescribed_pause ?? 0;
                                        const hasAnySnapshot = ex.prescribed_sets != null || ex.prescribed_repetitions != null || ex.prescribed_time != null || ex.prescribed_pause != null || ex.prescribed_frequency != null;
                                        if (!hasAnySnapshot && !draftEx) return null;
                                        const useReps = (draftEx?.doseType === 'repetitions') || (reps > 0 && time === 0);
                                        const scheduleSrc = draftEx
                                          ? draftEx
                                          : (ex.prescribed_frequency != null || ex.prescribed_frequency_type != null)
                                            ? { frequencyType: ex.prescribed_frequency_type, frequency: ex.prescribed_frequency, daysInbetween: ex.prescribed_days_inbetween }
                                            : null;
                                        const completedBits = [];
                                        if (ex.completed_sets != null && ex.prescribed_sets != null && ex.completed_sets < ex.prescribed_sets) {
                                          completedBits.push(`${ex.completed_sets}/${ex.prescribed_sets} sets`);
                                        }
                                        if (ex.completed_repetitions != null && ex.prescribed_repetitions != null && ex.completed_repetitions < ex.prescribed_repetitions) {
                                          completedBits.push(`${ex.completed_repetitions}/${ex.prescribed_repetitions} reps`);
                                        }
                                        if (ex.completed_time != null && ex.prescribed_time != null && ex.completed_time < ex.prescribed_time) {
                                          completedBits.push(`${ex.completed_time}/${ex.prescribed_time}s`);
                                        }
                                        return (
                                          <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                              {` • ${sets} sets • ${useReps ? `${reps} reps` : `${time}s`} • pause ${pause}s • ${formatExerciseScheduleLabel(scheduleSrc || {})}`}
                                            </Typography>
                                            {completedBits.length > 0 && (
                                              <Typography style={{ color: '#c0392b', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', fontWeight: 600 }}>
                                                {`(completed: ${completedBits.join(', ')})`}
                                              </Typography>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {(ex.painLevel || ex.difficulty) && (
                                        <div style={{ marginTop: 'clamp(4px, 0.39vw, 6px)', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                          <span style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
                                            <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)', paddingLeft: 'clamp(8px, 0.65vw, 10px)' }}>Pain:</span>
                                            <Chip
                                              label={ex.painLevel ?? "-"}
                                              style={{
                                                marginRight: 'clamp(6px, 0.52vw, 8px)', marginBottom: 2, height: 'clamp(14px, 1.17vw, 18px)',
                                                backgroundColor:
                                                  ex.painLevel === undefined || ex.painLevel === null ? 'rgba(255,243,224,0.75)'
                                                  : ex.painLevel < 35 ? 'rgba(67,234,122,0.75)'
                                                  : ex.painLevel >= 35 && ex.painLevel <= 49 ? 'rgba(255,224,102,0.75)'
                                                  : ex.painLevel >= 50 && ex.painLevel <= 60 ? 'rgba(255,179,71,0.75)'
                                                  : ex.painLevel > 70 ? 'rgba(255,77,79,0.75)'
                                                  : 'rgba(255,243,224,0.75)',
                                                color: '#002D55',
                                                fontWeight: 'bold'
                                              }}
                                              size="small"
                                            />
                                          </span>
                                          <span style={{ color: '#002D55', fontWeight: 'bold', margin: '0 clamp(6px, 0.52vw, 8px)' }}>|</span>
                                          <span style={{ display: 'flex', alignItems: 'center', marginRight: 'clamp(8px, 0.78vw, 12px)' }}>
                                            <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)' }}>Difficulty:</span>
                                            {getDifficultyReasonKey(ex.difficulty) === 'good' ? (
                                              <Chip
                                                icon={<CheckCircleIcon style={{ color: '#43ea7a', fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />}
                                                label={"Good."}
                                                style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(18px, 1.43vw, 22px)', backgroundColor: 'rgba(67,234,122,0)', color: '#002D55', fontWeight: 'bold' }}
                                                size="small"
                                              />
                                            ) : (
                                              <Chip
                                                label={getDifficultyDisplayLabel(ex.difficulty)}
                                                style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(16px, 1.3vw, 20px)', backgroundColor: 'rgba(255,179,71,0.75)', color: '#002D55', fontWeight: 'bold' }}
                                                size="small"
                                              />
                                            )}
                                          </span>
                                        </div>
                                      )}
                                    </>,
                                    {
                                      thumbnailUrl: getJourneyExerciseThumbnailUrl(activeDraftExercise),
                                      thumbnailAlt: ex.exercise_title ? ex.exercise_title : (ex.exercise_id || 'Exercise thumbnail'),
                                    }
                                  ),
                                };
                              }

                              return {
                                sortIndex: rowSortIndex,
                                node: (
                                  <React.Fragment key={ex.id || exIdx}>
                                    {renderJourneyExerciseViewCardRow(
                                      `edit-exercise-${session.id}-${exIdx}`,
                                      <>
                                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                          <Box style={{ flex: 1, minWidth: 0 }}>
                                            <Chip
                                              label={ex.exercise_title ? ex.exercise_title : (ex.exercise_id || "-")}
                                              style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: isDimmed ? '#e0e0e0' : '#e0f7fa', color: isDimmed ? '#888' : '#002D55', fontWeight: 'bold', textDecoration: isDimmed ? 'line-through' : 'none' }}
                                              size="small"
                                            />
                                            {isReplaced && (
                                              <Typography component="span" style={{ fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', color: '#c0392b', fontWeight: 700, marginLeft: 4 }}>Replaced</Typography>
                                            )}
                                            {isRemoved && (
                                              <Typography component="span" style={{ fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', color: '#c0392b', fontWeight: 700, marginLeft: 4 }}>Removed</Typography>
                                            )}
                                            {ex.note && ex.note.trim() !== "" ? (
                                              <span style={{ marginLeft: 'clamp(6px, 0.52vw, 8px)', color: '#002D55' }}>{ex.note}</span>
                                            ) : null}
                                          </Box>
                                          {renderInlineDraftExerciseActions(activeDraftExercise)}
                                        </Box>
                                        {!isDimmed && (() => {
                                            const draftEx = activeDraftExercise;
                                          const sets = draftEx?.sets ?? ex.prescribed_sets ?? 0;
                                          const reps = draftEx?.repetitions ?? ex.prescribed_repetitions ?? 0;
                                          const time = draftEx?.time ?? ex.prescribed_time ?? 0;
                                          const pause = draftEx?.pause ?? ex.prescribed_pause ?? 0;
                                          const hasAnySnapshot = ex.prescribed_sets != null || ex.prescribed_repetitions != null || ex.prescribed_time != null || ex.prescribed_pause != null || ex.prescribed_frequency != null;
                                          if (!hasAnySnapshot && !draftEx) return null;
                                          const useReps = (draftEx?.doseType === 'repetitions') || (reps > 0 && time === 0);
                                          const scheduleSrc = draftEx
                                            ? draftEx
                                            : (ex.prescribed_frequency != null || ex.prescribed_frequency_type != null)
                                              ? { frequencyType: ex.prescribed_frequency_type, frequency: ex.prescribed_frequency, daysInbetween: ex.prescribed_days_inbetween }
                                              : null;
                                          const completedBits = [];
                                          if (ex.completed_sets != null && ex.prescribed_sets != null && ex.completed_sets < ex.prescribed_sets) {
                                            completedBits.push(`${ex.completed_sets}/${ex.prescribed_sets} sets`);
                                          }
                                          if (ex.completed_repetitions != null && ex.prescribed_repetitions != null && ex.completed_repetitions < ex.prescribed_repetitions) {
                                            completedBits.push(`${ex.completed_repetitions}/${ex.prescribed_repetitions} reps`);
                                          }
                                          if (ex.completed_time != null && ex.prescribed_time != null && ex.completed_time < ex.prescribed_time) {
                                            completedBits.push(`${ex.completed_time}/${ex.prescribed_time}s`);
                                          }
                                          return (
                                            <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                              <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                                {` • ${sets} sets • ${useReps ? `${reps} reps` : `${time}s`} • pause ${pause}s • ${formatExerciseScheduleLabel(scheduleSrc || {})}`}
                                              </Typography>
                                              {completedBits.length > 0 && (
                                                <Typography style={{ color: '#c0392b', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', fontWeight: 600 }}>
                                                  {`(completed: ${completedBits.join(', ')})`}
                                                </Typography>
                                              )}
                                            </div>
                                          );
                                        })()}
                                        {(ex.painLevel || ex.difficulty) && (
                                          <div style={{ marginTop: 'clamp(4px, 0.39vw, 6px)', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
                                              <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)', paddingLeft: 'clamp(8px, 0.65vw, 10px)' }}>Pain:</span>
                                              <Chip
                                                label={ex.painLevel ?? "-"}
                                                style={{
                                                  marginRight: 'clamp(6px, 0.52vw, 8px)', marginBottom: 2, height: 'clamp(14px, 1.17vw, 18px)',
                                                  backgroundColor:
                                                    ex.painLevel === undefined || ex.painLevel === null ? 'rgba(255,243,224,0.75)'
                                                    : ex.painLevel < 35 ? 'rgba(67,234,122,0.75)'
                                                    : ex.painLevel >= 35 && ex.painLevel <= 49 ? 'rgba(255,224,102,0.75)'
                                                    : ex.painLevel >= 50 && ex.painLevel <= 60 ? 'rgba(255,179,71,0.75)'
                                                    : ex.painLevel > 70 ? 'rgba(255,77,79,0.75)'
                                                    : 'rgba(255,243,224,0.75)',
                                                  color: '#002D55',
                                                  fontWeight: 'bold'
                                                }}
                                                size="small"
                                              />
                                            </span>
                                            <span style={{ color: '#002D55', fontWeight: 'bold', margin: '0 clamp(6px, 0.52vw, 8px)' }}>|</span>
                                            <span style={{ display: 'flex', alignItems: 'center', marginRight: 'clamp(8px, 0.78vw, 12px)' }}>
                                              <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)' }}>Difficulty:</span>
                                              {getDifficultyReasonKey(ex.difficulty) === 'good' ? (
                                                <Chip
                                                  icon={<CheckCircleIcon style={{ color: '#43ea7a', fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />}
                                                  label={"Good."}
                                                  style={{
                                                    marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(18px, 1.43vw, 22px)',
                                                    backgroundColor: 'rgba(67,234,122,0)',
                                                    color: '#002D55',
                                                    fontWeight: 'bold'
                                                  }}
                                                  size="small"
                                                />
                                              ) : (
                                                <Chip
                                                  label={getDifficultyDisplayLabel(ex.difficulty)}
                                                  style={{
                                                    marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(16px, 1.3vw, 20px)',
                                                    backgroundColor: 'rgba(255,179,71,0.75)',
                                                    color: '#002D55',
                                                    fontWeight: 'bold'
                                                  }}
                                                  size="small"
                                                />
                                              )}
                                            </span>
                                          </div>
                                        )}
                                      </>,
                                      {
                                        dimmed: isDimmed,
                                        ...getInlineDraftCardOptions(activeDraftExercise),
                                        thumbnailUrl: getJourneyExerciseThumbnailUrl(activeDraftExercise),
                                        thumbnailAlt: ex.exercise_title ? ex.exercise_title : (ex.exercise_id || 'Exercise thumbnail'),
                                      }
                                    )}
                                    {isReplaced && repEntry && renderJourneyExerciseViewCardRow(
                                      `inline-rep-${session.id}-${exIdx}`,
                                      <>
                                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                          <Box style={{ flex: 1, minWidth: 0 }}>
                                            <Chip
                                              label={repEntry.to}
                                              style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                              size="small"
                                            />
                                          </Box>
                                          {renderInlineDraftExerciseActions(replacementDraftEx)}
                                        </Box>
                                        {replacementDraftEx && (
                                          <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                              {` • ${replacementDraftEx.sets ?? 0} sets • ${replacementDraftEx.doseType === 'repetitions' || ((replacementDraftEx.repetitions ?? 0) > 0 && (replacementDraftEx.time ?? 0) === 0) ? `${replacementDraftEx.repetitions ?? 0} reps` : `${replacementDraftEx.time ?? 0}s`} • pause ${replacementDraftEx.pause ?? 0}s • ${formatExerciseScheduleLabel(replacementDraftEx)}`}
                                            </Typography>
                                          </div>
                                        )}
                                        </>,
                                        {
                                          ...getInlineDraftCardOptions(replacementDraftEx),
                                          thumbnailUrl: getJourneyExerciseThumbnailUrl(replacementDraftEx),
                                          thumbnailAlt: repEntry.to || 'Exercise thumbnail',
                                        }
                                    )}
                                  </React.Fragment>
                              ),
                              };
                            });

                              // === draft-only rows (idx === 0): exercises in draft with no patient feedback, not already shown ===
                              let draftOnlyRows = null;
                              if (idx === 0 && !sessionShowsHistoricalVersion) {
                                const feedbackIds = new Set(session.exercise_feedback.map(ef => ef.exercise_id).filter(Boolean));
                                const feedbackTitles = new Set(session.exercise_feedback.map(ef => ef.exercise_title).filter(Boolean));
                                const alreadyShown = new Set([...allAddedTitles, ...allReplacementToTitles]);
                                const newExercises = sessionPrescriptionExercises.filter(e =>
                                  !feedbackIds.has(e.identifier) &&
                                  !feedbackTitles.has(e.title) &&
                                  !alreadyShown.has(e.title)
                                );
                                if (newExercises.length) {
                                  draftOnlyRows = newExercises.map((e, i) => ({
                                    sortIndex: e.index ?? (2000 + i),
                                    node: renderJourneyExerciseViewCardRow(
                                      `draft-only-${session.id}-${i}`,
                                      <>
                                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                          <Box style={{ flex: 1, minWidth: 0 }}>
                                            <Chip
                                              label={e.title}
                                              style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                              size="small"
                                            />
                                          </Box>
                                          {renderInlineDraftExerciseActions(e)}
                                        </Box>
                                        <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                          <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                            {` • ${e.sets ?? 0} sets • ${e.doseType === 'repetitions' || ((e.repetitions ?? 0) > 0 && (e.time ?? 0) === 0) ? `${e.repetitions ?? 0} reps` : `${e.time ?? 0}s`} • pause ${e.pause ?? 0}s • ${formatExerciseScheduleLabel(e)}`}
                                          </Typography>
                                        </div>
                                      </>,
                                      {
                                        ...getInlineDraftCardOptions(e),
                                        thumbnailUrl: getJourneyExerciseThumbnailUrl(e),
                                        thumbnailAlt: e.title || e.identifier || 'Exercise thumbnail',
                                      }
                                    ),
                                  }));
                                }
                              }

                              // === added rows (staged + historical) ===
                              const addedRows = allAdded.map((ex, i) => {
                                const draftEx = (sessionExerciseDrafts[session.id]?.exercises || []).find(
                                  e => e.identifier === ex.identifier || e.title === ex.title
                                );
                                return {
                                  sortIndex: draftEx?.index ?? (3000 + i),
                                  node: renderJourneyExerciseViewCardRow(
                                    `staged-added-${session.id}-${i}`,
                                    <>
                                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                        <Box style={{ flex: 1, minWidth: 0 }}>
                                          <Chip
                                            label={ex.title}
                                            style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                            size="small"
                                          />
                                        </Box>
                                        {renderInlineDraftExerciseActions(draftEx)}
                                      </Box>
                                      {draftEx && (
                                        <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                          <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                            {` • ${draftEx.sets ?? 0} sets • ${draftEx.doseType === 'repetitions' || ((draftEx.repetitions ?? 0) > 0 && (draftEx.time ?? 0) === 0) ? `${draftEx.repetitions ?? 0} reps` : `${draftEx.time ?? 0}s`} • pause ${draftEx.pause ?? 0}s • ${formatExerciseScheduleLabel(draftEx)}`}
                                          </Typography>
                                        </div>
                                      )}
                                    </>,
                                    {
                                      ...getInlineDraftCardOptions(draftEx),
                                      thumbnailUrl: getJourneyExerciseThumbnailUrl(draftEx),
                                      thumbnailAlt: ex.title || ex.identifier || 'Exercise thumbnail',
                                    }
                                  ),
                                };
                              });

                              // === orphan replacement rows: replacements whose "from" had no patient feedback row.
                              // The original would never render dimmed (no feedback to attach to) and the inline
                              // replacement-after-row also wouldn't render. Show both rows here so the user still
                              // sees the original (dimmed/strikethrough/Replaced) followed by the new exercise.
                              const feedbackIdSet = new Set(session.exercise_feedback.map(ef => ef.exercise_id).filter(Boolean));
                              // Normalize titles so the orphan filter agrees with the
                              // dimmed-row classification above; otherwise a row that the
                              // dimmed path correctly recognizes (case/whitespace-insensitive)
                              // would also be treated as orphan and double-rendered without
                              // its Pain/Difficulty.
                              const feedbackTitleNormSet = new Set(
                                session.exercise_feedback
                                  .map(ef => (ef.exercise_title || '').toString().trim().toLowerCase())
                                  .filter(Boolean)
                              );
                              const orphanReplacements = allReplacements.filter(rep => {
                                const inFeedback = (rep.fromIdentifier && feedbackIdSet.has(rep.fromIdentifier))
                                  || feedbackTitleNormSet.has((rep.from || '').toString().trim().toLowerCase());
                                return !inFeedback;
                              });
                              const orphanReplacementRows = orphanReplacements.map((rep, i) => {
                                const draftEx = (sessionExerciseDrafts[session.id]?.exercises || []).find(
                                  e => (rep.toIdentifier && e.identifier === rep.toIdentifier) || e.title === rep.to
                                );
                                return {
                                  // Pair the orphan dimmed-pair with the
                                  // replacement's current draft index so sidebar
                                  // reorders move both rows together in editing
                                  // mode (live, before save).
                                  sortIndex: draftEx?.index ?? (5000 + i),
                                  node: (
                                    <React.Fragment key={`orphan-rep-${session.id}-${i}`}>
                                      {renderJourneyExerciseViewCardRow(
                                        `orphan-rep-from-${session.id}-${i}`,
                                        <>
                                          <Chip
                                            label={rep.from}
                                            style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0e0e0', color: '#888', fontWeight: 'bold', textDecoration: 'line-through' }}
                                            size="small"
                                          />
                                          <Typography component="span" style={{ fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', color: '#c0392b', fontWeight: 700, marginLeft: 4 }}>Replaced</Typography>
                                          {(() => {
                                            const repFromNorm = (rep.from || '').toString().trim().toLowerCase();
                                            const fb = session.exercise_feedback.find(ef =>
                                              (rep.fromIdentifier && String(ef.exercise_id) === String(rep.fromIdentifier)) ||
                                              (ef.exercise_title || '').toString().trim().toLowerCase() === repFromNorm
                                            );
                                            if (!fb || (!fb.painLevel && !fb.difficulty)) return null;
                                            return (
                                              <div style={{ marginTop: 'clamp(4px, 0.39vw, 6px)', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
                                                  <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)', paddingLeft: 'clamp(8px, 0.65vw, 10px)' }}>Pain:</span>
                                                  <Chip
                                                    label={fb.painLevel ?? "-"}
                                                    style={{
                                                      marginRight: 'clamp(6px, 0.52vw, 8px)', marginBottom: 2, height: 'clamp(14px, 1.17vw, 18px)',
                                                      backgroundColor:
                                                        fb.painLevel === undefined || fb.painLevel === null ? 'rgba(255,243,224,0.75)'
                                                        : fb.painLevel < 35 ? 'rgba(67,234,122,0.75)'
                                                        : fb.painLevel >= 35 && fb.painLevel <= 49 ? 'rgba(255,224,102,0.75)'
                                                        : fb.painLevel >= 50 && fb.painLevel <= 60 ? 'rgba(255,179,71,0.75)'
                                                        : fb.painLevel > 70 ? 'rgba(255,77,79,0.75)'
                                                        : 'rgba(255,243,224,0.75)',
                                                      color: '#002D55', fontWeight: 'bold'
                                                    }}
                                                    size="small"
                                                  />
                                                </span>
                                                <span style={{ color: '#002D55', fontWeight: 'bold', margin: '0 clamp(6px, 0.52vw, 8px)' }}>|</span>
                                                <span style={{ display: 'flex', alignItems: 'center', marginRight: 'clamp(8px, 0.78vw, 12px)' }}>
                                                  <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)' }}>Difficulty:</span>
                                                  {getDifficultyReasonKey(fb.difficulty) === 'good' ? (
                                                    <Chip
                                                      icon={<CheckCircleIcon style={{ color: '#43ea7a', fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />}
                                                      label={"Good."}
                                                      style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(18px, 1.43vw, 22px)', backgroundColor: 'rgba(67,234,122,0)', color: '#002D55', fontWeight: 'bold' }}
                                                      size="small"
                                                    />
                                                  ) : (
                                                    <Chip
                                                      label={getDifficultyDisplayLabel(fb.difficulty)}
                                                      style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(16px, 1.3vw, 20px)', backgroundColor: 'rgba(255,179,71,0.75)', color: '#002D55', fontWeight: 'bold' }}
                                                      size="small"
                                                    />
                                                  )}
                                                </span>
                                              </div>
                                            );
                                          })()}
                                        </>,
                                        { dimmed: true }
                                      )}
                                      {renderJourneyExerciseViewCardRow(
                                        `orphan-rep-to-${session.id}-${i}`,
                                        <>
                                          <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                                            <Box style={{ flex: 1, minWidth: 0 }}>
                                              <Chip
                                                label={rep.to}
                                                style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                                size="small"
                                              />
                                            </Box>
                                            {renderInlineDraftExerciseActions(draftEx)}
                                          </Box>
                                          {draftEx && idx === 0 && (
                                            <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                              <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                                {` • ${draftEx.sets ?? 0} sets • ${draftEx.doseType === 'repetitions' || ((draftEx.repetitions ?? 0) > 0 && (draftEx.time ?? 0) === 0) ? `${draftEx.repetitions ?? 0} reps` : `${draftEx.time ?? 0}s`} • pause ${draftEx.pause ?? 0}s • ${formatExerciseScheduleLabel(draftEx)}`}
                                              </Typography>
                                            </div>
                                          )}
                                        </>,
                                        {
                                          ...getInlineDraftCardOptions(draftEx),
                                          thumbnailUrl: getJourneyExerciseThumbnailUrl(draftEx),
                                          thumbnailAlt: rep.to || 'Exercise thumbnail',
                                        }
                                      )}
                                    </React.Fragment>
                                  ),
                                };
                              });

                              // === orphan removed rows: removed exercises that had no patient feedback row ===
                              const orphanRemoved = allRemoved.filter(r => {
                                const restoredInDraft = (sessionExerciseDrafts[session.id]?.exercises || []).some(
                                  e => (r.identifier && e.identifier === r.identifier)
                                    || (e.title || '').toString().trim().toLowerCase() === (r.title || '').toString().trim().toLowerCase()
                                );
                                if (restoredInDraft) {
                                  return false;
                                }
                                const inFeedback = (r.identifier && feedbackIdSet.has(r.identifier))
                                  || feedbackTitleNormSet.has((r.title || '').toString().trim().toLowerCase());
                                return !inFeedback;
                              });
                              const orphanRemovedRows = orphanRemoved.map((r, i) => {
                                const rTitleNorm = (r.title || '').toString().trim().toLowerCase();
                                const fbR = session.exercise_feedback.find(ef =>
                                  (r.identifier && String(ef.exercise_id) === String(r.identifier)) ||
                                  (ef.exercise_title || '').toString().trim().toLowerCase() === rTitleNorm
                                );
                                return {
                                  // Removed orphans have no successor in the
                                  // active draft — keep them after every active
                                  // row but in a stable order.
                                  sortIndex: 6000 + i,
                                  node: (
                                    renderJourneyExerciseViewCardRow(
                                      `orphan-rem-${session.id}-${i}`,
                                      <>
                                        <Chip
                                          label={r.title}
                                          style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0e0e0', color: '#888', fontWeight: 'bold', textDecoration: 'line-through' }}
                                          size="small"
                                        />
                                        <Typography component="span" style={{ fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)', color: '#c0392b', fontWeight: 700, marginLeft: 4 }}>Removed</Typography>
                                        {fbR && (fbR.painLevel || fbR.difficulty) && (
                                          <div style={{ marginTop: 'clamp(4px, 0.39vw, 6px)', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', marginRight: 0 }}>
                                              <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)', paddingLeft: 'clamp(8px, 0.65vw, 10px)' }}>Pain:</span>
                                              <Chip
                                                label={fbR.painLevel ?? "-"}
                                                style={{
                                                  marginRight: 'clamp(6px, 0.52vw, 8px)', marginBottom: 2, height: 'clamp(14px, 1.17vw, 18px)',
                                                  backgroundColor:
                                                    fbR.painLevel === undefined || fbR.painLevel === null ? 'rgba(255,243,224,0.75)'
                                                    : fbR.painLevel < 35 ? 'rgba(67,234,122,0.75)'
                                                    : fbR.painLevel >= 35 && fbR.painLevel <= 49 ? 'rgba(255,224,102,0.75)'
                                                    : fbR.painLevel >= 50 && fbR.painLevel <= 60 ? 'rgba(255,179,71,0.75)'
                                                    : fbR.painLevel > 70 ? 'rgba(255,77,79,0.75)'
                                                    : 'rgba(255,243,224,0.75)',
                                                  color: '#002D55', fontWeight: 'bold'
                                                }}
                                                size="small"
                                              />
                                            </span>
                                            <span style={{ color: '#002D55', fontWeight: 'bold', margin: '0 clamp(6px, 0.52vw, 8px)' }}>|</span>
                                            <span style={{ display: 'flex', alignItems: 'center', marginRight: 'clamp(8px, 0.78vw, 12px)' }}>
                                              <span style={{ color: '#002D55', fontWeight: 'bold', marginRight: 'clamp(3px, 0.26vw, 4px)' }}>Difficulty:</span>
                                              {getDifficultyReasonKey(fbR.difficulty) === 'good' ? (
                                                <Chip
                                                  icon={<CheckCircleIcon style={{ color: '#43ea7a', fontSize: 'clamp(0.88rem, 0.95vw, 1.125rem)' }} />}
                                                  label={"Good."}
                                                  style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(18px, 1.43vw, 22px)', backgroundColor: 'rgba(67,234,122,0)', color: '#002D55', fontWeight: 'bold' }}
                                                  size="small"
                                                />
                                              ) : (
                                                <Chip
                                                  label={getDifficultyDisplayLabel(fbR.difficulty)}
                                                  style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', height: 'clamp(16px, 1.3vw, 20px)', backgroundColor: 'rgba(255,179,71,0.75)', color: '#002D55', fontWeight: 'bold' }}
                                                  size="small"
                                                />
                                              )}
                                            </span>
                                          </div>
                                        )}
                                      </>,
                                      { dimmed: true }
                                    )
                                  ),
                                };
                              });

                              // === view-mode orphan replacement rows: just the
                              // new "to" exercise (no dimmed original), so the
                              // active program still includes replacements whose
                              // "from" had no patient feedback row.
                              const viewOrphanReplacementRows = orphanReplacements.map((rep, i) => {
                                const draftEx = (sessionExerciseDrafts[session.id]?.exercises || []).find(
                                  e => (rep.toIdentifier && e.identifier === rep.toIdentifier) || e.title === rep.to
                                );
                                return {
                                  sortIndex: draftEx?.index ?? (4000 + i),
                                  node: renderJourneyExerciseViewCardRow(
                                    `view-orphan-rep-${session.id}-${i}`,
                                    <>
                                      <Chip
                                        label={rep.to}
                                        style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: '#e0f7fa', color: '#002D55', fontWeight: 'bold' }}
                                        size="small"
                                      />
                                      {draftEx && (
                                        <div style={{ marginTop: 'clamp(2px, 0.2vw, 3px)', paddingLeft: 4 }}>
                                          <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.6rem, 0.65vw, 0.75rem)' }}>
                                            {` • ${draftEx.sets ?? 0} sets • ${draftEx.doseType === 'repetitions' || ((draftEx.repetitions ?? 0) > 0 && (draftEx.time ?? 0) === 0) ? `${draftEx.repetitions ?? 0} reps` : `${draftEx.time ?? 0}s`} • pause ${draftEx.pause ?? 0}s • ${formatExerciseScheduleLabel(draftEx)}`}
                                          </Typography>
                                        </div>
                                      )}
                                    </>
                                  ),
                                };
                              });

                              // Helpers to flatten the structured row arrays
                              // ({sortIndex, node}) into renderable nodes.
                              const flattenRows = (rows) =>
                                (rows || [])
                                  .filter(r => r && r.node)
                                  .map(r => r.node);

                              // Merge every visible row from all sources and
                              // sort by the current draft index. This is used
                              // for both editing and view modes so sidebar
                              // reorders move rows live (before save), and so
                              // dimmed/replaced rows follow their replacement's
                              // current slot rather than being pinned to the
                              // original feedback group.
                              const allRowGroups = sessionIsEditing
                                ? (shouldRenderEditingCurrentProgram
                                  ? [editingCurrentProgramRows]
                                  : [feedbackRows, draftOnlyRows, addedRows, orphanReplacementRows, orphanRemovedRows])
                                : (shouldRenderReadOnlyCurrentProgram
                                  ? [readOnlyCurrentProgramRows]
                                  : [feedbackRows, draftOnlyRows, addedRows, viewOrphanReplacementRows]);
                              const mergedRows = allRowGroups
                                .flatMap(rows => (rows || []).filter(r => r && r.node))
                                .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

                              // Avoid an unused-var warning when the editing
                              // branch isn't taken.
                              void flattenRows;

                              return (
                                <>
                                  {mergedRows.map(r => r.node)}
                                  {sessionIsEditing && (
                                    renderJourneyExerciseViewCardRow(
                                      `add-exercise-placeholder-${session.id}`,
                                      renderJourneyInlineAddPlaceholderRow([
                                        {
                                          label: 'Add exercise',
                                          hint: 'Open library for this phase',
                                          onClick: () => openProgramLibraryDrawer({ type: 'program-add', sessionId: session.id }),
                                        },
                                        {
                                          label: 'Add custom exercise',
                                          hint: 'Create exercise from scratch',
                                          onClick: () => openCustomDraftExerciseDialog(session.id),
                                        },
                                      ]),
                                      {
                                        style: {
                                          background: 'transparent',
                                          border: 'none',
                                          boxShadow: 'none',
                                          padding: 0,
                                        },
                                      }
                                    )
                                  )}
                                </>
                              );
                            })()}
                            {/* Session Feedback row integrated here */}
                            {!Boolean(journeyAdviceVisibility[session.id]) && (
                            <TableRow>
                                <TableCell colSpan={responsiveColSpan(2)} style={{ border: 'none', padding: 'clamp(12px, 1.04vw, 16px) 0 0 0', background: 'none' }}>
                                  <Box style={{ background: '#f7f7fa', borderRadius: 16, padding: 'clamp(8px, 0.78vw, 12px) clamp(14px, 1.17vw, 18px)', marginTop: 'clamp(6px, 0.52vw, 8px)', boxShadow: '0 1px 4px rgba(0,45,85,0.04)' }}>
                                    <Typography variant="subtitle2" style={{ color: "#002D55", fontWeight: "bold", marginBottom: 'clamp(4px, 0.39vw, 6px)', display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)' }}>
                                    <span role="img" aria-label="feedback">💬</span> Patient feedback on session
                                  </Typography>
                                  {session.session_feedback && session.session_feedback.length > 0 ? (
                                    session.session_feedback.map((sf, sfIdx) => (
                                      <Box key={sf.id || sfIdx} mb={1} style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px, 0.52vw, 8px)', marginBottom: 'clamp(4px, 0.39vw, 6px)' }}>
                                        {sf.like_status ? (
                                          <Chip
                                            label={sf.like_status === "like" ? "👍 Liked" : sf.like_status}
                                            style={{ marginRight: 'clamp(6px, 0.52vw, 8px)', backgroundColor: "#e0f7fa", color: "#002D55", fontWeight: "bold" }}
                                            size="small"
                                          />
                                        ) : null}
                                        <span style={{ color: '#333', fontSize: 'clamp(0.76rem, 0.81vw, 0.9375rem)' }}>{sf.feedback && sf.feedback.trim() !== "" ? sf.feedback : "No additional feedback provided."}</span>
                                      </Box>
                                    ))
                                  ) : (
                                    <Typography variant="body2" color="textSecondary">No session feedback.</Typography>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      ) : "-"}
                    </TableCell>
                    {/* Journal Note Column - removed timestamp/phase, now in Exercises & Feedback */}
                  </TableRow>
                );

                sessionRowCacheRef.current[sessionKey] = {
                  sessionRef: session,
                  notesRef: notes,
                  noteIndex,
                  isEditing: sessionIsEditing,
                  editValue: sessionEditValue,
                  isSaving: sessionIsSaving,
                  isCopied: sessionIsCopied,
                  selectedAuthor: sessionSelectedAuthor,
                  newNoteAuthor: sessionNewNoteAuthor,
                  draftVersion: sessionDraftVersion,
                  prescriptionSignature: sessionPrescriptionSignature,
                  adviceDraftVersion: sessionAdviceDraftVersion,
                  issueSignature: sessionIssueSignature,
                  programSummarySignature: sessionProgramSummarySignature,
                  adviceSummarySignature: sessionAdviceSummarySignature,
                  consultationAdjustmentSignature: sessionConsultationAdjustmentSignature,
                  directAdjustmentSignature: sessionDirectAdjustmentSignature,
                  suggestionSignature: sessionSuggestionSignature,
                  baselineGoalSignature: sessionBaselineGoalSignature,
                  adviceMode: sessionAdviceMode,
                  bulkEditSignature: sessionBulkEditSignature,
                  adviceSignature: sessionAdviceSignature,
                  inlineProgramDragSignature: sessionInlineProgramDragSignature,
                  inlineAdviceDragSignature: sessionInlineAdviceDragSignature,
                  expandedAdviceSignature: sessionExpandedAdviceSignature,
                  element: rowElement,
                };

                return rowElement;
              })
            ) : (
              // Fallback: only reached when there is no selectedPatient (i.e.
              // syntheticPatientSession could not be built). The synthetic
              // session normally fills the empty-state slot with the full
              // session-row UI (toolbar, Auto-generate, baseline/goal,
              // follow-up, program editor) so users can author a patient-
              // scope journal note before any session is completed.
              <TableRow>
                <TableCell colSpan={responsiveColSpan(12)} align="center" style={{ padding: 'clamp(16px, 1.56vw, 24px)' }}>
                  <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)' }}>
                    Select a patient to view or create journal notes.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        {/* You can add a progress bar here if needed, e.g. <DiscreteSlider value={progress} /> */}
        {/* Load-more sentinel: reveals next 25 sessions when scrolled into view */}
        {visibleSessionCount < reversedSessions.length && (
          <Box
            ref={loadMoreSentinelRef}
            style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}
          >
            <CircularProgress size={28} thickness={4} style={{ color: '#002D55' }} />
          </Box>
        )}
        <Drawer
          anchor="right"
          open={programEditorOpen}
          onClose={() => setProgramEditorOpen(false)}
          PaperProps={{ style: { width: '58vw', minWidth: 380, background: '#f0f4fa', overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
        >
          <Box style={{ padding: 18, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap', marginBottom: 'clamp(8px, 0.78vw, 12px)' }}>
              <Box>
                <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.72rem, 0.8vw, 0.94rem)' }}>
                  Journal program editor
                </Typography>
                <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)' }}>
                  {programEditorContext?.phaseName || 'Selected phase'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 'clamp(6px, 0.52vw, 8px)', flexWrap: 'wrap' }}>
                {activeProgramDraft?.dirty && (
                  <Chip
                    label="Changes staged for note save"
                    size="small"
                    style={{ backgroundColor: '#fff3e0', color: '#8a5b00', fontWeight: 700 }}
                  />
                )}
                <Button
                  size="small"
                  variant="contained"
                  className={classes.confirmButton}
                  onClick={() => programEditorContext && openProgramLibraryDrawer({ type: 'program-add', sessionId: programEditorContext.sessionId })}
                >
                  Add exercise
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={() => setProgramEditorOpen(false)}
                >
                  Done
                </Button>
              </Box>
            </Box>
            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginBottom: 'clamp(10px, 0.85vw, 14px)' }}>
              Program changes are staged here and will be applied when the journal note is saved.
            </Typography>
            <Box style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
              {programEditorLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 180 }}>
                  <CircularProgress size={32} thickness={4.5} />
                </Box>
              ) : !activeProgramDraft ? (
                <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.68rem, 0.72vw, 0.84rem)' }}>
                  Select a session edit action to load this phase program.
                </Typography>
              ) : activeProgramDraft.exercises.length === 0 ? (
                <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.68rem, 0.72vw, 0.84rem)' }}>
                  No exercises are currently configured in this phase.
                </Typography>
              ) : (
                activeProgramDraft.exercises
                  .slice()
                  .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
                  .map((exercise, listIndex) => {
                  const relatedIssue = (generatedIssuesBySession[programEditorContext?.sessionId] || []).find(issue =>
                    (issue.originalIdentifier && exercise.identifier === issue.originalIdentifier) ||
                    (issue.replacementIdentifier && exercise.identifier === issue.replacementIdentifier)
                  );
                  const exerciseKey = getDraftExerciseKey(exercise);
                  const totalCount = activeProgramDraft.exercises.length;
                  const positionLabel = listIndex + 1;
                  const indexDraftKey = `${programEditorContext?.sessionId}::${exerciseKey}`;
                  const indexDraftValue = programEditorIndexDrafts[indexDraftKey];
                  const indexFieldValue = indexDraftValue !== undefined ? indexDraftValue : String(positionLabel);
                  const commitIndexEdit = () => {
                    const raw = (indexDraftValue !== undefined ? indexDraftValue : String(positionLabel)).trim();
                    setProgramEditorIndexDrafts(prev => {
                      if (!(indexDraftKey in prev)) return prev;
                      const { [indexDraftKey]: _omit, ...rest } = prev;
                      return rest;
                    });
                    if (raw === '') return;
                    const parsed = parseInt(raw, 10);
                    if (!Number.isFinite(parsed)) return;
                    const clamped = Math.max(1, Math.min(totalCount, parsed));
                    if (clamped - 1 === listIndex) return;
                    reorderProgramDraftExercise(programEditorContext.sessionId, listIndex, clamped - 1);
                  };
                  const isDragSource = programEditorDragIndex === listIndex;
                  const isDragOver = programEditorDragOverIndex === listIndex && programEditorDragIndex !== null && programEditorDragIndex !== listIndex;
                  return (
                    <Box
                      key={exerciseKey}
                      draggable
                      onDragStart={(e) => {
                        setProgramEditorDragIndex(listIndex);
                        try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(listIndex)); } catch (err) { /* noop */ }
                      }}
                      onDragOver={(e) => {
                        if (programEditorDragIndex === null) return;
                        e.preventDefault();
                        try { e.dataTransfer.dropEffect = 'move'; } catch (err) { /* noop */ }
                        if (programEditorDragOverIndex !== listIndex) setProgramEditorDragOverIndex(listIndex);
                      }}
                      onDragLeave={() => {
                        if (programEditorDragOverIndex === listIndex) setProgramEditorDragOverIndex(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = programEditorDragIndex;
                        setProgramEditorDragIndex(null);
                        setProgramEditorDragOverIndex(null);
                        if (from === null || from === listIndex) return;
                        reorderProgramDraftExercise(programEditorContext.sessionId, from, listIndex);
                      }}
                      onDragEnd={() => {
                        setProgramEditorDragIndex(null);
                        setProgramEditorDragOverIndex(null);
                      }}
                      style={{
                        background: relatedIssue ? '#fffaf0' : '#ffffff',
                        border: isDragOver ? '2px dashed #1976d2' : (relatedIssue ? '1px solid #ffd08a' : '1px solid #dce7f2'),
                        borderRadius: 14,
                        padding: 'clamp(10px, 0.9vw, 14px)',
                        marginBottom: 'clamp(8px, 0.72vw, 12px)',
                        boxShadow: '0 2px 8px rgba(0,45,85,0.05)',
                        opacity: isDragSource ? 0.5 : 1,
                        cursor: 'grab',
                      }}
                    >
                      <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                        <Box display="flex" alignItems="flex-start" style={{ flex: 1, minWidth: 220, gap: 'clamp(6px, 0.52vw, 10px)' }}>
                          <Chip
                            label={`#${positionLabel}`}
                            size="small"
                            title="Drag the card to reorder"
                            style={{ backgroundColor: '#e3eef9', color: '#002D55', fontWeight: 700, marginTop: 2 }}
                          />
                          <ExerciseImage
                            exercise={exercise}
                            style={{ width: 54, height: 42, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                            skeletonStyle={{ width: 54, height: 42, borderRadius: 8, flexShrink: 0 }}
                          />
                          <Box style={{ flex: 1, minWidth: 160 }}>
                            <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)', opacity: exercise.is_paused ? 0.55 : 1 }}>
                              {exercise.title || exercise.identifier || 'Exercise'}
                            </Typography>
                            {exercise.is_paused && (
                              <Typography style={{ color: '#8a5b00', fontSize: 'clamp(0.62rem, 0.66vw, 0.76rem)', fontWeight: 700, marginTop: 2 }}>
                                Paused
                              </Typography>
                            )}
                            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginTop: 4 }}>
                              {formatSharedExercisePrescriptionSummary(exercise).replace(/^•\s*/, '')}
                            </Typography>
                            {relatedIssue && (
                              <Typography style={{ color: '#8a5b00', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginTop: 4, fontWeight: 600 }}>
                                Related session issue: {relatedIssue.name} ({relatedIssue.reason})
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 'clamp(4px, 0.39vw, 6px)', flexWrap: 'wrap' }}>
                          <TextField
                            type="number"
                            size="small"
                            variant="outlined"
                            value={indexFieldValue}
                            inputProps={{ min: 1, max: totalCount, 'aria-label': 'Exercise position', style: { textAlign: 'center', padding: '4px 6px', width: 36, fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)' } }}
                            title={`Position 1–${totalCount}. Press Enter to apply.`}
                            onChange={(e) => {
                              const v = e.target.value;
                              setProgramEditorIndexDrafts(prev => ({ ...prev, [indexDraftKey]: v }));
                            }}
                            onBlur={commitIndexEdit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); commitIndexEdit(); e.target.blur && e.target.blur(); }
                              if (e.key === 'Escape') {
                                setProgramEditorIndexDrafts(prev => {
                                  if (!(indexDraftKey in prev)) return prev;
                                  const { [indexDraftKey]: _omit, ...rest } = prev;
                                  return rest;
                                });
                                e.target.blur && e.target.blur();
                              }
                            }}
                            // Prevent the surrounding draggable Box from
                            // intercepting clicks/drags on the input.
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            draggable={false}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.cancelButton}
                            style={{ minWidth: 'auto', padding: 4 }}
                            title={exercise.is_paused ? 'Resume exercise' : 'Pause exercise'}
                            onClick={() => {
                              setSessionExerciseDrafts(prev => {
                                const currentDraft = prev[programEditorContext.sessionId];
                                if (!currentDraft) return prev;
                                return {
                                  ...prev,
                                  [programEditorContext.sessionId]: {
                                    ...currentDraft,
                                    exercises: currentDraft.exercises.map(item => (
                                      getDraftExerciseKey(item) === exerciseKey
                                        ? { ...item, is_paused: !Boolean(item.is_paused) }
                                        : item
                                    )),
                                    dirty: true,
                                    version: (currentDraft.version || 0) + 1,
                                  },
                                };
                              });
                            }}
                          >
                            {exercise.is_paused ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.cancelButton}
                            style={{ minWidth: 'auto' }}
                            onClick={() => openDraftExerciseEditDialog(programEditorContext.sessionId, exercise)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.cancelButton}
                            style={{ minWidth: 'auto' }}
                            onClick={() => openProgramLibraryDrawer({ type: 'program-replace', sessionId: programEditorContext.sessionId, targetExerciseKey: exerciseKey })}
                          >
                            Replace
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.cancelButton}
                            style={{ minWidth: 'auto' }}
                            onClick={() => removeProgramDraftExercise(programEditorContext.sessionId, exercise)}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Drawer>
        <Drawer
          anchor="right"
          open={libraryDrawerOpen}
          onClose={() => {
            setLibraryDrawerOpen(false);
            setEditingSuggestionContext(null);
          }}
          PaperProps={{ style: { width: '75vw', background: '#f0f4fa', overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
        >
          <Box style={{ padding: 18, flex: 1, overflowY: 'auto' }}>
            <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)', marginBottom: 'clamp(8px, 0.78vw, 12px)' }}>
              {libraryDrawerTitle}
            </Typography>
            <ExerciseLibrary
              onAddExercise={handleLibraryExerciseSelect}
              exercises={availableLibraryExercises}
              initialFilters={editingSuggestionContext?.libraryInitialFilters || null}
              initialFiltersKey={editingSuggestionContext?.libraryStateKey || null}
              phoneUploadContext={{
                patientId: selectedPatient?.user_id || selectedPatient?.id || null,
                treatmentId: resolvedJourneyTreatmentId,
              }}
            />
          </Box>
        </Drawer>
        <Drawer
          anchor="right"
          open={adviceEditorOpen}
          onClose={() => {
            setAdviceEditorDragIndex(null);
            setAdviceEditorDragOverIndex(null);
            setAdviceEditorOpen(false);
          }}
          PaperProps={{ style: { width: '58vw', minWidth: 380, background: '#f0f4fa', overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
        >
          <Box style={{ padding: 18, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap', marginBottom: 'clamp(8px, 0.78vw, 12px)' }}>
              <Box>
                <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.72rem, 0.8vw, 0.94rem)' }}>
                  Journal advice editor
                </Typography>
                <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)' }}>
                  {adviceEditorContext?.phaseName || 'Selected phase'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" style={{ gap: 'clamp(6px, 0.52vw, 8px)', flexWrap: 'wrap' }}>
                {activeAdviceDraft?.dirty && (
                  <Chip
                    label="Advice changes staged for note save"
                    size="small"
                    style={{ backgroundColor: '#fff3e0', color: '#8a5b00', fontWeight: 700 }}
                  />
                )}
                <Button
                  size="small"
                  variant="contained"
                  className={classes.confirmButton}
                  onClick={() => openAdviceLibraryDrawer({ type: 'add', sessionId: adviceEditorContext?.sessionId })}
                  disabled={!adviceEditorContext || adviceEditorSaving}
                >
                  Add advice
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={() => {
                    setAdviceEditorDragIndex(null);
                    setAdviceEditorDragOverIndex(null);
                    setAdviceEditorOpen(false);
                  }}
                  disabled={adviceEditorSaving}
                >
                  Done
                </Button>
              </Box>
            </Box>
            <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginBottom: 'clamp(10px, 0.85vw, 14px)' }}>
              Review, reorder, edit, add, or remove phase advices without leaving the journal. Staged changes are persisted when the journal note is saved.
            </Typography>
            <Box style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
              {adviceEditorLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" style={{ minHeight: 180 }}>
                  <CircularProgress size={32} thickness={4.5} />
                </Box>
              ) : !activeAdviceDraft ? (
                <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.68rem, 0.72vw, 0.84rem)' }}>
                  Select a session edit action to load this phase advice set.
                </Typography>
              ) : activeAdviceDraft.advices.length === 0 ? (
                <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.68rem, 0.72vw, 0.84rem)' }}>
                  No advices are currently configured in this phase.
                </Typography>
              ) : (
                activeAdviceDraft.advices
                  .slice()
                  .sort((firstAdvice, secondAdvice) => (firstAdvice.importance ?? 0) - (secondAdvice.importance ?? 0))
                  .map((advice, listIndex) => {
                    const adviceKey = getJourneyAdviceDraftKey(advice);
                    const totalCount = activeAdviceDraft.advices.length;
                    const positionLabel = listIndex + 1;
                    const indexDraftKey = `${adviceEditorContext?.sessionId}::${adviceKey}`;
                    const indexDraftValue = adviceEditorIndexDrafts[indexDraftKey];
                    const indexFieldValue = indexDraftValue !== undefined ? indexDraftValue : String(positionLabel);
                    const commitIndexEdit = () => {
                      const raw = (indexDraftValue !== undefined ? indexDraftValue : String(positionLabel)).trim();
                      setAdviceEditorIndexDrafts((prev) => {
                        if (!(indexDraftKey in prev)) return prev;
                        const { [indexDraftKey]: _omit, ...restDrafts } = prev;
                        return restDrafts;
                      });
                      if (raw === '') return;
                      const parsed = parseInt(raw, 10);
                      if (!Number.isFinite(parsed)) return;
                      const clamped = Math.max(1, Math.min(totalCount, parsed));
                      if (clamped - 1 === listIndex) return;
                      reorderJourneyDraftAdvice(adviceEditorContext.sessionId, listIndex, clamped - 1);
                    };
                    const isDragSource = adviceEditorDragIndex === listIndex;
                    const isDragOver = adviceEditorDragOverIndex === listIndex && adviceEditorDragIndex !== null && adviceEditorDragIndex !== listIndex;

                    return (
                      <Box
                        key={adviceKey}
                        draggable
                        onDragStart={(event) => {
                          setAdviceEditorDragIndex(listIndex);
                          try { event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(listIndex)); } catch (err) { /* noop */ }
                        }}
                        onDragOver={(event) => {
                          if (adviceEditorDragIndex === null) return;
                          event.preventDefault();
                          try { event.dataTransfer.dropEffect = 'move'; } catch (err) { /* noop */ }
                          if (adviceEditorDragOverIndex !== listIndex) setAdviceEditorDragOverIndex(listIndex);
                        }}
                        onDragLeave={() => {
                          if (adviceEditorDragOverIndex === listIndex) setAdviceEditorDragOverIndex(null);
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          const from = adviceEditorDragIndex;
                          setAdviceEditorDragIndex(null);
                          setAdviceEditorDragOverIndex(null);
                          if (from === null || from === listIndex) return;
                          reorderJourneyDraftAdvice(adviceEditorContext.sessionId, from, listIndex);
                        }}
                        onDragEnd={() => {
                          setAdviceEditorDragIndex(null);
                          setAdviceEditorDragOverIndex(null);
                        }}
                        style={{
                          background: '#ffffff',
                          border: isDragOver ? '2px dashed #1976d2' : '1px solid #dce7f2',
                          borderRadius: 14,
                          padding: 'clamp(10px, 0.9vw, 14px)',
                          marginBottom: 'clamp(8px, 0.72vw, 12px)',
                          boxShadow: '0 2px 8px rgba(0,45,85,0.05)',
                          opacity: isDragSource ? 0.5 : 1,
                          cursor: 'grab',
                          userSelect: 'none',
                          WebkitUserSelect: 'none',
                        }}
                      >
                        <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 'clamp(8px, 0.78vw, 12px)', flexWrap: 'wrap' }}>
                          <Box display="flex" alignItems="flex-start" style={{ flex: 1, minWidth: 220, gap: 'clamp(6px, 0.52vw, 10px)' }}>
                            <Chip
                              label={`#${positionLabel}`}
                              size="small"
                              title="Drag the card to reorder"
                              draggable={false}
                              style={{ backgroundColor: '#e3eef9', color: '#002D55', fontWeight: 700, marginTop: 2 }}
                            />
                            <Box style={{ flex: 1, minWidth: 160 }}>
                              <Typography draggable={false} style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.72rem, 0.78vw, 0.9rem)' }}>
                                {advice.title || 'Advice'}
                              </Typography>
                              <Typography draggable={false} style={{ color: '#5f7287', fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)', marginTop: 4, whiteSpace: 'pre-line', lineHeight: 1.45 }}>
                                {advice.advice || 'No description available.'}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="center" style={{ gap: 'clamp(4px, 0.39vw, 6px)', flexWrap: 'wrap' }}>
                            <TextField
                              type="number"
                              size="small"
                              variant="outlined"
                              value={indexFieldValue}
                              inputProps={{ min: 1, max: totalCount, 'aria-label': 'Advice position', style: { textAlign: 'center', padding: '4px 6px', width: 36, fontSize: 'clamp(0.64rem, 0.68vw, 0.8rem)' } }}
                              title={`Position 1–${totalCount}. Press Enter to apply.`}
                              onChange={(event) => {
                                const value = event.target.value;
                                setAdviceEditorIndexDrafts((prev) => ({ ...prev, [indexDraftKey]: value }));
                              }}
                              onBlur={commitIndexEdit}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  event.preventDefault();
                                  commitIndexEdit();
                                  event.target.blur && event.target.blur();
                                }
                                if (event.key === 'Escape') {
                                  setAdviceEditorIndexDrafts((prev) => {
                                    if (!(indexDraftKey in prev)) return prev;
                                    const { [indexDraftKey]: _omit, ...restDrafts } = prev;
                                    return restDrafts;
                                  });
                                  event.target.blur && event.target.blur();
                                }
                              }}
                              onMouseDown={(event) => event.stopPropagation()}
                              onClick={(event) => event.stopPropagation()}
                              draggable={false}
                              className={classes.journeyKpiDialogInlineField}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              className={classes.cancelButton}
                              style={{ minWidth: 'auto' }}
                              draggable={false}
                              onClick={() => openDraftAdviceEditDialog(adviceEditorContext.sessionId, advice)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              className={classes.cancelButton}
                              style={{ minWidth: 'auto' }}
                              draggable={false}
                              onClick={() => openAdviceLibraryDrawer({ type: 'replace', sessionId: adviceEditorContext.sessionId, targetAdviceKey: adviceKey })}
                            >
                              Replace
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              className={classes.cancelButton}
                              style={{ minWidth: 'auto' }}
                              draggable={false}
                              onClick={() => removeJourneyDraftAdvice(adviceEditorContext.sessionId, advice)}
                            >
                              Remove
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
              )}
            </Box>
          </Box>
        </Drawer>
        <Drawer
          anchor="right"
          open={adviceLibraryDrawerOpen}
          onClose={() => {
            setAdviceLibraryDrawerOpen(false);
            setAdviceLibraryContext(null);
          }}
          PaperProps={{ style: { width: '75vw', background: '#f0f4fa', overflowX: 'hidden', overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
        >
          <Box style={{ padding: 18, flex: 1, overflowY: 'auto' }}>
            <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)', marginBottom: 'clamp(8px, 0.78vw, 12px)' }}>
              {adviceLibraryContext?.type === 'replace' ? 'Replace advice in journal phase' : 'Add advice to journal phase'}
            </Typography>
            <AdviceLibrary
              onAddAdvice={(advice) => {
                const sessionId = adviceLibraryContext?.sessionId || adviceEditorContext?.sessionId;
                if (!sessionId) return;
                if (adviceLibraryContext?.type === 'replace' && adviceLibraryContext?.targetAdviceKey) {
                  const targetAdvice = (sessionAdviceDrafts[sessionId]?.advices || []).find((entry) => getJourneyAdviceDraftKey(entry) === adviceLibraryContext.targetAdviceKey);
                  if (targetAdvice) {
                    replaceJourneyDraftAdvice(sessionId, targetAdvice, advice);
                  }
                } else {
                  addJourneyDraftAdvice(sessionId, advice);
                }
                setAdviceLibraryDrawerOpen(false);
                setAdviceLibraryContext(null);
              }}
              handleRemoveAdviceFrontend={() => {}}
            />
          </Box>
        </Drawer>
        <ExerciseEditDialog
          open={draftExerciseDialogOpen}
          onClose={closeDraftExerciseDialog}
          onConfirm={handleConfirmDraftExerciseEdit}
          selectedExercise={activeDraftExercise}
          editedParams={draftEditedExerciseParams}
          onParamChange={handleDraftExerciseParamChange}
          dialogTitle="Edit exercise parameters"
          libraryExercises={availableLibraryExercises}
          phoneUploadContext={{
            patientId: selectedPatient?.user_id || selectedPatient?.id || null,
            treatmentId: resolvedJourneyTreatmentId,
            allowDraftUpload: allowDraftExercisePhoneUpload,
            autoStartOnOpen: allowDraftExercisePhoneUpload,
          }}
        />
      <Dialog
        open={draftAdviceDialogOpen}
        onClose={closeDraftAdviceDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ style: { borderRadius: 18, maxWidth: 800 } }}
      >
        <DialogTitle className={classes.journeyAdviceDialogTitle}>
          <Box className={classes.journeyAdviceDialogTitleRow}>
            <Typography className={classes.journeyAdviceDialogTitleText}>Edit Advice</Typography>
            <IconButton
              onClick={() => {
                setDraftAdviceDialogOpen(false);
                setDraftAdviceEditContext(null);
              }}
              className={classes.journeyAdviceDialogCloseButton}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.journeyAdviceDialogContent}>
          {activeDraftAdvice && (
            <>
              <Typography className={classes.journeyAdviceDialogSectionLabel}>Advice details</Typography>
              <Box className={classes.journeyAdviceDialogSectionPanel}>
                <Box style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 0.85vw, 14px)' }}>
                  <TextField
                    label="Title"
                    fullWidth
                    value={draftEditedAdviceParams.title}
                    onChange={(event) => handleDraftAdviceParamChange('title', event.target.value)}
                    variant="outlined"
                    size="small"
                    className={classes.journeyKpiDialogInlineField}
                  />
                  <TextField
                    label="Advice"
                    multiline
                    minRows={4}
                    fullWidth
                    value={draftEditedAdviceParams.advice}
                    onChange={(event) => handleDraftAdviceParamChange('advice', event.target.value)}
                    variant="outlined"
                    size="small"
                    className={classes.journeyKpiDialogInlineField}
                  />
                </Box>
              </Box>
              <Typography className={classes.journeyAdviceDialogSectionLabel}>Ordering</Typography>
              <Box className={classes.journeyAdviceDialogSectionPanel} style={{ marginBottom: 0 }}>
                <TextField
                  label="Index / Importance"
                  type="number"
                  fullWidth
                  value={draftEditedAdviceParams.importance}
                  onChange={(event) => handleDraftAdviceParamChange('importance', event.target.value)}
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 1 }}
                  className={classes.journeyKpiDialogInlineField}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions className={classes.journeyAdviceDialogActions}>
          <Button
            onClick={closeDraftAdviceDialog}
            variant="outlined"
            className={classes.journeyAdviceDialogCancelButton}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirmDraftAdviceEdit} variant="contained" className={classes.journeyAdviceDialogConfirmButton}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <ClinicianFollowupDialog
        open={followupDialogOpen}
        onClose={() => setFollowupDialogOpen(false)}
        onSaved={() => setFollowupDialogOpen(false)}
        patientId={selectedPatient?.user_id || selectedPatient?.id}
        diagnosisIdentifier={diagnosis?.diagnosis || diagnosis?.diagnosis_identifier}
        treatmentId={treatmentId || selectedPatient?.treatment_id}
        initialStep={followupInitialStep}
      />
      <AddBaselineGoalDialog
        open={addCreatorDialog.open}
        mode={addCreatorDialog.mode}
        initialItem={addCreatorDialog.initialItem}
        onClose={() => setAddCreatorDialog((prev) => ({ ...prev, open: false, initialItem: null }))}
        onSaved={() => setAddCreatorDialog((prev) => ({ ...prev, open: false, initialItem: null }))}
        onStage={({ mode, edit, lineText, id }) => {
          if (!addCreatorDialog.sessionId) return;
          const sessionId = addCreatorDialog.sessionId;
          if (id) {
            updateBaselineGoalStaged(sessionId, id, { mode, edit, lineText });
          } else {
            addBaselineGoalStaged(sessionId, { mode, edit, lineText });
          }
        }}
        treatmentId={treatmentId || selectedPatient?.treatment_id}
        patientId={selectedPatient?.user_id || selectedPatient?.id}
        diagnosisIdentifier={diagnosis?.diagnosis || diagnosis?.diagnosis_identifier}
      />
      <Dialog
        open={Boolean(journeyActionMenu.open)}
        onClose={handleCancelJourneyActionMenu}
        fullWidth
        maxWidth="md"
        PaperProps={{ className: classes.journeyKpiDialogPaper }}
      >
        <DialogTitle disableTypography className={classes.journeyKpiDialogTitle}>
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ gap: 16 }}>
            <Box>
              <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(1rem, 1.05vw, 1.2rem)', lineHeight: 1.2, textAlign: 'left' }}>
                📊 Adjust Follow-up Questions
              </Typography>
              <Typography style={{ color: '#64748B', fontSize: 'clamp(0.72rem, 0.76vw, 0.86rem)', marginTop: 24, paddingLeft: 2, lineHeight: 1.45, textAlign: 'left' }}>
                Add, review, pause or resume pain baselines or functional goals with auto-update of follow-up questions.
              </Typography>
            </Box>
            <IconButton onClick={handleCancelJourneyActionMenu} style={{ color: '#64748B', padding: 6 }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={classes.journeyKpiDialogContent}>
          <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => openJourneyInlineEditor('baseline')}
                className={classes.journeyKpiDialogAddButton}
              >
                + Add baseline
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => openJourneyInlineEditor('goal')}
                className={classes.journeyKpiDialogAddButton}
              >
                + Add goal
              </Button>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <Box className={classes.journeyKpiDialogGroup}>
              <Box className={classes.journeyKpiDialogGroupHeader}>
                <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.78rem, 0.82vw, 0.92rem)' }}>
                  Baselines
                </Typography>
                <Chip
                  label={`${currentJourneyBaselineItems.length} item${currentJourneyBaselineItems.length === 1 ? '' : 's'}`}
                  size="small"
                  style={{ backgroundColor: '#ffffff', color: '#002D55', fontWeight: 700 }}
                />
              </Box>
              {journeyActionMenu.inlineEditor && journeyActionMenu.inlineEditor.mode === 'baseline' && (
                <Box style={{ padding: '14px 16px', borderBottom: currentJourneyBaselineItems.length > 0 ? '1px solid #b3afaa' : 'none', background: '#fff7ee' }}>
                  <Box display="flex" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField
                      className={classes.journeyKpiDialogInlineField}
                      select
                      label="Baseline type"
                      variant="outlined"
                      size="small"
                      value={journeyActionMenu.inlineEditor.baselineType}
                      onChange={(event) => updateJourneyInlineEditor({ baselineType: event.target.value, customMetric: event.target.value === 'custom' ? journeyActionMenu.inlineEditor.customMetric : '' })}
                      style={{ minWidth: 180 }}
                    >
                      {JOURNEY_INLINE_BASELINE_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={`journey-inline-baseline-${option.value}`} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {journeyActionMenu.inlineEditor.baselineType === 'custom' && (
                      <TextField
                        className={classes.journeyKpiDialogInlineField}
                        label="Custom metric"
                        variant="outlined"
                        size="small"
                        value={journeyActionMenu.inlineEditor.customMetric}
                        onChange={(event) => updateJourneyInlineEditor({ customMetric: event.target.value })}
                        style={{ minWidth: 220, flex: 1 }}
                      />
                    )}
                    <TextField
                      className={classes.journeyKpiDialogInlineField}
                      label={journeyActionMenu.inlineEditor.baselineType === 'frequency' ? 'Score (0-7)' : 'Score (0-100)'}
                      variant="outlined"
                      size="small"
                      type="number"
                      value={journeyActionMenu.inlineEditor.score}
                      onChange={(event) => updateJourneyInlineEditor({ score: event.target.value })}
                      inputProps={{ min: 0, max: journeyActionMenu.inlineEditor.baselineType === 'frequency' ? 7 : 100 }}
                      style={{ width: 150 }}
                    />
                  </Box>
                  {journeyActionMenu.inlineEditor.error && (
                    <Typography style={{ color: '#c45100', fontSize: 'clamp(0.66rem, 0.68vw, 0.78rem)', marginTop: 8 }}>
                      {journeyActionMenu.inlineEditor.error}
                    </Typography>
                  )}
                  <Box display="flex" justifyContent="flex-end" style={{ gap: 8, marginTop: 10 }}>
                    <Button variant="outlined" className={classes.cancelButton} onClick={closeJourneyInlineEditor}>
                      Cancel
                    </Button>
                    <Button variant="contained" className={classes.confirmButton} onClick={stageJourneyInlineEditorDraft}>
                      {journeyActionMenu.inlineEditor.editingItemId ? 'Update draft' : 'Add to draft'}
                    </Button>
                  </Box>
                </Box>
              )}
              {currentJourneyBaselineItems.length === 0 ? (
                <Box style={{ padding: '16px 18px' }}>
                  <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.68rem, 0.72vw, 0.84rem)' }}>
                    No baselines are available for this treatment yet.
                  </Typography>
                </Box>
              ) : currentJourneyBaselineItems.map((item, index) => {
                const isPaused = getJourneyEffectivePausedState(journeyActionMenu.sessionId, item);
                const isDraftItem = Boolean(item.is_draft_added);
                return (
                  <Box
                    key={`journey-baseline-${item.id}`}
                    className={clsx(classes.journeyKpiDialogRow, {
                      [classes.journeyKpiDialogDraftRow]: isDraftItem,
                      [classes.journeyKpiDialogRowLast]: index === currentJourneyBaselineItems.length - 1,
                    })}
                  >
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.76rem, 0.8vw, 0.9rem)', lineHeight: 1.3 }}>
                        {getJourneyKpiMenuLabel(item)}
                      </Typography>
                      <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap', marginTop: 5 }}>
                        <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.66rem, 0.7vw, 0.8rem)' }}>
                          {getJourneyKpiMetaText(item)}
                        </Typography>
                        <Chip
                          label={isDraftItem ? 'Draft' : `Status: ${isPaused ? 'Inactive' : 'Active'}`}
                          size="small"
                          className={isDraftItem ? classes.journeyKpiDialogDraftChip : undefined}
                          style={isDraftItem ? undefined : {
                            backgroundColor: isPaused ? '#fff0e4' : '#e9f6ee',
                            color: isPaused ? '#b05b00' : '#23633b',
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      {isDraftItem && (
                        <>
                          <Tooltip title="Edit draft baseline">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const stagedItem = (baselineGoalStaged[journeyActionMenu.sessionId] || []).find((entry) => entry && entry.id === item.stagedItemId);
                                if (!stagedItem) return;
                                openJourneyInlineEditor('baseline', stagedItem);
                              }}
                              style={{ color: '#fb741a' }}
                            >
                              <EditIcon style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove draft baseline">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveJourneyDraftItem(journeyActionMenu.sessionId, item.stagedItemId)}
                              style={{ color: '#fb741a' }}
                            >
                              <CloseIcon style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={isDraftItem ? 'Saved on next journal save' : (isPaused ? 'Resume baseline' : 'Pause baseline')}>
                        <IconButton
                          className={clsx(classes.journeyKpiDialogToggleButton, {
                            [classes.journeyKpiDialogToggleButtonPaused]: isPaused,
                            [classes.journeyKpiDialogDraftToggleButton]: isDraftItem,
                          })}
                          onClick={() => stageJourneyFollowupToggleItem(journeyActionMenu.sessionId, item, !isPaused, { closeMenu: false })}
                          disabled={isDraftItem}
                        >
                          {isPaused ? <PlayArrowIcon style={{ fontSize: 22 }} /> : <PauseIcon style={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Box className={classes.journeyKpiDialogGroup}>
              <Box className={classes.journeyKpiDialogGroupHeader}>
                <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.78rem, 0.82vw, 0.92rem)' }}>
                  Goals
                </Typography>
                <Chip
                  label={`${currentJourneyGoalItems.length} item${currentJourneyGoalItems.length === 1 ? '' : 's'}`}
                  size="small"
                  style={{ backgroundColor: '#ffffff', color: '#002D55', fontWeight: 700 }}
                />
              </Box>
              {journeyActionMenu.inlineEditor && journeyActionMenu.inlineEditor.mode === 'goal' && (
                <Box style={{ padding: '14px 16px', borderBottom: currentJourneyGoalItems.length > 0 ? '1px solid #ffd7a8' : 'none', background: '#fff7ee' }}>
                  <Box display="flex" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <TextField
                      className={classes.journeyKpiDialogInlineField}
                      label="Goal description"
                      variant="outlined"
                      size="small"
                      value={journeyActionMenu.inlineEditor.goalText}
                      onChange={(event) => updateJourneyInlineEditor({ goalText: event.target.value })}
                      style={{ minWidth: 280, flex: 1 }}
                    />
                    <TextField
                      className={classes.journeyKpiDialogInlineField}
                      label="Score (0-100)"
                      variant="outlined"
                      size="small"
                      type="number"
                      value={journeyActionMenu.inlineEditor.score}
                      onChange={(event) => updateJourneyInlineEditor({ score: event.target.value })}
                      inputProps={{ min: 0, max: 100 }}
                      style={{ width: 150 }}
                    />
                  </Box>
                  {journeyActionMenu.inlineEditor.error && (
                    <Typography style={{ color: '#c45100', fontSize: 'clamp(0.66rem, 0.68vw, 0.78rem)', marginTop: 8 }}>
                      {journeyActionMenu.inlineEditor.error}
                    </Typography>
                  )}
                  <Box display="flex" justifyContent="flex-end" style={{ gap: 8, marginTop: 10 }}>
                    <Button variant="outlined" className={classes.cancelButton} onClick={closeJourneyInlineEditor}>
                      Cancel
                    </Button>
                    <Button variant="contained" className={classes.confirmButton} onClick={stageJourneyInlineEditorDraft}>
                      {journeyActionMenu.inlineEditor.editingItemId ? 'Update draft' : 'Add to draft'}
                    </Button>
                  </Box>
                </Box>
              )}
              {currentJourneyGoalItems.length === 0 ? (
                <Box style={{ padding: '16px 18px' }}>
                  <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.68rem, 0.72vw, 0.84rem)' }}>
                    No goals are available for this treatment yet.
                  </Typography>
                </Box>
              ) : currentJourneyGoalItems.map((item, index) => {
                const isPaused = getJourneyEffectivePausedState(journeyActionMenu.sessionId, item);
                const isDraftItem = Boolean(item.is_draft_added);
                return (
                  <Box
                    key={`journey-goal-${item.id}`}
                    className={clsx(classes.journeyKpiDialogRow, {
                      [classes.journeyKpiDialogDraftRow]: isDraftItem,
                      [classes.journeyKpiDialogRowLast]: index === currentJourneyGoalItems.length - 1,
                    })}
                  >
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Typography style={{ color: '#002D55', fontWeight: 700, fontSize: 'clamp(0.76rem, 0.8vw, 0.9rem)', lineHeight: 1.3 }}>
                        {getJourneyKpiMenuLabel(item)}
                      </Typography>
                      <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap', marginTop: 5 }}>
                        <Typography style={{ color: '#5f7287', fontSize: 'clamp(0.66rem, 0.7vw, 0.8rem)' }}>
                          {getJourneyKpiMetaText(item)}
                        </Typography>
                        <Chip
                          label={isDraftItem ? 'Draft' : `Status: ${isPaused ? 'Inactive' : 'Active'}`}
                          size="small"
                          className={isDraftItem ? classes.journeyKpiDialogDraftChip : undefined}
                          style={isDraftItem ? undefined : {
                            backgroundColor: isPaused ? '#fff0e4' : '#e9f6ee',
                            color: isPaused ? '#b05b00' : '#23633b',
                            fontWeight: 700,
                          }}
                        />
                      </Box>
                    </Box>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      {isDraftItem && (
                        <>
                          <Tooltip title="Edit draft goal">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const stagedItem = (baselineGoalStaged[journeyActionMenu.sessionId] || []).find((entry) => entry && entry.id === item.stagedItemId);
                                if (!stagedItem) return;
                                openJourneyInlineEditor('goal', stagedItem);
                              }}
                              style={{ color: '#fb741a' }}
                            >
                              <EditIcon style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove draft goal">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveJourneyDraftItem(journeyActionMenu.sessionId, item.stagedItemId)}
                              style={{ color: '#fb741a' }}
                            >
                              <CloseIcon style={{ fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title={isDraftItem ? 'Saved on next journal save' : (isPaused ? 'Resume goal' : 'Pause goal')}>
                        <IconButton
                          className={clsx(classes.journeyKpiDialogToggleButton, {
                            [classes.journeyKpiDialogToggleButtonPaused]: isPaused,
                            [classes.journeyKpiDialogDraftToggleButton]: isDraftItem,
                          })}
                          onClick={() => stageJourneyFollowupToggleItem(journeyActionMenu.sessionId, item, !isPaused, { closeMenu: false })}
                          disabled={isDraftItem}
                        >
                          {isPaused ? <PlayArrowIcon style={{ fontSize: 22 }} /> : <PauseIcon style={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px', gap: 8, backgroundColor: '#f7faff' }}>
          <Button
            onClick={handleCancelJourneyActionMenu}
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Box flex={1} />
          <Button
            onClick={handleConfirmJourneyActionMenu}
            className={classes.confirmButton}
          >
            Save KPI draft
          </Button>
        </DialogActions>
      </Dialog>
      <ExerciseTrendDialog
        open={exerciseTrendDialogOpen}
        onClose={() => setExerciseTrendDialogOpen(false)}
        sessions={reversedSessions}
        assessmentDate={assessmentDate}
        plannedSessionsPerDayOverall={plannedSessionsPerDayOverall}
        normalizeExerciseKey={normalizeExerciseKey}
        getDifficultyDisplayLabel={getDifficultyDisplayLabel}
      />
    </Card>
    </>
  );
};

SelectedPatientJourney.propTypes = {
  className: PropTypes.string,
  assessmentDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string])
};

export default SelectedPatientJourney;
